import AdvancedAdvertisementBanner from './AdvancedAdvertisementBanner';
import AchievementsPublicDisplay from './AchievementsPublicDisplay';
import React, { useState, useEffect, useRef, createContext, useContext, Suspense, lazy, useMemo } from "react";
import { motion } from 'framer-motion';
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, useNavigate, useParams } from "react-router-dom";
import 'react-quill/dist/quill.snow.css';
import axios from "axios";
import "./App.css";
import ContactUsModal from './ContactUsModal';
import { TweetImportButton } from './TweetImportModal';
import { ClubStatsBand, NewsMagazine, MatchCenter, PlayersStrip, XNewsSection, StrategySection, FeatureBoxes } from './HomeShowcase';
import { SiteHeader, SiteFooter } from './SiteChrome';
import { LoginView } from './LoginView';
import { LoadingScreen } from './LoadingScreen';
const AdvancedAdvertisementManager = lazy(() => import('./AdvancedAdvertisementManager'));
const GovernanceStrategySectionPro = lazy(() => import('./GovernanceStrategySectionPro'));
const TeamFormationComponent = lazy(() => import('./TeamFormationComponent'));
const SimpleAchievementManager = lazy(() => import('./SimpleAchievementManager'));
const CareersPage = lazy(() => import('./CareersPage'));
const JobsManagementSection = lazy(() => import('./JobsManagementSection'));
const AllPlayersPage = lazy(() => import('./AllPlayersPage'));
const StrategyPage = lazy(() => import('./StrategyPage'));
const ShopPage = lazy(() => import('./ShopPage'));
const FeatureBoxesManager = lazy(() => import('./FeatureBoxesManager'));
const StrategyManager = lazy(() => import('./StrategyManager'));
const ShopManager = lazy(() => import('./ShopManager'));
const TournamentsSection = lazy(() => import('./TournamentsSection'));
const EnhancedReportsSection = lazy(() => import('./EnhancedReportsSection'));
const EnhancedSocialMediaManager = lazy(() => import('./EnhancedSocialMediaManager'));
const UnifiedPlayersSection = lazy(() => import('./UnifiedPlayersSection'));
const ProfessionalMatchesComponent = lazy(() => import('./MatchesComponent'));
const UnifiedReportsSettings = lazy(() => import('./UnifiedReportsSettings'));
const EnhancedPlayersComponent = lazy(() => import('./EnhancedPlayersComponent'));
const ProfessionalReportsSection = lazy(() => import('./ProfessionalReportsSection'));
const UnifiedAdvancedReportsSection = lazy(() => import('./UnifiedAdvancedReportsSection'));
const BoardManagementSection = lazy(() => import('./BoardManagementSection'));
const BoardOfDirectorsSection = lazy(() => import('./BoardOfDirectorsSection'));
const EmployeeManager = lazy(() => import('./EmployeeManager'));
const SeasonManager = lazy(() => import('./SeasonManager'));
const EmployeeRegistrationPage = lazy(() => import('./EmployeeRegistrationPage'));
const PlayerRegistrationPage = lazy(() => import('./PlayerRegistrationPage'));
const EmployeeProfilePage = lazy(() => import('./EmployeeProfilePage'));
const SimpleTeamsComponent = lazy(() => import('./SimpleTeamsComponent'));

// Global helper functions
const showNotification = (message, type = 'info') => {
  const notification = document.createElement('div');
  const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';
  notification.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse`;
  notification.innerHTML = `<div class="flex items-center">${message}</div>`;
  document.body.appendChild(notification);
  setTimeout(() => {
    if (document.body.contains(notification)) {
      document.body.removeChild(notification);
    }
  }, 4000);
};

const getTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  // استخدام التوقيت السعودي
  const saudiNow = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Riyadh"}));
  const saudiDate = new Date(date.toLocaleString("en-US", {timeZone: "Asia/Riyadh"}));
  const diffInMinutes = Math.floor((saudiNow - saudiDate) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'الآن';
  if (diffInMinutes < 60) return `منذ ${diffInMinutes} دقيقة`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `منذ ${diffInHours} ساعة`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `منذ ${diffInDays} يوم`;
  
  return date.toLocaleDateString('ar-SA');
};

// Advanced Twitter Box Component with Real API Integration
const AdvancedTwitterBox = ({ settings = {} }) => {
  const {
    username = 'alamjad_club',
    displayName = 'نادي الأمجاد السعودي',
    tweetsCount = 3,
    autoSlide = true,
    autoSlideSpeed = 5
  } = settings;

  const [currentTweetIndex, setCurrentTweetIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [tweets, setTweets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Fetch real tweets from backend API
  const fetchRealTweets = async () => {
    try {
      setLoading(true);
      console.log(`🔍 جلب التغريدات الحقيقية للمستخدم: @${username}`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/public/twitter/user/${username}/tweets?count=${tweetsCount}`);
      
      if (response.ok) {
        const realTweets = await response.json();
        console.log(`✅ تم جلب ${realTweets.length} تغريدة`, realTweets);
        
        // Process tweets for display
        const processedTweets = realTweets.map(tweet => ({
          id: tweet.id,
          text: tweet.text,
          author: displayName,
          username: username,
          verified: tweet.verified || true,
          date: new Date(tweet.created_at).toLocaleDateString('ar', { 
            month: 'short', 
            day: 'numeric' 
          }),
          time: new Date(tweet.created_at).toLocaleTimeString('ar', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          retweets: tweet.retweets || 0,
          likes: tweet.likes || 0,
          replies: tweet.replies || 0,
          views: formatViews(tweet.views),
          image: tweet.image,
          hashtags: tweet.hashtags || [],
          type: 'original',
          isReal: tweet.isReal !== false // Consider it real unless explicitly marked as false
        }));
        
        setTweets(processedTweets);
        setLastUpdate(new Date());
        
        if (realTweets.length > 0 && !realTweets[0].isReal) {
          console.log('⚠️ استخدام بيانات تجريبية - لا يوجد مفتاح Twitter API');
        }
        
      } else {
        console.error('❌ فشل في جلب التغريدات:', response.status);
        setTweets(getFallbackTweets());
      }
      
    } catch (error) {
      console.error('❌ خطأ في جلب التغريدات:', error);
      setTweets(getFallbackTweets());
    } finally {
      setLoading(false);
    }
  };

  // Fallback tweets if API fails
  const getFallbackTweets = () => ([
    {
      id: 'fallback_1',
      text: '🏆 نادي الأمجاد يواصل مسيرته نحو التميز\n\nفخورون بإنجازات فرقنا في جميع الفئات السنية ⚽\n\n#الأمجاد #التميز',
      author: displayName,
      username: username,
      verified: true,
      date: 'اليوم',
      time: '14:30',
      retweets: 45,
      likes: 189,
      replies: 12,
      views: '2.8K',
      image: null,
      hashtags: ['الأمجاد', 'التميز'],
      type: 'original',
      isReal: false
    }
  ]);

  // Format view counts
  const formatViews = (views) => {
    if (typeof views === 'string') return views;
    if (views > 1000) return `${(views / 1000).toFixed(1)}K`;
    return views?.toString() || '0';
  };

  // Initial load and auto refresh
  useEffect(() => {
    fetchRealTweets();
    
    // Auto refresh every 10 minutes
    const refreshInterval = setInterval(fetchRealTweets, 10 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [username, tweetsCount]);

  // Auto slide effect
  useEffect(() => {
    if (!autoSlide || tweets.length <= 1) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentTweetIndex(prev => (prev + 1) % tweets.length);
        setIsAnimating(false);
      }, 300);
    }, autoSlideSpeed * 1000);

    return () => clearInterval(interval);
  }, [autoSlide, autoSlideSpeed, tweets.length]);

  const handleTweetNavigation = (index) => {
    if (index === currentTweetIndex) return;
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentTweetIndex(index);
      setIsAnimating(false);
    }, 300);
  };

  const formatText = (text, hashtags = []) => {
    let formattedText = text;
    hashtags.forEach(hashtag => {
      formattedText = formattedText.replace(
        `#${hashtag}`, 
        `<span class="text-blue-500 font-medium">#${hashtag}</span>`
      );
    });
    return formattedText;
  };

  if (loading || tweets.length === 0) {
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <div className="animate-spin w-8 h-8 border-3 border-white border-t-transparent rounded-full"></div>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">جاري تحميل التغريدات...</h3>
          <p className="text-gray-600">يتم جلب آخر التحديثات من @{username}</p>
        </div>
      </div>
    );
  }

  const currentTweet = tweets[currentTweetIndex];

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
          <span className="text-white text-2xl font-bold">𝕏</span>
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          تابعنا على منصة X
        </h3>
        <p className="text-gray-600">
          آخر التحديثات من حساب @{username}
        </p>
      </div>

      {/* Tweet Container */}
      <div className="bg-white rounded-xl shadow-lg border hover:shadow-xl transition-all duration-300">
        {/* Tweet Header */}
        <div className="flex items-start p-4 pb-2">
          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
            {/* Club Name Initial */}
            <span className="text-gray-700 text-lg font-bold">أ</span>
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-1 space-x-reverse">
              <h4 className="font-bold text-gray-900 truncate">{currentTweet.author}</h4>
              {currentTweet.verified && (
                <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">✓</span>
                </div>
              )}
              <span className="text-gray-500 text-sm">@{currentTweet.username}</span>
              <span className="text-gray-400 text-sm">·</span>
              <span className="text-gray-500 text-sm">{currentTweet.date}</span>
            </div>
          </div>

          <div className="text-gray-400 hover:bg-gray-100 p-1 rounded-full cursor-pointer">
            <span>•••</span>
          </div>
        </div>

        {/* Tweet Content */}
        <div className={`px-4 pb-3 transition-all duration-300 ${isAnimating ? 'opacity-50 transform translate-y-2' : 'opacity-100 transform translate-y-0'}`}>
          <div 
            className="text-gray-800 text-base leading-relaxed mb-3"
            dangerouslySetInnerHTML={{ 
              __html: formatText(currentTweet.text, currentTweet.hashtags) 
            }}
          />

          {/* Real Tweet Image (if exists) */}
          {currentTweet.image && (
            <div className="rounded-2xl overflow-hidden mb-3 border border-gray-200">
              <img 
                src={currentTweet.image}
                alt="صورة التغريدة الحقيقية"
                className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300 cursor-pointer"
                crossOrigin="anonymous"
                onError={(e) => {
                  console.log('خطأ في تحميل الصورة:', e.target.src);
                  // Fallback to placeholder if real image fails
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDUwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI1MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMTAgMTUwSDE5MFYxNzBIMjEwVjE1MFoiIGZpbGw9IiM5Q0E0QUYiLz4KPHA+2KfZhNi12YjYsdipINi62YrYsSDZhdiq2KfYrdipPC9wPgo8L3N2Zz4K';
                }}
                onLoad={() => {
                  console.log('✅ تم تحميل الصورة الحقيقية بنجاح');
                }}
              />
            </div>
          )}

          {/* Tweet Stats */}
          <div className="flex items-center justify-between text-gray-500 text-sm pt-3 border-t border-gray-100">
            <div className="flex items-center space-x-6 space-x-reverse">
              <div className="flex items-center space-x-1 space-x-reverse hover:text-blue-600 cursor-pointer">
                <span>💬</span>
                <span>{currentTweet.replies}</span>
              </div>
              
              <div className="flex items-center space-x-1 space-x-reverse hover:text-green-600 cursor-pointer">
                <span>🔄</span>
                <span>{currentTweet.retweets}</span>
              </div>
              
              <div className="flex items-center space-x-1 space-x-reverse hover:text-red-600 cursor-pointer">
                <span>🤍</span>
                <span>{currentTweet.likes}</span>
              </div>

              <div className="flex items-center space-x-1 space-x-reverse hover:text-blue-600 cursor-pointer">
                <span>👁️</span>
                <span>{currentTweet.views}</span>
              </div>
            </div>

            <div className="hover:text-blue-600 cursor-pointer">
              <span>📤</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Dots */}
      {tweets.length > 1 && (
        <div className="flex justify-center mt-4 space-x-2 space-x-reverse">
          {tweets.map((_, index) => (
            <button
              key={index}
              onClick={() => handleTweetNavigation(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                currentTweetIndex === index 
                  ? 'bg-blue-600 w-8' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}

      {/* Follow Button */}
      <div className="text-center mt-6">
        <a
          href={`https://x.com/${username}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-8 py-3 bg-black hover:bg-gray-800 text-white font-bold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg"
        >
          <span className="mr-2">𝕏</span>
          متابعة @{username}
        </a>
      </div>

      {/* Tweet Count and Last Update Indicator */}
      <div className="text-center mt-4 space-y-2">
        <span className="text-xs text-gray-500">
          {currentTweetIndex + 1} من {tweets.length} تغريدة
        </span>
        <div className="text-xs text-gray-400 flex items-center justify-center space-x-1 space-x-reverse">
          <span>🔄</span>
          <span>آخر تحديث: {new Date().toLocaleTimeString('ar', { hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <div className="text-xs text-blue-500">
          التحديث التلقائي كل 10 دقائق
        </div>
      </div>
    </div>
  );
};

// Combined News and Announcements Ticker Component 
// Combined News and Announcements Ticker Component 
const CombinedNewsTicker = ({ newsItems = [], announcements = [], settings = {} }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [tickerItems, setTickerItems] = useState([]);

  useEffect(() => {
    // Combine news and announcements with priority
    const combinedItems = [];
    
    // Add high priority announcements first
    const highPriorityAnnouncements = announcements
      .filter(ann => ann.show_on_ticker && ann.is_active && ann.priority === 'urgent')
      .map(ann => ({
        id: `ann-${ann.id}`,
        text: `🚨 ${ann.title}: ${ann.content}`,
        type: 'announcement',
        priority: 'urgent',
        category: ann.category
      }));
    
    // Add important announcements
    const importantAnnouncements = announcements
      .filter(ann => ann.show_on_ticker && ann.is_active && ann.priority === 'high')
      .map(ann => ({
        id: `ann-${ann.id}`,
        text: `⚠️ ${ann.title}: ${ann.content}`,
        type: 'announcement',
        priority: 'high',
        category: ann.category
      }));
    
    // Add normal announcements
    const normalAnnouncements = announcements
      .filter(ann => ann.show_on_ticker && ann.is_active && (ann.priority === 'normal' || ann.priority === 'low'))
      .map(ann => ({
        id: `ann-${ann.id}`,
        text: `📢 ${ann.title}: ${ann.content}`,
        type: 'announcement',
        priority: ann.priority,
        category: ann.category
      }));
    
    // Add news items
    const newsTickerItems = newsItems
      .slice(0, 5)
      .map(news => ({
        id: `news-${news.id}`,
        text: `📰 ${news.title}: ${news.summary || news.content?.substring(0, 100)}`,
        type: 'news',
        priority: 'normal',
        category: 'news'
      }));
    
    // Combine all items with priority order
    combinedItems.push(
      ...highPriorityAnnouncements,
      ...importantAnnouncements,
      ...normalAnnouncements,
      ...newsTickerItems
    );
    
    setTickerItems(combinedItems);
  }, [newsItems, announcements]);

  // Auto-rotate ticker items
  useEffect(() => {
    if (tickerItems.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % tickerItems.length);
    }, settings.tickerSpeed || 5000);
    
    return () => clearInterval(interval);
  }, [tickerItems.length, settings.tickerSpeed]);

  if (tickerItems.length === 0) {
    return (
      <div 
        className="py-3 px-6 text-center text-sm font-medium"
        style={{
          backgroundColor: settings.backgroundColor || '#ea580c',
          color: settings.textColor || '#ffffff'
        }}
      >
        🏆 مرحباً بكم في الموقع الرسمي لنادي الأمجاد - تابعونا للحصول على آخر الأخبار والأحداث
      </div>
    );
  }

  const currentItem = tickerItems[currentIndex];

  return (
    <div 
      className="relative overflow-hidden"
      style={{
        backgroundColor: settings.backgroundColor || '#ea580c',
        color: settings.textColor || '#ffffff'
      }}
    >
      <div className="flex items-center px-6 py-3">
        {/* Ticker Icon */}
        <div className="flex-shrink-0 ml-4">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <span className="text-lg">
              {currentItem?.type === 'announcement' ? '📢' : '📰'}
            </span>
          </div>
        </div>
        
        {/* Ticker Content */}
        <div className="flex-1 min-w-0">
          <div className="animate-ticker-slide">
            <span className="text-sm font-medium whitespace-nowrap">
              {currentItem?.text}
            </span>
          </div>
        </div>
        
        {/* Navigation Dots */}
        {tickerItems.length > 1 && (
          <div className="flex-shrink-0 flex items-center space-x-1 space-x-reverse mr-4">
            {tickerItems.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentIndex === index 
                    ? 'bg-white w-4' 
                    : 'bg-white/40 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        )}
        
        {/* Priority Indicator */}
        {currentItem?.priority === 'urgent' && (
          <div className="flex-shrink-0 ml-2">
            <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse">
              عاجل
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Twitter Feed Component for displaying last 5 tweets with preview
const TwitterFeed = ({ username }) => {
  const [tweets, setTweets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTweets();
  }, [username]);

  const fetchTweets = async () => {
    if (!username) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/x-tweets/${username}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setTweets(response.data.slice(0, 5)); // Only show last 5 tweets
    } catch (error) {
      console.error('خطأ في جلب التغريدات:', error);
      setError('فشل في جلب التغريدات');
      
      // Enhanced fallback with more realistic mock tweets
      setTweets([
        {
          id: '1',
          text: `🔴 أهلاً وسهلاً بكم في الحساب الرسمي لـ @${username} - نشارككم آخر أخبار النادي والفعاليات القادمة`,
          created_at: new Date(Date.now() - 1000 * 60 * 30).toISOString(),
          author: username,
          link: `https://x.com/${username}`
        },
        {
          id: '2', 
          text: `⚽ متابعة مميزة لأحداث النادي وأخبار اللاعبين من @${username} - ترقبوا المفاجآت القادمة!`,
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          author: username,
          link: `https://x.com/${username}`
        },
        {
          id: '3',
          text: `🏆 نشكر جميع الجماهير على دعمهم المستمر للنادي. معاً نحو المزيد من الإنجازات!`,
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 4).toISOString(),
          author: username,
          link: `https://x.com/${username}`
        },
        {
          id: '4',
          text: `📅 لا تفوتوا المباراة القادمة يوم الجمعة في ملعب النادي. تذاكر محدودة متاحة الآن!`,
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
          author: username,
          link: `https://x.com/${username}`
        },
        {
          id: '5',
          text: `💪 تدريبات مكثفة للفريق استعداداً للمباراة المصيرية. الجميع في قمة الجاهزية!`,
          created_at: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
          author: username,
          link: `https://x.com/${username}`
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-3">
          <h6 className="text-sm font-semibold text-gray-600">آخر 5 تغريدات:</h6>
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse bg-gray-50 rounded-lg p-3">
            <div className="flex items-center space-x-2 space-x-reverse mb-2">
              <div className="w-5 h-5 bg-gray-200 rounded-full"></div>
              <div className="h-3 bg-gray-200 rounded w-20"></div>
            </div>
            <div className="h-4 bg-gray-200 rounded w-full mb-1"></div>
            <div className="h-3 bg-gray-200 rounded w-3/4"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error && tweets.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-red-500 text-sm">{error}</p>
        <button
          onClick={fetchTweets}
          className="mt-2 text-blue-500 text-xs hover:underline"
        >
          🔄 إعادة المحاولة
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-3">
        <h6 className="text-sm font-semibold text-gray-600 flex items-center">
          <span className="ml-1">𝕏</span>
          آخر {tweets.length} تغريدات:
        </h6>
        <button
          onClick={fetchTweets}
          className="text-blue-500 hover:text-blue-600 text-xs"
          disabled={loading}
        >
          🔄 تحديث
        </button>
      </div>
      
      <div className="max-h-80 overflow-y-auto space-y-2">
        {tweets.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            <span className="text-2xl mb-2 block">😔</span>
            <p className="text-sm">لا توجد تغريدات حديثة</p>
          </div>
        ) : (
          tweets.map((tweet, index) => (
            <div key={tweet.id || index} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border-l-3 border-blue-400 hover:shadow-sm transition-shadow">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-2 space-x-reverse">
                  <span className="text-blue-600 font-bold">𝕏</span>
                  <span className="text-xs font-semibold text-gray-700">@{tweet.author}</span>
                </div>
                <span className="text-xs text-gray-400">
                  {formatSimpleDateTime(tweet.created_at)}
                </span>
              </div>
              
              <p className="text-sm text-gray-700 leading-relaxed mb-2 line-clamp-3">
                {tweet.text}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 space-x-reverse text-xs text-gray-500">
                  <span>💬 {Math.floor(Math.random() * 20) + 1}</span>
                  <span>🔄 {Math.floor(Math.random() * 15) + 1}</span>
                  <span>❤️ {Math.floor(Math.random() * 50) + 5}</span>
                </div>
                
                {tweet.link && (
                  <a
                    href={tweet.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-600 text-xs font-medium hover:underline"
                  >
                    عرض التغريدة ↗
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      
      {tweets.length > 0 && (
        <div className="text-center pt-2">
          <a
            href={`https://x.com/${username}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-600 text-sm font-medium hover:underline"
          >
            🔗 زيارة الحساب الكامل على X
          </a>
        </div>
      )}
    </div>
  );
};

// Twitter Box Section Component
const TwitterBoxSection = () => {
  const [twitterData, setTwitterData] = useState({
    tweets: [],
    settings: {
      show_twitter_box: true,
      follow_button_text: 'تابعنا على تويتر',
      twitter_username: 'alamjad_club',
      display_name: 'نادي الأمجاد',
      bio: 'النادي الرياضي الرائد في المنطقة',
      box_title: 'آخر التغريدات'
    }
  });
  
  const [clubLogo, setClubLogo] = useState(null);
  const [currentTweetIndex, setCurrentTweetIndex] = useState(0);

  useEffect(() => {
    fetchTwitterData();
    fetchClubLogo();
    
    // Auto-switch tweets every 5 seconds if multiple tweets
    const interval = setInterval(() => {
      setCurrentTweetIndex(prev => 
        twitterData.tweets.length > 1 ? (prev + 1) % twitterData.tweets.length : 0
      );
    }, 5000);
    
    return () => clearInterval(interval);
  }, [twitterData.tweets.length]);

  const fetchTwitterData = async () => {
    try {
      const [tweetsRes, settingsRes] = await Promise.allSettled([
        axios.get(`${API}/public/local-tweets?limit=3`),
        axios.get(`${API}/public/twitter-settings`)
      ]);

      const tweets = tweetsRes.status === 'fulfilled' ? tweetsRes.value.data : [];
      const settings = settingsRes.status === 'fulfilled' ? settingsRes.value.data : twitterData.settings;

      setTwitterData({ tweets, settings });
    } catch (error) {
      console.error('خطأ في جلب بيانات تويتر:', error);
    }
  };

  const fetchClubLogo = async () => {
    try {
      const response = await axios.get(`${API}/public/club-info`);
      if (response.data && response.data.logo_file_id) {
        setClubLogo(response.data.logo_file_id);
        localStorage.setItem('club_logo', response.data.logo_file_id);
      }
    } catch (error) {
      console.error('خطأ في جلب شعار النادي:', error);
    }
  };

  // Don't render if disabled or no tweets
  if (!twitterData.settings.show_twitter_box || twitterData.tweets.length === 0) return null;

  const currentTweet = twitterData.tweets[currentTweetIndex];

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
          {/* Twitter-like Header */}
          <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-black text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 space-x-reverse">
                <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-orange-500">
                  {clubLogo ? (
                    <img 
                      src={`${API}/files/${clubLogo}`}
                      alt={twitterData.settings.display_name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">أ</span>
                    </div>
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-bold">{twitterData.settings.display_name}</h3>
                  <p className="text-gray-300 text-sm">@{twitterData.settings.twitter_username}</p>
                </div>
              </div>
              <div className="text-blue-400 text-xl">
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Single Tweet Display */}
          <div className="p-6">
            <div className="flex items-start space-x-3 space-x-reverse">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                {clubLogo ? (
                  <img 
                    src={`${API}/files/${clubLogo}`}
                    alt={currentTweet?.author_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center">
                    <span className="text-white font-bold text-sm">أ</span>
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 space-x-reverse mb-2">
                  <span className="font-bold text-gray-900">{currentTweet?.author_name}</span>
                  <span className="w-4 h-4 text-blue-500">
                    <svg fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 3.95-.36.1-.74.15-1.13.15-.27 0-.54-.03-.8-.08.54 1.69 2.11 2.95 4 2.98-1.46 1.16-3.31 1.84-5.33 1.84-.35 0-.69-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z"/>
                    </svg>
                  </span>
                  <span className="text-gray-500 text-sm">@{currentTweet?.author_username}</span>
                  <span className="text-gray-400 text-sm">·</span>
                  <span className="text-gray-500 text-sm">
                    {new Date(currentTweet?.created_at).toLocaleDateString('ar-SA')}
                  </span>
                </div>
                
                <div className="text-gray-800 text-lg leading-relaxed mb-4">
                  {currentTweet?.content}
                </div>
                
                <div className="flex items-center justify-between text-gray-500 text-sm max-w-md">
                  <div className="flex items-center space-x-1 space-x-reverse hover:text-blue-500 cursor-pointer transition-colors group">
                    <div className="w-8 h-8 rounded-full group-hover:bg-blue-50 flex items-center justify-center">
                      <span>💬</span>
                    </div>
                    <span>{currentTweet?.replies || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1 space-x-reverse hover:text-green-500 cursor-pointer transition-colors group">
                    <div className="w-8 h-8 rounded-full group-hover:bg-green-50 flex items-center justify-center">
                      <span>🔄</span>
                    </div>
                    <span>{currentTweet?.retweets || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1 space-x-reverse hover:text-red-500 cursor-pointer transition-colors group">
                    <div className="w-8 h-8 rounded-full group-hover:bg-red-50 flex items-center justify-center">
                      <span>❤️</span>
                    </div>
                    <span>{currentTweet?.likes || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1 space-x-reverse hover:text-blue-500 cursor-pointer transition-colors group">
                    <div className="w-8 h-8 rounded-full group-hover:bg-blue-50 flex items-center justify-center">
                      <span>📤</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tweet Navigation Dots (if multiple tweets) */}
          {twitterData.tweets.length > 1 && (
            <div className="flex justify-center space-x-2 space-x-reverse pb-4">
              {twitterData.tweets.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTweetIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentTweetIndex 
                      ? 'bg-orange-500 w-6' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Follow Button */}
          <div className="bg-gray-50 px-6 py-4">
            <a
              href={`https://twitter.com/${twitterData.settings.twitter_username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-2xl transition-all duration-300 flex items-center justify-center space-x-2 space-x-reverse shadow-lg hover:shadow-xl group"
            >
              <svg className="w-5 h-5 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
              <span>{twitterData.settings.follow_button_text}</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Add Google Fonts for Arabic fonts
const ARABIC_FONTS_LINKS = [
  'https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700&display=swap',
  'https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700&display=swap',
  'https://fonts.googleapis.com/css2?family=Almarai:wght@400;700&display=swap',
  'https://fonts.googleapis.com/css2?family=Amiri:wght@400;700&display=swap',
  'https://fonts.googleapis.com/css2?family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap'
];

// Load Arabic fonts
const loadArabicFonts = () => {
  ARABIC_FONTS_LINKS.forEach(href => {
    if (!document.querySelector(`link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.href = href;
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  });
};

// Initialize fonts on app start
loadArabicFonts();

// Format date utility function - Convert to Gregorian calendar
const formatDateGregorian = (dateString, includeTime = false) => {
  if (!dateString) return { date: 'غير محدد', time: '' };
  
  try {
    const date = new Date(dateString);
    const gregorianDate = date.toLocaleDateString('ar-SA-u-ca-gregory', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
    
    const time = includeTime ? date.toLocaleTimeString('ar-SA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }) : date.toLocaleTimeString('ar-SA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    
    return { 
      date: gregorianDate, 
      time: time,
      fullDateTime: `${gregorianDate} - ${time}`
    };
  } catch (error) {
    console.error('Date formatting error:', error);
    return { date: 'غير صحيح', time: '' };
  }
};

// Simple date formatter for common use cases - Always Gregorian
const formatSimpleDate = (dateString) => {
  if (!dateString) return 'غير محدد';
  try {
    return new Date(dateString).toLocaleDateString('ar-SA-u-ca-gregory');
  } catch (error) {
    return 'غير صحيح';
  }
};

// Simple datetime formatter - Always Gregorian
const formatSimpleDateTime = (dateString) => {
  if (!dateString) return 'غير محدد';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA-u-ca-gregory', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  } catch (error) {
    return 'غير صحيح';
  }
};

// Enhanced Match Card Component - Professional and Simplified Design
const ProfessionalMatchCard = ({ match, onEditResult, onEditMatch, onManageGoals, onManageCards, onDeleteMatch, isFinished = false }) => {
  const { date, time } = formatDateGregorian(match.match_date, true);
  
  // Determine match result for Al-Amjad - only if scores are available and not zero
  const homeScore = match.home_score;
  const awayScore = match.away_score;
  const hasValidScores = homeScore !== null && homeScore !== undefined && awayScore !== null && awayScore !== undefined && (homeScore > 0 || awayScore > 0);
  const isAlAmjadHome = (match.home_team && typeof match.home_team === 'string' && (match.home_team.includes('الأمجاد') || match.home_team.includes('امجاد') || match.home_team === 'الأمجاد')) || 
                        (match.home_team_name && typeof match.home_team_name === 'string' && (match.home_team_name.includes('الأمجاد') || match.home_team_name.includes('امجاد') || match.home_team_name === 'الأمجاد'));
  const isAlAmjadAway = (match.away_team && typeof match.away_team === 'string' && (match.away_team.includes('الأمجاد') || match.away_team.includes('امجاد') || match.away_team === 'الأمجاد')) || 
                        (match.away_team_name && typeof match.away_team_name === 'string' && (match.away_team_name.includes('الأمجاد') || match.away_team_name.includes('امجاد') || match.away_team_name === 'الأمجاد'));
  
  let matchResult = 'draw'; // draw, win, loss
  if (isFinished && hasValidScores) {
    if ((isAlAmjadHome && homeScore > awayScore) || (isAlAmjadAway && awayScore > homeScore)) {
      matchResult = 'win';
    } else if (homeScore !== awayScore) {
      matchResult = 'loss';
    }
  }
  
  const resultStyles = {
    win: 'from-green-500 to-green-600 border-green-300',
    loss: 'from-red-500 to-red-600 border-red-300',
    draw: 'from-yellow-500 to-yellow-600 border-yellow-300',
    upcoming: 'from-blue-500 to-blue-600 border-blue-300'
  };
  
  const resultBadge = {
    win: { text: 'فوز', emoji: '🏆', color: 'bg-green-100 text-green-800' },
    loss: { text: 'هزيمة', emoji: '😔', color: 'bg-red-100 text-red-800' },
    draw: { text: 'تعادل', emoji: '🤝', color: 'bg-yellow-100 text-yellow-800' },
    upcoming: { text: 'قادمة', emoji: '⏰', color: 'bg-blue-100 text-blue-800' }
  };
  
  const currentResult = isFinished ? matchResult : 'upcoming';
  
  return (
    <div className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border-2 ${resultStyles[currentResult].split(' ')[2]} transform hover:scale-105 relative`}>
      {/* Delete Button */}
      <button
        onClick={() => onDeleteMatch(match)}
        className="absolute top-2 left-2 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center transition-colors z-10 shadow-md"
        title="حذف المباراة"
      >
        <span className="text-sm font-bold">×</span>
      </button>

      {/* Header with gradient */}
      <div className={`bg-gradient-to-r ${resultStyles[currentResult].split(' ').slice(0, 2).join(' ')} text-white p-4`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 space-x-reverse">
            <span className="text-lg">{resultBadge[currentResult].emoji}</span>
            <div>
              <h3 className="font-bold text-sm">{match.tournament_name || 'دوري النادي'}</h3>
              <p className="text-xs opacity-90">{date}</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-bold ${resultBadge[currentResult].color} text-gray-800`}>
            {resultBadge[currentResult].text}
          </div>
        </div>
      </div>
      
      {/* Teams Section */}
      <div className="p-6">
        <div className="flex items-center justify-between">
          {/* Home Team */}
          <div className="text-center flex-1">
            <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white text-xl font-bold">🏠</span>
            </div>
            <h4 className="font-bold text-gray-800 text-sm mb-1 truncate">{match.home_team}</h4>
            <span className="text-xs text-gray-500">مضيف</span>
          </div>
          
          {/* Score or VS */}
          <div className="px-6 text-center">
            {isFinished && hasValidScores ? (
              <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                <div className="flex items-center space-x-4 space-x-reverse">
                  <span className="text-3xl font-bold text-gray-800">{homeScore}</span>
                  <span className="text-lg text-gray-400">-</span>
                  <span className="text-3xl font-bold text-gray-800">{awayScore}</span>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-2 border-2 border-gray-200">
                  <span className="font-bold text-gray-600 text-sm">VS</span>
                </div>
                <p className="text-sm font-bold text-gray-700">{time}</p>
              </div>
            )}
          </div>
          
          {/* Away Team */}
          <div className="text-center flex-1">
            <div className="w-14 h-14 mx-auto mb-3 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white text-xl font-bold">⚔️</span>
            </div>
            <h4 className="font-bold text-gray-800 text-sm mb-1 truncate">{match.away_team}</h4>
            <span className="text-xs text-gray-500">ضيف</span>
          </div>
        </div>
        
        {/* Venue */}
        {match.venue && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500 flex items-center justify-center">
              <span className="ml-1">📍</span>
              {match.venue}
            </p>
          </div>
        )}
        
        {/* Action Buttons - 4 Buttons Grid */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            onClick={() => onEditMatch(match)}
            className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white py-3 px-3 rounded-lg font-bold text-xs transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
            title="تعديل بيانات المباراة"
          >
            <div className="text-center">
              <div className="text-base mb-1">✏️</div>
              <div className="font-bold">تعديل</div>
            </div>
          </button>
          
          <button
            onClick={() => onEditResult(match)}
            className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 px-3 rounded-lg font-bold text-xs transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
            title="إدخال/تعديل نتيجة المباراة"
          >
            <div className="text-center">
              <div className="text-base mb-1">⚽</div>
              <div className="font-bold">النتيجة</div>
            </div>
          </button>
          
          <button
            onClick={() => onManageGoals(match)}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white py-3 px-3 rounded-lg font-bold text-xs transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
            title="إدارة الأهداف"
          >
            <div className="text-center">
              <div className="text-base mb-1">🥅</div>
              <div className="font-bold">أهداف</div>
            </div>
          </button>
          
          <button
            onClick={() => onManageCards(match)}
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white py-3 px-3 rounded-lg font-bold text-xs transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
            title="إدارة الكروت"
          >
            <div className="text-center">
              <div className="text-base mb-1">🟨</div>
              <div className="font-bold">كروت</div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

// Smart Backend URL detection for custom domains
const BACKEND_URL = (() => {
  // Always prioritize environment variable if available
  if (process.env.REACT_APP_BACKEND_URL) {
    console.log('✅ Using REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL);
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Fallback to window origin only for localhost
  console.log('⚠️ Using window.location.origin:', window.location.origin);
  return window.location.origin;
})();

const API = `${BACKEND_URL}/api`;
console.log('🔗 Final API URL:', API);

// Employee Verification Page (Public)
const EmployeeVerifyPage = () => {
  const { employeeId } = useParams();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(false);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/public/verify-employee/${employeeId}`);
        setData(res.data);
      } catch (e) { setError(true); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [employeeId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" style={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>
      <div className="text-slate-400">جاري التحقق...</div>
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" dir="rtl" style={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>
      <div className="bg-white rounded-2xl shadow-lg p-10 text-center max-w-md mx-4">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m15 9-6 6"/><path d="m9 9 6 6"/></svg>
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">بطاقة غير صالحة</h1>
        <p className="text-slate-500 text-sm">لم يتم العثور على بيانات لهذه البطاقة. قد تكون غير صالحة أو منتهية.</p>
      </div>
    </div>
  );

  const emp = data.employee;
  const club = data.club;
  const photoUrl = emp.photo_file_id ? `${API}/files/${emp.photo_file_id}` : null;
  const logoUrl = club.logo_file_id ? `${API}/files/${club.logo_file_id}` : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center p-4" dir="rtl" style={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}>
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        {/* Green verified banner */}
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
          </div>
          <div>
            <div className="text-white font-bold text-lg">بطاقة موثقة</div>
            <div className="text-green-100 text-xs">تم التحقق من صحة هذه البطاقة</div>
          </div>
        </div>

        {/* Employee Info */}
        <div className="p-6">
          {/* Club header */}
          <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
            {logoUrl && <img src={logoUrl} alt="" className="w-10 h-10 rounded-full object-contain bg-slate-50 p-1" />}
            <div className="text-sm font-bold text-slate-700">{club.club_name || 'نادي الأمجاد'}</div>
          </div>

          {/* Photo + Name */}
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 rounded-full border-3 border-orange-400 overflow-hidden bg-slate-100 flex-shrink-0" style={{ borderWidth: '3px', borderColor: '#F97316' }}>
              {photoUrl ? <img src={photoUrl} alt="" className="w-full h-full object-cover" /> :
                <div className="w-full h-full flex items-center justify-center text-2xl font-black text-orange-500">{emp.full_name?.charAt(0)}</div>}
            </div>
            <div>
              <div className="text-xl font-bold text-slate-900">{emp.full_name}</div>
              <div className="text-sm text-orange-600 font-semibold mt-0.5">{emp.job_title}</div>
              {emp.department && <div className="text-xs text-slate-400 mt-0.5">{emp.department}</div>}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-3 bg-slate-50 rounded-xl p-4">
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">رقم الموظف</span>
              <span className="font-bold text-slate-800 font-mono">{emp.employee_number}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">الحالة</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${emp.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {emp.is_active ? 'نشط' : 'غير نشط'}
              </span>
            </div>
            {emp.hire_date && <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">تاريخ الالتحاق</span>
              <span className="font-medium text-slate-700">{new Date(emp.hire_date).toLocaleDateString('ar-SA')}</span>
            </div>}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-center">
          <p className="text-[10px] text-slate-400">تم التحقق في {new Date().toLocaleDateString('ar-SA')} • {club.club_name || 'نادي الأمجاد'}</p>
        </div>
      </div>
    </div>
  );
};



// Advanced Permissions System Constants
const PERMISSIONS = {
  // View Permissions
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_PLAYERS: 'view_players', 
  VIEW_TEAMS: 'view_teams',
  VIEW_MATCHES: 'view_matches',
  VIEW_TOURNAMENTS: 'view_tournaments',
  VIEW_NEWS: 'view_news',
  VIEW_STATISTICS: 'view_statistics',
  VIEW_USERS: 'view_users',
  VIEW_SETTINGS: 'view_settings',
  VIEW_ACTIVITY_LOG: 'view_activity_log',

  // Create Permissions
  CREATE_PLAYERS: 'create_players',
  CREATE_TEAMS: 'create_teams', 
  CREATE_MATCHES: 'create_matches',
  CREATE_TOURNAMENTS: 'create_tournaments',
  CREATE_NEWS: 'create_news',
  CREATE_USERS: 'create_users',

  // Edit Permissions
  EDIT_PLAYERS: 'edit_players',
  EDIT_TEAMS: 'edit_teams',
  EDIT_MATCHES: 'edit_matches', 
  EDIT_TOURNAMENTS: 'edit_tournaments',
  EDIT_NEWS: 'edit_news',
  EDIT_USERS: 'edit_users',
  EDIT_SETTINGS: 'edit_settings',

  // Delete Permissions
  DELETE_PLAYERS: 'delete_players',
  DELETE_TEAMS: 'delete_teams',
  DELETE_MATCHES: 'delete_matches',
  DELETE_TOURNAMENTS: 'delete_tournaments', 
  DELETE_NEWS: 'delete_news',
  DELETE_USERS: 'delete_users',

  // System Permissions
  MANAGE_PERMISSIONS: 'manage_permissions',
  EXPORT_DATA: 'export_data',
  IMPORT_DATA: 'import_data',
  BACKUP_SYSTEM: 'backup_system',
  SYSTEM_SETTINGS: 'system_settings'
};

const ROLE_TEMPLATES = {
  administrator: {
    name: 'مدير النظام',
    description: 'صلاحيات كاملة على جميع أقسام النظام',
    permissions: Object.values(PERMISSIONS),
    color: 'bg-red-500',
    icon: '👑'
  },
  supervisor: {
    name: 'مشرف عام', 
    description: 'إدارة البيانات والمحتوى مع صلاحيات محدودة',
    permissions: [
      PERMISSIONS.VIEW_DASHBOARD, PERMISSIONS.VIEW_PLAYERS, PERMISSIONS.VIEW_TEAMS,
      PERMISSIONS.VIEW_MATCHES, PERMISSIONS.VIEW_TOURNAMENTS, PERMISSIONS.VIEW_NEWS,
      PERMISSIONS.VIEW_STATISTICS, PERMISSIONS.CREATE_PLAYERS, PERMISSIONS.CREATE_TEAMS,
      PERMISSIONS.CREATE_MATCHES, PERMISSIONS.EDIT_PLAYERS, PERMISSIONS.EDIT_TEAMS,
      PERMISSIONS.EDIT_MATCHES, PERMISSIONS.DELETE_PLAYERS
    ],
    color: 'bg-blue-500',
    icon: '👨‍💼'
  },
  coach: {
    name: 'جهاز فني',
    description: 'إدارة اللاعبين والمباريات والتدريب',
    permissions: [
      PERMISSIONS.VIEW_DASHBOARD, PERMISSIONS.VIEW_PLAYERS, PERMISSIONS.VIEW_TEAMS,
      PERMISSIONS.VIEW_MATCHES, PERMISSIONS.VIEW_STATISTICS, PERMISSIONS.CREATE_PLAYERS,
      PERMISSIONS.EDIT_PLAYERS, PERMISSIONS.EDIT_MATCHES
    ],
    color: 'bg-green-500',
    icon: '🏃‍♂️'
  },
  medical: {
    name: 'جهاز طبي',
    description: 'إدارة الملفات الطبية واللياقة البدنية',
    permissions: [
      PERMISSIONS.VIEW_DASHBOARD, PERMISSIONS.VIEW_PLAYERS, PERMISSIONS.VIEW_TEAMS,
      PERMISSIONS.VIEW_MATCHES, PERMISSIONS.VIEW_STATISTICS
    ],
    color: 'bg-teal-500',
    icon: '🏥'
  },
  analyst: {
    name: 'محلل',
    description: 'مشاهدة التقارير والإحصائيات فقط',
    permissions: [
      PERMISSIONS.VIEW_DASHBOARD, PERMISSIONS.VIEW_PLAYERS, PERMISSIONS.VIEW_TEAMS,
      PERMISSIONS.VIEW_MATCHES, PERMISSIONS.VIEW_TOURNAMENTS, PERMISSIONS.VIEW_STATISTICS
    ],
    color: 'bg-purple-500',
    icon: '📊'
  },
  reporter: {
    name: 'مراسل', 
    description: 'إدارة الأخبار والمحتوى الإعلامي',
    permissions: [
      PERMISSIONS.VIEW_DASHBOARD, PERMISSIONS.VIEW_NEWS, PERMISSIONS.CREATE_NEWS,
      PERMISSIONS.EDIT_NEWS, PERMISSIONS.DELETE_NEWS
    ],
    color: 'bg-yellow-500',
    icon: '📰'
  }
};

// SEO Component for Dynamic Meta Tags
const SEOHead = ({ clubSettings = {} }) => {
  useEffect(() => {
    // Update page title
    const pageTitle = clubSettings.page_title || clubSettings.app_name || `${clubSettings.club_name || 'نادي الأمجاد'} - الموقع الرسمي`;
    document.title = pageTitle;
    
    // Update meta description
    const metaDescription = clubSettings.meta_description || clubSettings.description || 'الموقع الرسمي لنادي الأمجاد والرياضي. تابع آخر أخبار النادي، المباريات واللاعبين.';
    let descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute('content', metaDescription);
    }
    
    // Update keywords
    if (clubSettings.keywords) {
      let keywordsMeta = document.querySelector('meta[name="keywords"]');
      if (keywordsMeta) {
        keywordsMeta.setAttribute('content', clubSettings.keywords);
      }
    }
    
    // Update Open Graph tags
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', pageTitle);
    }
    
    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', metaDescription);
    }
    
    // Update Twitter tags
    let twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', pageTitle);
    }
    
    let twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', metaDescription);
    }
    
    // Update favicon if favicon exists
    if (clubSettings.favicon_file_id) {
      const faviconLink = document.querySelector('link[rel="icon"]') || 
                          document.querySelector('link[rel="shortcut icon"]');
      if (faviconLink) {
        faviconLink.href = `${API}/files/${clubSettings.favicon_file_id}`;
      } else {
        // Create new favicon link if it doesn't exist
        const newFaviconLink = document.createElement('link');
        newFaviconLink.rel = 'icon';
        newFaviconLink.type = 'image/x-icon';
        newFaviconLink.href = `${API}/files/${clubSettings.favicon_file_id}`;
        document.head.appendChild(newFaviconLink);
      }
    } else if (clubSettings.logo_file_id) {
      // Use club logo as fallback favicon if no specific favicon is set
      const faviconLink = document.querySelector('link[rel="icon"]') || 
                          document.querySelector('link[rel="shortcut icon"]');
      if (faviconLink) {
        faviconLink.href = `${API}/files/${clubSettings.logo_file_id}`;
      } else {
        // Create new favicon link if it doesn't exist
        const newFaviconLink = document.createElement('link');
        newFaviconLink.rel = 'icon';
        newFaviconLink.type = 'image/x-icon';
        newFaviconLink.href = `${API}/files/${clubSettings.logo_file_id}`;
        document.head.appendChild(newFaviconLink);
      }
    }
    
  }, [clubSettings]);

  return null; // This component doesn't render anything
};

// Professional Landing Page Component (before login)
// Next Match Countdown Component
const NextMatchCountdown = ({ match, clubLogo }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isLive, setIsLive] = useState(false);

  const matchDateTime = match ? new Date(match.date || match.match_date) : null;
  const matchDayName = matchDateTime ? matchDateTime.toLocaleDateString('ar-SA', { weekday: 'long' }) : '';

  useEffect(() => {
    if (!match) return;

    const updateCountdown = () => {
      const matchDate = new Date(match.date || match.match_date);
      const now = new Date();
      const difference = matchDate.getTime() - now.getTime();

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [match]);

  if (!match) return null;

  const matchDate = new Date(match.date || match.match_date);
  const isValidDate = !isNaN(matchDate.getTime());
  const displayDate = isValidDate ? matchDate : new Date(Date.now() + (7 * 24 * 60 * 60 * 1000));

  return (
    <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-4 sm:p-6 shadow-2xl">
      {/* Teams Display with Time in Center */}
      <div className="flex items-center justify-center mb-6 space-x-4 sm:space-x-8 space-x-reverse">
        {/* Home Team (Al-Amjad) */}
        <div className="text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-2 relative">
            {clubLogo ? (
              <img 
                src={`${API}/files/${clubLogo}`}
                alt="نادي الأمجاد"
                className="w-full h-full object-contain rounded-full border-2 border-orange-500 shadow-lg"
              />
            ) : (
              <div className="w-full h-full rounded-full border-2 border-orange-500 bg-orange-600 flex items-center justify-center shadow-lg">
                <span className="text-white text-lg sm:text-xl font-bold">أ</span>
              </div>
            )}
          </div>
          <h3 className="text-sm sm:text-base font-bold text-orange-400">نادي الأمجاد</h3>
          <div className="bg-orange-600 text-white text-xs px-2 py-1 rounded-full mt-1">المضيف</div>
        </div>

        {/* VS with Match Time */}
        <div className="text-center">
          <div className="bg-gradient-to-br from-gray-600 to-gray-700 rounded-xl px-3 py-2 sm:px-4 sm:py-3 mb-2">
            <div className="text-orange-400 text-lg sm:text-2xl font-bold">VS</div>
          </div>
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg px-2 py-1 sm:px-3 sm:py-2">
            <div className="text-white text-xs sm:text-sm font-bold">
              {match.time || (isValidDate ? displayDate.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' }) : '20:00')}
            </div>
            <div className="text-blue-100 text-xs">توقيت المباراة</div>
          </div>
        </div>

        {/* Away Team */}
        <div className="text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-2 relative">
            <div className="w-full h-full rounded-full border-2 border-gray-500 bg-gray-700 flex items-center justify-center shadow-lg">
              <span className="text-white text-lg sm:text-xl font-bold">{(match.opponent || match.away_team || 'الضيف').charAt(0)}</span>
            </div>
          </div>
          <h3 className="text-sm sm:text-base font-bold text-gray-300">{match.opponent || match.away_team || 'الفريق الضيف'}</h3>
          <div className="bg-gray-600 text-white text-xs px-2 py-1 rounded-full mt-1">الضيف</div>
        </div>
      </div>

      {/* Two Info Boxes - Venue and Countdown */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-4">
        {/* Match Venue */}
        <div className="text-center">
          <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl p-3 sm:p-4 shadow-lg">
            <div className="flex items-center justify-center mb-2">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div className="text-sm sm:text-base font-bold text-white mb-1">المكان</div>
            <div className="text-orange-100 text-xs sm:text-sm font-medium leading-tight">
              {match.venue || match.location || 'ملعب الأمير فيصل بن فهد'}
            </div>
          </div>
        </div>

        {/* Countdown Timer */}
        <div className="text-center">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-3 sm:p-4 shadow-lg">
            <div className="flex items-center justify-center mb-2">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-100" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-sm sm:text-base font-bold text-white mb-1">العد التنازلي</div>
            <div className="text-blue-100 text-xs sm:text-sm font-medium leading-tight">
              {timeLeft.days > 0 ? `${timeLeft.days} يوم` :
               timeLeft.hours > 0 ? `${timeLeft.hours}:${timeLeft.minutes.toString().padStart(2, '0')}` :
               timeLeft.minutes > 0 ? `${timeLeft.minutes}:${timeLeft.seconds.toString().padStart(2, '0')}` :
               timeLeft.seconds > 0 ? `${timeLeft.seconds} ثانية` : 'بدأت!'}
            </div>
          </div>
        </div>
      </div>

      {/* Match Full Date Display */}
      <div className="text-center">
        <div className="flex items-center justify-center space-x-2 space-x-reverse text-gray-300">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span className="text-sm font-medium">
            {isValidDate 
              ? displayDate.toLocaleDateString('ar-SA', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })
              : "الخميس، 27 صفر 1447 هـ"
            }
          </span>
        </div>
      </div>
    </div>
  );
};

// Live Match Countdown Component
const LiveMatchCountdown = ({ matchDate }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    if (!matchDate) return;

    const updateCountdown = () => {
      const match = new Date(matchDate);
      const now = new Date();
      const difference = match.getTime() - now.getTime();

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        setIsLive(false);
      } else {
        // Check if match is currently happening (within 2 hours of start time)
        const timeSinceStart = Math.abs(difference);
        if (timeSinceStart <= 2 * 60 * 60 * 1000) { // 2 hours
          setIsLive(true);
        }
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [matchDate]);

  if (isLive) {
    return (
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 text-center shadow-2xl animate-pulse">
        <div className="flex items-center justify-center mb-4">
          <div className="w-4 h-4 bg-white rounded-full animate-ping mr-3"></div>
          <span className="text-white text-2xl font-bold">🔴 مباشر الآن</span>
          <div className="w-4 h-4 bg-white rounded-full animate-ping ml-3"></div>
        </div>
        <p className="text-red-100 text-lg font-medium">المباراة جارية حالياً!</p>
      </div>
    );
  }

  if (timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0) {
    return (
      <div className="bg-gradient-to-r from-gray-600 to-gray-700 rounded-xl p-6 text-center shadow-2xl">
        <div className="text-white text-xl font-bold mb-2">⏰ انتهت المباراة</div>
        <p className="text-gray-200">تابعونا للحصول على النتائج</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-orange-600 to-orange-700 rounded-xl p-6 shadow-2xl">
      <div className="text-center mb-4">
        <h3 className="text-white text-xl font-bold mb-2">⏰ العد التنازلي للمباراة</h3>
        <p className="text-orange-100 text-sm">الوقت المتبقي حتى بداية المباراة</p>
      </div>
      
      <div className="grid grid-cols-4 gap-2 text-center">
        {/* Days */}
        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
          <div className="text-lg font-bold text-white mb-1">{timeLeft.days}</div>
          <div className="text-orange-100 text-xs font-medium">يوم</div>
        </div>
        
        {/* Hours */}
        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
          <div className="text-lg font-bold text-white mb-1">{timeLeft.hours}</div>
          <div className="text-orange-100 text-xs font-medium">ساعة</div>
        </div>
        
        {/* Minutes */}
        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
          <div className="text-lg font-bold text-white mb-1">{timeLeft.minutes}</div>
          <div className="text-orange-100 text-xs font-medium">دقيقة</div>
        </div>
        
        {/* Seconds */}
        <div className="bg-white/20 backdrop-blur-sm rounded-lg p-2">
          <div className="text-lg font-bold text-white mb-1 animate-pulse">{timeLeft.seconds}</div>
          <div className="text-orange-100 text-xs font-medium">ثانية</div>
        </div>
      </div>
      
      {/* Motivational Message */}
      <div className="mt-4 text-center">
        <p className="text-orange-100 text-sm font-medium">
          {timeLeft.days > 0 ? 
           (() => {
             const matchDay = new Date(matchDate);
             const dayNames = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
             return `يوم ${dayNames[matchDay.getDay()]}`;
           })() :
           timeLeft.hours > 0 ? `أقل من ${timeLeft.hours + 1} ساعة` :
           timeLeft.minutes > 0 ? `أقل من ساعة واحدة` :
           'المباراة على وشك البدء!'}
        </p>
      </div>
    </div>
  );
};

// Clean & Professional Sponsors Showcase - Simplified White Version
const SponsorsMarquee = ({ API, setShowContactModal }) => {
  const [publicSponsors, setPublicSponsors] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  
  useEffect(() => {
    const fetchPublicSponsors = async () => {
      try {
        const response = await axios.get(`${API}/public/sponsors`);
        setPublicSponsors(response.data || []);
      } catch (error) {
        console.error('خطأ في جلب الرعاة:', error);
      }
    };
    fetchPublicSponsors();
  }, [API]);

  const handleSponsorClick = (sponsor) => {
    setSelectedSponsor(sponsor);
    setShowSponsorModal(true);
  };

  if (publicSponsors.length === 0) return null;

  // Show each sponsor only once (no more tripling)
  const extendedSponsors = publicSponsors;

  return (
    <>
      <section className="relative bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 overflow-hidden border-y border-gray-200">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(249, 115, 22, 0.05) 0%, transparent 50%)',
        }}></div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          {/* Clean Simple Header */}
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
              شركاء النجاح
            </h2>
            <p className="text-gray-600 text-base">
              نفخر بشراكتنا الاستراتيجية مع
            </p>
          </div>

          {/* Compact Infinite Scroll Container */}
          <div 
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Simple Fade Edges */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-50 to-transparent z-20 pointer-events-none"></div>
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 to-transparent z-20 pointer-events-none"></div>

            {/* Sponsors Scroll - Smaller Cards */}
            <div className="overflow-hidden py-4">
              <div 
                className={`flex items-center gap-6 ${isPaused ? '' : 'animate-scroll-infinite'}`}
                style={{ width: 'fit-content' }}
              >
                {extendedSponsors.map((sponsor, index) => (
                  <div
                    key={`${sponsor.id}-${index}`}
                    onClick={() => handleSponsorClick(sponsor)}
                    className="group relative flex-shrink-0 w-40 h-28 bg-white rounded-xl p-4 
                              border-2 border-gray-200 hover:border-orange-400 
                              transition-all duration-500 hover:shadow-xl hover:-translate-y-1
                              cursor-pointer"
                  >
                    {/* Logo Container */}
                    <div className="relative h-full flex items-center justify-center">
                      <img
                        src={`${API}/files/${sponsor.logo_file_id}`}
                        alt={sponsor.name}
                        className="max-w-full max-h-full object-contain 
                                   group-hover:scale-110 transition-all duration-500"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = `
                            <div class="text-center">
                              <div class="text-3xl mb-1">🤝</div>
                              <div class="text-gray-700 text-xs font-bold">${sponsor.name}</div>
                            </div>
                          `;
                        }}
                      />
                    </div>

                    {/* Simple Tooltip */}
                    <div className="absolute -bottom-10 left-1/2 transform -translate-x-1/2 
                                    opacity-0 group-hover:opacity-100 transition-opacity duration-300
                                    bg-gray-900 text-white px-3 py-1 rounded-lg text-xs font-semibold
                                    whitespace-nowrap">
                      {sponsor.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Small Pause Indicator */}
            {isPaused && (
              <div className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 z-30
                            bg-gray-900 text-white px-4 py-1.5 rounded-full text-xs font-semibold
                            flex items-center gap-2">
                <div className="w-1.5 h-1.5 bg-orange-500 rounded-full"></div>
                <span>مؤقتاً</span>
              </div>
            )}
          </div>

          {/* Compact Partnership CTA - Contact Us Button */}
          <div className="mt-12 flex items-center justify-center">
            <div className="inline-flex items-center gap-4 bg-white border-2 border-gray-200 
                          rounded-2xl px-6 py-3 hover:border-orange-400 transition-all duration-300 shadow-sm">
              <span className="text-gray-700 font-bold text-sm">هل تريد أن تكون شريكاً؟</span>
              <button 
                onClick={() => setShowContactModal(true)}
                className="bg-gradient-to-r from-orange-500 to-orange-600 
                         hover:from-orange-600 hover:to-orange-700
                         text-white px-5 py-2 rounded-xl font-bold text-sm 
                         transition-all duration-300 hover:shadow-lg 
                         flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>تواصل معنا</span>
              </button>
            </div>
          </div>
        </div>

        {/* CSS Animation - Faster Speed */}
        <style jsx>{`
          @keyframes scroll-infinite {
            0% { transform: translateX(0); }
            100% { transform: translateX(-100%); }
          }
          
          .animate-scroll-infinite {
            animation: scroll-infinite 15s linear infinite;
          }
        `}</style>
      </section>

      {/* Premium Sponsor Details Modal - Logo Showcase */}
      {showSponsorModal && selectedSponsor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full overflow-hidden animate-scale-in">
            {/* Stunning Logo Header */}
            <div className="relative bg-gradient-to-br from-orange-500 via-orange-600 to-red-500 p-10">
              {/* Animated Background */}
              <div className="absolute inset-0 opacity-20" style={{
                backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.3) 0%, transparent 50%)',
                animation: 'pulse 3s ease-in-out infinite'
              }}></div>
              
              <div className="relative flex flex-col items-center text-center">
                {/* Giant Logo Display */}
                <div className="w-48 h-48 bg-white rounded-3xl shadow-2xl p-8 mb-6 transform hover:scale-105 transition-all duration-500 relative overflow-hidden">
                  {/* Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent transform -translate-x-full hover:translate-x-full transition-transform duration-1000"></div>
                  
                  {selectedSponsor.logo_file_id ? (
                    <img
                      src={`${API}/files/${selectedSponsor.logo_file_id}`}
                      alt={selectedSponsor.name}
                      className="w-full h-full object-contain relative z-10"
                    />
                  ) : (
                    <div className="text-8xl flex items-center justify-center h-full">🤝</div>
                  )}
                </div>
                
                {/* Sponsor Name */}
                <h2 className="text-4xl font-black text-white mb-2 drop-shadow-lg">{selectedSponsor.name}</h2>
                <p className="text-orange-100 text-lg font-semibold">شريك استراتيجي</p>
                
                {/* Close Button - Improved for Mobile */}
                <button
                  onClick={() => setShowSponsorModal(false)}
                  className="absolute top-2 left-2 md:top-4 md:left-4 
                           bg-white/20 hover:bg-white/40 backdrop-blur-sm 
                           text-white rounded-full p-3 md:p-2.5 
                           transition-all hover:rotate-90 transform
                           shadow-lg hover:shadow-xl z-50"
                >
                  <svg className="w-6 h-6 md:w-7 md:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-8 space-y-5">
              {/* Description */}
              {selectedSponsor.description && (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-6 border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center text-white text-lg shadow-lg">
                      ℹ️
                    </div>
                    <h3 className="text-xl font-black text-gray-900">نبذة عن الشريك</h3>
                  </div>
                  <p className="text-gray-700 text-lg leading-relaxed">{selectedSponsor.description}</p>
                </div>
              )}

              {/* Website Card */}
              {selectedSponsor.website && (
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center text-white text-lg shadow-lg">
                      🌐
                    </div>
                    <h3 className="text-xl font-black text-gray-900">الموقع الإلكتروني</h3>
                  </div>
                  <a
                    href={selectedSponsor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 font-bold text-lg underline decoration-2 underline-offset-4 flex items-center gap-2 group"
                  >
                    <span>{selectedSponsor.website}</span>
                    <svg className="w-5 h-5 transform group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" 
                         fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}

              {/* Website Action Button - Visit Website */}
              <div className="pt-2">
                <a
                  href={selectedSponsor.website || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    if (!selectedSponsor.website) {
                      e.preventDefault();
                      alert('لا يوجد رابط موقع لهذا الشريك');
                    }
                  }}
                  className="w-full bg-gradient-to-r from-orange-500 via-orange-600 to-red-500 
                           hover:from-orange-600 hover:via-orange-700 hover:to-red-600
                           text-white py-4 px-8 rounded-2xl font-black text-lg 
                           transition-all duration-500 shadow-xl hover:shadow-2xl 
                           hover:shadow-orange-500/50 flex items-center justify-center gap-3 group
                           transform hover:scale-105"
                >
                  <svg className="w-6 h-6 transform group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} 
                          d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <span>زيارة الموقع</span>
                  <svg className="w-5 h-5 transform group-hover:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

const ProfessionalLandingPage = () => {
  const [landingData, setLandingData] = useState({
    clubInfo: { name: 'نادي الأمجاد', logo: null, description: '', established: '1974' },
    news: [],
    images: [],
    newsFeeds: [],
    achievements: [],
    matches: { last_match: null, next_match: null },
    customTicker: [],
    staticAnnouncementEnabled: false,
    staticAnnouncementText: ''
  });
  
  // Contact Us Modal State
  const [showContactModal, setShowContactModal] = useState(false);
  
  // Header visibility on scroll
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show header when at top or scrolling up
      if (currentScrollY < 100) {
        setShowHeader(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up
        setShowHeader(true);
      } else {
        // Scrolling down
        setShowHeader(false);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  
  // Club Basic Info State
  const [clubBasicInfo, setClubBasicInfo] = useState({
    club_name: 'نادي الأمجاد',
    description: 'نادي رياضي يسعى للتميز في جميع البطولات',
    established_year: 1974,
    location: 'صبيا - المملكة العربية السعودية',
    website_subtitle: 'الموقع الرسمي'
  });
  const [clubLogo, setClubLogo] = useState(() => {
    // Get logo from localStorage immediately on first render
    return localStorage.getItem('club_logo') || null;
  }); // Logo state for immediate display
  
  // Public page settings state for controlling sections visibility
  const [publicPageSettings, setPublicPageSettings] = useState({
    showHero: true,
    showMatches: true,
    showNews: true,
    showAchievements: true,
    showTwitter: true,
    showGallery: true,
    showAnnouncements: true,
    showPlayerStars: true,
    showNewsTicker: false,
    // New settings
    newsTickerSpeed: 'medium',
    newsTickerBgColor: '#dc2626',
    newsTickerCustomText: '',
    playerStarsTitle: 'نجوم الأمجاد',
    playerStarsSubtitle: 'تعرف على أبرز لاعبي النادي',
    playerStarsCount: 6,
    playerStarsLayout: 'grid',
    showPlayerStats: true,
    achievementsTitle: 'إنجازات النادي',
    achievementsSubtitle: 'إنجازاتنا وجوائزنا عبر السنوات',
    foundingYear: 1974,
    activePlayers: 250,
    localTrophies: 20,
    wonMatches: 150,
    twitterSectionTitle: 'تابعنا على منصة X',
    twitterUsername: 'alamjad_club',
    twitterTweetsCount: 3,
    twitterDisplayStyle: 'cards',
    twitterShowImages: true,
    heroWelcomeText: 'مرحباً بكم في نادي الأمجاد',
    heroSubText: 'نادي رياضي متميز يهدف لتطوير المواهب الشابة',
    heroPrimaryColor: '#f97316',
    heroShowActionButton: true,
    customSections: []
  });

  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [news, setNews] = useState([]);
  
  // Loading page settings for the landing page
  const [loadingPageSettings, setLoadingPageSettings] = useState({
    enabled: true,
    title: 'نادي الأمجاد',
    subtitle: 'مرحباً بكم في موقعنا الرسمي',
    backgroundColor: '#ea580c',
    textColor: '#ffffff',
    logoEnabled: true,
    animationType: 'fade',
    duration: 3000
  });

  // Load public page settings
  const loadPublicPageSettings = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.get(`${API}/public-page-settings`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data) {
        console.log('Loaded public page settings for homepage:', response.data);
        setPublicPageSettings(response.data);
      }
    } catch (error) {
      console.log('Public page settings not available, using defaults:', error);
      // Use default settings if API fails
      setPublicPageSettings({
        showHero: true,
        showMatches: true,
        showNews: true,
        showAchievements: true,
        showTwitter: true,
        showGallery: true,
        showAnnouncements: true,
        customSections: []
      });
    }
  };

  // Load loading page settings from backend
  const loadLoadingPageSettings = async () => {
    try {
      const response = await axios.get(`${API}/public/loading-page-settings`);
      if (response.data) {
        setLoadingPageSettings(prev => ({...prev, ...response.data}));
      }
    } catch (error) {
      console.log('Loading page settings not found, using defaults');
    }
  };
  const [pageSettings, setPageSettings] = useState({
    sections: [
      { id: 'hero', name: 'القسم الرئيسي', visible: true, order: 1 },
      { id: 'news', name: 'الأخبار', visible: true, order: 2 },
      { id: 'matches', name: 'المباريات', visible: true, order: 3 },
      { id: 'players', name: 'اللاعبين', visible: true, order: 4 },
      { id: 'achievements', name: 'الإنجازات', visible: true, order: 5 },
      { id: 'next_match', name: 'المباراة القادمة', visible: true, order: 6 }
    ]
  });
  const [loading, setLoading] = useState(true);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const loadingTimerRef = useRef(null); // Prevent multiple timers
  const initialLoadingSettings = useRef(null); // Store initial loading settings
  const [currentNewsSlide, setCurrentNewsSlide] = useState(0);
  const [selectedNews, setSelectedNews] = useState(null);
  const [imageLoadStatus, setImageLoadStatus] = useState({});
  const [notification, setNotification] = useState(null);

  // Legal Pages State for public view
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalModalType, setLegalModalType] = useState(''); // 'privacy' or 'terms'
  const [publicLegalPages, setPublicLegalPages] = useState({
    privacy_policy: '',
    terms_of_use: ''
  });

  // Board of Directors Modal State
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [publicBoardMembers, setPublicBoardMembers] = useState([]);

  // All News Modal State
  const [showAllNewsModal, setShowAllNewsModal] = useState(false);

  // All News Modal Component
  const AllNewsModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 space-x-reverse">
              <span className="text-3xl">📰</span>
              <h2 className="text-2xl font-bold text-white">جميع الأخبار</h2>
            </div>
            <button
              onClick={() => setShowAllNewsModal(false)}
              className="text-white hover:text-orange-200 transition-colors text-2xl font-bold"
            >
              ✕
            </button>
          </div>
        </div>

        {/* News Grid */}
        <div className="p-6 max-h-[calc(90vh-120px)] overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {landingData.news && landingData.news.length > 0 ? (
              landingData.news.map((newsItem, index) => (
                <div key={newsItem.id || index} className="bg-gray-50 rounded-lg p-4 hover:shadow-lg transition-all duration-300 cursor-pointer"
                     onClick={() => {
                       setSelectedNews(newsItem);
                       setShowAllNewsModal(false);
                     }}>
                  {/* News Image */}
                  {((newsItem.images && newsItem.images.length > 0) || newsItem.image_url || newsItem.image_file_id) && (
                    <div className="mb-4">
                      <img 
                        src={
                          newsItem.images && newsItem.images.length > 0 
                            ? newsItem.images[0] 
                            : newsItem.image_url 
                            ? newsItem.image_url
                            : `${API}/files/${newsItem.image_file_id}`
                        }
                        alt={newsItem.title}
                        className="w-full h-48 object-cover rounded-lg"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}
                  
                  {/* News Content */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
                      {newsItem.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-3">
                      {newsItem.summary || newsItem.content?.substring(0, 150) + '...'}
                    </p>
                    
                    {/* Date and Actions - Hidden likes */}
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {newsItem.published_at ? new Date(newsItem.published_at).toLocaleDateString('ar-SA') : 'اليوم'}
                      </span>
                      <div className="flex items-center space-x-2 space-x-reverse" style={{display: 'none'}}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleLike(newsItem.id);
                          }}
                          className={`flex items-center space-x-1 space-x-reverse px-2 py-1 rounded text-xs transition-colors ${
                            isLiked(newsItem.id) 
                              ? 'bg-red-100 text-red-600' 
                              : 'bg-gray-200 text-gray-600 hover:bg-orange-100 hover:text-orange-600'
                          }`}
                        >
                          <span>{isLiked(newsItem.id) ? '❤️' : '👍'}</span>
                          <span>{getLikes(newsItem.id)}</span>
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShare(newsItem);
                          }}
                          className="flex items-center space-x-1 space-x-reverse px-2 py-1 rounded text-xs bg-gray-200 text-gray-600 hover:bg-blue-100 hover:text-blue-600 transition-colors"
                        >
                          <span>📤</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-8">
                <span className="text-6xl mb-4 block">📰</span>
                <h3 className="text-xl font-bold text-gray-600 mb-2">لا توجد أخبار متاحة</h3>
                <p className="text-gray-500">سيتم إضافة الأخبار قريباً</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  
  // Mobile Menu State
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  // Contact Info State
  const [contactInfo, setContactInfo] = useState({
    contact: {},
    social_media: {},
    social_icons: {}
  });

  // Board Members Management State
  const [boardMembers, setBoardMembers] = useState([]);
  const [showBoardMemberModal, setShowBoardMemberModal] = useState(false);
  const [editingBoardMember, setEditingBoardMember] = useState(null);
  const [boardMemberForm, setBoardMemberForm] = useState({
    name: '',
    position: '',
    description: '',
    email: '',
    phone: '',
    experience: '',
    image_url: '',
    order: 0
  });

  useEffect(() => {
    // Only run after initial loading is complete
    if (!loadingComplete) return;
    
    fetchPublicData();
    fetchContactInfo(); // Fetch contact info including social media
    loadPublicPageSettings(); // Load public page settings to control sections visibility
    
    // تتبع زيارة الصفحة العامة
    trackPageVisit();
    
    // استماع لـ event تحديث الشعار
    const handleLogoUpdate = (event) => {
      const { fileId } = event.detail;
      setLandingData(prev => ({
        ...prev,
        clubInfo: {
          ...prev.clubInfo,
          logo: fileId
        }
      }));
    };
    
    // استماع لـ event إعادة جلب البيانات العامة
    const handleRefreshPublicData = () => {
      fetchPublicData();
      fetchClubBasicInfo(); // Also refresh club basic info
      fetchContactInfo(); // Also refresh contact info
      loadPublicPageSettings(); // Also reload settings when data is refreshed
    };
    
    window.addEventListener('logoUpdated', handleLogoUpdate);
    window.addEventListener('refreshPublicData', handleRefreshPublicData);
    
    // تنظيف الـ event listeners عند إلغاء التحميل
    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate);
      window.removeEventListener('refreshPublicData', handleRefreshPublicData);
    };
  }, [loadingComplete]);

  // تتبع زيارة الصفحة العامة
  const trackPageVisit = async () => {
    try {
      await axios.post(`${API}/track-visit`);
    } catch (error) {
      // تجاهل الأخطاء - تتبع الزيارات لا يجب أن يعطل الموقع
      console.log('Visit tracking failed, but continuing...');
    }
  };

  const fetchPageSettings = async () => {
    try {
      // Try public endpoint first (no authentication required)
      const response = await axios.get(`${API}/public/page-settings`);
      if (response.data) {
        setPageSettings(response.data);
      }
    } catch (error) {
      console.log('Public page settings not available, using defaults');
      // Use default settings if API fails
      setPageSettings({
        sections: [
          { id: 'hero', name: 'القسم الرئيسي', visible: true, order: 1 },
          { id: 'news', name: 'الأخبار', visible: true, order: 2 },
          { id: 'matches', name: 'المباريات', visible: true, order: 3 },
          { id: 'players', name: 'اللاعبين', visible: true, order: 4 },
          { id: 'achievements', name: 'الإنجازات', visible: true, order: 5 },
          { id: 'next_match', name: 'المباراة القادمة', visible: true, order: 6 }
        ]
      });
    }
  };

  // Auto-update matches data for public page
  const getUpcomingMatch = () => {
    // Check if matches is an object with next_match
    if (landingData.matches && typeof landingData.matches === 'object') {
      return landingData.matches.next_match;
    }
    // Legacy support for array format
    if (landingData.matches && Array.isArray(landingData.matches)) {
      const upcoming = landingData.matches
        .filter(m => new Date(m.date || m.match_date) > new Date())
        .sort((a, b) => new Date(a.date || a.match_date) - new Date(b.date || b.match_date));
      return upcoming[0] || null;
    }
    return null;
  };

  const getLastMatch = () => {
    // Check if matches is an object with last_match
    if (landingData.matches && typeof landingData.matches === 'object') {
      return landingData.matches.last_match;
    }
    // Legacy support for array format
    if (landingData.matches && Array.isArray(landingData.matches)) {
      const completed = landingData.matches
        .filter(m => new Date(m.date || m.match_date) < new Date())
        .sort((a, b) => new Date(b.date || b.match_date) - new Date(a.date || a.match_date));
      return completed[0] || null;
    }
    return null;
  };

  // Enhanced auto-rotate news slides with pause on interaction
  useEffect(() => {
    if (landingData.news.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentNewsSlide(prev => {
        const nextSlide = prev === landingData.news.slice(0, 5).length - 1 ? 0 : prev + 1;
        return nextSlide;
      });
    }, 4000); // Faster rotation - every 4 seconds
    
    return () => clearInterval(interval);
  }, [landingData.news.length]);

  // Load loading page settings on component mount
  useEffect(() => {
    loadLoadingPageSettings();
  }, []);

  // Notification auto-hide effect
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => {
        setNotification(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Navigate to specific news slide
  const goToNewsSlide = (index) => {
    setCurrentNewsSlide(index);
  };

  // Share and Like functionality
  const handleLike = async (newsId) => {
    try {
      const currentLikes = localStorage.getItem(`news_${newsId}_likes`) || '0';
      const userLiked = localStorage.getItem(`news_${newsId}_user_liked`) === 'true';
      
      if (userLiked) {
        // Unlike
        const newLikes = Math.max(0, parseInt(currentLikes) - 1);
        localStorage.setItem(`news_${newsId}_likes`, newLikes.toString());
        localStorage.setItem(`news_${newsId}_user_liked`, 'false');
        setNotification({ message: '💔 تم إلغاء الإعجاب', type: 'info' });
      } else {
        // Like
        const newLikes = parseInt(currentLikes) + 1;
        localStorage.setItem(`news_${newsId}_likes`, newLikes.toString());
        localStorage.setItem(`news_${newsId}_user_liked`, 'true');
        setNotification({ message: '❤️ شكراً لك! تم تسجيل إعجابك', type: 'success' });
      }
    } catch (error) {
      console.error('Error handling like:', error);
    }
  };

  const handleShare = async (news) => {
    const shareData = {
      title: news.title || 'أخبار نادي الأمجاد',
      url: news.id ? `${API}/share/news/${news.id}` : window.location.href
    };

    try {
      if (navigator.share) {
        // Use native share API if available
        await navigator.share(shareData);
        setNotification({ message: '📤 تم مشاركة الخبر بنجاح', type: 'success' });
      } else {
        // Fallback: Copy to clipboard
        await navigator.clipboard.writeText(shareData.url);
        setNotification({ message: '📋 تم نسخ رابط الخبر للمشاركة', type: 'success' });
      }
    } catch (error) {
      // Fallback share options
      if (error?.name === 'AbortError') return;
      const shareUrl = `https://wa.me/?text=${encodeURIComponent(shareData.url)}`;
      window.open(shareUrl, '_blank');
      setNotification({ message: '📱 تم فتح واتساب للمشاركة', type: 'success' });
    }
  };

  const getLikes = (newsId) => {
    return localStorage.getItem(`news_${newsId}_likes`) || '0';
  };

  const isLiked = (newsId) => {
    return localStorage.getItem(`news_${newsId}_user_liked`) === 'true';
  };

  const fetchPublicData = async () => {
    try {
      
      // تكوين timeout للطلبات
      const timeoutConfig = { timeout: 10000 }; // 10 seconds timeout
      
      // جلب البيانات الأساسية أولاً للعرض السريع
      const [clubRes, newsRes, matchesRes, announcementsRes, achievementsRes, imagesRes, feedsRes, featureBoxesRes] = await Promise.allSettled([
        axios.get(`${API}/public/club-info`, timeoutConfig),
        axios.get(`${API}/public/news`, timeoutConfig),
        axios.get(`${API}/public/matches`, timeoutConfig),
        axios.get(`${API}/public/club-announcements`, timeoutConfig),
        axios.get(`${API}/public/achievements`, { timeout: 5000 }),
        axios.get(`${API}/public/images`, { timeout: 5000 }),
        axios.get(`${API}/public/news-feeds`, { timeout: 5000 }),
        axios.get(`${API}/public/feature-boxes`, { timeout: 5000 })
      ]);

      console.log('Basic data fetched:', { 
        clubRes: clubRes.status, 
        newsRes: newsRes.status, 
        matchesRes: matchesRes.status,
        announcementsRes: announcementsRes.status 
      });

      // تحديث البيانات الأساسية فوراً
      const newData = {
        clubInfo: { name: 'نادي الأمجاد', logo: null, description: '', established: '1974' },
        news: [],
        images: [],
        newsFeeds: [],
        achievements: [],
        matches: { last_match: null, next_match: null },
        customTicker: []
      };
      
      if (clubRes.status === 'fulfilled') {
        const logoId = clubRes.value.data.logo_file_id || clubRes.value.data.logo || clubRes.value.data.logo_id;
        console.log('Club data received:', clubRes.value.data);
        console.log('Logo ID extracted:', logoId);
        newData.clubInfo = {
          ...clubRes.value.data,
          logo: logoId
        };
      }

      if (newsRes.status === 'fulfilled') {
        newData.news = newsRes.value.data || [];
        console.log('News data loaded:', newData.news.length, 'items');
        if (newData.news.length > 0) {
          console.log('First news item:', newData.news[0]);
          console.log('First news image_file_id:', newData.news[0].image_file_id);
          console.log('API URL:', API);
          console.log('Full image URL example:', `${API}/files/${newData.news[0].image_file_id}`);
        }
      } else {
        console.log('News loading failed:', newsRes.reason);
        newData.news = [];
      }

      if (matchesRes.status === 'fulfilled') {
        const matchesData = matchesRes.value.data;
        console.log('Matches data received:', matchesData);
        
        if (matchesData && (matchesData.next_match || matchesData.last_match)) {
          // Use API data if available
          newData.matches = matchesData;
        } else if (Array.isArray(matchesData)) {
          // Handle array format
          const now = new Date();
          const upcomingMatches = matchesData
            .filter(m => new Date(m.date || m.match_date) > now)
            .sort((a, b) => new Date(a.date || a.match_date) - new Date(b.date || b.match_date)); // من الأقرب للأبعد
          
          const pastMatches = matchesData
            .filter(m => new Date(m.date || m.match_date) < now)
            .sort((a, b) => new Date(b.date || b.match_date) - new Date(a.date || a.match_date)); // الأحدث أولاً
          
          newData.matches = {
            all_matches: matchesData,
            next_match: upcomingMatches[0] || null,
            last_match: pastMatches[0] || null,
            upcoming_matches: upcomingMatches.slice(0, 5) // أول 5 مباريات قادمة
          };
        } else {
          // No real matches data - add development matches
          newData.matches = {
            next_match: {
              opponent: 'جدة',
              date: new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)).toISOString(), // 7 days from now
              venue: 'ملعب الأمير فيصل بن فهد',
              time: '20:00',
              competition: 'دوري المحترفين السعودي'
            },
            last_match: {
              opponent: 'الطائي',
              date: new Date(Date.now() - (3 * 24 * 60 * 60 * 1000)).toISOString(), // 3 days ago
              venue: 'ملعب الأمجاد',
              time: '18:00',
              result: '2-1',
              competition: 'دوري المحترفين السعودي'
            }
          };
        }
      } else {
        console.log('Matches loading failed, using development matches:', matchesRes.reason);
        // Add development matches as fallback
        newData.matches = {
          next_match: {
            opponent: 'جدة',
            date: new Date(Date.now() + (7 * 24 * 60 * 60 * 1000)).toISOString(), // 7 days from now
            venue: 'ملعب الأمير فيصل بن فهد',
            time: '20:00',
            competition: 'دوري المحترفين السعودي'
          },
          last_match: {
            opponent: 'الطائي',
            date: new Date(Date.now() - (3 * 24 * 60 * 60 * 1000)).toISOString(), // 3 days ago
            venue: 'ملعب الأمجاد',
            time: '18:00',
            result: '2-1',
            competition: 'دوري المحترفين السعودي'
          }
        };
      }

      // تحديث البيانات فوراً بعد الإصلاحات
      console.log('Setting landing data:', newData);
      
      // معالجة الإعلانات وتحويلها لشريط متحرك
      if (announcementsRes.status === 'fulfilled') {
        const announcements = announcementsRes.value.data || [];
        console.log('Announcements loaded:', announcements.length, 'items');
        
        // تحويل الإعلانات النشطة التي تظهر في الشريط إلى رسائل الشريط المتحرك
        const tickerMessages = announcements
          .filter(ann => ann.is_active && ann.show_on_ticker)
          .sort((a, b) => {
            // ترتيب حسب الأولوية: عاجل، مهم، عادي
            const priorityOrder = { 'urgent': 0, 'high': 1, 'normal': 2 };
            return (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
          })
          .slice(0, 5) // أقصى 5 إعلانات في الشريط
          .map(ann => ({
            text: ann.title,
            priority: ann.priority,
            category: ann.category,
            id: ann.id
          }));
        
        // إضافة رسائل افتراضية إذا لم تكن هناك إعلانات
        newData.customTicker = tickerMessages.length > 0 
          ? tickerMessages.map(msg => msg.text) // استخراج النص فقط
          : [
              'مرحباً بكم في موقع نادي الأمجاد الرسمي',
              'تابعونا للحصول على آخر الأخبار والفعاليات',
              'النادي يدعم الشباب الرياضي في المنطقة',
              'انضموا إلى عائلة الأمجاد واكونوا جزءاً من النجاح'
            ];
        
        console.log('Ticker messages created:', newData.customTicker.length, 'messages');
      } else {
        console.log('Announcements loading failed:', announcementsRes.reason);
        newData.customTicker = [
          'مرحباً بكم في موقع نادي الأمجاد الرسمي',
          'تابعونا للحصول على آخر الأخبار والفعاليات'
        ];
      }
      
      setLandingData(newData);
      
      // Also set matches state for sidebar use
      if (newData.matches?.all_matches) {
        setMatches(newData.matches.all_matches);
      }

      // جلب إعدادات الصفحة العامة للإعلان الثابت
      try {
        const pageSettingsRes = clubRes.status === 'fulfilled' ? clubRes.value : {};
        if (pageSettingsRes.data) {
          newData.staticAnnouncementEnabled = pageSettingsRes.data.staticAnnouncementEnabled || false;
          newData.staticAnnouncementText = pageSettingsRes.data.staticAnnouncementText || '';
          console.log('Static announcement settings loaded:', {
            enabled: newData.staticAnnouncementEnabled,
            text: newData.staticAnnouncementText
          });
        }
      } catch (error) {
        console.log('Could not load static announcement settings:', error.message);
        newData.staticAnnouncementEnabled = false;
        newData.staticAnnouncementText = '';
      }

      // جلب البيانات الثانوية مع timeout أقصر

      console.log('Secondary data fetched:', { 
        achievementsRes: achievementsRes.status, 
        imagesRes: imagesRes.status, 
        feedsRes: feedsRes.status,
        featureBoxesRes: featureBoxesRes.status 
      });

      // تحديث البيانات الثانوية
      newData.achievements = (achievementsRes.status === 'fulfilled' ? achievementsRes.value.data : null) || [
        {"title": "بطولة محلية", "count": 20, "icon": "🏆"},
        {"title": "لاعب نشط", "count": 250, "icon": "⭐"},
        {"title": "سنة تأسيس", "count": 1974, "icon": "📅"}
      ];
      newData.images = (imagesRes.status === 'fulfilled' ? imagesRes.value.data : null) || [];
      newData.newsFeeds = (feedsRes.status === 'fulfilled' ? feedsRes.value.data : null) || [];
      newData.featureBoxes = (featureBoxesRes.status === 'fulfilled' ? featureBoxesRes.value.data : null) || [];

      // تطبيق جميع البيانات مرة واحدة
      setLandingData(newData);
      fetchPublicLegalPages();
      fetchPublicBoardMembers();
      fetchContactInfo();
      
      console.log('All data set, loading complete...');

    } catch (error) {
      console.error('خطأ في جلب البيانات العامة:', error);
      
      // في حالة الخطأ، عرض البيانات الافتراضية
      setLandingData({
        clubInfo: { name: 'نادي الأمجاد', logo: null, description: '', established: '1974' },
        news: [],
        images: [],
        newsFeeds: [],
        achievements: [
          {"title": "بطولة محلية", "count": 20, "icon": "🏆"},
          {"title": "لاعب نشط", "count": 250, "icon": "⭐"},
          {"title": "سنة تأسيس", "count": 1974, "icon": "📅"}
        ],
        matches: { last_match: null, next_match: null },
        customTicker: [
          'مرحباً بكم في موقع نادي الأمجاد الرسمي',
          'تابعونا للحصول على آخر الأخبار والفعاليات',
          'النادي يدعم الشباب الرياضي في المنطقة',
          'انضموا إلى عائلة الأمجاد واكونوا جزءاً من النجاح'
        ]
      });
      
      // في حالة الخطأ - البيانات الافتراضية تكفي
    }
  };

  const fetchPublicLegalPages = async () => {
    try {
      const response = await axios.get(`${API}/public/legal-pages`);
      setPublicLegalPages(response.data);
    } catch (error) {
      console.error('خطأ في جلب الصفحات القانونية:', error);
    }
  };

  const fetchPublicBoardMembers = async () => {
    try {
      const response = await axios.get(`${API}/public/board-members`);
      const membersData = response.data.members || [];
      
      if (membersData.length > 0) {
        setPublicBoardMembers(membersData);
      } else {
        // Use default data if no members found
        setPublicBoardMembers([
          {
            id: '1',
            name: 'عبدالله محمد الأمجاد',
            position: 'رئيس مجلس الإدارة',
            description: 'رئيس مجلس الإدارة والمؤسس، يتمتع بخبرة واسعة في إدارة الأندية الرياضية والعمل المجتمعي.',
            image_url: null,
            email: 'chairman@alamjadclub.sa',
            phone: '+966 XX XXX XXXX',
            experience: '15 عاماً في إدارة الأندية الرياضية'
          },
          {
            id: '2',
            name: 'محمد أحمد الأمجاد',
            position: 'نائب رئيس مجلس الإدارة',
            description: 'نائب الرئيس ومسؤول عن الشؤون الفنية والرياضية، حاصل على شهادات دولية في الإدارة الرياضية.',
            image_url: null,
            email: 'vice@alamjadclub.sa',
            phone: '+966 XX XXX XXXX',
            experience: '12 عاماً في الإدارة الرياضية'
          },
          {
            id: '3',
            name: 'فهد سالم الأمجاد',
            position: 'المدير التنفيذي',
            description: 'المدير التنفيذي ومسؤول عن العمليات اليومية للنادي وتطوير الأنشطة الرياضية والثقافية.',
            image_url: null,
            email: 'ceo@alamjadclub.sa',
            phone: '+966 XX XXX XXXX',
            experience: '10 أعوام في الإدارة التنفيذية'
          }
        ]);
      }
    } catch (error) {
      console.error('خطأ في جلب أعضاء مجلس الإدارة:', error);
      // Use default data on error
      setPublicBoardMembers([
        {
          id: '1',
          name: 'عبدالله محمد الأمجاد',
          position: 'رئيس مجلس الإدارة',
          description: 'رئيس مجلس الإدارة والمؤسس، يتمتع بخبرة واسعة في إدارة الأندية الرياضية والعمل المجتمعي.',
          image_url: null,
          email: 'chairman@alamjadclub.sa',
          phone: '+966 XX XXX XXXX',
          experience: '15 عاماً في إدارة الأندية الرياضية'
        }
      ]);
    }
  };

  const fetchContactInfo = async () => {
    try {
      console.log('🔄 جاري جلب معلومات التواصل...');
      const response = await axios.get(`${API}/public/contact-info`);
      console.log('✅ تم جلب معلومات التواصل بنجاح:', response.data);
      console.log('📋 الأيقونات المتوفرة:', response.data.social_icons);
      setContactInfo(response.data);
    } catch (error) {
      console.error('❌ خطأ في جلب معلومات التواصل:', error);
    }
  };

  const fetchClubBasicInfo = async () => {
    try {
      // استخدام endpoint العام بدلاً من المحمي لتجنب خطأ 401
      const response = await axios.get(`${API}/public/club-info`);
      const settings = response.data;
      
      // Set logo immediately for loading screen AND save to localStorage
      if (settings?.logo_file_id) {
        setClubLogo(settings.logo_file_id);
        localStorage.setItem('club_logo', settings.logo_file_id);
      }
      
      if (settings) {
        console.log('🔄 تم جلب بيانات النادي من Backend (العام):', {
          club_name: settings.club_name,
          description: settings.description,
          established_year: settings.established_year,
          location: settings.location
        });
        
        setClubBasicInfo({
          club_name: settings.club_name || 'نادي الأمجاد',
          description: settings.description || 'نادي رياضي يسعى للتميز والإنجازات، مع التزام راسخ بتطوير المواهب وتحقيق البطولات',
          established_year: settings.established_year || 1974,
          location: settings.location || 'صبيا',
          website_subtitle: settings.website_subtitle || 'الموقع الرسمي'
        });
        
        // Update landing data as well
        setLandingData(prev => ({
          ...prev,
          clubInfo: {
            ...prev.clubInfo,
            name: settings.club_name || 'نادي الأمجاد',
            description: settings.description || 'نادي رياضي يسعى للتميز والإنجازات',
            established: settings.established_year?.toString() || '1974'
          }
        }));

        // Update favicon and page title
        if (settings.club_name) {
          document.title = `${settings.club_name} - الموقع الرسمي`;
        }
        
        // Update favicon with club logo if available
        if (settings.logo_file_id) {
          const favicon = document.querySelector('link[rel="icon"]') || document.createElement('link');
          favicon.rel = 'icon';
          favicon.href = `${API}/files/${settings.logo_file_id}`;
          favicon.type = 'image/png';
          
          // Remove existing favicon if different
          const existingFavicon = document.querySelector('link[rel="icon"]');
          if (existingFavicon && existingFavicon !== favicon) {
            existingFavicon.remove();
          }
          
          if (!document.querySelector('link[rel="icon"]')) {
            document.head.appendChild(favicon);
          }
        }
      }
    } catch (error) {
      console.error('خطأ في جلب معلومات النادي الأساسية:', error);
      // Use default values if fetch fails
      setClubBasicInfo({
        club_name: 'نادي الأمجاد',
        description: 'نادي رياضي يسعى للتميز والإنجازات',
        established_year: 1974,
        location: 'صبيا'
      });
    }
  };

  // Loading screen - short minimum display then smooth fade
  const [loadingFadeOut, setLoadingFadeOut] = useState(false);
  const loadStartTime = useRef(Date.now());
  
  useEffect(() => {
    initialLoadingSettings.current = {
      title: loadingPageSettings.title,
      subtitle: loadingPageSettings.subtitle,
      logoEnabled: loadingPageSettings.logoEnabled,
      backgroundColor: loadingPageSettings.backgroundColor,
      textColor: loadingPageSettings.textColor
    };
    
    const loadData = async () => {
      await Promise.all([fetchClubBasicInfo(), fetchPublicData()]);
      
      const elapsed = Date.now() - loadStartTime.current;
      const remaining = Math.max(0, 1200 - elapsed);
      
      setTimeout(() => {
        setLoadingFadeOut(true);
        setTimeout(() => {
          setLoadingComplete(true);
          setLoading(false);
        }, 500);
      }, remaining);
    };
    
    loadData();
  }, []);

  const openLegalModal = (type) => {
    setLegalModalType(type);
    setShowLegalModal(true);
  };

  if (loading) {
    const displaySettings = initialLoadingSettings.current || loadingPageSettings;
    const logoId = clubLogo || landingData.clubInfo?.logo || clubBasicInfo?.logo_file_id;
    return (
      <LoadingScreen
        logoUrl={logoId ? `${API}/files/${logoId}` : '/club-logo-512.png'}
        showLogo={displaySettings.logoEnabled !== false}
        title={displaySettings.title}
        subtitle={displaySettings.subtitle}
        fadeOut={loadingFadeOut}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50" dir="rtl">
      {/* SEO Component */}
      <SEOHead clubSettings={landingData.clubInfo || {}} />
      
      {/* Notification Component */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transition-all duration-300 ${
          notification.type === 'success' ? 'bg-green-500 text-white' :
          notification.type === 'error' ? 'bg-red-500 text-white' :
          notification.type === 'info' ? 'bg-blue-500 text-white' :
          'bg-gray-500 text-white'
        }`}>
          <div className="flex items-center space-x-2 space-x-reverse">
            <span>{notification.message}</span>
            <button
              onClick={() => setNotification(null)}
              className="ml-2 text-white hover:text-gray-200"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      {/* Site Header */}
      <SiteHeader
        API={API}
        logoUrl={landingData.clubInfo.logo ? `${API}/files/${landingData.clubInfo.logo}` : '/club-logo-512.png'}
        clubName={clubBasicInfo.club_name || 'نادي الأمجاد'}
        subtitle={clubBasicInfo.website_subtitle || 'الموقع الرسمي'}
        showHeader={showHeader}
        showLogin={pageSettings.header?.show_login !== false}
        contactInfo={contactInfo}
        onBoard={() => setShowBoardModal(true)}
        onContact={() => setShowContactModal(true)}
      />

      {/* Professional Hero Section - Al-Amjad Orange Theme with Dynamic Background */}
      {publicPageSettings.showHero && (
        <section id="home" className="relative min-h-screen overflow-hidden">
        {/* Dynamic Background Image with Overlay */}
        {landingData.news.length > 0 && landingData.news[currentNewsSlide]?.image_file_id ? (
          <>
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-all duration-1000 ease-in-out"
              style={{
                backgroundImage: `url('${API}/files/${landingData.news[currentNewsSlide].image_file_id}')`
              }}
            />
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-orange-900/60 to-black/80"></div>
            {/* Additional subtle pattern overlay */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}></div>
            </div>
          </>
        ) : (
          <>
            {/* Fallback orange gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-orange-700 via-orange-600 to-orange-800"></div>
            {/* Club logo as background when no news image */}
            {landingData.clubInfo?.logo && (
              <div 
                className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-10"
                style={{
                  backgroundImage: `url('${API}/files/${landingData.clubInfo.logo}')`,
                  backgroundSize: '400px 400px',
                  backgroundPosition: 'right center'
                }}
              />
            )}
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}></div>
            </div>
          </>
        )}

        {/* Mobile/Desktop Responsive Layout */}
        <div className="relative z-10 min-h-screen flex flex-col lg:flex-row">
          {/* Left Side - Main News Content */}
          <div className="flex-1 flex items-center px-4 sm:px-6 lg:px-8 py-12">
            <div className="max-w-4xl w-full">
              {landingData.news.length > 0 ? (
                <div className="space-y-8">
                  {/* Club Logo and Badge - Mobile Optimized */}
                  <div className="flex items-center space-x-4 space-x-reverse">
                    <div className="flex-shrink-0">
                      {landingData.clubInfo.logo ? (
                        <img 
                          src={`${API}/files/${landingData.clubInfo.logo}`}
                          alt="شعار النادي"
                          className="w-16 h-16 lg:w-20 lg:h-20 object-contain bg-white/10 rounded-full p-2"
                        />
                      ) : (
                        <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white/10 rounded-full flex items-center justify-center">
                          <span className="text-white text-2xl lg:text-3xl font-bold">أ</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="bg-orange-600/20 backdrop-blur-sm px-4 py-2 rounded-full inline-block">
                        <span className="text-orange-200 text-sm font-bold">آخر التحديثات</span>
                      </div>
                    </div>
                  </div>

                  {/* Main News Article with Enhanced Navigation */}
                  <div className="space-y-6">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight cursor-pointer hover:text-orange-200 transition-colors"
                        onClick={() => setSelectedNews(landingData.news[currentNewsSlide] || landingData.news[0])}>
                      {landingData.news[currentNewsSlide]?.title || landingData.news[0]?.title || `الأمجاد يدعم الشباب الرياضي - ${new Date().getFullYear()}`}
                    </h1>
                    
                    <p className="text-lg md:text-xl lg:text-2xl text-orange-100 leading-relaxed max-w-3xl">
                      {landingData.news[currentNewsSlide]?.summary || landingData.news[currentNewsSlide]?.content?.substring(0, 150) + '...' || landingData.news[0]?.summary || landingData.news[0]?.content?.substring(0, 150) + '...' || 'النادي يواصل مسيرة دعم الشباب الرياضي وتطوير المواهب المحلية في إطار رؤيته الطموحة للمستقبل'}
                    </p>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 sm:space-x-reverse">
                      <button 
                        onClick={() => setSelectedNews(landingData.news[currentNewsSlide] || landingData.news[0])}
                        className="bg-white text-orange-800 px-8 py-4 rounded-xl font-bold text-lg hover:bg-orange-50 transition-all duration-300 transform hover:scale-105 shadow-lg"
                      >
                        اقرأ التفاصيل كاملة
                      </button>
                      <div className="flex items-center space-x-4 space-x-reverse">
                        <div className="text-orange-200 text-sm">
                          {formatSimpleDate(landingData.news[currentNewsSlide]?.date || landingData.news[currentNewsSlide]?.created_at || landingData.news[0]?.date || landingData.news[0]?.created_at)}
                        </div>
                        
                        {/* News Navigation Dots */}
                        {landingData.news.length > 1 && (
                          <div className="flex space-x-2 space-x-reverse">
                            {landingData.news.slice(0, 5).map((_, index) => (
                              <button
                                key={index}
                                onClick={() => goToNewsSlide(index)}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                  currentNewsSlide === index 
                                    ? 'bg-white w-8' 
                                    : 'bg-orange-300 hover:bg-orange-100'
                                }`}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // Default content when no news
                <div className="space-y-8">
                  <div className="flex items-center space-x-4 space-x-reverse">
                    <div className="flex-shrink-0">
                      {landingData.clubInfo.logo ? (
                        <img 
                          src={`${API}/files/${landingData.clubInfo.logo}`}
                          alt="شعار النادي"
                          className="w-16 h-16 lg:w-20 lg:h-20 object-contain bg-white/10 rounded-full p-2"
                        />
                      ) : (
                        <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white/10 rounded-full flex items-center justify-center">
                          <span className="text-white text-2xl lg:text-3xl font-bold">أ</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="bg-orange-600/20 backdrop-blur-sm px-4 py-2 rounded-full inline-block">
                        <span className="text-orange-200 text-sm font-bold">مرحباً بكم</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight">
                      {landingData.clubInfo.name || 'نادي الأمجاد'}
                    </h1>
                    
                    <p className="text-lg md:text-xl lg:text-2xl text-orange-100 leading-relaxed max-w-3xl">
                      العزيمة والإصرار طريقنا للنجاح - نفخر بتاريخنا ونتطلع للمستقبل بثقة وتميز
                    </p>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-6 sm:space-x-reverse">
                      <a href="#news" className="bg-white text-orange-800 px-8 py-4 rounded-xl font-bold text-lg hover:bg-orange-50 transition-all duration-300 transform hover:scale-105 shadow-lg">
                        تصفح الأخبار
                      </a>
                      <a href="#matches" className="bg-orange-600/30 backdrop-blur-sm text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-orange-600/50 transition-all duration-300 border-2 border-orange-400/30">
                        جدول المباريات
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Side - Enhanced News Gallery with More News */}
          {landingData.news.length > 0 && (
            <div className="lg:w-96 xl:w-[450px] p-4 lg:p-8">
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold text-lg">آخر الأخبار</h3>
                  <span className="text-orange-200 text-sm">{landingData.news.length} خبر</span>
                </div>
                
                {landingData.news.slice(1, 6).map((newsItem, index) => (
                  <div key={index} className="group">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden hover:bg-white/20 transition-all duration-300 hover:scale-105">
                      <div className="cursor-pointer" onClick={() => setSelectedNews(newsItem)}>
                        <div className="flex items-center space-x-4 space-x-reverse p-4">
                          <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden group-hover:scale-110 transition-transform duration-300 relative">
                            {(newsItem.images && newsItem.images.length > 0) || newsItem.image_file_id ? (
                              <>
                                <img 
                                  src={newsItem.images && newsItem.images.length > 0 ? newsItem.images[0] : `${API}/files/${newsItem.image_file_id}`}
                                  alt={newsItem.title || `خبر رقم ${index + 2}`}
                                  className="w-full h-full object-contain bg-slate-900 transition-transform duration-300"
                                  onLoad={(e) => {
                                    console.log(`✅ Image loaded successfully for news ${index + 2}:`, e.target.src);
                                  }}
                                  onError={(e) => {
                                    console.error(`❌ Image failed to load for news ${index + 2}:`, e.target.src);
                                    // Hide broken image and show fallback
                                    e.target.style.display = 'none';
                                    const fallback = e.target.parentElement.querySelector('.fallback-bg');
                                    if (fallback) {
                                      fallback.style.display = 'flex';
                                    }
                                  }}
                                />
                                {/* Professional Fallback with Club Logo */}
                                <div 
                                  className="fallback-bg absolute inset-0 w-full h-full items-center justify-center"
                                  style={{
                                    background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 50%, #9a3412 100%)',
                                    display: 'none'
                                  }}
                                >
                                  {landingData.clubInfo?.logo ? (
                                    <img 
                                      src={`${API}/files/${landingData.clubInfo.logo}`}
                                      alt="شعار النادي"
                                      className="w-10 h-10 object-contain opacity-30 drop-shadow-lg"
                                      style={{
                                        filter: 'brightness(2) contrast(1.2) saturate(0.8)'
                                      }}
                                    />
                                  ) : (
                                    <div className="text-center">
                                      <span className="text-white text-2xl opacity-60 drop-shadow-md">⚽</span>
                                      <div className="text-white text-xs opacity-40 mt-1 font-medium">الأمجاد</div>
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : (
                              // No image - show orange background with club logo
                              <div 
                                className="w-full h-full flex items-center justify-center relative"
                                style={{
                                  background: 'linear-gradient(135deg, #ea580c 0%, #c2410c 50%, #9a3412 100%)'
                                }}
                              >
                                {landingData.clubInfo?.logo ? (
                                  <img 
                                    src={`${API}/files/${landingData.clubInfo.logo}`}
                                    alt="شعار النادي"
                                    className="w-10 h-10 object-contain opacity-40 drop-shadow-lg"
                                    style={{
                                      filter: 'brightness(2) contrast(1.2) saturate(0.8)'
                                    }}
                                  />
                                ) : (
                                  <div className="text-center">
                                    <span className="text-white text-2xl opacity-60 drop-shadow-md">⚽</span>
                                    <div className="text-white text-xs opacity-50 mt-1 font-medium">الأمجاد</div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-sm leading-tight line-clamp-2 group-hover:text-orange-200 transition-colors">
                              {newsItem.title || `خبر رقم ${index + 2}`}
                            </h3>
                            <p className="text-orange-200 text-xs mt-1">
                              {formatSimpleDate(newsItem.date || newsItem.created_at)}</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Action Buttons - Hidden likes and views */}
                      <div className="px-4 pb-3" style={{display: 'none'}}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 space-x-reverse">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLike(newsItem.id);
                              }}
                              className={`flex items-center space-x-1 space-x-reverse px-2 py-1 rounded-lg text-xs transition-colors ${
                                isLiked(newsItem.id)
                                  ? 'bg-red-500/20 text-red-200 hover:bg-red-500/30'
                                  : 'bg-white/10 text-white hover:bg-white/20'
                              }`}
                            >
                              <span>{isLiked(newsItem.id) ? '❤️' : '👍'}</span>
                              <span>{getLikes(newsItem.id)}</span>
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShare(newsItem);
                              }}
                              className="flex items-center space-x-1 space-x-reverse bg-white/10 text-white hover:bg-white/20 px-2 py-1 rounded-lg text-xs transition-colors"
                            >
                              <span>📤</span>
                              <span>مشاركة</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                <div className="text-center pt-4">
                  <button 
                    onClick={() => setShowAllNewsModal(true)}
                    className="text-orange-200 hover:text-white transition-colors text-sm font-medium cursor-pointer"
                  >
                    عرض جميع الأخبار →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Large Club Logo Overlay - Background */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-5 pointer-events-none">
          {landingData.clubInfo.logo ? (
            <img 
              src={`${API}/files/${landingData.clubInfo.logo}`}
              alt="شعار النادي"
              className="w-96 h-96 lg:w-[500px] lg:h-[500px] object-contain"
            />
          ) : (
            <div className="w-96 h-96 lg:w-[500px] lg:h-[500px] rounded-full border-8 border-white flex items-center justify-center">
              <span className="text-white text-9xl font-bold">أ</span>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Enhanced Professional News Ticker - Fixed for Desktop */}
      <section className="bg-orange-600 text-white py-3 lg:py-4 overflow-hidden relative shadow-lg">
        <div className="flex items-center h-12 lg:h-14">
          <div className="bg-orange-700 px-4 lg:px-6 py-2 lg:py-3 font-bold text-sm lg:text-base whitespace-nowrap shadow-md flex-shrink-0 z-10">
            <span className="text-base lg:text-lg mr-2">📢</span>
            إعلانات النادي
          </div>
          <div className="flex-1 relative ticker-container ml-2">
            <div className="animate-scroll flex items-center">
              {/* Static Announcement (if enabled) - moved to ticker */}
              {landingData.staticAnnouncementEnabled && landingData.staticAnnouncementText && (
                <>
                  <div className="inline-flex items-center">
                    <span className="font-bold text-sm lg:text-lg mx-6 lg:mx-8 whitespace-nowrap text-yellow-200">
                      {landingData.staticAnnouncementText}
                    </span>
                    <span className="text-orange-200 text-lg lg:text-xl">✦</span>
                  </div>
                </>
              )}
              
              {/* Dynamic Ticker Messages */}
              {landingData.customTicker?.length > 0 ? (
                landingData.customTicker.map((item, index) => (
                  <div key={index} className="inline-flex items-center">
                    <span className="font-medium text-sm lg:text-lg mx-6 lg:mx-8 whitespace-nowrap">
                      {item.text || item}
                    </span>
                    {index < landingData.customTicker.length - 1 && (
                      <span className="text-orange-200 text-lg lg:text-xl">✦</span>
                    )}
                  </div>
                ))
              ) : (
                // Default ticker messages
                [
                  'مرحباً بكم في موقع نادي الأمجاد الرسمي',
                  'تابعونا للحصول على آخر الأخبار والفعاليات',
                  'النادي يدعم الشباب الرياضي في المنطقة',
                  'انضموا إلى عائلة الأمجاد واكونوا جزءاً من النجاح'
                ].map((message, index) => (
                  <div key={index} className="inline-flex items-center">
                    <span className="font-medium text-sm lg:text-lg mx-6 lg:mx-8 whitespace-nowrap">{message}</span>
                    {index < 3 && <span className="text-orange-200 text-lg lg:text-xl">✦</span>}
                  </div>
                ))
              )}
              
              {/* Duplicate content for seamless loop */}
              {landingData.staticAnnouncementEnabled && landingData.staticAnnouncementText && (
                <>
                  <div className="inline-flex items-center">
                    <span className="font-bold text-sm lg:text-lg mx-6 lg:mx-8 whitespace-nowrap text-yellow-200">
                      {landingData.staticAnnouncementText}
                    </span>
                    <span className="text-orange-200 text-lg lg:text-xl">✦</span>
                  </div>
                </>
              )}
              
              {landingData.customTicker?.length > 0 ? (
                landingData.customTicker.map((item, index) => (
                  <div key={`dup-${index}`} className="inline-flex items-center">
                    <span className="font-medium text-sm lg:text-lg mx-6 lg:mx-8 whitespace-nowrap">
                      {item.text || item}
                    </span>
                    {index < landingData.customTicker.length - 1 && (
                      <span className="text-orange-200 text-lg lg:text-xl">✦</span>
                    )}
                  </div>
                ))
              ) : (
                [
                  'مرحباً بكم في موقع نادي الأمجاد الرسمي',
                  'تابعونا للحصول على آخر الأخبار والفعاليات',
                  'النادي يدعم الشباب الرياضي في المنطقة',
                  'انضموا إلى عائلة الأمجاد واكونوا جزءاً من النجاح'
                ].map((message, index) => (
                  <div key={`dup-${index}`} className="inline-flex items-center">
                    <span className="font-medium text-sm lg:text-lg mx-6 lg:mx-8 whitespace-nowrap">{message}</span>
                    {index < 3 && <span className="text-orange-200 text-lg lg:text-xl">✦</span>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Next Match Section - Professional Banner Design with Real Data */}
      {publicPageSettings.showMatches && landingData.matches?.next_match && (
        <section id="matches" className="py-12 lg:py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-black relative overflow-hidden">
          {/* Background Effects */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M50 10c22.091 0 40 17.909 40 40s-17.909 40-40 40-40-17.909-40-40 17.909-40 40-40zm0 5c-19.33 0-35 15.67-35 35s15.67 35 35 35 35-15.67 35-35-15.67-35-35-35zm0 15c11.046 0 20 8.954 20 20s-8.954 20-20 20-20-8.954-20-20 8.954-20 20-20z'/%3E%3C/g%3E%3C/svg%3E")`
            }}></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Section Header - Mobile Optimized - Outside Grid */}
            <div className="text-center mb-4 sm:mb-6 lg:mb-8 px-4">
              <div className="inline-flex items-center bg-gradient-to-r from-orange-500/20 to-orange-600/20 backdrop-blur-sm border border-orange-400/30 px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 lg:py-2.5 rounded-full mb-2 sm:mb-3 lg:mb-4">
                <span className="text-lg sm:text-xl lg:text-2xl mr-2 sm:mr-3">⚽</span>
                <span className="text-orange-400 font-bold text-sm sm:text-base lg:text-base">المباراة القادمة</span>
              </div>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-white via-orange-200 to-orange-400 bg-clip-text text-transparent mb-2 sm:mb-3 lg:mb-3">
                معركة الأمجاد
              </h2>
              <p className="text-gray-300 text-sm sm:text-base lg:text-base max-w-xl lg:max-w-xl mx-auto leading-relaxed">
                استعدوا لمواجهة مثيرة مليئة بالحماس والإثارة
              </p>
            </div>
            
            {/* Grid Layout - Main Match + Sidebar */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
              {/* Main Match Section - Takes 2/3 */}
              <div className="lg:col-span-2">
                {/* Professional Match Card - Mobile Optimized & Smaller */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl sm:rounded-2xl lg:rounded-2xl border border-white/20 shadow-2xl overflow-hidden mx-2 sm:mx-0">
              {/* Tournament Badge - Mobile Optimized */}
              <div className="bg-gradient-to-r from-orange-600 to-orange-700 text-white px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-3 relative">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3 space-x-reverse">
                    <span className="text-lg sm:text-xl lg:text-xl">🏆</span>
                    <span className="font-bold text-sm sm:text-base lg:text-lg">
                      {landingData.matches.next_match.tournament_name || 'دوري النادي المحترف'}
                    </span>
                  </div>
                  <div className="bg-black/20 px-2 sm:px-3 lg:px-3 py-1 sm:py-1.5 lg:py-1.5 rounded-full">
                    <span className="text-white font-medium text-xs sm:text-sm lg:text-sm">
                      {formatSimpleDate(landingData.matches.next_match.match_date)}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* Teams Face-off - Enhanced and Mobile Optimized */}
              <div className="p-3 sm:p-4 lg:p-8">
                <div className="flex items-center justify-between mb-4 lg:mb-6">
                  {/* Home Team - Enhanced with Same Colors */}
                  <div className="text-center flex-1">
                    <div className="relative mb-2 sm:mb-3 lg:mb-4 group">
                      <div className="absolute -inset-1 sm:-inset-2 bg-gradient-to-r from-orange-400 to-orange-600 rounded-xl sm:rounded-2xl opacity-0 group-hover:opacity-75 transition-opacity blur-lg"></div>
                      {(landingData.matches.next_match.home_team_logo_url || landingData.matches.next_match.home_team_logo) ? (
                        <img 
                          src={landingData.matches.next_match.home_team_logo_url || `${API}/files/${landingData.matches.next_match.home_team_logo}`}
                          alt={landingData.matches.next_match.home_team_name || landingData.matches.next_match.home_team || landingData.clubInfo.name || "الأمجاد"}
                          className="relative w-14 h-14 sm:w-16 sm:h-16 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl object-cover mx-auto shadow-xl transform transition-transform group-hover:scale-110"
                          style={{ backgroundColor: 'white' }}
                        />
                      ) : landingData.clubInfo.logo ? (
                        <img 
                          src={`${API}/files/${landingData.clubInfo.logo}`}
                          alt={landingData.matches.next_match.home_team_name || landingData.matches.next_match.home_team || landingData.clubInfo.name || "الأمجاد"}
                          className="relative w-14 h-14 sm:w-16 sm:h-16 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl object-cover mx-auto shadow-xl transform transition-transform group-hover:scale-110"
                          style={{ backgroundColor: 'white' }}
                        />
                      ) : (
                        <div className="relative w-14 h-14 sm:w-16 sm:h-16 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto shadow-xl transform transition-transform group-hover:scale-110" style={{ backgroundColor: 'white' }}>
                          <span className="text-orange-600 text-lg sm:text-xl lg:text-3xl font-bold">أ</span>
                        </div>
                      )}
                    </div>
                    <div className="font-bold text-xs sm:text-sm lg:text-xl text-white mb-1 sm:mb-1 px-1">
                      {landingData.matches.next_match.home_team_name || landingData.matches.next_match.home_team || landingData.clubInfo.name || 'الأمجاد'}
                    </div>
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-2 sm:px-2 lg:px-3 py-0.5 sm:py-0.5 rounded-full text-xs sm:text-xs font-medium inline-block">
                      المضيف
                    </div>
                  </div>
                  
                  {/* VS with Enhanced Time Display */}
                  <div className="text-center px-2 sm:px-3 lg:px-8">
                    <div className="bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl w-10 h-10 sm:w-12 sm:h-12 lg:w-18 lg:h-18 flex items-center justify-center mx-auto mb-2 sm:mb-2 lg:mb-3 border border-white/30 shadow-xl">
                      <span className="text-sm sm:text-lg lg:text-2xl font-bold bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                        VS
                      </span>
                    </div>
                    <div className="bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-sm rounded-lg sm:rounded-xl px-2 sm:px-2 lg:px-3 py-1 sm:py-1 lg:py-2 border border-white/20">
                      <div className="text-xs sm:text-sm lg:text-xl font-bold text-white mb-0.5 sm:mb-0.5">
                        {new Date(landingData.matches.next_match.match_date).toLocaleTimeString('ar-SA', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      <div className="text-orange-300 text-xs sm:text-xs font-medium">توقيت المباراة</div>
                    </div>
                  </div>
                  
                  {/* Away Team - Enhanced with Same Colors */}
                  <div className="text-center flex-1">
                    <div className="relative mb-2 sm:mb-3 lg:mb-4 group">
                      <div className="absolute -inset-1 sm:-inset-2 bg-gradient-to-r from-orange-400 to-orange-600 rounded-xl sm:rounded-2xl opacity-0 group-hover:opacity-50 transition-opacity blur-lg"></div>
                      {(landingData.matches.next_match.away_team_logo_url || landingData.matches.next_match.away_team_logo) ? (
                        <img 
                          src={landingData.matches.next_match.away_team_logo_url || `${API}/files/${landingData.matches.next_match.away_team_logo}`}
                          alt={landingData.matches.next_match.away_team_name || landingData.matches.next_match.away_team || "الفريق الضيف"}
                          className="relative w-14 h-14 sm:w-16 sm:h-16 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl object-cover mx-auto shadow-xl transform transition-transform group-hover:scale-110"
                          style={{ backgroundColor: 'white' }}
                        />
                      ) : (
                        <div className="relative w-14 h-14 sm:w-16 sm:h-16 lg:w-24 lg:h-24 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto shadow-xl transform transition-transform group-hover:scale-110" style={{ backgroundColor: 'white' }}>
                          <span className="text-orange-600 text-lg sm:text-xl lg:text-3xl font-bold">
                            {(landingData.matches.next_match.away_team_name || landingData.matches.next_match.away_team || 'الفريق الضيف').charAt(0) || '🏴'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="font-bold text-xs sm:text-sm lg:text-xl text-white mb-1 sm:mb-1 px-1">
                      {landingData.matches.next_match.away_team_name || landingData.matches.next_match.away_team || 'الفريق الضيف'}
                    </div>
                    <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-2 sm:px-2 lg:px-3 py-0.5 sm:py-0.5 rounded-full text-xs sm:text-xs font-medium inline-block">
                      الضيف
                    </div>
                  </div>
                </div>

                {/* Live Countdown Section - Mobile Optimized */}
                <div className="mb-3 sm:mb-4 lg:mb-5">
                  <LiveMatchCountdown matchDate={landingData.matches.next_match.match_date} />
                </div>

                {/* Venue Text Below Countdown - Mobile Optimized */}
                <div className="text-center">
                  <div className="text-white text-xs sm:text-sm lg:text-base font-medium mb-2 px-2">
                    📍 {landingData.matches.next_match.venue || 'ملعب النادي الرئيسي'}
                  </div>
                  
                  {/* Live Stream Option */}
                  {landingData.matches.next_match.live_stream_enabled && (
                    <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                      {landingData.matches.next_match.live_stream_url ? (
                        <a
                          href={landingData.matches.next_match.live_stream_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        >
                          <span className="w-4 h-4 bg-red-400 rounded-full mr-2 animate-pulse"></span>
                          <span className="text-sm sm:text-base">🔴 البث المباشر</span>
                        </a>
                      ) : (
                        <div className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold rounded-xl">
                          <span className="w-4 h-4 bg-gray-400 rounded-full mr-2"></span>
                          <span className="text-sm sm:text-base">📺 البث قريباً</span>
                        </div>
                      )}
                      
                      {landingData.matches.next_match.live_stream_platforms && landingData.matches.next_match.live_stream_platforms.length > 0 && (
                        <div className="flex items-center gap-2">
                          <span className="text-white text-xs sm:text-sm opacity-80">متاح على:</span>
                          <div className="flex gap-2">
                            {landingData.matches.next_match.live_stream_platforms.map((platform, index) => (
                              <span key={index} className="px-2 py-1 bg-white/20 text-white text-xs rounded-md">
                                {platform}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {landingData.matches.next_match.broadcast_info && (
                    <div className="mt-2 text-orange-300 text-xs sm:text-sm opacity-90">
                      {landingData.matches.next_match.broadcast_info}
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
          
          {/* Sidebar - Next 5 Upcoming Matches - 1/3 width */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl border border-white/20 shadow-2xl p-6">
              <h3 className="text-white text-xl font-bold mb-6 flex items-center">
                <span className="mr-3 text-2xl">📅</span>
                المباريات القادمة
              </h3>
              
              <div className="space-y-3">
                {/* Display upcoming matches from API */}
                {(() => {
                  // Get upcoming matches from landingData
                  const upcomingMatches = landingData.matches?.upcoming_matches || [];
                  
                  // Fallback: Show "no matches" message
                  if (upcomingMatches.length === 0) {
                    return (
                      <div className="text-center text-white/60 py-8">
                        <div className="text-4xl mb-3">📅</div>
                        <p className="text-sm">لا توجد مباريات قادمة أخرى</p>
                        <p className="text-xs mt-2 text-white/40">المباراة الرئيسية معروضة بالأعلى</p>
                      </div>
                    );
                  }

                  return upcomingMatches.map((match, index) => {
                    const matchDate = new Date(match.match_date || match.date);
                    
                    return (
                      <div key={match.id || index} className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/10 transition-all duration-300 hover:border-orange-400/50">
                        <div className="flex items-center justify-between text-sm mb-2">
                          <div className="flex items-center gap-2 flex-1">
                            <div className="text-white font-semibold text-xs">{match.home_team || 'الأمجاد'}</div>
                            <span className="text-orange-400 text-xs">×</span>
                            <div className="text-white font-semibold text-xs">{match.away_team || 'المنافس'}</div>
                          </div>
                        </div>
                        <div className="text-orange-300 text-[10px] flex items-center gap-2 flex-wrap">
                          <span className="flex items-center">
                            <span className="mr-1">📅</span>
                            {matchDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                          </span>
                          {match.tournament_name && (
                            <span className="flex items-center text-green-300">
                              <span className="mr-1">🏆</span>
                              {match.tournament_name}
                            </span>
                          )}
                          {match.venue && (
                            <span className="flex items-center text-blue-300">
                              <span className="mr-1">🏟️</span>
                              {match.venue}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
          
        </div>
        </div>
        </section>
      )}

      {/* Homepage showcase sections */}
      <ClubStatsBand API={API} />
      <NewsMagazine API={API} onOpenNews={setSelectedNews} />
      <MatchCenter API={API} />
      <PlayersStrip API={API} />

      {/* Board Member Modal */}
      {showBoardMemberModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 space-x-reverse">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <span className="text-2xl">👔</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">
                      {editingBoardMember ? 'تعديل عضو مجلس الإدارة' : 'إضافة عضو مجلس إدارة جديد'}
                    </h2>
                    <p className="text-blue-100 text-sm">
                      {editingBoardMember ? 'تحديث بيانات العضو' : 'إضافة عضو جديد لمجلس الإدارة'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowBoardMemberModal(false)}
                  className="text-white hover:text-blue-200 transition-colors"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      اسم العضو *
                    </label>
                    <input
                      type="text"
                      value={boardMemberForm.name}
                      onChange={(e) => setBoardMemberForm({...boardMemberForm, name: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="اسم العضو الكامل"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      المنصب *
                    </label>
                    <input
                      type="text"
                      value={boardMemberForm.position}
                      onChange={(e) => setBoardMemberForm({...boardMemberForm, position: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="رئيس مجلس الإدارة، المدير التنفيذي، إلخ"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    نبذة عن العضو
                  </label>
                  <textarea
                    value={boardMemberForm.description}
                    onChange={(e) => setBoardMemberForm({...boardMemberForm, description: e.target.value})}
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none"
                    placeholder="نبذة مختصرة عن العضو وخبراته..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      value={boardMemberForm.email}
                      onChange={(e) => setBoardMemberForm({...boardMemberForm, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="example@alamjadclub.sa"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      value={boardMemberForm.phone}
                      onChange={(e) => setBoardMemberForm({...boardMemberForm, phone: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="+966 XX XXX XXXX"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      سنوات الخبرة
                    </label>
                    <input
                      type="text"
                      value={boardMemberForm.experience}
                      onChange={(e) => setBoardMemberForm({...boardMemberForm, experience: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="15 عاماً في إدارة الأندية الرياضية"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ترتيب العرض
                    </label>
                    <input
                      type="number"
                      value={boardMemberForm.order}
                      onChange={(e) => setBoardMemberForm({...boardMemberForm, order: parseInt(e.target.value) || 0})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    رابط الصورة الشخصية
                  </label>
                  <input
                    type="url"
                    value={boardMemberForm.image_url}
                    onChange={(e) => setBoardMemberForm({...boardMemberForm, image_url: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                    placeholder="https://example.com/photo.jpg"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-between">
              <button
                onClick={() => setShowBoardMemberModal(false)}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    
                    if (editingBoardMember) {
                      // Update existing member
                      await axios.put(`${API}/api/board-members/${editingBoardMember.id}`, boardMemberForm, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                    } else {
                      // Create new member
                      await axios.post(`${API}/api/board-members`, boardMemberForm, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                    }
                    
                    // إشعار نجاح
                    const notification = document.createElement('div');
                    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50';
                    notification.innerHTML = `
                      <div class="flex items-center">
                        <span class="text-xl mr-2">✅</span>
                        <span>تم ${editingBoardMember ? 'تحديث' : 'إضافة'} عضو مجلس الإدارة بنجاح</span>
                      </div>
                    `;
                    document.body.appendChild(notification);
                    setTimeout(() => {
                      if (document.body.contains(notification)) {
                        document.body.removeChild(notification);
                      }
                    }, 3000);
                    
                    // Reset form
                    setEditingBoardMember(null);
                    setBoardMemberForm({
                      name: '',
                      position: '',
                      description: '',
                      email: '',
                      phone: '',
                      experience: '',
                      image_url: '',
                      order: 0
                    });
                    setShowBoardMemberModal(false);
                    
                    // Refresh board members
                    const response = await axios.get(`${API}/api/board-members`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    setBoardMembers(response.data.members || []);
                    
                  } catch (error) {
                    console.error('خطأ في حفظ عضو مجلس الإدارة:', error);
                    alert('حدث خطأ في حفظ عضو مجلس الإدارة');
                  }
                }}
                disabled={!boardMemberForm.name || !boardMemberForm.position}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingBoardMember ? 'تحديث' : 'إضافة'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Legal Pages Modal */}
      {showLegalModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 space-x-reverse">
                  <div className="bg-white/20 p-2 rounded-lg">
                    <span className="text-2xl">{legalModalType === 'privacy' ? '🔒' : '📋'}</span>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">
                      {legalModalType === 'privacy' ? 'سياسة الخصوصية' : 'شروط الاستخدام'}
                    </h2>
                    <p className="text-orange-100 text-sm">
                      {legalModalType === 'privacy' 
                        ? 'كيف نحمي ونستخدم معلوماتك الشخصية'
                        : 'الشروط والأحكام لاستخدام موقعنا'
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowLegalModal(false)}
                  className="text-white hover:text-orange-200 transition-colors"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="prose prose-lg max-w-none text-right" dir="rtl">
                {legalModalType === 'privacy' ? (
                  <div className="whitespace-pre-wrap leading-relaxed text-gray-700">
                    {publicLegalPages.privacy_policy || `نحن في نادي الأمجاد نلتزم بحماية خصوصية زوار موقعنا. تشرح هذه السياسة كيف نجمع ونستخدم ونحمي معلوماتك الشخصية.

المعلومات التي نجمعها:
- الاسم والبريد الإلكتروني عند التواصل معنا
- معلومات الاستخدام العامة للموقع

كيف نستخدم معلوماتك:
- للرد على استفساراتك
- لتحسين خدماتنا
-  لإرسال النشرات الإخبارية (بموافقتك)

حماية المعلومات:
نستخدم تقنيات أمان متقدمة لحماية معلوماتك الشخصية.

التواصل معنا:
في حال كان لديك أي استفسارات حول سياسة الخصوصية، يمكنك التواصل معنا عبر البريد الإلكتروني أو الهاتف.`}
                  </div>
                ) : (
                  <div className="whitespace-pre-wrap leading-relaxed text-gray-700">
                    {publicLegalPages.terms_of_use || `بإستخدامك لموقع نادي الأمجاد، فإنك توافق على الشروط والأحكام التالية:

الاستخدام المقبول:
- يُسمح بتصفح الموقع للأغراض الشخصية والتعليمية
- يُمنع استخدام الموقع لأي أنشطة غير قانونية
- يُمنع محاولة اختراق أو إلحاق الضرر بالموقع

حقوق الملكية الفكرية:
- جميع المحتويات محمية بحقوق الطبع والنشر
- شعار النادي وعلاماته التجارية مملوكة للنادي
- يُمنع استخدام المحتوى دون إذن مسبق

المسؤولية:
- النادي غير مسؤول عن أي أضرار ناتجة عن استخدام الموقع
- المحتوى مقدم كما هو دون ضمانات

التحديثات:
- يحق للنادي تحديث هذه الشروط في أي وقت
- ستصبح التحديثات سارية فور نشرها

التواصل:
للاستفسارات حول شروط الاستخدام، يرجى التواصل معنا.`}
                  </div>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4">
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-500">
                  آخر تحديث: {new Date().toLocaleDateString('ar-SA')}
                </div>
                <button
                  onClick={() => setShowLegalModal(false)}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
                >
                  إغلاق
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Premium Board of Directors Modal with Ultra Animations */}
      {showBoardModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-md z-50 flex items-center justify-center p-4
                       animate-modal-backdrop-fade">
          <div className="bg-white rounded-3xl max-w-6xl w-full max-h-[90vh] overflow-hidden shadow-2xl
                         animate-modal-slide-in transform-gpu hover:shadow-3xl transition-shadow duration-500">
            
            {/* Premium Modal Header with Gradient Animation */}
            <div className="bg-gradient-to-r from-orange-500 via-orange-600 to-red-600 text-white px-8 py-6 
                           flex items-center justify-between relative overflow-hidden animate-gradient-x">
              
              {/* Background Animation Elements */}
              <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-3 right-16 w-24 h-24 bg-white/10 rounded-full animate-float"></div>
                <div className="absolute bottom-3 left-16 w-16 h-16 bg-white/5 rounded-full animate-float" 
                     style={{animationDelay: '1.5s'}}></div>
                <div className="absolute top-1/2 left-1/3 w-8 h-8 bg-white/5 rounded-full animate-ping-slow"></div>
                <div className="absolute top-2 left-1/2 w-12 h-12 bg-white/5 rounded-full animate-ping-slow" 
                     style={{animationDelay: '0.8s'}}></div>
              </div>
              
              <div className="flex items-center relative z-10">
                <div className="text-4xl mr-4 animate-bounce animate-slide-in-right" style={{animationDelay: '0.3s'}}>👥</div>
                <div className="animate-slide-in-right animation-delay-200">
                  <h2 className="text-3xl font-bold">مجلس الإدارة</h2>
                  <p className="text-orange-100 mt-1 animate-fade-in-up animation-delay-400">
                    تعرف على أعضاء مجلس إدارة نادي الأمجاد
                  </p>
                </div>
              </div>
              
              <button
                onClick={() => setShowBoardModal(false)}
                className="text-white hover:text-orange-200 transition-all duration-300 text-3xl font-light
                          transform hover:scale-110 hover:rotate-90 hover:shadow-lg p-2 rounded-xl
                          hover:bg-white/20 animate-fade-in-left relative z-10"
              >
                ×
              </button>
            </div>

            {/* Enhanced Content with Premium Styling */}
            <div className="p-8 max-h-[calc(90vh-140px)] overflow-y-auto bg-gradient-to-br from-white to-orange-50/20">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {publicBoardMembers.map((member, index) => (
                  <div key={member.id || index} 
                       className="bg-gradient-to-br from-white to-gray-50 rounded-3xl p-6 border border-orange-100 
                                 hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 
                                 hover:border-orange-200 animate-fade-in-up relative overflow-hidden
                                 transform hover:scale-105"
                       style={{animationDelay: `${index * 0.1}s`}}>
                    
                    {/* Sparkle Effects */}
                    <div className="absolute top-2 right-2 w-2 h-2 bg-orange-400 rounded-full animate-ping opacity-60"></div>
                    <div className="absolute bottom-2 left-2 w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce opacity-50" 
                         style={{animationDelay: `${index * 0.2}s`}}></div>
                    
                    {/* Member Image with Enhanced Animation */}
                    <div className="text-center mb-6">
                      {member.image_url ? (
                        <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-4 border-orange-200 shadow-lg
                                       transform hover:scale-110 hover:rotate-6 transition-all duration-300
                                       animate-premium-logo-float relative">
                          <img 
                            src={member.image_url.startsWith('http') ? member.image_url : `${API}${member.image_url}`}
                            alt={member.name}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 rounded-full border-2 border-orange-300 animate-ping-slow opacity-0 hover:opacity-100"></div>
                        </div>
                      ) : (
                        <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600 
                                       flex items-center justify-center border-4 border-orange-200 shadow-lg
                                       transform hover:scale-110 hover:rotate-6 transition-all duration-300
                                       animate-premium-logo-float animate-gradient-x relative">
                          <span className="text-white text-3xl font-bold">
                            {member.name ? member.name.charAt(0) : '👤'}
                          </span>
                          <div className="absolute inset-0 rounded-full border-2 border-orange-300 animate-ping-slow opacity-0 hover:opacity-100"></div>
                        </div>
                      )}
                    </div>

                    {/* Member Info with Enhanced Styling */}
                    <div className="text-center space-y-3">
                      <h3 className="text-xl font-bold text-gray-900 hover:text-orange-600 transition-colors duration-300">
                        {member.name}
                      </h3>
                      <p className="text-lg font-semibold text-orange-600 bg-gradient-to-r from-orange-50 to-red-50 
                                   px-3 py-1 rounded-full border border-orange-200 hover:shadow-md transition-shadow duration-300">
                        {member.position}
                      </p>
                      
                      {member.description && (
                        <p className="text-gray-600 text-sm leading-relaxed hover:text-gray-800 transition-colors duration-300">
                          {member.description}
                        </p>
                      )}

                      {member.experience && (
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-3 mt-4 
                                       border border-blue-200 hover:shadow-md transition-all duration-300 hover:scale-105">
                          <p className="text-blue-800 text-sm font-medium flex items-center justify-center">
                            <span className="text-lg mr-2 animate-bounce">🏆</span>
                            {member.experience}
                          </p>
                        </div>
                      )}

                      {/* Enhanced Contact Info */}
                      <div className="space-y-2 mt-4">
                        {member.email && (
                          <p className="text-gray-600 text-sm flex items-center justify-center hover:text-orange-600 
                                       transition-colors duration-300 cursor-pointer">
                            <span className="text-lg mr-2 animate-bounce" style={{animationDelay: '0.5s'}}>📧</span>
                            {member.email}
                          </p>
                        )}
                        {member.phone && (
                          <p className="text-gray-600 text-sm flex items-center justify-center hover:text-orange-600 
                                       transition-colors duration-300 cursor-pointer">
                            <span className="text-lg mr-2 animate-bounce" style={{animationDelay: '0.7s'}}>📱</span>
                            {member.phone}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {publicBoardMembers.length === 0 && (
                <div className="text-center py-12 animate-fade-in-up">
                  <div className="text-6xl mb-4 animate-bounce">👥</div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">لا توجد معلومات متاحة</h3>
                  <p className="text-gray-500">سيتم إضافة معلومات أعضاء مجلس الإدارة قريباً</p>
                </div>
              )}
            </div>

            {/* Premium Modal Footer with Enhanced Styling */}
            <div className="bg-gradient-to-r from-gray-50 to-orange-50/30 px-8 py-6 flex justify-center
                           border-t border-orange-100/50">
              <button
                onClick={() => setShowBoardModal(false)}
                className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 
                          text-white px-8 py-3 rounded-2xl font-semibold transition-all duration-300 
                          flex items-center transform hover:scale-105 hover:-translate-y-1 
                          shadow-lg hover:shadow-xl animate-fade-in-up animation-delay-600
                          relative overflow-hidden group"
              >
                {/* Button Background Animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-red-500 to-orange-500 
                               transform scale-x-0 group-hover:scale-x-100 transition-transform 
                               duration-300 origin-left"></div>
                
                <span className="text-lg mr-2 relative z-10 transform group-hover:rotate-12 transition-transform duration-300">✕</span>
                <span className="relative z-10">إغلاق</span>
                
                {/* Sparkle Effect */}
                <div className="absolute top-1 right-2 w-2 h-2 bg-white rounded-full 
                               opacity-0 group-hover:opacity-100 animate-ping transition-opacity duration-300"></div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact Us Modal */}
      <ContactUsModal 
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        clubInfo={landingData.clubInfo}
      />

 







      {/* Achievements Section */}
      <AchievementsPublicDisplay />

      <FeatureBoxes boxes={landingData.featureBoxes} />

      {/* Twitter Box Section - Moved below Achievements */}
      <StrategySection API={API} />
      <XNewsSection API={API} onOpenNews={setSelectedNews} xUrl={contactInfo.social_media?.x_url} />

      {/* Sponsors Marquee Component */}
      <SponsorsMarquee API={API} setShowContactModal={setShowContactModal} />

      {/* Footer */}
      <SiteFooter
        API={API}
        logoUrl={landingData.clubInfo.logo ? `${API}/files/${landingData.clubInfo.logo}` : '/club-logo-512.png'}
        clubName={clubBasicInfo.club_name || 'نادي الأمجاد'}
        description={clubBasicInfo.description}
        established={clubBasicInfo.established_year}
        location={clubBasicInfo.location}
        contactInfo={contactInfo}
        onContact={() => setShowContactModal(true)}
        onBoard={() => setShowBoardModal(true)}
        onLegal={openLegalModal}
      />

      {/* News Modal - Full News Display */}
      {selectedNews && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 flex-1">
                  تفاصيل الخبر
                </h2>
                <button
                  onClick={() => setSelectedNews(null)}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                >
                  <span className="text-gray-500 text-xl">×</span>
                </button>
              </div>
            </div>
            
            {/* Modal Content */}
            <div className="p-6">
              {/* News Images */}
              {selectedNews.images && selectedNews.images.length > 0 ? (
                <div className="mb-6 space-y-4">
                  {selectedNews.images.map((imageUrl, index) => (
                    <div key={index}>
                      <img 
                        src={imageUrl}
                        alt={`${selectedNews.title} - صورة ${index + 1}`}
                        className="w-full h-auto object-contain rounded-xl shadow-lg"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999"%3E❌%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (selectedNews.image_file_id || selectedNews.image_url) ? (
                <div className="mb-6">
                  <img 
                    src={selectedNews.image_file_id ? (selectedNews.image_file_id.startsWith('http') ? selectedNews.image_file_id : `${API}/files/${selectedNews.image_file_id}`) : selectedNews.image_url}
                    alt={selectedNews.title}
                    className="w-full max-h-[70vh] object-contain rounded-xl bg-slate-100"
                  />
                </div>
              ) : null}
              {selectedNews.source === 'twitter' && selectedNews.source_url && (
                <a data-testid="news-modal-source-link" href={selectedNews.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mb-4 text-xs font-bold text-white bg-black hover:bg-gray-800 px-3 py-1.5 rounded-full">
                  <svg viewBox="0 0 24 24" className="w-3 h-3" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                  عرض التغريدة الأصلية
                </a>
              )}
              
              {/* News Title */}
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 leading-tight">
                {selectedNews.title}
              </h1>
              
              {/* News Date and Info */}
              <div className="flex items-center space-x-4 space-x-reverse mb-6 text-sm text-gray-600">
                <div className="flex items-center">
                  <span className="ml-2">📅</span>
                  {formatSimpleDate(selectedNews.date || selectedNews.created_at)}
                </div>
                <div className="flex items-center" style={{display: 'none'}}>
                  <span className="ml-2">👁️</span>
                  مشاهدات: {Math.floor(Math.random() * 1000) + 100}
                </div>
              </div>
              
              {/* News Content */}
              <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                {selectedNews.summary && (
                  <div className="bg-orange-50 border-r-4 border-orange-500 p-4 mb-6 rounded">
                    <h3 className="font-bold text-orange-800 mb-2">ملخص الخبر:</h3>
                    <p className="text-orange-700">{selectedNews.summary}</p>
                  </div>
                )}
                
                <div className="text-gray-800 text-lg leading-relaxed whitespace-pre-wrap">
                  {selectedNews.content || selectedNews.summary || 'محتوى الخبر غير متوفر في الوقت الحالي. نعتذر عن هذا الإزعاج.'}
                </div>
              </div>
              
              {/* Share and Actions */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <button 
                      onClick={() => handleLike(selectedNews.id)}
                      style={{display: 'none'}}
                      className={`flex items-center space-x-2 space-x-reverse px-4 py-2 rounded-lg transition-colors ${
                        isLiked(selectedNews.id) 
                          ? 'bg-red-100 hover:bg-red-200 text-red-700' 
                          : 'bg-orange-100 hover:bg-orange-200 text-orange-700'
                      }`}
                    >
                      <span>{isLiked(selectedNews.id) ? '❤️' : '👍'}</span>
                      <span className="text-sm font-medium">
                        {isLiked(selectedNews.id) ? 'معجب' : 'أعجبني'} ({getLikes(selectedNews.id)})
                      </span>
                    </button>
                    <button 
                      onClick={() => handleShare(selectedNews)}
                      className="flex items-center space-x-2 space-x-reverse bg-blue-100 hover:bg-blue-200 text-blue-700 px-4 py-2 rounded-lg transition-colors"
                    >
                      <span>📤</span>
                      <span className="text-sm font-medium">مشاركة</span>
                    </button>
                  </div>
                  <div className="text-xs text-gray-500">
                    🏷️ رياضة • أخبار النادي
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All News Modal */}
      {showAllNewsModal && <AllNewsModal />}

      {/* Notification System */}
      {notification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[60] animate-bounce">
          <div className={`px-6 py-3 rounded-lg shadow-lg text-white font-medium ${
            notification.type === 'success' ? 'bg-green-500' :
            notification.type === 'error' ? 'bg-red-500' :
            'bg-blue-500'
          }`}>
            {notification.message}
          </div>
        </div>
      )}

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        
        /* Marquee Animation */
        @keyframes marquee {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes marquee-slow {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        @keyframes marquee-fast {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .animate-marquee-slow {
          animation: marquee-slow 45s linear infinite;
        }
        .animate-marquee-fast {
          animation: marquee-fast 15s linear infinite;
        }
      `}</style>
    </div>
  );
};

// Loading Component
const LoadingSpinner = ({ message = "جاري التحميل..." }) => (
  <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-600 mx-auto mb-4"></div>
      <p className="text-orange-600 font-medium text-lg">{message}</p>
    </div>
  </div>
);

// Authentication Context
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // التحقق من وجود token في localStorage عند تحميل التطبيق
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    console.log('🔄 فحص حالة الجلسة عند بدء التطبيق:', { hasToken: !!token, hasUserData: !!userData });
    
    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        console.log('👤 تم العثور على بيانات المستخدم المحفوظة:', parsedUser);
        
        // إضافة التوكن للـ axios headers
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // تحديث حالة المستخدم مباشرة
        setUser(parsedUser);
        console.log('✅ تم استعادة الجلسة بنجاح');
        
        // التحقق من صحة التوكن
        checkTokenValidity();
      } catch (error) {
        console.error('❌ خطأ في تحليل بيانات المستخدم المحفوظة:', error);
        logout();
      }
    } else {
      console.log('❌ لا يوجد جلسة محفوظة');
      setLoading(false);
    }
  }, []);

  const checkTokenValidity = async () => {
    try {
      console.log('🔍 فحص صحة التوكن...');
      const token = localStorage.getItem('token');
      console.log('🎟️ التوكن المحفوظ:', token ? `موجود (${token.length} حرف)` : 'مفقود');
      
      if (!token) {
        console.log('❌ لا يوجد توكن للفحص');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('✅ التوكن صالح:', response.data);
      setUser(response.data);
    } catch (error) {
      console.error('❌ التوكن غير صالح أو منتهي الصلاحية:', error.response?.status);
      
      if (error.response?.status === 401) {
        console.log('🔓 إزالة الجلسة المنتهية الصلاحية');
        logout();
      } else {
        console.error('❌ خطأ في فحص التوكن:', error.message);
        setLoading(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      console.log('🔑 محاولة تسجيل الدخول للمستخدم:', username);
      
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const { access_token, user_info } = response.data;
      console.log('📦 استجابة الخادم:', { access_token: access_token ? 'موجود' : 'مفقود', user_info });
      
      if (!access_token || !user_info) {
        console.log('❌ بيانات الاستجابة ناقصة');
        return { success: false, error: 'بيانات الاستجابة ناقصة' };
      }
      
      // حفظ التوكن ومعلومات المستخدم
      console.log('💾 محاولة حفظ البيانات في localStorage...');
      
      try {
        localStorage.setItem('token', access_token);
        localStorage.setItem('user', JSON.stringify(user_info));
        
        // التحقق من نجاح الحفظ
        const savedToken = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        
        console.log('✅ تم حفظ البيانات في localStorage:', {
          tokenSaved: !!savedToken,
          userSaved: !!savedUser,
          tokenLength: savedToken ? savedToken.length : 0
        });
        
        if (!savedToken || !savedUser) {
          console.error('❌ فشل في حفظ البيانات في localStorage');
          return { success: false, error: 'فشل في حفظ البيانات محلياً' };
        }
      } catch (storageError) {
        console.error('❌ خطأ في الوصول إلى localStorage:', storageError);
        return { success: false, error: 'مشكلة في تخزين البيانات محلياً' };
      }
      
      // إضافة التوكن للـ axios headers
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      console.log('🔗 تم إضافة التوكن لـ axios headers');
      
      // تحديث حالة المستخدم - هذا مهم للتحديث الفوري
      setUser(user_info);
      console.log('👤 تم تحديث حالة المستخدم:', user_info.username);
      
      // Force a delay to ensure React state is updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log('✅ تم إكمال عملية تسجيل الدخول بنجاح');
      
      return { success: true, user: user_info };
    } catch (error) {
      console.log('❌ خطأ في تسجيل الدخول:', error.response?.data?.detail || error.message);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'خطأ في تسجيل الدخول' 
      };
    }
  };

  const logout = () => {
    // إزالة التوكن ومعلومات المستخدم
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      loading,
      isAuthenticated: !!user
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Protected Route Component
const ProtectedRoute = ({ children, requiredRoles = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRoles && !requiredRoles.includes(user.role)) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">غير مسموح بالوصول</h2>
          <p className="text-gray-600">ليس لديك الصلاحية للوصول لهذه الصفحة</p>
        </div>
      </div>
    );
  }

  return children;
};

// Login Component
const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clubSettings, setClubSettings] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Fetch club settings for logo and name (using public endpoint)
  useEffect(() => {
    const fetchClubSettings = async () => {
      try {
        const response = await axios.get(`${API}/public/club-info`);
        setClubSettings(response.data);
        
        // Update document title
        if (response.data?.club_name) {
          document.title = `${response.data.club_name} - نظام إدارة النادي`;
        }
        
        // Update favicon in public page
        if (response.data?.logo_file_id) {
          const faviconLink = document.querySelector('link[rel="icon"]') || 
                              document.querySelector('link[rel="shortcut icon"]');
          if (faviconLink) {
            faviconLink.href = `${API}/files/${response.data.logo_file_id}`;
          } else {
            // Create new favicon link if it doesn't exist
            const newFaviconLink = document.createElement('link');
            newFaviconLink.rel = 'icon';
            newFaviconLink.type = 'image/x-icon';
            newFaviconLink.href = `${API}/files/${response.data.logo_file_id}`;
            document.head.appendChild(newFaviconLink);
          }
        }
      } catch (error) {
        console.error('خطأ في جلب إعدادات النادي:', error);
        // Fallback club info
        setClubSettings({
          club_name: 'نادي الأمجاد',
          logo_file_id: null
        });
      }
    };

    const fetchContactInfo = async () => {
      try {
        const response = await axios.get(`${API}/public/contact-info`);
        setContactInfo(response.data);
      } catch (error) {
        console.error('خطأ في جلب معلومات التواصل:', error);
      }
    };

    fetchClubSettings();
    fetchContactInfo();
  }, []);

  // إذا كان المستخدم مسجل دخول، توجيهه للوحة التحكم
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    console.log('🚀 handleSubmit triggered!', { username: formData.username, hasPassword: !!formData.password });
    
    e.preventDefault();
    setLoading(true);
    setError('');

    console.log('🔑 بدء عملية تسجيل الدخول...');
    
    try {
      const result = await login(formData.username, formData.password);
      
      console.log('📊 Login result:', result);
      
      if (result.success) {
        console.log('✅ تم تسجيل الدخول بنجاح!');
        setError('تم تسجيل الدخول بنجاح! جاري التوجيه...');
        
        // Force navigation after a short delay to ensure state update
        setTimeout(() => {
          console.log('🔄 Redirecting to dashboard...');
          navigate('/dashboard', { replace: true });
        }, 500);
        
      } else {
        console.log('❌ فشل تسجيل الدخول:', result.error);
        setError(result.error || 'خطأ في تسجيل الدخول');
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ خطأ غير متوقع في تسجيل الدخول:', error);
      setError('حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.');
      setLoading(false);
    }
  };

  return (
    <LoginView
      logoUrl={clubSettings?.logo_file_id ? `${API}/files/${clubSettings.logo_file_id}` : '/club-logo-512.png'}
      clubName={clubSettings?.club_name || 'نادي الأمجاد'}
      formData={formData}
      setFormData={setFormData}
      loading={loading}
      error={error}
      onSubmit={handleSubmit}
    />
  );
};

// App Layout component
const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [clubSettings, setClubSettings] = useState(null);

  // Fetch club settings for logo and name
  useEffect(() => {
    const fetchClubSettings = async () => {
      try {
        const response = await axios.get(`${API}/club-settings`);
        setClubSettings(response.data);
      } catch (error) {
        console.error('خطأ في جلب إعدادات النادي:', error);
      }
    };

    fetchClubSettings();
    
    // Listen for logo updates
    const handleLogoUpdate = () => {
      fetchClubSettings();
    };
    
    window.addEventListener('logoUpdated', handleLogoUpdate);
    window.addEventListener('refreshPublicData', handleLogoUpdate);
    
    return () => {
      window.removeEventListener('logoUpdated', handleLogoUpdate);
      window.removeEventListener('refreshPublicData', handleLogoUpdate);
    };
  }, []);

  const navigation = [
    { name: 'لوحة التحكم', href: '/dashboard', icon: '🏠' },
    { name: 'الفئات', href: '/categories', icon: '🏷️' },
    { name: 'الفرق', href: '/teams', icon: '👥' },
    { name: 'اللاعبين والإحصائيات', href: '/admin/players', icon: '👥' },
    { name: 'المباريات', href: '/matches', icon: '🏟️' },
    { name: 'البطولات', href: '/tournaments', icon: '🏆' },
    { name: 'الحوكمة و الاستراتيجية (برو)', href: '/governance-strategy-pro', icon: '🏛️' },
    { name: 'الإعدادات', href: '/settings', icon: '⚙️' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100" dir="rtl">
      {/* FIXED: Single Level Navigation - Remove Sticky Issues */}
      <nav className="bg-white/95 backdrop-blur-sm shadow-lg border-b border-gray-200/50 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Top Section: Logo + User Info */}
          <div className="flex justify-between items-center h-16 border-b border-gray-100">
            {/* Enhanced Logo Section */}
            <div className="flex items-center group">
              <div className="relative">
                {clubSettings?.logo_file_id ? (
                  <img 
                    src={`${API}/files/${clubSettings.logo_file_id}`}
                    alt="شعار النادي"
                    className="w-10 h-10 rounded-xl object-cover shadow-md ring-2 ring-orange-200/50 group-hover:ring-orange-300 transition-all duration-300"
                  />
                ) : (
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-all duration-300">
                    <span className="text-white font-bold text-xl">🏆</span>
                  </div>
                )}
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white animate-pulse"></div>
              </div>
              <div className="mr-3">
                <span className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                  {clubSettings?.name || 'نادي الأمجاد'}
                </span>
                <div className="text-xs text-gray-500 font-medium">نظام الإدارة المتكامل</div>
              </div>
            </div>

            {/* Enhanced User Menu */}
            <div className="flex items-center space-x-4 space-x-reverse">
              <div className="hidden sm:block text-right">
                <div className="flex items-center space-x-2 space-x-reverse bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-2 rounded-xl border border-gray-200/50">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <div>
                    <span className="text-sm font-bold text-gray-800">{user?.full_name}</span>
                    <div className="text-xs text-gray-500 capitalize">{user?.role}</div>
                  </div>
                </div>
              </div>
              
              <button
                onClick={logout}
                className="group p-2 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all duration-300 transform hover:scale-110"
                title="تسجيل الخروج"
              >
                <span className="text-xl group-hover:animate-bounce">🚪</span>
              </button>

              {/* Mobile menu button */}
              <button
                type="button"
                className="md:hidden p-2 rounded-xl text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-all duration-300 transform hover:scale-110"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <span className="text-xl transition-transform duration-300">
                  {isMobileMenuOpen ? '✖️' : '☰'}
                </span>
              </button>
            </div>
          </div>

          {/* Navigation Menu - Same Level */}
          <div className="flex justify-center py-4">
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-3 space-x-reverse">
              {navigation.map((item, index) => {
                const isActive = location.pathname === item.href;
                const isGovernance = item.href === '/governance-strategy-pro';
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      relative px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 group
                      ${isActive 
                        ? isGovernance 
                          ? 'text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg shadow-purple-200' 
                          : 'text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-200'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-white hover:shadow-md'
                      }
                      ${isGovernance && !isActive ? 'ring-2 ring-purple-200 hover:ring-purple-300' : ''}
                    `}
                  >
                    <div className="flex items-center space-x-2 space-x-reverse">
                      <span className={`text-lg transition-transform duration-300 group-hover:scale-110 ${isActive ? 'animate-pulse' : ''}`}>
                        {item.icon}
                      </span>
                      <span className="font-medium">{item.name}</span>
                    </div>
                    
                    {/* Active indicator */}
                    {isActive && (
                      <div className={`absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full animate-bounce ${
                        isGovernance ? 'bg-purple-300' : 'bg-orange-300'
                      }`}></div>
                    )}
                    
                    {/* Governance Pro Badge */}
                    {isGovernance && (
                      <div className="absolute -top-2 -right-2 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-xs px-2 py-0.5 rounded-full font-bold shadow-md">
                        PRO
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200/50 py-4">
              <div className="space-y-2">
                {navigation.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  const isGovernance = item.href === '/governance-strategy-pro';
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`
                        relative flex items-center gap-3 px-4 py-3 rounded-xl text-base font-semibold transition-all duration-300 transform hover:scale-105 group
                        ${isActive 
                          ? isGovernance 
                            ? 'text-white bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg' 
                            : 'text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg'
                          : 'text-gray-600 hover:text-gray-800 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100'
                        }
                        ${isGovernance && !isActive ? 'border-2 border-purple-200' : ''}
                      `}
                    >
                      <span className="flex-1">{item.name}</span>
                      
                      {/* Governance Pro Badge */}
                      {isGovernance && (
                        <div className="bg-purple-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                          PRO
                        </div>
                      )}
                      
                      {/* Active indicator */}
                      {isActive && (
                        <div className={`w-2 h-2 rounded-full animate-bounce ${
                          isGovernance ? 'bg-purple-300' : 'bg-orange-300'
                        }`}></div>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Enhanced Main Content */}
      <main className="py-8 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {children}
        </div>
      </main>
    </div>
  );
};

// Enhanced Dashboard Component with Professional Design
const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [clubSettings, setClubSettings] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeOfDay, setTimeOfDay] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [dashboardBg, setDashboardBg] = useState(null);
  const [activities, setActivities] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [visitStats, setVisitStats] = useState({
    today: 0,
    week: 0,
    month: 0,
    total: 0,
    daily_visits: []
  });

  useEffect(() => {
    fetchData();
    setTimeOfDay(getTimeOfDay());
    
    // Update time every second using Saudi time
    const timeInterval = setInterval(() => {
      const now = new Date();
      const saudiTime = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Riyadh"}));
      setCurrentTime(saudiTime);
      setTimeOfDay(getTimeOfDay());
    }, 1000);
    
    // Auto refresh dashboard data every 30 seconds
    const refreshInterval = setInterval(() => {
      if (autoRefreshEnabled) {
        refreshDashboardData();
      }
    }, 30000);
    
    // Cleanup intervals on component unmount
    return () => {
      clearInterval(timeInterval);
      clearInterval(refreshInterval);
    };
  }, [autoRefreshEnabled]);

  const getTimeOfDay = () => {
    // استخدام توقيت السعودية
    const now = new Date();
    const saudiTime = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Riyadh"}));
    const hour = saudiTime.getHours();
    if (hour < 12) return 'صباح الخير';
    if (hour < 17) return 'مساء الخير';
    return 'مساء الخير';
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [statsRes, settingsRes, bgRes, userRes, visitStatsRes] = await Promise.all([
        axios.get(`${API}/statistics/dashboard`),
        axios.get(`${API}/club-settings`),
        axios.get(`${API}/dashboard-background`).catch(() => ({ data: null })),
        axios.get(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: null })),
        axios.get(`${API}/api/statistics/visits`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => ({ data: { today: 0, week: 0, month: 0, total: 0, daily_visits: [] } }))
      ]);
      setStats(statsRes.data);
      setClubSettings(settingsRes.data);
      setDashboardBg(bgRes.data);
      setCurrentUser(userRes.data);
      setVisitStats(visitStatsRes.data);
      
      // Set auto refresh setting from backend
      setAutoRefreshEnabled(settingsRes.data?.auto_refresh_enabled !== false);
      
      // Also fetch activities and latest news
      await refreshDashboardData();
    } catch (error) {
      console.error("خطأ في تحميل البيانات:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch activities from backend
  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/activity-log?limit=4`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const activitiesData = response.data.map(activity => ({
        ...activity,
        ...getActivityDisplay(activity.action)
      }));
      setActivities(activitiesData);
    } catch (error) {
      console.error('خطأ في جلب سجل النشاط:', error);
      // Use fallback data if API fails
      setActivities([
        { action: "إضافة لاعب جديد", user_name: "الإدارة", created_at: new Date(Date.now() - 300000).toISOString(), icon: "👤", color: "bg-green-100 text-green-600" },
        { action: "تحديث نتيجة مباراة", user_name: "المدرب", created_at: new Date(Date.now() - 900000).toISOString(), icon: "🏟️", color: "bg-blue-100 text-blue-600" },
        { action: "إضافة خبر جديد", user_name: "المسؤول", created_at: new Date(Date.now() - 3600000).toISOString(), icon: "📰", color: "bg-yellow-100 text-yellow-600" },
        { action: "حذف فريق منافس", user_name: "الإدارة", created_at: new Date(Date.now() - 7200000).toISOString(), icon: "👥", color: "bg-red-100 text-red-600" }
      ]);
    }
  };

  // Fetch latest news from backend
  const fetchLatestNews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/news?limit=3`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLatestNews(response.data.filter(news => news.published).slice(0, 3));
    } catch (error) {
      console.error('خطأ في جلب آخر الأخبار:', error);
      // Use fallback data if API fails
      setLatestNews([
        { title: "فوز النادي في المباراة الأخيرة", category: "نتائج", created_at: new Date(Date.now() - 7200000).toISOString() },
        { title: "تجديد عقد اللاعب محمد أحمد", category: "انتقالات", created_at: new Date(Date.now() - 86400000).toISOString() },
        { title: "بدء التسجيل للموسم الجديد", category: "إعلانات", created_at: new Date(Date.now() - 172800000).toISOString() }
      ]);
    }
  };

  // Auto refresh data function
  const refreshDashboardData = async () => {
    if (autoRefreshEnabled) {
      await Promise.all([
        fetchActivities(),
        fetchLatestNews()
      ]);
    }
  };

  // Helper function to get activity icon and color
  const getActivityDisplay = (action) => {
    if (action.includes('لاعب')) return { icon: "👤", color: "bg-green-100 text-green-600" };
    if (action.includes('مباراة')) return { icon: "🏟️", color: "bg-blue-100 text-blue-600" };
    if (action.includes('خبر')) return { icon: "📰", color: "bg-yellow-100 text-yellow-600" };
    if (action.includes('بطولة')) return { icon: "🏆", color: "bg-purple-100 text-purple-600" };
    if (action.includes('فريق')) return { icon: "👥", color: "bg-indigo-100 text-indigo-600" };
    if (action.includes('إعدادات')) return { icon: "⚙️", color: "bg-gray-100 text-gray-600" };
    return { icon: "📋", color: "bg-orange-100 text-orange-600" };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">جاري تحميل لوحة التحكم...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100" dir="rtl">
      {/* Enhanced Professional Header with Smart Background Integration */}
      <div className="relative overflow-hidden bg-gradient-to-br from-orange-700 via-orange-600 to-orange-800 text-white shadow-2xl">
        {/* Background Image Overlay with Enhanced Blend */}
        {dashboardBg && (
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ 
              backgroundImage: `url(${API}/files/${dashboardBg})`,
              opacity: 0.4
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-orange-800/60 via-orange-700/40 to-orange-900/70"></div>
          </div>
        )}
        
        {/* Dynamic Overlay for Better Text Contrast */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/20 via-transparent to-black/30"></div>
        
        {/* Animated Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 transform translate-x-32 -translate-y-32 opacity-10">
          <div className="w-full h-full rounded-full bg-gradient-to-br from-white/20 to-transparent animate-pulse"></div>
        </div>
        <div className="absolute bottom-0 left-0 w-64 h-64 transform -translate-x-16 translate-y-16 opacity-10">
          <div className="w-full h-full rounded-full bg-gradient-to-br from-white/15 to-transparent animate-pulse animation-delay-1000"></div>
        </div>
        <div className="absolute top-1/2 left-1/4 w-48 h-48 transform -translate-x-12 -translate-y-12 opacity-5">
          <div className="w-full h-full rounded-full bg-gradient-to-br from-white/10 to-transparent animate-pulse animation-delay-2000"></div>
        </div>
        
        <div className="relative px-8 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between">
              <div className="space-y-4">
                <div className="relative flex items-center">
                  {/* Enhanced Professional Background Logo with Perfect Transparency */}
                  {clubSettings?.logo_file_id && (
                    <div className="absolute -right-40 -top-40 w-[500px] h-[500px] opacity-5 pointer-events-none">
                      <img 
                        src={`${API}/files/${clubSettings.logo_file_id}`}
                        alt=""
                        className="w-full h-full object-contain filter drop-shadow-2xl transform rotate-6 transition-all duration-1000"
                        style={{
                          filter: 'drop-shadow(0 25px 50px rgba(255,255,255,0.1)) brightness(1.3) contrast(1.2)',
                          mixBlendMode: 'overlay',
                          opacity: '0.12'
                        }}
                      />
                      {/* Additional subtle logo for depth */}
                      <img 
                        src={`${API}/files/${clubSettings.logo_file_id}`}
                        alt=""
                        className="absolute inset-0 w-full h-full object-contain transform -rotate-3 transition-all duration-1500"
                        style={{
                          filter: 'drop-shadow(0 15px 35px rgba(255,255,255,0.08)) brightness(0.9)',
                          mixBlendMode: 'soft-light',
                          opacity: '0.08'
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Enhanced Welcome Text with Professional Design and Dual Calendar */}
                  <div className="relative z-20">
                    <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 drop-shadow-2xl text-white bg-gradient-to-r from-white to-orange-100 bg-clip-text text-transparent">
                      {timeOfDay}
                    </h1>
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-white/95 mb-4 drop-shadow-lg">
                      مرحباً، {currentUser?.username || currentUser?.name || "المدير"}
                    </h2>
                    <p className="text-white/85 text-lg md:text-xl drop-shadow mb-8">لوحة الإدارة المتقدمة</p>
                    
                    {/* Enhanced Professional Date & Time Display */}
                    <div className="bg-gradient-to-r from-white/15 to-white/10 backdrop-blur-lg rounded-2xl p-4 lg:p-6 border border-white/20 max-w-lg shadow-2xl">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Gregorian Date */}
                        <div className="text-center md:text-right">
                          <div className="text-orange-100 text-xs font-medium mb-1">التاريخ الميلادي</div>
                          <div className="text-white text-sm md:text-base font-bold">
                            {currentTime.toLocaleDateString('ar-SA-u-ca-gregory', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </div>
                        </div>
                        
                        {/* Hijri Date */}
                        <div className="text-center md:text-left">
                          <div className="text-orange-100 text-xs font-medium mb-1">التاريخ الهجري</div>
                          <div className="text-white text-sm md:text-base font-bold">
                            {currentTime.toLocaleDateString('ar-SA-u-ca-islamic', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            }).replace(/\d{4}/, (match) => match + 'هـ')}
                          </div>
                        </div>
                      </div>
                      
                      {/* Time Display */}
                      <div className="mt-4 text-center border-t border-white/20 pt-3">
                        <div className="text-orange-100 text-xs font-medium mb-1">الوقت الحالي</div>
                        <div className="text-white text-xl md:text-2xl font-bold tracking-wider">
                          {currentTime.toLocaleTimeString('ar-SA', {
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                            hour12: true
                          })}
                        </div>
                        <div className="text-orange-200 text-xs mt-1">
                          {currentTime.toLocaleDateString('ar-SA', { weekday: 'long' })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Enhanced Statistics Dashboard with Professional Glassmorphism Design */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          
          {/* Total Players - Enhanced */}
          <div className="group relative bg-gradient-to-br from-emerald-400 via-green-500 to-teal-600 rounded-3xl shadow-2xl p-6 text-white hover:shadow-emerald-300/50 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-8 -translate-x-8"></div>
            
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <div className="w-2 h-2 bg-emerald-200 rounded-full ml-2 animate-pulse"></div>
                  <h3 className="text-sm font-bold text-emerald-50">إجمالي اللاعبين</h3>
                </div>
                <p className="text-4xl font-black mb-1 group-hover:scale-110 transition-transform duration-300">
                  {stats?.total_players || 0}
                </p>
                <p className="text-xs text-emerald-100/80 font-medium">مسجل حالياً</p>
              </div>
              <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-md shadow-xl group-hover:rotate-12 transition-transform duration-300">
                <span className="text-3xl">👤</span>
              </div>
            </div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-full group-hover:-translate-x-full transition-transform duration-1000"></div>
          </div>

          {/* Total Matches - Enhanced */}
          <div className="group relative bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 rounded-3xl shadow-2xl p-6 text-white hover:shadow-blue-300/50 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-8 -translate-x-8"></div>
            
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <div className="w-2 h-2 bg-blue-200 rounded-full ml-2 animate-pulse"></div>
                  <h3 className="text-sm font-bold text-blue-50">إجمالي المباريات</h3>
                </div>
                <p className="text-4xl font-black mb-1 group-hover:scale-110 transition-transform duration-300">
                  {stats?.total_matches || 0}
                </p>
                <p className="text-xs text-blue-100/80 font-medium">هذا الموسم</p>
              </div>
              <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-md shadow-xl group-hover:rotate-12 transition-transform duration-300">
                <span className="text-3xl">🏟️</span>
              </div>
            </div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-full group-hover:-translate-x-full transition-transform duration-1000"></div>
          </div>

          {/* Total Teams - Enhanced */}
          <div className="group relative bg-gradient-to-br from-purple-400 via-purple-500 to-violet-600 rounded-3xl shadow-2xl p-6 text-white hover:shadow-purple-300/50 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-8 -translate-x-8"></div>
            
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <div className="w-2 h-2 bg-purple-200 rounded-full ml-2 animate-pulse"></div>
                  <h3 className="text-sm font-bold text-purple-50">الفرق المنافسة</h3>
                </div>
                <p className="text-4xl font-black mb-1 group-hover:scale-110 transition-transform duration-300">
                  {stats?.total_teams || 0}
                </p>
                <p className="text-xs text-purple-100/80 font-medium">مسجلة</p>
              </div>
              <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-md shadow-xl group-hover:rotate-12 transition-transform duration-300">
                <span className="text-3xl">👥</span>
              </div>
            </div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-full group-hover:-translate-x-full transition-transform duration-1000"></div>
          </div>

          {/* Total Goals - Enhanced */}
          <div className="group relative bg-gradient-to-br from-orange-400 via-orange-500 to-red-500 rounded-3xl shadow-2xl p-6 text-white hover:shadow-orange-300/50 transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-white/5 backdrop-blur-sm"></div>
            <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-10 translate-x-10"></div>
            <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/10 rounded-full translate-y-8 -translate-x-8"></div>
            
            <div className="relative z-10 flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <div className="w-2 h-2 bg-orange-200 rounded-full ml-2 animate-pulse"></div>
                  <h3 className="text-sm font-bold text-orange-50">إجمالي الأهداف</h3>
                </div>
                <p className="text-4xl font-black mb-1 group-hover:scale-110 transition-transform duration-300">
                  {stats?.total_goals || 0}
                </p>
                <p className="text-xs text-orange-100/80 font-medium">المسجلة</p>
              </div>
              <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-md shadow-xl group-hover:rotate-12 transition-transform duration-300">
                <span className="text-3xl">⚽</span>
              </div>
            </div>
            
            {/* Shine effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -skew-x-12 transform translate-x-full group-hover:-translate-x-full transition-transform duration-1000"></div>
          </div>
        </div>

        {/* Enhanced Additional Statistics Row with Premium Animation */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          {/* Tournaments */}
          <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 border-r-4 border-yellow-500 
                         hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1
                         animate-fade-in-up group">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xs lg:text-sm font-medium text-gray-500 mb-1 group-hover:text-gray-700 transition-colors duration-300">
                  البطولات النشطة
                </h3>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 group-hover:text-yellow-600 transition-colors duration-300">
                  {stats?.total_tournaments || 0}
                </p>
                <p className="text-xs lg:text-sm text-yellow-600 font-medium mt-1 group-hover:text-yellow-700 transition-colors duration-300">
                  قيد التنفيذ
                </p>
              </div>
              <div className="p-3 lg:p-4 rounded-full bg-yellow-50 ml-2 group-hover:bg-yellow-100 transition-all duration-300">
                <span className="text-xl lg:text-2xl group-hover:scale-125 transition-transform duration-300">🏆</span>
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 border-r-4 border-indigo-500 
                         hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1
                         animate-fade-in-up animation-delay-100 group">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xs lg:text-sm font-medium text-gray-500 mb-1 group-hover:text-gray-700 transition-colors duration-300">
                  فئات اللاعبين
                </h3>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors duration-300">
                  {stats?.total_categories || 0}
                </p>
                <p className="text-xs lg:text-sm text-indigo-600 font-medium mt-1 group-hover:text-indigo-700 transition-colors duration-300">
                  مختلفة
                </p>
              </div>
              <div className="p-3 lg:p-4 rounded-full bg-indigo-50 ml-2 group-hover:bg-indigo-100 transition-all duration-300">
                <span className="text-xl lg:text-2xl group-hover:scale-125 transition-transform duration-300">🏷️</span>
              </div>
            </div>
          </div>

          {/* Total Visits Statistics - Fixed to use correct stats */}
          <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 border-r-4 border-cyan-500 hover:shadow-xl transition-all duration-300
                         transform hover:scale-105 hover:-translate-y-1 animate-fade-in-up animation-delay-200 group">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xs lg:text-sm font-medium text-gray-500 mb-1 group-hover:text-gray-700 transition-colors duration-300">
                  إجمالي الزيارات
                </h3>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 group-hover:text-cyan-600 transition-colors duration-300">
                  {stats?.total_visits || stats?.visits || 0}
                </p>
                <p className="text-xs lg:text-sm text-cyan-600 font-medium mt-1 group-hover:text-cyan-700 transition-colors duration-300">
                  منذ البداية
                </p>
              </div>
              <div className="p-3 lg:p-4 rounded-full bg-cyan-50 ml-2 group-hover:bg-cyan-100 transition-all duration-300">
                <span className="text-xl lg:text-2xl group-hover:scale-125 transition-transform duration-300">👥</span>
              </div>
            </div>
          </div>

          {/* Upcoming Matches - Next Two Weeks */}
          <div className="bg-white rounded-2xl shadow-lg p-4 lg:p-6 border-r-4 border-green-500 
                         hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:-translate-y-1
                         animate-fade-in-up animation-delay-300 group">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-xs lg:text-sm font-medium text-gray-500 mb-1 group-hover:text-gray-700 transition-colors duration-300">
                  المباريات القادمة
                </h3>
                <p className="text-2xl lg:text-3xl font-bold text-gray-900 group-hover:text-green-600 transition-colors duration-300">
                  {stats?.upcoming_matches || 0}
                </p>
                <p className="text-xs lg:text-sm text-green-600 font-medium mt-1 group-hover:text-green-700 transition-colors duration-300">
                  خلال أسبوعين
                </p>
              </div>
              <div className="p-3 lg:p-4 rounded-full bg-green-50 ml-2 group-hover:bg-green-100 transition-all duration-300">
                <span className="text-xl lg:text-2xl group-hover:scale-125 transition-transform duration-300">📅</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Performance Metrics with Premium Animation */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Win Rate */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 
                         transform hover:scale-105 hover:-translate-y-2 transition-all duration-500
                         animate-fade-in-up hover:shadow-2xl group">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <span className="text-2xl mr-3 group-hover:scale-110 transition-transform duration-300">📈</span>
              معدل الفوز
            </h3>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2 group-hover:text-green-500 transition-colors duration-300">
                {stats?.win_rate ? `${stats.win_rate}%` : '--'}
              </div>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                من إجمالي {stats?.total_club_matches || 0} مباراة
              </p>
              <div className="mt-4 bg-gray-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-green-400 to-green-600 h-3 rounded-full 
                           transition-all duration-1000 ease-out transform origin-left
                           hover:from-green-500 hover:to-green-700"
                  style={{ 
                    width: `${stats?.win_rate || 0}%`,
                    transform: `scaleX(${(stats?.win_rate || 0) / 100})`
                  }}
                ></div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {stats?.wins || 0} فوز • {stats?.draws || 0} تعادل • {stats?.losses || 0} هزيمة
              </div>
            </div>
          </div>

          {/* Goals Average - Enhanced with Club-specific Goals */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100
                         transform hover:scale-105 hover:-translate-y-2 transition-all duration-500
                         animate-fade-in-up animation-delay-200 hover:shadow-2xl group">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <span className="text-2xl mr-3 group-hover:scale-110 transition-transform duration-300">⚽</span>
              معدل الأهداف
            </h3>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2 group-hover:text-orange-500 transition-colors duration-300">
                {stats?.goals_average || '--'}
              </div>
              <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                هدف لكل مباراة
              </p>
              <div className="mt-4 flex justify-center space-x-1 space-x-reverse">
                {[1,2,3,4,5].map((star) => (
                  <span 
                    key={star} 
                    className={`text-2xl transition-all duration-300 hover:scale-125 cursor-pointer
                              ${(stats?.goals_average || 0) >= star ? 'text-orange-400 animate-pulse' : 'text-gray-200'}`}
                    style={{animationDelay: `${star * 0.1}s`}}
                  >
                    ⭐
                  </span>
                ))}
              </div>
              <div className="mt-2 text-xs text-gray-600 space-y-1">
                <div className="font-semibold text-green-600">
                  أهداف الأمجاد: {stats?.club_goals_for || 0}
                </div>
                {stats?.club_goals_against > 0 && (
                  <div className="text-gray-500 text-[10px]">
                    الأهداف المستقبلة: {stats.club_goals_against}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Carded Players - Enhanced with Animation */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100
                         transform hover:scale-105 hover:-translate-y-2 transition-all duration-500
                         animate-fade-in-up animation-delay-400 hover:shadow-2xl group">
            <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
              <span className="text-2xl mr-3 group-hover:scale-110 transition-transform duration-300">🟡</span>
              أكثر 3 لاعبين حصولاً على الكروت
            </h3>
            <div className="space-y-3">
              {stats?.top_carded_players && stats.top_carded_players.length > 0 ? (
                stats.top_carded_players.map((player, index) => (
                  <div key={player.player_id} 
                       className="flex justify-between items-center p-3 bg-gradient-to-r from-yellow-50 to-red-50 
                                 rounded-lg border border-yellow-200 hover:border-yellow-300
                                 transform hover:scale-105 transition-all duration-300 hover:shadow-md
                                 animate-slide-in-right group/item"
                       style={{animationDelay: `${index * 0.1}s`}}>
                    <div className="flex items-center">
                      <span className="text-lg mr-2 group-hover/item:scale-125 transition-transform duration-300">
                        {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                      </span>
                      <div>
                        <span className="font-semibold text-gray-800 group-hover/item:text-gray-900 transition-colors duration-300">
                          {player.player_name}
                        </span>
                        <div className="text-xs text-gray-600 group-hover/item:text-gray-700 transition-colors duration-300">
                          {player.position}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 space-x-reverse">
                      {player.yellow_cards > 0 && (
                        <div className="flex items-center hover:scale-110 transition-transform duration-300">
                          <span className="text-yellow-500 mr-1 animate-pulse">🟡</span>
                          <span className="font-bold text-yellow-600">{player.yellow_cards}</span>
                        </div>
                      )}
                      {player.red_cards > 0 && (
                        <div className="flex items-center hover:scale-110 transition-transform duration-300">
                          <span className="text-red-500 mr-1 animate-pulse" style={{animationDelay: '0.5s'}}>🔴</span>
                          <span className="font-bold text-red-600">{player.red_cards}</span>
                        </div>
                      )}
                      <div className="bg-gray-100 px-2 py-1 rounded-full hover:bg-gray-200 transition-colors duration-300">
                        <span className="text-xs font-medium text-gray-700">المجموع: {player.total_cards}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4 animate-bounce">
                  <div className="text-4xl mb-2">⚽</div>
                  <p className="text-gray-500 text-sm">لا توجد بيانات للكروت</p>
                  <p className="text-xs text-gray-400">سيتم عرض الإحصائيات عند تسجيل أحداث المباريات</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Grid - Activity Log, News, Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-8">
          {/* Activity Log */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center">
                <span className="text-2xl mr-3">📊</span>
                سجل النشاط
              </h3>
              <Link 
                to="/activity-log" 
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                عرض الكل
              </Link>
            </div>
            
            <div className="space-y-4">
              {activities.length > 0 ? activities.slice(0, 4).map((activity, index) => (
                <div key={index} className="flex items-center space-x-3 space-x-reverse p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200">
                  <div className={`w-10 h-10 ${activity.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <span className="text-lg">{activity.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{activity.action}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-600">{activity.user_name || 'المستخدم'}</p>
                      <p className="text-xs text-gray-500">{getTimeAgo(activity.created_at)}</p>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8">
                  <span className="text-4xl mb-2 block">📊</span>
                  <p className="text-gray-500 text-sm">لا توجد أنشطة حديثة</p>
                </div>
              )}
            </div>
          </div>

          {/* News Section */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center">
                <span className="text-2xl mr-3">📰</span>
                آخر الأخبار
              </h3>
              <Link 
                to="/settings" 
                className="text-sm text-orange-600 hover:text-orange-700 font-medium"
              >
                إدارة الأخبار
              </Link>
            </div>
            
            <div className="space-y-4">
              {latestNews.length > 0 ? latestNews.map((news, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded-lg hover:border-orange-300 transition-colors">
                  <h4 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2">{news.title}</h4>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{getTimeAgo(news.created_at)}</span>
                    <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded">{news.category || 'عام'}</span>
                  </div>
                </div>
              )) : (
                <div className="text-center py-8">
                  <span className="text-4xl mb-2 block">📰</span>
                  <p className="text-gray-500 text-sm">لا توجد أخبار حديثة</p>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions Card */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
              <span className="text-2xl mr-3">⚡</span>
              إجراءات سريعة
            </h3>
            <div className="space-y-3">
              {[
                { to: '/admin/players', label: 'إضافة لاعب جديد', icon: '👤', color: 'bg-blue-500' },
                { to: '/matches', label: 'جدولة مباراة', icon: '🏟️', color: 'bg-green-500' },
                { to: '/categories', label: 'إنشاء فئة جديدة', icon: '🏷️', color: 'bg-purple-500' },
                { to: '/settings', label: 'إعدادات النادي', icon: '⚙️', color: 'bg-gray-500' }
              ].map((action, index) => (
                <Link
                  key={index}
                  to={action.to}
                  className="flex items-center p-3 rounded-xl hover:bg-gray-50 transition-all duration-300 group border border-transparent hover:border-gray-200"
                >
                  <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center mr-3 group-hover:scale-105 transition-transform`}>
                    <span className="text-white text-lg">{action.icon}</span>
                  </div>
                  <span className="font-medium text-gray-700 group-hover:text-gray-900 flex-1">{action.label}</span>
                  <svg className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Management Sections Grid - Mobile 2 columns, Desktop 3 columns */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {[
            { 
              to: '/teams', 
              title: 'إدارة الفرق', 
              desc: 'تنظيم فرق النادي والمنافسين', 
              icon: '👥', 
              color: 'from-blue-500 to-blue-700', 
              stats: `${stats?.total_teams || 0} فريق`,
              bgIcon: '🏟️'
            },
            { 
              to: '/players', 
              title: 'إدارة اللاعبين', 
              desc: 'تسجيل ومتابعة اللاعبين', 
              icon: '⚽', 
              color: 'from-green-500 to-green-700', 
              stats: `${stats?.total_players || 0} لاعب`,
              bgIcon: '🎽'
            },
            { 
              to: '/categories', 
              title: 'إدارة الفئات', 
              desc: 'تنظيم وإدارة فئات اللاعبين', 
              icon: '🏷️', 
              color: 'from-orange-500 to-orange-700', 
              stats: `${stats?.total_categories || 0} فئة`,
              bgIcon: '📋'
            },
            { 
              to: '/talents', 
              title: 'قائمة المواهبين', 
              desc: 'إحصائيات وترتيب المواهبين', 
              icon: '⭐', 
              color: 'from-yellow-500 to-yellow-700', 
              stats: '0 مهتم',
              bgIcon: '🌟'
            },
            { 
              to: '/tournaments', 
              title: 'إدارة البطولات', 
              desc: 'تسجيل البطولات حسب الفئات', 
              icon: '🏆', 
              color: 'from-indigo-500 to-indigo-700', 
              stats: `${stats?.total_tournaments || 0} بطولة`,
              bgIcon: '🏅'
            },
            { 
              to: '/matches', 
              title: 'جدولة المباريات', 
              desc: 'تنظيم وإدارة المباريات', 
              icon: '🏟️', 
              color: 'from-purple-500 to-purple-700', 
              stats: `${stats?.total_matches || 0} مباراة`,
              bgIcon: '⚽'
            },
            // مكون التشكيلة تم نقله لقسم المباريات
            // { 
            //   to: '/team-formation', 
            //   title: 'تشكيل الفرق', 
            //   desc: 'إدارة تشكيلات المباريات', 
            //   icon: '🧩', 
            //   color: 'from-teal-500 to-teal-700', 
            //   stats: 'تشكيلات احترافية',
            //   bgIcon: '⚽'
            // },
            { 
              to: '/achievements', 
              title: 'إدارة الإنجازات', 
              desc: 'إنجازات النادي والبطولات', 
              icon: '🏆', 
              color: 'from-yellow-500 to-yellow-700', 
              stats: 'الإنجازات والجوائز',
              bgIcon: '🥇'
            },
            { 
              to: '/feature-boxes', 
              title: 'المربعات القابلة للنقر', 
              desc: 'إدارة مربعات الصفحة الرئيسية', 
              icon: '🎯', 
              color: 'from-orange-500 to-red-600', 
              stats: 'المربعات التفاعلية',
              bgIcon: '📦'
            },
            { 
              to: '/strategy-management', 
              title: 'إدارة الاستراتيجية', 
              desc: 'رؤية ورسالة وأهداف النادي', 
              icon: '🎯', 
              color: 'from-pink-500 to-rose-600', 
              stats: 'الرؤية والرسالة',
              bgIcon: '🚀'
            },
            { 
              to: '/shop-management', 
              title: 'إدارة المتجر', 
              desc: 'المنتجات والطلبات', 
              icon: '🛒', 
              color: 'from-green-500 to-emerald-700', 
              stats: 'المتجر الإلكتروني',
              bgIcon: '🏪'
            },
            { 
              to: '/jobs-management', 
              title: 'إدارة التوظيف', 
              desc: 'الوظائف والمتقدمين', 
              icon: '💼', 
              color: 'from-indigo-500 to-indigo-700', 
              stats: 'التوظيف والموارد البشرية',
              bgIcon: '👥'
            },
            { 
              to: '/settings', 
              title: 'إعدادات النظام', 
              desc: 'تخصيص إعدادات النادي', 
              icon: '⚙️', 
              color: 'from-gray-500 to-gray-700', 
              stats: 'الإعدادات العامة',
              bgIcon: '🔧'
            }
          ].map((section, index) => (
            <Link
              key={index}
              to={section.to}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl border border-gray-100 hover:border-gray-200 transition-all duration-300 overflow-hidden transform hover:scale-105"
            >
              {/* Header with gradient and background icon */}
              <div className={`h-20 lg:h-28 bg-gradient-to-br ${section.color} relative overflow-hidden`}>
                {/* Background decorative icon */}
                <div className="absolute -top-2 -left-2 text-6xl lg:text-8xl text-white opacity-10 transform -rotate-12">
                  {section.bgIcon}
                </div>
                
                {/* Animated particles effect */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent group-hover:from-black/5 transition-all"></div>
                
                {/* Main icon */}
                <div className="absolute top-3 lg:top-4 right-3 lg:right-4 text-2xl lg:text-3xl text-white opacity-95 transform group-hover:scale-110 transition-transform duration-300">
                  {section.icon}
                </div>
                
                {/* Statistics badge */}
                <div className="absolute bottom-2 lg:bottom-3 left-3 lg:left-4">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg px-2 py-1 lg:px-3 lg:py-1">
                    <span className="text-xs lg:text-sm font-bold text-white">
                      {section.stats}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 lg:p-6">
                <div className="mb-3">
                  <h3 className="text-sm lg:text-lg font-bold text-gray-900 mb-1 lg:mb-2 group-hover:text-gray-700 transition-colors">
                    {section.title}
                  </h3>
                  <p className="text-xs lg:text-sm text-gray-600 leading-relaxed">
                    {section.desc}
                  </p>
                </div>
                
                {/* Action button */}
                <div className="flex items-center justify-between">
                  <span className="text-xs lg:text-sm font-medium text-orange-600 group-hover:text-orange-700">
                    انتقال للقسم
                  </span>
                  <svg 
                    className="w-4 h-4 lg:w-5 lg:h-5 text-orange-500 transform group-hover:translate-x-1 transition-transform" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                  </svg>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

// Full Activity Log Component
const ActivityLog = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  // Helper function to format time ago with Saudi timezone
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    // استخدام التوقيت السعودي
    const saudiNow = new Date(now.toLocaleString("en-US", {timeZone: "Asia/Riyadh"}));
    const saudiDate = new Date(date.toLocaleString("en-US", {timeZone: "Asia/Riyadh"}));
    const diffInMinutes = Math.floor((saudiNow - saudiDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'الآن';
    if (diffInMinutes < 60) return `منذ ${diffInMinutes} دقيقة`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `منذ ${diffInHours} ساعة`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `منذ ${diffInDays} يوم`;
    
    return date.toLocaleDateString('ar-SA');
  };

  useEffect(() => {
    fetchAllActivities();
  }, []);

  const fetchAllActivities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/activity-log?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const activitiesData = response.data.map(activity => ({
        ...activity,
        ...getActivityDisplay(activity.action)
      }));
      setActivities(activitiesData);
    } catch (error) {
      console.error('خطأ في جلب سجل النشاط:', error);
      // Generate sample data
      const sampleActivities = [];
      const actions = [
        'إضافة لاعب جديد', 'تحديث نتيجة مباراة', 'إضافة خبر جديد', 'حذف فريق منافس',
        'إنشاء بطولة جديدة', 'تحديث إعدادات النادي', 'إضافة مباراة جديدة', 'حذف لاعب',
        'تعديل معلومات الفريق', 'إضافة مستخدم جديد', 'تحديث الصلاحيات', 'نشر خبر'
      ];
      const users = ['الإدارة', 'المدرب', 'المسؤول', 'رئيس النادي', 'المدير الفني'];
      
      for (let i = 0; i < 50; i++) {
        const randomAction = actions[Math.floor(Math.random() * actions.length)];
        const randomUser = users[Math.floor(Math.random() * users.length)];
        const randomDate = new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000);
        
        sampleActivities.push({
          id: i + 1,
          action: randomAction,
          user_name: randomUser,
          created_at: randomDate.toISOString(),
          ...getActivityDisplay(randomAction)
        });
      }
      setActivities(sampleActivities);
    } finally {
      setLoading(false);
    }
  };

  const getActivityDisplay = (action) => {
    if (action.includes('لاعب')) return { icon: "👤", color: "bg-green-100 text-green-600" };
    if (action.includes('مباراة')) return { icon: "🏟️", color: "bg-blue-100 text-blue-600" };
    if (action.includes('خبر')) return { icon: "📰", color: "bg-yellow-100 text-yellow-600" };
    if (action.includes('بطولة')) return { icon: "🏆", color: "bg-purple-100 text-purple-600" };
    if (action.includes('فريق')) return { icon: "👥", color: "bg-indigo-100 text-indigo-600" };
    if (action.includes('إعدادات') || action.includes('مستخدم') || action.includes('صلاحيات')) return { icon: "⚙️", color: "bg-gray-100 text-gray-600" };
    return { icon: "📋", color: "bg-orange-100 text-orange-600" };
  };

  // Filter activities
  const filteredActivities = activities.filter(activity => {
    if (filter !== 'all') {
      if (filter === 'players' && !activity.action.includes('لاعب')) return false;
      if (filter === 'matches' && !activity.action.includes('مباراة')) return false;
      if (filter === 'news' && !activity.action.includes('خبر')) return false;
      if (filter === 'system' && !activity.action.includes('إعدادات') && !activity.action.includes('مستخدم')) return false;
    }
    
    if (searchTerm && !activity.action.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !activity.user_name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    if (dateRange.start && new Date(activity.created_at) < new Date(dateRange.start)) return false;
    if (dateRange.end && new Date(activity.created_at) > new Date(dateRange.end)) return false;
    
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredActivities.length / itemsPerPage);
  const paginatedActivities = filteredActivities.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">جاري تحميل سجل النشاط...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link to="/dashboard" className="ml-4 text-gray-600 hover:text-gray-900">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <span className="text-orange-600 mr-3">📊</span>
                سجل النشاط الكامل
              </h1>
            </div>
            <div className="text-sm text-gray-500">
              {filteredActivities.length} نشاط
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">البحث</label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="البحث في الأنشطة أو المستخدمين..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            {/* Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">التصنيف</label>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              >
                <option value="all">جميع الأنشطة</option>
                <option value="players">أنشطة اللاعبين</option>
                <option value="matches">أنشطة المباريات</option>
                <option value="news">أنشطة الأخبار</option>
                <option value="system">أنشطة النظام</option>
              </select>
            </div>

            {/* Date Range */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">من تاريخ</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">إلى تاريخ</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={() => {
                setSearchTerm('');
                setFilter('all');
                setDateRange({ start: '', end: '' });
                setCurrentPage(1);
              }}
              className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm"
            >
              مسح الفلاتر
            </button>
          </div>
        </div>

        {/* Activities List */}
        <div className="bg-white rounded-xl shadow-sm">
          {paginatedActivities.length === 0 ? (
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">📊</span>
              <h3 className="text-lg font-medium text-gray-900 mb-2">لا توجد أنشطة</h3>
              <p className="text-gray-500">لم يتم العثور على أنشطة تطابق المعايير المحددة</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200">
                {paginatedActivities.map((activity, index) => (
                  <div key={activity.id || index} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center flex-1">
                        <div className={`w-12 h-12 ${activity.color} rounded-full flex items-center justify-center flex-shrink-0`}>
                          <span className="text-xl">{activity.icon}</span>
                        </div>
                        <div className="mr-4 flex-1">
                          <p className="text-base font-medium text-gray-900">{activity.action}</p>
                          <div className="flex items-center text-sm text-gray-500 mt-1">
                            <span className="ml-4">{activity.user_name}</span>
                            <span>•</span>
                            <span className="mr-4">{getTimeAgo(activity.created_at)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-400">
                        {new Date(activity.created_at).toLocaleDateString('ar-SA')} - {new Date(activity.created_at).toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-500">
                      عرض {((currentPage - 1) * itemsPerPage) + 1} إلى {Math.min(currentPage * itemsPerPage, filteredActivities.length)} من {filteredActivities.length} نشاط
                    </div>
                    <div className="flex space-x-2 space-x-reverse">
                      <button
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        السابق
                      </button>
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-1 text-sm border rounded ${
                              currentPage === page
                                ? 'bg-orange-600 text-white border-orange-600'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        التالي
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Enhanced Categories Management Component
const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: '', description: '', min_age: '', max_age: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('خطأ في تحميل الفئات:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const categoryData = {
        ...newCategory,
        min_age: newCategory.min_age ? parseInt(newCategory.min_age) : null,
        max_age: newCategory.max_age ? parseInt(newCategory.max_age) : null
      };
      await axios.post(`${API}/categories`, categoryData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم إضافة الفئة بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      setShowAddModal(false);
      setNewCategory({ name: '', description: '', min_age: '', max_age: '' });
      fetchCategories();
    } catch (error) {
      console.error('خطأ في إضافة الفئة:', error);
      alert('فشل في إضافة الفئة');
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory({
      ...category,
      min_age: category.min_age?.toString() || '',
      max_age: category.max_age?.toString() || ''
    });
    setShowEditModal(true);
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const categoryData = {
        ...editingCategory,
        min_age: editingCategory.min_age ? parseInt(editingCategory.min_age) : null,
        max_age: editingCategory.max_age ? parseInt(editingCategory.max_age) : null
      };
      
      await axios.put(`${API}/categories/${editingCategory.id}`, categoryData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم تحديث الفئة بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      setShowEditModal(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      console.error('خطأ في تحديث الفئة:', error);
      alert('فشل في تحديث الفئة');
    }
  };

  const handleDeleteCategory = async (categoryId, categoryName) => {
    if (!confirm(`هل أنت متأكد من حذف فئة "${categoryName}"؟\n\nتنبيه: سيؤثر هذا على جميع اللاعبين في هذه الفئة`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/categories/${categoryId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حذف الفئة بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      fetchCategories();
    } catch (error) {
      console.error('خطأ في حذف الفئة:', error);
      const errorMsg = error.response?.data?.detail || 'فشل في حذف الفئة';
      alert(errorMsg);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800 text-white p-8 rounded-lg shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-16 -translate-y-16">
          <div className="w-full h-full rounded-full bg-gradient-to-br from-white/10 to-transparent"></div>
        </div>
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center">
                <span className="text-5xl mr-4">🏷️</span>
                إدارة الفئات
              </h1>
              <p className="text-purple-200">تنظيم وإدارة فئات اللاعبين حسب العمر والمستوى</p>
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-medium transition-colors backdrop-blur-sm border border-white/30"
            >
              + إضافة فئة جديدة
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-4 border-r-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">إجمالي الفئات</h3>
              <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
            </div>
            <div className="p-3 rounded-full bg-purple-50">
              <span className="text-xl">🏷️</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 border-r-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">فئات الأطفال</h3>
              <p className="text-2xl font-bold text-gray-900">{categories.filter(c => c.max_age && c.max_age <= 12).length}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-50">
              <span className="text-xl">👶</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 border-r-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">فئات الناشئين</h3>
              <p className="text-2xl font-bold text-gray-900">{categories.filter(c => c.min_age && c.min_age >= 13 && c.max_age && c.max_age <= 17).length}</p>
            </div>
            <div className="p-3 rounded-full bg-green-50">
              <span className="text-xl">🧒</span>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 border-r-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">فئات الكبار</h3>
              <p className="text-2xl font-bold text-gray-900">{categories.filter(c => !c.max_age || c.max_age >= 18).length}</p>
            </div>
            <div className="p-3 rounded-full bg-orange-50">
              <span className="text-xl">👨</span>
            </div>
          </div>
        </div>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <div className="text-6xl mb-4">🏷️</div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">لا توجد فئات</h3>
            <p className="text-gray-500 mb-4">ابدأ بإضافة فئة جديدة لتنظيم اللاعبين</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              + إضافة فئة جديدة
            </button>
          </div>
        ) : (
          categories.map((category) => (
            <div key={category.id} className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-800 mb-2 flex items-center">
                    <span className="text-2xl mr-2">🏷️</span>
                    {category.name}
                  </h3>
                  {category.description && (
                    <p className="text-gray-600 mb-3 leading-relaxed">{category.description}</p>
                  )}
                </div>
              </div>

              {/* Age Information */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h4 className="font-semibold text-gray-800 mb-2">📊 معلومات الفئة العمرية</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-gray-500">السن الأدنى</div>
                    <div className="text-lg font-bold text-blue-600">
                      {category.min_age ? `${category.min_age} سنة` : 'غير محدد'}
                    </div>
                  </div>
                  <div className="text-center">
                    <div className="text-gray-500">السن الأقصى</div>
                    <div className="text-lg font-bold text-green-600">
                      {category.max_age ? `${category.max_age} سنة` : 'غير محدد'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="text-xs text-gray-500 mb-4">
                <p>تاريخ الإنشاء: {new Date(category.created_at).toLocaleDateString('ar-SA')}</p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3 space-x-reverse">
                <button
                  onClick={() => handleEditCategory(category)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
                >
                  ✏️ تعديل
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id, category.name)}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
                >
                  🗑️ حذف
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Category Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">إضافة فئة جديدة</h2>
            <form onSubmit={handleAddCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم الفئة *</label>
                <input
                  type="text"
                  required
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="مثل: تحت 15، تحت 17، الفريق الأول"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الوصف</label>
                <textarea
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="وصف مختصر للفئة"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">السن الأدنى</label>
                  <input
                    type="number"
                    value={newCategory.min_age}
                    onChange={(e) => setNewCategory({ ...newCategory, min_age: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">السن الأقصى</label>
                  <input
                    type="number"
                    value={newCategory.max_age}
                    onChange={(e) => setNewCategory({ ...newCategory, max_age: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
              </div>
              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إضافة الفئة
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditModal && editingCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">تعديل الفئة</h2>
            <form onSubmit={handleUpdateCategory} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم الفئة *</label>
                <input
                  type="text"
                  required
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="مثل: تحت 15، تحت 17، الفريق الأول"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الوصف</label>
                <textarea
                  value={editingCategory.description}
                  onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="وصف مختصر للفئة"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">السن الأدنى</label>
                  <input
                    type="number"
                    value={editingCategory.min_age}
                    onChange={(e) => setEditingCategory({ ...editingCategory, min_age: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">السن الأقصى</label>
                  <input
                    type="number"
                    value={editingCategory.max_age}
                    onChange={(e) => setEditingCategory({ ...editingCategory, max_age: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  حفظ التغييرات
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingCategory(null);
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Enhanced Players Management Component with Statistics
const Players = () => {
  return <EnhancedPlayersComponent />;
};

// Professional Teams Management Component
const Teams = () => {
  return <SimpleTeamsComponent API={API} />;
};

// Professional Matches Management Component - Enhanced & Simplified
const Matches = () => {
  return <ProfessionalMatchesComponent />;
};

// Advanced Reports Component - Unified Version
const Reports = (props) => {
  return <UnifiedAdvancedReportsSection {...props} />;
};

// Professional Scorers Management Component
const Scorers = () => {
  const [scorers, setScorers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [newGoal, setNewGoal] = useState({
    player_id: '',
    match_info: '',
    goal_type: 'regular',
    minute: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [scorersRes, playersRes] = await Promise.allSettled([
        axios.get(`${API}/statistics/scorers`),
        axios.get(`${API}/players`)
      ]);
      
      if (scorersRes.status === 'fulfilled') {
        setScorers(scorersRes.value.data);
      } else {
        // Mock data if API fails
        setScorers([
          { player_id: '1', name: 'محمد أحمد', jersey_number: 10, total_goals: 13, matches_played: 15, position: 'forward', yellow_cards: 2, red_cards: 0 },
          { player_id: '2', name: 'أحمد محمود', jersey_number: 9, total_goals: 11, matches_played: 14, position: 'forward', yellow_cards: 1, red_cards: 0 },
          { player_id: '3', name: 'علي حسن', jersey_number: 7, total_goals: 8, matches_played: 16, position: 'midfielder', yellow_cards: 3, red_cards: 1 },
          { player_id: '4', name: 'خالد سالم', jersey_number: 11, total_goals: 6, matches_played: 12, position: 'forward', yellow_cards: 0, red_cards: 0 },
          { player_id: '5', name: 'عبدالله ناصر', jersey_number: 8, total_goals: 4, matches_played: 13, position: 'midfielder', yellow_cards: 4, red_cards: 0 }
        ]);
      }
      
      if (playersRes.status === 'fulfilled') {
        setPlayers(playersRes.value.data);
      }

    } catch (error) {
      console.error('خطأ في تحميل البيانات:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddGoal = async (e) => {
    e.preventDefault();
    try {
      // This would normally add a goal record
      console.log('إضافة هدف جديد:', newGoal);
      setShowAddGoalModal(false);
      setNewGoal({ player_id: '', match_info: '', goal_type: 'regular', minute: '' });
      
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم إضافة الهدف بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
    } catch (error) {
      console.error('خطأ في إضافة الهدف:', error);
    }
  };

  const getPositionName = (position) => {
    const positions = {
      goalkeeper: 'حارس مرمى',
      defender: 'مدافع', 
      midfielder: 'وسط',
      forward: 'مهاجم'
    };
    return positions[position] || position;
  };

  const getGoalAverage = (goals, matches) => {
    return matches > 0 ? (goals / matches).toFixed(1) : '0.0';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-20 w-20 border-4 border-orange-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-yellow-600 via-yellow-700 to-yellow-800 text-white p-8 rounded-lg shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-16 -translate-y-16">
          <div className="w-full h-full rounded-full bg-gradient-to-br from-white/10 to-transparent"></div>
        </div>
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2 flex items-center">
                <span className="text-5xl mr-4">🥅</span>
                قائمة الهدافين
              </h1>
              <p className="text-yellow-200">إحصائيات مفصلة للهدافين والإنجازات</p>
            </div>
            <button
              onClick={() => setShowAddGoalModal(true)}
              className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-lg font-medium transition-colors backdrop-blur-sm border border-white/30"
            >
              + إضافة هدف جديد
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards - Enhanced with Total Matches */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border-r-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">إجمالي الأهداف</h3>
              <p className="text-3xl font-bold text-gray-900">{scorers.reduce((sum, player) => sum + player.total_goals, 0)}</p>
              <p className="text-sm text-yellow-600 font-medium mt-1">هذا الموسم</p>
            </div>
            <div className="p-4 rounded-full bg-yellow-50">
              <span className="text-3xl">⚽</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border-r-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">أفضل هداف</h3>
              <p className="text-2xl font-bold text-gray-900">{scorers[0]?.name || 'لا يوجد'}</p>
              <p className="text-sm text-green-600 font-medium mt-1">{scorers[0]?.total_goals || 0} هدف</p>
            </div>
            <div className="p-4 rounded-full bg-green-50">
              <span className="text-3xl">🏆</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border-r-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">معدل الأهداف</h3>
              <p className="text-3xl font-bold text-gray-900">
                {scorers.length > 0 ? getGoalAverage(
                  scorers.reduce((sum, p) => sum + p.total_goals, 0),
                  scorers.reduce((sum, p) => sum + p.matches_played, 0)
                ) : '0.0'}
              </p>
              <p className="text-sm text-blue-600 font-medium mt-1">هدف/مباراة</p>
            </div>
            <div className="p-4 rounded-full bg-blue-50">
              <span className="text-3xl">📊</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border-r-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">الهدافين النشطين</h3>
              <p className="text-3xl font-bold text-gray-900">{scorers.filter(p => p.total_goals > 0).length}</p>
              <p className="text-sm text-purple-600 font-medium mt-1">لاعب مسجل</p>
            </div>
            <div className="p-4 rounded-full bg-purple-50">
              <span className="text-3xl">🎯</span>
            </div>
          </div>
        </div>

        {/* New Total Matches Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 border-r-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-1">إجمالي المباريات</h3>
              <p className="text-3xl font-bold text-gray-900">{scorers.reduce((sum, player) => sum + player.matches_played, 0)}</p>
              <p className="text-sm text-orange-600 font-medium mt-1">
                مباراة لعبت
              </p>
            </div>
            <div className="p-4 rounded-full bg-orange-50">
              <span className="text-3xl">🏟️</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scorers Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <span className="text-3xl mr-3">🥇</span>
            ترتيب الهدافين
          </h2>
          <p className="text-gray-600 mt-1">ترتيب اللاعبين حسب عدد الأهداف المسجلة</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">الترتيب</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">اللاعب</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">الأهداف</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">المباريات</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">المعدل</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">البطاقات</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">المركز</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {scorers.map((player, index) => (
                <tr key={player.player_id} className={`hover:bg-gray-50 ${index < 3 ? 'bg-gradient-to-r from-yellow-50 to-transparent' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {index === 0 && <span className="text-2xl mr-2">🥇</span>}
                      {index === 1 && <span className="text-2xl mr-2">🥈</span>}
                      {index === 2 && <span className="text-2xl mr-2">🥉</span>}
                      <span className="text-xl font-bold text-gray-900">#{index + 1}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mr-4">
                        <span className="text-white font-bold text-lg">#{player.jersey_number}</span>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{player.name}</div>
                        <div className="text-sm text-gray-500">رقم القميص: {player.jersey_number}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="text-3xl font-bold text-green-600">{player.total_goals}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="text-lg font-semibold text-blue-600">{player.matches_played}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="text-lg font-semibold text-orange-600">
                      {getGoalAverage(player.total_goals, player.matches_played)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2 space-x-reverse">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        🟨 {player.yellow_cards || 0}
                      </span>
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        🟥 {player.red_cards || 0}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      {getPositionName(player.position)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Goal Modal */}
      {showAddGoalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">إضافة هدف جديد</h2>
            <form onSubmit={handleAddGoal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اللاعب</label>
                <select
                  required
                  value={newGoal.player_id}
                  onChange={(e) => setNewGoal({ ...newGoal, player_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                >
                  <option value="">اختر اللاعب</option>
                  {scorers.map((player) => (
                    <option key={player.player_id} value={player.player_id}>
                      #{player.jersey_number} - {player.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">تفاصيل المباراة</label>
                <input
                  type="text"
                  required
                  value={newGoal.match_info}
                  onChange={(e) => setNewGoal({ ...newGoal, match_info: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  placeholder="مثل: ضد نادي الشباب"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع الهدف</label>
                  <select
                    value={newGoal.goal_type}
                    onChange={(e) => setNewGoal({ ...newGoal, goal_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="regular">هدف عادي</option>
                    <option value="penalty">ركلة جزاء</option>
                    <option value="freekick">ركلة حرة</option>
                    <option value="header">هدف بالرأس</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الدقيقة</label>
                  <input
                    type="number"
                    value={newGoal.minute}
                    onChange={(e) => setNewGoal({ ...newGoal, minute: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                    placeholder="45"
                    min="1"
                    max="120"
                  />
                </div>
              </div>
              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إضافة الهدف
                </button>
                <button
                  type="button"
                  onClick={() => setShowAddGoalModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Professional Landing Page Management Component
const LandingPageManagement = () => {
  const [pageSettings, setPageSettings] = useState({
    sections: [
      { id: 'hero', name: 'القسم الرئيسي', visible: true, order: 1, type: 'default' },
      { id: 'ticker', name: 'الشريط المتحرك', visible: true, order: 2, type: 'default' },
      { id: 'next_match', name: 'المباراة القادمة', visible: true, order: 3, type: 'default' },
      { id: 'news', name: 'أحدث الأخبار', visible: true, order: 4, type: 'default' },
      { id: 'achievements', name: 'الإنجازات والإحصائيات', visible: true, order: 5, type: 'default' },
      { id: 'matches_results', name: 'نتائج المباريات', visible: true, order: 6, type: 'default' },
      { id: 'tweets', name: 'تغريدات X', visible: true, order: 7, type: 'default' },
      { id: 'gallery', name: 'معرض الصور', visible: true, order: 8, type: 'default' },
      { id: 'contact', name: 'تواصل معنا', visible: true, order: 9, type: 'default' },
      { id: 'footer', name: 'ذيل الصفحة', visible: true, order: 10, type: 'default' }
    ],
    availableCustomSections: [
      { id: 'custom_banner', name: 'بنر مخصص', description: 'إضافة بنر ترويجي مخصص' },
      { id: 'video_section', name: 'قسم الفيديو', description: 'عرض فيديوهات النادي' },
      { id: 'sponsor_section', name: 'الرعاة والشركاء', description: 'عرض شعارات الرعاة' },
      { id: 'team_info', name: 'معلومات الفريق', description: 'تفاصيل عن الفريق واللاعبين' },
      { id: 'club_history', name: 'تاريخ النادي', description: 'قسم عن تاريخ وإنجازات النادي' },
      { id: 'training_schedule', name: 'جدول التدريبات', description: 'مواعيد تدريبات الفرق' },
      { id: 'membership', name: 'العضوية', description: 'معلومات وطلبات الانضمام' },
      { id: 'store', name: 'متجر النادي', description: 'منتجات وبضائع النادي' }
    ],
    hero: {
      title: 'نادي الأمجاد',
      description: 'نحن نسعى للتميز والإبداع في عالم كرة القدم',
      show_logo: true
    },
    matches: {
      next_match: null,
      last_match: null,
      show_next_match: true,
      show_last_match: true
    },
    customTicker: [
      'مرحباً بكم في موقع نادي الأمجاد الرسمي',
      'تابعونا للحصول على آخر الأخبار والفعاليات',
      'النادي يدعم الشباب الرياضي في المنطقة',
      'انضموا إلى عائلة الأمجاد واكونوا جزءاً من النجاح'
    ],
    staticAnnouncementEnabled: false,
    staticAnnouncementText: '📢 منصة نادي الأمجاد الرسمية - إطلاق تجريبي',
    banners: [],
    social_media: {
      twitter: '',
      facebook: '',
      instagram: '',
      youtube: '',
      telegram: '',
      snapchat: ''
    },
    contact_info: {
      phone: '',
      email: '',
      address: ''
    }
  });

  const [matches, setMatches] = useState([]);
  const [banners, setBanners] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [newSponsor, setNewSponsor] = useState({
    name: '',
    logo_file_id: null,
    logo_url: '',
    website_url: '',
    description: '',
    is_active: true,
    order: 0
  });
  const [landingData, setLandingData] = useState({
    clubInfo: { name: 'نادي الأمجاد' },
    news: [],
    matches: { next_match: null, last_match: null },
    staticAnnouncementEnabled: false,
    staticAnnouncementText: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('sections');
  const [draggedItem, setDraggedItem] = useState(null);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [editingMatch, setEditingMatch] = useState(null);
  const [newBanner, setNewBanner] = useState({
    title: '',
    description: '',
    image: null,
    link: '',
    visible: true
  });
  const [newMatch, setNewMatch] = useState({
    opponent: '',
    date: '',
    time: '',
    venue: '',
    match_type: 'friendly',
    is_home: true,
    home_team_id: '',
    away_team_id: '',
    tournament_id: '',
    category_id: ''
  });
  
  // States for form data
  const [matchFormTeams, setMatchFormTeams] = useState([]);
  const [matchFormTournaments, setMatchFormTournaments] = useState([]);
  const [matchFormCategories, setMatchFormCategories] = useState([]);
  
  // Advanced Public Page Customization States
  const [publicSectionSettings, setPublicSectionSettings] = useState({
    nextMatch: {
      enabled: true,
      title: "المباراة القادمة",
      titleColor: "#ffffff",
      backgroundColor: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
      showCountdown: true,
      showTeamLogos: true,
      showVenue: true,
      position: 1
    },
    players: {
      enabled: true,
      title: "نجوم الأمجاد",
      titleColor: "#1f2937",
      backgroundColor: "#f9fafb",
      maxPlayers: 6,
      showStats: true,
      showAvatars: true,
      position: 2
    },
    achievements: {
      enabled: true,
      title: "إنجازات النادي",
      subtitle: "فخورون بتاريخنا العريق",
      titleColor: "#1f2937",
      backgroundColor: "#f9fafb", 
      achievements: [
        { title: "سنة تأسيس", value: "1974", icon: "🏢", color: "#f97316" },
        { title: "لاعب نشط", value: "250", icon: "⭐", color: "#f97316" },
        { title: "بطولة محلية", value: "20", icon: "🏆", color: "#f97316" },
        { title: "مباراة فوز", value: "150", icon: "🎯", color: "#f97316" }
      ],
      position: 3
    },
    footer: {
      enabled: true,
      title: "نادي الأمجاد",
      description: "نادي الأمجاد الرياضي يسعى لتحقيق التميز في جميع البطولات والمسابقات المحلية",
      backgroundColor: "#1f2937",
      textColor: "#ffffff",
      contact: {
        phone: "+966 123 456 789",
        email: "info@alamjad-fc.sa",
        address: "الرياض، المملكة العربية السعودية"
      },
      links: [
        { title: "الأخبار", url: "#news" },
        { title: "المباريات", url: "#matches" },
        { title: "اللاعبين", url: "#players" },
        { title: "الإنجازات", url: "#achievements" }
      ],
      socialMedia: {
        twitter: "https://twitter.com/alamjad_club",
        instagram: "https://instagram.com/alamjad_club", 
        youtube: "https://youtube.com/@alamjad_club",
        facebook: "https://facebook.com/alamjad_club"
      },
      position: 4
    }
  });

  useEffect(() => {
    fetchPageSettings();
    fetchBanners();
    fetchSponsors();
    fetchMatches();
    fetchLandingData();
    // Load form data for match modal
    fetchMatchFormData();
  }, []);

  // Fetch teams, tournaments, and categories for match form
  const fetchMatchFormData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [teamsRes, tournamentsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/teams`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/tournaments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/categories`, { headers }).catch(() => ({ data: [] }))
      ]);

      setMatchFormTeams(teamsRes.data || []);
      setMatchFormTournaments(tournamentsRes.data || []);
      setMatchFormCategories(categoriesRes.data || []);
      
      console.log('Match form data loaded:', {
        teams: teamsRes.data?.length || 0,
        tournaments: tournamentsRes.data?.length || 0,
        categories: categoriesRes.data?.length || 0
      });
    } catch (error) {
      console.error('خطأ في تحميل بيانات النموذج:', error);
    }
  };

  const showNotification = (message, type = 'info') => {
    const notification = document.createElement('div');
    const bgColor = type === 'success' ? 'bg-green-500' : type === 'error' ? 'bg-red-500' : 'bg-blue-500';
    notification.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse`;
    notification.innerHTML = `<div class="flex items-center">${message}</div>`;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 4000);
  };

  const fetchMatches = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/matches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const matchesData = response.data || [];
      setMatches(matchesData);
      
      // Update landingData with matches info
      const nextMatch = matchesData.find(m => new Date(m.date || m.match_date) > new Date()) || null;
      const lastMatch = matchesData.filter(m => new Date(m.date || m.match_date) < new Date())
                                   .sort((a, b) => new Date(b.date || b.match_date) - new Date(a.date || a.match_date))[0] || null;
      
      setLandingData(prev => ({
        ...prev,
        matches: { next_match: nextMatch, last_match: lastMatch }
      }));
    } catch (error) {
      console.error('خطأ في جلب المباريات:', error);
      setMatches([]);
    }
  };

  const fetchPageSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/public-page-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data) {
        setPageSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('خطأ في جلب إعدادات الصفحة العامة:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBanners = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/banners`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBanners(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب البنرات:', error);
    }
  };

  const fetchSponsors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sponsors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('✅ تم جلب الرعاة:', response.data);
      setSponsors(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب الرعاة:', error);
    }
  };

  const handleSaveSponsor = async () => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('🔄 محاولة حفظ الراعي:', newSponsor);
      
      if (editingSponsor) {
        // Update existing sponsor
        console.log('📝 تحديث راعي موجود:', editingSponsor.id);
        const response = await axios.put(`${API}/sponsors/${editingSponsor.id}`, newSponsor, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ تم التحديث:', response.data);
        showNotification('تم تحديث الراعي بنجاح', 'success');
      } else {
        // Create new sponsor
        console.log('➕ إضافة راعي جديد');
        const response = await axios.post(`${API}/sponsors`, newSponsor, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ تم الإضافة:', response.data);
        showNotification('تم إضافة الراعي بنجاح', 'success');
      }
      
      fetchSponsors();
      setShowSponsorModal(false);
      setEditingSponsor(null);
      setNewSponsor({
        name: '',
        logo_file_id: null,
        logo_url: '',
        website_url: '',
        description: '',
        is_active: true,
        order: 0
      });
    } catch (error) {
      console.error('❌ خطأ في حفظ الراعي:', error);
      console.error('❌ تفاصيل الخطأ:', error.response?.data);
      showNotification('فشل حفظ الراعي: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  const handleDeleteSponsor = async (sponsorId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الراعي؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/sponsors/${sponsorId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      showNotification('تم حذف الراعي بنجاح', 'success');
      fetchSponsors();
    } catch (error) {
      console.error('خطأ في حذف الراعي:', error);
      showNotification('فشل حذف الراعي', 'error');
    }
  };

  const handleUploadSponsorLogo = async (file) => {
    try {
      console.log('📤 بدء رفع شعار الراعي:', file.name);
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('✅ تم رفع الشعار بنجاح، file_id:', response.data.file_id);
      
      setNewSponsor(prev => ({
        ...prev,
        logo_file_id: response.data.file_id
      }));
      
      showNotification('تم رفع الشعار بنجاح', 'success');
    } catch (error) {
      console.error('❌ خطأ في رفع الشعار:', error);
      console.error('❌ تفاصيل الخطأ:', error.response?.data);
      showNotification('فشل رفع الشعار: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  const fetchLandingData = async () => {
    try {
      const [clubRes, newsRes] = await Promise.allSettled([
        axios.get(`${API}/public/club-info`),
        axios.get(`${API}/public/news`)
      ]);

      const newLandingData = { ...landingData };
      
      if (clubRes.status === 'fulfilled') {
        newLandingData.clubInfo = clubRes.value.data;
      }

      if (newsRes.status === 'fulfilled') {
        newLandingData.news = newsRes.value.data || [];
      }

      // Update matches data from existing matches state
      if (matches.length > 0) {
        const nextMatch = matches.find(m => new Date(m.date || m.match_date) > new Date()) || null;
        const lastMatch = matches.filter(m => new Date(m.date || m.match_date) < new Date())
                                 .sort((a, b) => new Date(b.date || b.match_date) - new Date(a.date || a.match_date))[0] || null;
        newLandingData.matches = { next_match: nextMatch, last_match: lastMatch };
      }
      
      setLandingData(newLandingData);
    } catch (error) {
      console.error('خطأ في جلب بيانات الصفحة العامة:', error);
    }
  };

  const savePageSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/public-page-settings`, pageSettings, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حفظ إعدادات الصفحة العامة بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);

      // Refresh public page
      window.dispatchEvent(new CustomEvent('refreshPublicData'));
      
    } catch (error) {
      console.error('خطأ في حفظ إعدادات الصفحة:', error);
      alert('فشل في حفظ الإعدادات');
    } finally {
      setSaving(false);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedItem === null) return;
    
    const sections = [...pageSettings.sections];
    const draggedSection = sections[draggedItem];
    
    // Remove dragged item
    sections.splice(draggedItem, 1);
    
    // Insert at new position
    sections.splice(dropIndex, 0, draggedSection);
    
    // Update order
    sections.forEach((section, index) => {
      section.order = index + 1;
    });
    
    setPageSettings(prev => ({ ...prev, sections }));
    setDraggedItem(null);
  };

  const toggleSectionVisibility = (sectionId) => {
    setPageSettings(prev => ({
      ...prev,
      sections: prev.sections.map(section =>
        section.id === sectionId
          ? { ...section, visible: !section.visible }
          : section
      )
    }));
  };

  // Add custom section
  const addCustomSection = (customSectionType) => {
    const customSection = pageSettings.availableCustomSections.find(s => s.id === customSectionType);
    if (customSection) {
      const newSection = {
        id: `${customSection.id}_${Date.now()}`,
        name: customSection.name,
        visible: true,
        order: pageSettings.sections.length + 1,
        type: 'custom',
        customType: customSection.id
      };
      
      setPageSettings(prev => ({
        ...prev,
        sections: [...prev.sections, newSection]
      }));
    }
  };

  // Remove section
  const removeSection = (sectionId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا القسم؟')) {
      setPageSettings(prev => ({
        ...prev,
        sections: prev.sections.filter(s => s.id !== sectionId)
      }));
    }
  };

  // Reset to default sections
  const resetToDefaultSections = () => {
    if (window.confirm('هل أنت متأكد من إعادة تعيين الأقسام إلى الافتراضية؟ سيتم فقدان جميع التخصيصات.')) {
      setPageSettings(prev => ({
        ...prev,
        sections: [
          { id: 'hero', name: 'القسم الرئيسي', visible: true, order: 1, type: 'default' },
          { id: 'ticker', name: 'الشريط المتحرك', visible: true, order: 2, type: 'default' },
          { id: 'next_match', name: 'المباراة القادمة', visible: true, order: 3, type: 'default' },
          { id: 'news', name: 'أحدث الأخبار', visible: true, order: 4, type: 'default' },
          { id: 'achievements', name: 'الإنجازات والإحصائيات', visible: true, order: 5, type: 'default' },
          { id: 'matches_results', name: 'نتائج المباريات', visible: true, order: 6, type: 'default' },
          { id: 'tweets', name: 'تغريدات X', visible: true, order: 7, type: 'default' },
          { id: 'gallery', name: 'معرض الصور', visible: true, order: 8, type: 'default' },
          { id: 'contact', name: 'تواصل معنا', visible: true, order: 9, type: 'default' },
          { id: 'footer', name: 'ذيل الصفحة', visible: true, order: 10, type: 'default' }
        ]
      }));
    }
  };

  const handleBannerSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (newBanner.image) {
        formData.append('file', newBanner.image);
        const uploadRes = await axios.post(`${API}/files/upload`, formData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        const bannerData = {
          ...newBanner,
          image_file_id: uploadRes.data.file_id
        };
        delete bannerData.image;
        
        if (editingBanner) {
          await axios.put(`${API}/banners/${editingBanner.id}`, bannerData, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        } else {
          await axios.post(`${API}/banners`, bannerData, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
        }
        
        fetchBanners();
        setShowBannerModal(false);
        setEditingBanner(null);
        setNewBanner({
          title: '',
          description: '',
          image: null,
          link: '',
          visible: true
        });
      }
      
    } catch (error) {
      console.error('خطأ في حفظ البنر:', error);
      alert('فشل في حفظ البنر');
    } finally {
      setSaving(false);
    }
  };

  const deleteBanner = async (bannerId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا البنر؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/banners/${bannerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchBanners();
    } catch (error) {
      console.error('خطأ في حذف البنر:', error);
      alert('فشل في حذف البنر');
    }
  };

  // Landing Page Match Add Function (Enhanced with proper data binding)
  const handleAddMatchSimple = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      
      // Use proper form data or smart defaults
      const matchData = {
        home_team_id: newMatch.home_team_id || (matchFormTeams.find(t => t.name.includes('الأمجاد'))?.id) || 'alamjad_club',
        away_team_id: newMatch.away_team_id || 'opponent_team',
        category_id: newMatch.category_id || (matchFormCategories[0]?.id) || 'default_category',
        tournament_id: newMatch.tournament_id || null, // Now properly using selected tournament
        match_type: newMatch.match_type?.toUpperCase() === 'FRIENDLY' ? 'FRIENDLY' : 
                   newMatch.match_type?.toUpperCase() === 'LEAGUE' ? 'LEAGUE' : 
                   newMatch.match_type?.toUpperCase() === 'CUP' ? 'CUP' : 'FRIENDLY',
        match_date: new Date(`${newMatch.date}T${newMatch.time || '15:00'}`).toISOString(),
        venue: newMatch.venue || 'ملعب نادي الأمجاد',
        status: 'UPCOMING',
        notes: newMatch.opponent ? `مباراة ضد ${newMatch.opponent}` : 'مباراة جديدة'
      };

      console.log('Sending enhanced match data:', matchData);
      
      await axios.post(`${API}/matches`, matchData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setNewMatch({
        opponent: '',
        date: '',
        time: '',
        venue: '',
        match_type: 'friendly',
        is_home: true,
        home_team_id: '',
        away_team_id: '',
        tournament_id: '',
        category_id: ''
      });
      
      setShowMatchModal(false);
      
      // Refresh matches data
      fetchMatches();
      fetchLandingData();
      
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-lg mr-2">✅</span>
          تم إضافة المباراة بنجاح!
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);
      
    } catch (error) {
      console.error('خطأ في إضافة المباراة:', error);
      
      // Show error notification
      let errorMessage = 'حدث خطأ غير متوقع';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 max-w-md';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-lg mr-2">❌</span>
          <div>
            <div class="font-bold">خطأ في إضافة المباراة</div>
            <div class="text-sm mt-1">${errorMessage}</div>
          </div>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 8000);
    }
  };

  const deleteMatch = async (matchId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه المباراة؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/matches/${matchId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchMatches();
    } catch (error) {
      console.error('خطأ في حذف المباراة:', error);
      alert('فشل في حذف المباراة');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  // Helper function to get section icon
  const getSectionIcon = (sectionId) => {
    const icons = {
      'hero': '🏠',
      'ticker': '📢',
      'next_match': '⚽',
      'news': '📰',
      'achievements': '🏆',
      'matches_results': '📊',
      'tweets': '𝕏',
      'gallery': '🖼️',
      'contact': '📞',
      'footer': '📄',
      'custom_banner': '🎯',
      'video_section': '📹',
      'sponsor_section': '🤝',
      'team_info': '👥',
      'club_history': '📜',
      'training_schedule': '🏃',
      'membership': '🎫',
      'store': '🛍️'
    };
    return icons[sectionId] || icons[sectionId?.split('_')[0]] || '📋';
  };

  const tabs = [
    { id: 'overview', label: 'نظرة عامة', icon: '📊' },
    { id: 'content', label: 'إدارة المحتوى', icon: '📝' },
    { id: 'ticker', label: 'الشريط المتحرك', icon: '📢' },
    { id: 'sections', label: 'تخصيص الأقسام', icon: '📋' },
    { id: 'banners', label: 'البنرات الترويجية', icon: '🎯' },
    { id: 'sponsors', label: 'الرعاة', icon: '🤝' },
    { id: 'social', label: 'وسائل التواصل', icon: '📱' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">🌐 إدارة الصفحة العامة</h2>
        <button
          onClick={savePageSettings}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors disabled:opacity-50"
        >
          {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
        </button>
      </div>

      {/* Professional Tabs */}
      <div className="bg-white rounded-lg shadow-md border">
        <div className="border-b border-gray-200">
          <div className="flex space-x-2 space-x-reverse p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'bg-orange-500 text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className="ml-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab - New Professional Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">📊 نظرة عامة على الصفحة العامة</h3>
                <p className="text-gray-600">إحصائيات ومعاينة سريعة لمحتوى صفحة الجمهور</p>
              </div>

              {/* Quick Stats */}
              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-orange-600 mb-2">{landingData.news?.length || 0}</div>
                  <div className="text-sm font-medium text-gray-700">📰 الأخبار المنشورة</div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">{matches?.length || 0}</div>
                  <div className="text-sm font-medium text-gray-700">⚽ إجمالي المباريات</div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">
                    {pageSettings.sections?.filter(s => s.visible).length || 0}
                  </div>
                  <div className="text-sm font-medium text-gray-700">👁️ الأقسام المرئية</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">{banners?.length || 0}</div>
                  <div className="text-sm font-medium text-gray-700">🎯 البنرات النشطة</div>
                </div>
              </div>

              {/* Live Preview Link */}
              <div className="bg-gradient-to-r from-orange-600 to-orange-700 rounded-xl p-6 text-white mb-8">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-xl font-bold mb-2">🌐 معاينة الصفحة العامة</h4>
                    <p className="text-orange-100">شاهد كيف تبدو صفحة الجمهور للزوار</p>
                  </div>
                  <a
                    href="/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-orange-600 px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors"
                  >
                    👁️ معاينة مباشرة
                  </a>
                </div>
              </div>

              {/* Current Status */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Next Match Card */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">⚽</span>
                    المباراة القادمة
                  </h4>
                  {landingData.matches?.next_match ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-bold">الأمجاد</span>
                        <span className="text-gray-500">VS</span>
                        <span className="font-bold">{landingData.matches.next_match.opponent}</span>
                      </div>
                      <div className="text-sm text-gray-600">
                        📅 {new Date(landingData.matches.next_match.date).toLocaleDateString('ar-SA')}
                      </div>
                      {landingData.matches.next_match.time && (
                        <div className="text-sm text-gray-600">
                          ⏰ {landingData.matches.next_match.time}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm">لا توجد مباريات قادمة مجدولة</div>
                  )}
                </div>

                {/* Latest News Card */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <span className="mr-2">📰</span>
                    آخر الأخبار
                  </h4>
                  {landingData.news?.length > 0 ? (
                    <div className="space-y-2">
                      <div className="font-medium text-gray-900 line-clamp-2">
                        {landingData.news[0].title}
                      </div>
                      <div className="text-sm text-gray-600">
                        {new Date(landingData.news[0].created_at || landingData.news[0].date).toLocaleDateString('ar-SA')}
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm">لا توجد أخبار منشورة</div>
                  )}
                </div>
              </div>

              {/* Section Visibility Status */}
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4">📋 حالة أقسام الصفحة</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  {pageSettings.sections?.map((section, index) => (
                    <div
                      key={section.id}
                      className={`p-4 rounded-lg border ${
                        section.visible
                          ? 'bg-green-50 border-green-200 text-green-800'
                          : 'bg-gray-50 border-gray-200 text-gray-600'
                      }`}
                    >
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <span className={`w-3 h-3 rounded-full ${section.visible ? 'bg-green-500' : 'bg-gray-400'}`}></span>
                        <span className="font-medium">{section.name}</span>
                        <span className="text-xs">#{section.order}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Matches Management Tab - Enhanced and Connected */}
          {activeTab === 'matches' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">⚽ جدول المباريات</h3>
                  <p className="text-gray-600">إدارة مباريات النادي - ستظهر تلقائياً في الصفحة العامة</p>
                </div>
                <button
                  onClick={() => setShowMatchModal(true)}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors flex items-center space-x-2 space-x-reverse shadow-lg"
                >
                  <span>➕</span>
                  <span>إضافة مباراة</span>
                </button>
              </div>

              {/* Status Cards */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-2">
                    {matches.filter(m => new Date(m.date || m.match_date) > new Date()).length}
                  </div>
                  <div className="text-sm font-medium text-gray-700">🔄 مباريات قادمة</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                  <div className="text-2xl font-bold text-green-600 mb-2">
                    {matches.filter(m => new Date(m.date || m.match_date) < new Date() && m.status === 'completed').length}
                  </div>
                  <div className="text-sm font-medium text-gray-700">✅ مباريات منتهية</div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 text-center">
                  <div className="text-2xl font-bold text-orange-600 mb-2">{matches.length}</div>
                  <div className="text-sm font-medium text-gray-700">📊 إجمالي المباريات</div>
                </div>
              </div>

              {/* Auto-Display Info */}
              <div className="bg-gradient-to-r from-orange-100 to-orange-50 border border-orange-200 rounded-xl p-6 mb-6">
                <h4 className="text-lg font-bold text-orange-800 mb-2">🔄 العرض التلقائي في الصفحة العامة</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div className="bg-white rounded-lg p-4">
                    <div className="font-bold text-gray-800 mb-2">📅 المباراة القادمة</div>
                    {landingData.matches?.next_match ? (
                      <div className="text-gray-600">
                        الأمجاد ضد {landingData.matches.next_match.opponent}<br/>
                        {new Date(landingData.matches.next_match.date).toLocaleDateString('ar-SA')}
                      </div>
                    ) : (
                      <div className="text-gray-500">لا توجد مباريات قادمة</div>
                    )}
                  </div>
                  <div className="bg-white rounded-lg p-4">
                    <div className="font-bold text-gray-800 mb-2">📊 المباراة السابقة</div>
                    {landingData.matches?.last_match ? (
                      <div className="text-gray-600">
                        الأمجاد ضد {landingData.matches.last_match.opponent}<br/>
                        {new Date(landingData.matches.last_match.date).toLocaleDateString('ar-SA')}
                      </div>
                    ) : (
                      <div className="text-gray-500">لا توجد مباريات سابقة</div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Matches List */}
              {matches.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl">
                  <div className="text-6xl mb-4">⚽</div>
                  <h4 className="text-xl font-bold text-gray-700 mb-2">لا توجد مباريات</h4>
                  <p className="text-gray-500 mb-6">ابدأ بإضافة مباريات النادي القادمة والسابقة</p>
                  <button
                    onClick={() => setShowMatchModal(true)}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                  >
                    ➕ إضافة أول مباراة
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {matches
                    .sort((a, b) => new Date(a.date || a.match_date) - new Date(b.date || b.match_date))
                    .map((match, index) => (
                    <div key={match.id || index} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-6 space-x-reverse mb-4">
                            <div className="flex items-center space-x-3 space-x-reverse">
                              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                                <span className="text-white text-lg font-bold">أ</span>
                              </div>
                              <span className="font-bold text-lg">الأمجاد</span>
                            </div>
                            <div className="text-2xl font-bold text-gray-600">VS</div>
                            <div className="flex items-center space-x-3 space-x-reverse">
                              <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center">
                                <span className="text-gray-600 text-lg">⚽</span>
                              </div>
                              <span className="font-bold text-lg">{match.opponent || match.away_team}</span>
                            </div>
                          </div>
                          
                          <div className="grid md:grid-cols-4 gap-4 text-sm">
                            <div className="flex items-center space-x-2 space-x-reverse">
                              <span className="text-gray-500">📅</span>
                              <span>{new Date(match.date || match.match_date).toLocaleDateString('ar-SA')}</span>
                            </div>
                            {match.time && (
                              <div className="flex items-center space-x-2 space-x-reverse">
                                <span className="text-gray-500">⏰</span>
                                <span>{match.time}</span>
                              </div>
                            )}
                            <div className="flex items-center space-x-2 space-x-reverse">
                              <span className="text-gray-500">📍</span>
                              <span>{match.venue || match.location || 'غير محدد'}</span>
                            </div>
                            <div className="flex items-center space-x-2 space-x-reverse">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                new Date(match.date || match.match_date) > new Date() 
                                  ? 'bg-blue-100 text-blue-600' 
                                  : 'bg-green-100 text-green-600'
                              }`}>
                                {new Date(match.date || match.match_date) > new Date() ? '🔄 قادمة' : '✅ منتهية'}
                              </span>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4 space-x-reverse mt-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              match.match_type === 'league' ? 'bg-yellow-100 text-yellow-700' :
                              match.match_type === 'cup' ? 'bg-purple-100 text-purple-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {match.match_type === 'league' ? '🏆 دوري' :
                               match.match_type === 'cup' ? '🥇 كأس' : 
                               '🤝 ودية'}
                            </span>
                            {match.is_home !== undefined && (
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                match.is_home ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {match.is_home ? '🏠 على الأرض' : '✈️ خارج الأرض'}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-2 space-x-reverse">
                          <button
                            onClick={() => deleteMatch(match.id)}
                            className="bg-red-100 hover:bg-red-200 text-red-600 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                          >
                            🗑️ حذف
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sections Tab */}
          {/* Enhanced Sections Management Tab - Professional with Add/Remove */}
          {activeTab === 'sections' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">📋 تخصيص أقسام الصفحة العامة</h3>
                  <p className="text-gray-600 text-sm mt-1">إدارة شاملة لجميع أقسام الصفحة مع إمكانية الإضافة والحذف</p>
                </div>
                <div className="flex items-center space-x-3 space-x-reverse">
                  <div className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-lg">
                    إجمالي: {pageSettings.sections?.length || 0} قسم
                  </div>
                  <button
                    onClick={resetToDefaultSections}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    🔄 إعادة تعيين
                  </button>
                </div>
              </div>

              {/* Statistics Overview */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">✅</div>
                  <div className="text-xl font-bold text-green-800">
                    {pageSettings.sections?.filter(s => s.visible).length || 0}
                  </div>
                  <div className="text-sm text-green-600 font-medium">أقسام مفعّلة</div>
                </div>
                
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">❌</div>
                  <div className="text-xl font-bold text-red-800">
                    {pageSettings.sections?.filter(s => !s.visible).length || 0}
                  </div>
                  <div className="text-sm text-red-600 font-medium">أقسام مخفية</div>
                </div>
                
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">🎨</div>
                  <div className="text-xl font-bold text-purple-800">
                    {pageSettings.sections?.filter(s => s.type === 'custom').length || 0}
                  </div>
                  <div className="text-sm text-purple-600 font-medium">أقسام مخصصة</div>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                  <div className="text-3xl mb-2">⚡</div>
                  <div className="text-xl font-bold text-blue-800">
                    {pageSettings.sections?.filter(s => s.type === 'default').length || 0}
                  </div>
                  <div className="text-sm text-blue-600 font-medium">أقسام افتراضية</div>
                </div>
              </div>

              {/* Add Custom Section */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <span className="text-xl mr-2">➕</span>
                  إضافة قسم مخصص جديد
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  {pageSettings.availableCustomSections?.map((customSection) => (
                    <div key={customSection.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-800">{customSection.name}</h5>
                          <p className="text-sm text-gray-600 mt-1">{customSection.description}</p>
                        </div>
                        <button
                          onClick={() => addCustomSection(customSection.id)}
                          className="bg-indigo-500 hover:bg-indigo-600 text-white p-2 rounded-lg transition-colors flex-shrink-0 ml-3"
                          title={`إضافة ${customSection.name}`}
                        >
                          <span className="text-sm">+</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                  <span className="text-xl mr-2">💡</span>
                  دليل الاستخدام
                </h4>
                <div className="grid md:grid-cols-3 gap-4 text-blue-700 text-sm">
                  <div>
                    <h5 className="font-semibold mb-2">🖱️ السحب والإفلات</h5>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>اسحب الأقسام لإعادة ترتيبها</li>
                      <li>الترقيم يتحديث تلقائياً</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-2">👁️ التحكم في الرؤية</h5>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>أزرار إخفاء/إظهار سريعة</li>
                      <li>معاينة مباشرة للتغييرات</li>
                    </ul>
                  </div>
                  <div>
                    <h5 className="font-semibold mb-2">🎨 الأقسام المخصصة</h5>
                    <ul className="space-y-1 list-disc list-inside">
                      <li>إضافة أقسام جديدة حسب الحاجة</li>
                      <li>حذف الأقسام غير المرغوبة</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              {/* Current Sections List - Enhanced */}
              <div className="space-y-3">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <span className="text-xl mr-2">📑</span>
                  الأقسام الحالية ({pageSettings.sections?.length || 0})
                </h4>
                
                {pageSettings.sections?.length === 0 ? (
                  <div className="text-center py-12 bg-gray-50 rounded-xl">
                    <div className="text-6xl mb-4">📄</div>
                    <h4 className="text-xl font-bold text-gray-700 mb-2">لا توجد أقسام</h4>
                    <p className="text-gray-500">ابدأ بإضافة أقسام مخصصة أو إعادة تعيين الأقسام الافتراضية</p>
                  </div>
                ) : (
                  pageSettings.sections
                    .sort((a, b) => a.order - b.order)
                    .map((section, index) => (
                      <div
                        key={section.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`border-2 rounded-xl p-6 cursor-move transition-all duration-300 hover:shadow-lg ${
                          section.visible
                            ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 hover:border-green-400'
                            : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-300 opacity-70'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4 space-x-reverse">
                            {/* Order Badge */}
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg ${
                              section.visible ? 'bg-gradient-to-r from-green-500 to-green-600' : 'bg-gray-400'
                            }`}>
                              {section.order}
                            </div>
                            
                            {/* Section Info */}
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 space-x-reverse mb-1">
                                <span className="text-xl">{getSectionIcon(section.id)}</span>
                                <h5 className="text-lg font-bold text-gray-800">{section.name}</h5>
                                {section.type === 'custom' && (
                                  <span className="bg-purple-100 text-purple-600 text-xs px-2 py-1 rounded-full font-medium">
                                    مخصص
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {section.type === 'default' ? 'قسم افتراضي' : `قسم مخصص - ${section.customType || 'عام'}`}
                              </p>
                            </div>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex items-center space-x-2 space-x-reverse">
                            <button
                              onClick={() => toggleSectionVisibility(section.id)}
                              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                                section.visible
                                  ? 'bg-red-500 hover:bg-red-600 text-white'
                                  : 'bg-green-500 hover:bg-green-600 text-white'
                              }`}
                            >
                              {section.visible ? '👁️ إخفاء' : '👁️‍🗨️ إظهار'}
                            </button>
                            
                            {section.type === 'custom' && (
                              <button
                                onClick={() => removeSection(section.id)}
                                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                                title="حذف القسم المخصص"
                              >
                                <span className="text-sm">🗑️</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    savePageSettings();
                    // Show success notification
                    const notification = document.createElement('div');
                    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
                    notification.textContent = 'تم حفظ ترتيب الأقسام بنجاح!';
                    document.body.appendChild(notification);
                    setTimeout(() => {
                      if (document.body.contains(notification)) {
                        document.body.removeChild(notification);
                      }
                    }, 3000);
                  }}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-3 rounded-xl font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg flex items-center space-x-2 space-x-reverse"
                >
                  <span className="text-xl">💾</span>
                  <span>حفظ ترتيب الأقسام</span>
                </button>
              </div>
            </div>
          )}

          {/* News Ticker Management Tab */}
          {activeTab === 'ticker' && (
            <div className="space-y-6">
              {/* Static Announcement Section */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-6 shadow-md">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <div className="bg-blue-600 text-white w-10 h-10 rounded-full flex items-center justify-center">
                      <span className="text-xl">📢</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">الإعلان الثابت</h3>
                      <p className="text-sm text-gray-600">إعلان مهم يظهر فوق الشريط المتحرك</p>
                    </div>
                  </div>
                  <label className="flex items-center cursor-pointer">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={pageSettings.staticAnnouncementEnabled || false}
                        onChange={(e) => setPageSettings({
                          ...pageSettings,
                          staticAnnouncementEnabled: e.target.checked
                        })}
                        className="sr-only"
                      />
                      <div className={`block w-14 h-8 rounded-full transition-colors ${
                        pageSettings.staticAnnouncementEnabled ? 'bg-green-600' : 'bg-gray-300'
                      }`}></div>
                      <div className={`absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform ${
                        pageSettings.staticAnnouncementEnabled ? 'transform translate-x-6' : ''
                      }`}></div>
                    </div>
                    <span className="mr-3 text-sm font-medium text-gray-700">
                      {pageSettings.staticAnnouncementEnabled ? 'مفعّل' : 'معطّل'}
                    </span>
                  </label>
                </div>

                <div className="space-y-3">
                  <label className="block text-sm font-semibold text-gray-700">نص الإعلان:</label>
                  <input
                    type="text"
                    value={pageSettings.staticAnnouncementText || ''}
                    onChange={(e) => setPageSettings({
                      ...pageSettings,
                      staticAnnouncementText: e.target.value
                    })}
                    placeholder="مثال: 📢 منصة نادي الأمجاد الرسمية - إطلاق تجريبي"
                    className="w-full px-4 py-3 border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    disabled={!pageSettings.staticAnnouncementEnabled}
                  />
                  
                  {pageSettings.staticAnnouncementEnabled && pageSettings.staticAnnouncementText && (
                    <div className="bg-blue-600 text-white py-3 px-6 rounded-lg text-center">
                      <div className="animate-ticker-slide">
                        <span className="font-bold text-lg">{pageSettings.staticAnnouncementText}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 bg-blue-100 border-l-4 border-blue-600 p-3 rounded">
                  <p className="text-sm text-blue-800">
                    <strong>💡 نصيحة:</strong> سيظهر هذا الإعلان كشريط ثابت فوق الشريط المتحرك في الصفحة الرئيسية
                  </p>
                </div>
              </div>

              <hr className="border-gray-300" />

              {/* Moving Ticker Section */}
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">🎯 إدارة الشريط المتحرك</h3>
                <button
                  onClick={() => setPageSettings({
                    ...pageSettings,
                    customTicker: [...(pageSettings.customTicker || []), '']
                  })}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  + إضافة رسالة جديدة
                </button>
              </div>
              
              {/* Preview of Ticker */}
              <div className="bg-green-600 text-white py-4 px-6 rounded-lg overflow-hidden">
                <div className="flex items-center">
                  <div className="bg-green-700 px-4 py-2 font-bold text-sm whitespace-nowrap rounded mr-4">
                    <span className="text-lg mr-2">📢</span>
                    معاينة
                  </div>
                  <div className="flex-1 relative overflow-hidden">
                    <div className="animate-scroll flex items-center whitespace-nowrap">
                      {(pageSettings.customTicker || []).map((message, index) => (
                        message && (
                          <div key={index} className="inline-flex items-center mx-8">
                            <span className="font-medium text-lg">{message}</span>
                            {index < pageSettings.customTicker.length - 1 && (
                              <span className="mx-6 text-green-200 text-xl">✦</span>
                            )}
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ticker Messages Management */}
              <div className="space-y-4">
                {(pageSettings.customTicker || []).map((message, index) => (
                  <div key={index} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center space-x-4 space-x-reverse">
                      <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-bold text-sm">{index + 1}</span>
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          value={message}
                          onChange={(e) => {
                            const newTicker = [...(pageSettings.customTicker || [])];
                            newTicker[index] = e.target.value;
                            setPageSettings({
                              ...pageSettings,
                              customTicker: newTicker
                            });
                          }}
                          placeholder="أدخل رسالة الشريط المتحرك..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        />
                      </div>
                      <button
                        onClick={() => {
                          const newTicker = (pageSettings.customTicker || []).filter((_, i) => i !== index);
                          setPageSettings({
                            ...pageSettings,
                            customTicker: newTicker
                          });
                        }}
                        className="flex-shrink-0 bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                        title="حذف الرسالة"
                      >
                        <span className="text-sm">🗑️</span>
                      </button>
                    </div>
                  </div>
                ))}
                
                {(!pageSettings.customTicker || pageSettings.customTicker.length === 0) && (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-6xl mb-4">📢</div>
                    <h3 className="text-xl font-semibold mb-2">لا توجد رسائل في الشريط المتحرك</h3>
                    <p className="text-gray-400">اضغط على "إضافة رسالة جديدة" لإضافة رسالة</p>
                  </div>
                )}
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">💡 تعليمات الاستخدام:</h4>
                <ul className="text-blue-700 text-sm space-y-1 list-disc list-inside">
                  <li>يمكنك إضافة عدة رسائل وستظهر متتالية في الشريط المتحرك</li>
                  <li>الرسائل ستتحرك من اليسار إلى اليمين تلقائياً</li>
                  <li>احرص على كتابة رسائل واضحة ومختصرة</li>
                  <li>يمكن حذف أي رسالة أو تعديلها في أي وقت</li>
                </ul>
              </div>
            </div>
          )}

          {/* Banners Management Tab */}
          {activeTab === 'banners' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">إدارة البنرات المتحركة</h3>
                <button
                  onClick={() => setShowBannerModal(true)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  + إضافة بنر جديد
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {banners.map((banner) => (
                  <div key={banner.id} className="border rounded-lg overflow-hidden shadow-md">
                    {banner.image_file_id && (
                      <img
                        src={`${API}/files/${banner.image_file_id}`}
                        alt={banner.title}
                        className="w-full h-32 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900">{banner.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{banner.description}</p>
                      {banner.link && (
                        <a
                          href={banner.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 text-xs hover:underline mt-2 block"
                        >
                          {banner.link}
                        </a>
                      )}
                      <div className="flex justify-between items-center mt-3">
                        <span className={`text-xs px-2 py-1 rounded ${
                          banner.visible
                            ? 'bg-green-100 text-green-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {banner.visible ? 'مرئي' : 'مخفي'}
                        </span>
                        <div className="flex space-x-2 space-x-reverse">
                          <button
                            onClick={() => {
                              setEditingBanner(banner);
                              setNewBanner({
                                title: banner.title,
                                description: banner.description,
                                link: banner.link,
                                visible: banner.visible,
                                image: null
                              });
                              setShowBannerModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => deleteBanner(banner.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sponsors Management Tab */}
          {activeTab === 'sponsors' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">🤝 إدارة الرعاة</h3>
                  <p className="text-gray-600 text-sm mt-1">إضافة وتعديل رعاة النادي</p>
                </div>
                <button
                  onClick={() => {
                    setEditingSponsor(null);
                    setNewSponsor({
                      name: '',
                      logo_file_id: null,
                      logo_url: '',
                      website_url: '',
                      description: '',
                      is_active: true,
                      order: sponsors.length
                    });
                    setShowSponsorModal(true);
                  }}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                >
                  <span className="mr-2">+</span>
                  إضافة راعي جديد
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sponsors.map((sponsor) => (
                  <div key={sponsor.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center h-40">
                      {sponsor.logo_file_id ? (
                        <img
                          src={`${API}/files/${sponsor.logo_file_id}`}
                          alt={sponsor.name}
                          className="max-h-32 max-w-full object-contain"
                        />
                      ) : (
                        <div className="text-4xl">🤝</div>
                      )}
                    </div>
                    <div className="p-4">
                      <h4 className="font-bold text-gray-900 text-lg mb-1">{sponsor.name}</h4>
                      {sponsor.description && (
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{sponsor.description}</p>
                      )}
                      {sponsor.website_url && (
                        <a
                          href={sponsor.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 text-xs hover:underline mb-3 block"
                        >
                          🌐 {sponsor.website_url}
                        </a>
                      )}
                      <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                        <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                          sponsor.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {sponsor.is_active ? '✓ نشط' : '○ غير نشط'}
                        </span>
                        <div className="flex space-x-2 space-x-reverse">
                          <button
                            onClick={() => {
                              setEditingSponsor(sponsor);
                              setNewSponsor({
                                name: sponsor.name,
                                logo_file_id: sponsor.logo_file_id,
                                logo_url: sponsor.logo_url || '',
                                website_url: sponsor.website_url || '',
                                description: sponsor.description || '',
                                is_active: sponsor.is_active,
                                order: sponsor.order
                              });
                              setShowSponsorModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            ✏️ تعديل
                          </button>
                          <button
                            onClick={() => handleDeleteSponsor(sponsor.id)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            🗑️ حذف
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {sponsors.length === 0 && (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <div className="text-5xl mb-4">🤝</div>
                  <p className="text-gray-600 font-medium">لا توجد رعاة مضافين</p>
                  <p className="text-gray-500 text-sm mt-2">انقر على "إضافة راعي جديد" لإضافة راعي</p>
                </div>
              )}
            </div>
          )}

          {/* Social Media Tab */}
          {activeTab === 'social' && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">روابط وسائل التواصل الاجتماعي</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(pageSettings.social_media).map(([platform, url]) => {
                  const platformIcons = {
                    twitter: '𝕏',
                    facebook: '👥',
                    instagram: '📷',
                    youtube: '📺',
                    telegram: '📨',
                    snapchat: '👻'
                  };
                  
                  const platformNames = {
                    twitter: 'X (تويتر)',
                    facebook: 'فيسبوك',
                    instagram: 'إنستقرام',
                    youtube: 'يوتيوب',
                    telegram: 'تلقرام',
                    snapchat: 'سناب شات'
                  };

                  return (
                    <div key={platform} className="border rounded-lg p-4">
                      <div className="flex items-center space-x-2 space-x-reverse mb-2">
                        <span className="text-2xl">{platformIcons[platform]}</span>
                        <label className="font-medium">{platformNames[platform]}</label>
                      </div>
                      <input
                        type="url"
                        value={url}
                        onChange={(e) => setPageSettings(prev => ({
                          ...prev,
                          social_media: {
                            ...prev.social_media,
                            [platform]: e.target.value
                          }
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder={`رابط ${platformNames[platform]}`}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Enhanced Content Management Tab */}
          {activeTab === 'content' && (
            <div className="space-y-8">
              {/* Hero Section Settings */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-3">🎨</span>
                  إعدادات القسم الرئيسي (Hero Section)
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">العنوان الرئيسي</label>
                    <input
                      type="text"
                      value={pageSettings.hero?.title || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        hero: { ...pageSettings.hero, title: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="نادي الأمجاد"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">الوصف</label>
                    <textarea
                      value={pageSettings.hero?.description || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        hero: { ...pageSettings.hero, description: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="2"
                      placeholder="نحن نسعى للتميز والإبداع في عالم كرة القدم"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <input
                      type="checkbox"
                      id="showLogo"
                      checked={pageSettings.hero?.show_logo !== false}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        hero: { ...pageSettings.hero, show_logo: e.target.checked }
                      })}
                      className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <label htmlFor="showLogo" className="text-sm font-medium text-gray-700">
                      إظهار شعار النادي في القسم الرئيسي
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">لون الخلفية</label>
                    <select
                      value={pageSettings.hero?.background_color || 'green'}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        hero: { ...pageSettings.hero, background_color: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="green">أخضر (الافتراضي)</option>
                      <option value="blue">أزرق</option>
                      <option value="orange">برتقالي</option>
                      <option value="purple">بنفسجي</option>
                      <option value="gray">رمادي</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Header Settings */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-3">🔝</span>
                  إعدادات الترويسة
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">نص الترويسة الثانوي</label>
                    <input
                      type="text"
                      value={pageSettings.header?.subtitle || 'الموقع الرسمي'}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        header: { ...pageSettings.header, subtitle: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="الموقع الرسمي"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <input
                      type="checkbox"
                      id="showLogin"
                      checked={pageSettings.header?.show_login !== false}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        header: { ...pageSettings.header, show_login: e.target.checked }
                      })}
                      className="w-5 h-5 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <label htmlFor="showLogin" className="text-sm font-medium text-gray-700">
                      إظهار زر تسجيل الدخول
                    </label>
                  </div>
                </div>
              </div>

              {/* Next Match Settings */}
              <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-3">⚽</span>
                  إعدادات المباراة القادمة
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <input
                      type="checkbox"
                      id="showNextMatch"
                      checked={pageSettings.matches?.show_next_match !== false}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        matches: { ...pageSettings.matches, show_next_match: e.target.checked }
                      })}
                      className="w-5 h-5 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                    />
                    <label htmlFor="showNextMatch" className="text-sm font-medium text-gray-700">
                      إظهار المباراة القادمة في الصفحة العامة
                    </label>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">عنوان قسم المباراة</label>
                    <input
                      type="text"
                      value={pageSettings.matches?.section_title || 'مباراة الأمجاد القادمة'}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        matches: { ...pageSettings.matches, section_title: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="مباراة الأمجاد القادمة"
                    />
                  </div>
                </div>
                
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <span className="font-semibold">ملاحظة:</span> 
                    البيانات تُجلب تلقائياً من قسم إدارة المباريات. المباراة القادمة حسب التاريخ ستظهر هنا.
                  </p>
                </div>
              </div>

              {/* Footer Settings */}
              <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6 border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-3">📧</span>
                  معلومات التواصل والذيل
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">رقم الهاتف</label>
                    <input
                      type="text"
                      value={pageSettings.contact_info?.phone || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        contact_info: { ...pageSettings.contact_info, phone: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                      placeholder="+966 xx xxx xxxx"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">البريد الإلكتروني</label>
                    <input
                      type="email"
                      value={pageSettings.contact_info?.email || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        contact_info: { ...pageSettings.contact_info, email: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                      placeholder="info@alamjad.sa"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">العنوان</label>
                    <textarea
                      value={pageSettings.contact_info?.address || ''}
                      onChange={(e) => setPageSettings({
                        ...pageSettings,
                        contact_info: { ...pageSettings.contact_info, address: e.target.value }
                      })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-500"
                      rows="2"
                      placeholder="المملكة العربية السعودية، الرياض"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Banner Modal */}
      {showBannerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editingBanner ? 'تعديل البنر' : 'إضافة بنر جديد'}
            </h2>
            <form onSubmit={handleBannerSubmit} className="space-y-4">
              <div>
                <label className="block font-medium mb-1">عنوان البنر *</label>
                <input
                  type="text"
                  required
                  value={newBanner.title}
                  onChange={(e) => setNewBanner({...newBanner, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">الوصف</label>
                <textarea
                  value={newBanner.description}
                  onChange={(e) => setNewBanner({...newBanner, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">صورة البنر *</label>
                <input
                  type="file"
                  accept="image/*"
                  required={!editingBanner}
                  onChange={(e) => setNewBanner({...newBanner, image: e.target.files[0]})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block font-medium mb-1">رابط البنر</label>
                <input
                  type="url"
                  value={newBanner.link}
                  onChange={(e) => setNewBanner({...newBanner, link: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="banner_visible"
                  checked={newBanner.visible}
                  onChange={(e) => setNewBanner({...newBanner, visible: e.target.checked})}
                  className="ml-2"
                />
                <label htmlFor="banner_visible" className="font-medium">عرض البنر</label>
              </div>

              <div className="flex space-x-2 space-x-reverse pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'جاري الحفظ...' : (editingBanner ? 'تحديث' : 'إضافة')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowBannerModal(false);
                    setEditingBanner(null);
                    setNewBanner({
                      title: '',
                      description: '',
                      image: null,
                      link: '',
                      visible: true
                    });
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sponsor Modal */}
      {showSponsorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingSponsor ? '✏️ تعديل راعي' : '🤝 إضافة راعي جديد'}
              </h2>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSaveSponsor(); }} className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اسم الراعي <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newSponsor.name}
                  onChange={(e) => setNewSponsor({...newSponsor, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="مثال: شركة النجوم"
                  required
                />
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  شعار الراعي
                </label>
                {newSponsor.logo_file_id && (
                  <div className="mb-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <img
                      src={`${API}/files/${newSponsor.logo_file_id}`}
                      alt="Preview"
                      className="max-h-32 mx-auto object-contain"
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleUploadSponsorLogo(e.target.files[0]);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">الشعار سيظهر في الشريط المتحرك</p>
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رابط الموقع الإلكتروني
                </label>
                <input
                  type="url"
                  value={newSponsor.website_url}
                  onChange={(e) => setNewSponsor({...newSponsor, website_url: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="https://example.com"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  وصف مختصر
                </label>
                <textarea
                  value={newSponsor.description}
                  onChange={(e) => setNewSponsor({...newSponsor, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="نبذة عن الراعي..."
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-3 space-x-reverse">
                <input
                  type="checkbox"
                  id="sponsor-active"
                  checked={newSponsor.is_active}
                  onChange={(e) => setNewSponsor({...newSponsor, is_active: e.target.checked})}
                  className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="sponsor-active" className="text-sm font-medium text-gray-700">
                  نشط (سيظهر في الشريط المتحرك)
                </label>
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الترتيب
                </label>
                <input
                  type="number"
                  value={newSponsor.order}
                  onChange={(e) => setNewSponsor({...newSponsor, order: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">ترتيب العرض في الشريط (0 = الأول)</p>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 space-x-reverse pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-6 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                >
                  {editingSponsor ? '💾 حفظ التعديلات' : '➕ إضافة الراعي'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSponsorModal(false);
                    setEditingSponsor(null);
                    setNewSponsor({
                      name: '',
                      logo_file_id: null,
                      logo_url: '',
                      website_url: '',
                      description: '',
                      is_active: true,
                      order: 0
                    });
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-lg font-medium transition-colors"
                >
                  ✕ إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Match Modal */}
      {showMatchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">➕ إضافة مباراة جديدة</h3>
            
            <form onSubmit={handleAddMatchSimple} className="space-y-4">
              {/* Teams Selection - Enhanced */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-1">الفريق المضيف</label>
                  <select
                    value={newMatch.home_team_id}
                    onChange={(e) => setNewMatch({...newMatch, home_team_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">اختر الفريق المضيف</option>
                    {matchFormTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-medium mb-1">الفريق الضيف</label>
                  <select
                    value={newMatch.away_team_id}
                    onChange={(e) => setNewMatch({...newMatch, away_team_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">اختر الفريق الضيف</option>
                    {matchFormTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Tournament and Category Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-1">البطولة</label>
                  <select
                    value={newMatch.tournament_id}
                    onChange={(e) => setNewMatch({...newMatch, tournament_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">اختر البطولة (اختياري)</option>
                    {matchFormTournaments.map((tournament) => (
                      <option key={tournament.id} value={tournament.id}>
                        {tournament.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block font-medium mb-1">الفئة</label>
                  <select
                    value={newMatch.category_id}
                    onChange={(e) => setNewMatch({...newMatch, category_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">اختر الفئة (اختياري)</option>
                    {matchFormCategories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name} ({category.min_age}-{category.max_age || '+'} سنة)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-medium mb-1">الفريق المنافس (نص بديل) *</label>
                <input
                  type="text"
                  required
                  value={newMatch.opponent}
                  onChange={(e) => setNewMatch({...newMatch, opponent: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="اسم الفريق المنافس للمرجع"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-1">تاريخ المباراة *</label>
                  <input
                    type="date"
                    required
                    value={newMatch.date}
                    onChange={(e) => setNewMatch({...newMatch, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">وقت المباراة</label>
                  <input
                    type="time"
                    value={newMatch.time}
                    onChange={(e) => setNewMatch({...newMatch, time: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>
              </div>

              <div>
                <label className="block font-medium mb-1">مكان المباراة</label>
                <input
                  type="text"
                  value={newMatch.venue}
                  onChange={(e) => setNewMatch({...newMatch, venue: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="اسم الملعب أو المكان"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block font-medium mb-1">نوع المباراة</label>
                  <select
                    value={newMatch.match_type}
                    onChange={(e) => setNewMatch({...newMatch, match_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="friendly">مباراة ودية</option>
                    <option value="league">مباراة دوري</option>
                    <option value="cup">مباراة كأس</option>
                  </select>
                </div>
                <div>
                  <label className="block font-medium mb-1">نوع الاستضافة</label>
                  <select
                    value={newMatch.is_home}
                    onChange={(e) => setNewMatch({...newMatch, is_home: e.target.value === 'true'})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="true">على الأرض (الأمجاد المضيف)</option>
                    <option value="false">خارج الأرض (ضيف)</option>
                  </select>
                </div>
              </div>

              <div className="flex space-x-2 space-x-reverse pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  ➕ إضافة المباراة
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowMatchModal(false);
                    setNewMatch({
                      opponent: '',
                      date: '',
                      time: '',
                      venue: '',
                      match_type: 'friendly',
                      is_home: true,
                      home_team_id: '',
                      away_team_id: '',
                      tournament_id: '',
                      category_id: ''
                    });
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Complete Settings Component
const Settings = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [clubSettings, setClubSettings] = useState({
    club_name: 'نادي الأمجاد',
    established_year: '',
    primary_color: '#ea580c',
    secondary_color: '#ffffff',
    description: ''
  });
  
  // Contact Requests State
  const [contactRequests, setContactRequests] = useState([]);
  const [showContactRequests, setShowContactRequests] = useState(false);
  const [contactRequestsFilter, setContactRequestsFilter] = useState('all'); // all, pending, read, replied
  const [deletedRequests, setDeletedRequests] = useState([]);

  // Legal Pages State
  const [legalPages, setLegalPages] = useState({
    privacy_policy: '',
    terms_of_use: ''
  });

  // Board Members Management State
  const [boardMembers, setBoardMembers] = useState([]);
  const [showBoardMemberModal, setShowBoardMemberModal] = useState(false);
  const [editingBoardMember, setEditingBoardMember] = useState(null);
  const [boardMemberForm, setBoardMemberForm] = useState({
    name: '',
    position: '',
    description: '',
    email: '',
    phone: '',
    experience: '',
    image_url: '',
    order: 0
  });

  // Sponsors Management State
  const [sponsors, setSponsors] = useState([]);
  const [showSponsorModal, setShowSponsorModal] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState(null);
  const [newSponsor, setNewSponsor] = useState({
    name: '',
    logo_file_id: null,
    logo_url: '',
    website_url: '',
    description: '',
    is_active: true,
    order: 0
  });

  // Data states for reports
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [users, setUsers] = useState([]);

  // Enhanced User Management State - Additional Variables Only
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [userSortBy, setUserSortBy] = useState('created_at');
  const [showRoleManagerModal, setShowRoleManagerModal] = useState(false);
  const [showUserStatsModal, setShowUserStatsModal] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState(null);
  const [rolesData, setRolesData] = useState({});
  const [permissionsData, setPermissionsData] = useState({});

  // Helper functions for user management
  const getRoleColor = (role) => {
    const colors = {
      'super_admin': 'bg-red-100 text-red-800',
      'admin': 'bg-purple-100 text-purple-800',
      'manager': 'bg-blue-100 text-blue-800', 
      'coach': 'bg-green-100 text-green-800',
      'medical': 'bg-teal-100 text-teal-800',
      'analyst': 'bg-indigo-100 text-indigo-800',
      'editor': 'bg-orange-100 text-orange-800',
      'moderator': 'bg-yellow-100 text-yellow-800',
      'captain': 'bg-teal-100 text-teal-800',
      'treasurer': 'bg-emerald-100 text-emerald-800',
      'secretary': 'bg-pink-100 text-pink-800',
      'viewer': 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleName = (role) => {
    const names = {
      'super_admin': 'مشرف خارق',
      'admin': 'مدير عام',
      'manager': 'مدير',
      'coach': 'جهاز فني', 
      'medical': 'جهاز طبي',
      'analyst': 'محلل',
      'editor': 'محرر',
      'moderator': 'مشرف محتوى',
      'captain': 'قائد فريق',
      'treasurer': 'أمين صندوق',
      'secretary': 'سكرتير',
      'viewer': 'مشاهد'
    };
    return names[role] || role;
  };

  // Filter and sort users
  const filteredUsers = useMemo(() => {
    let filtered = [...users];

    // Search filter
    if (userSearchTerm) {
      filtered = filtered.filter(user => 
        user.username?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.role?.includes(userSearchTerm.toLowerCase())
      );
    }

    // Role filter
    if (selectedRoleFilter) {
      filtered = filtered.filter(user => user.role === selectedRoleFilter);
    }

    // Status filter
    if (selectedStatusFilter === 'active') {
      filtered = filtered.filter(user => user.is_active);
    } else if (selectedStatusFilter === 'inactive') {
      filtered = filtered.filter(user => !user.is_active);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (userSortBy) {
        case 'username':
          return (a.username || '').localeCompare(b.username || '');
        case 'role':
          return (a.role || '').localeCompare(b.role || '');
        case 'last_login':
          return new Date(b.last_login || 0) - new Date(a.last_login || 0);
        default:
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }
    });

    return filtered;
  }, [users, userSearchTerm, selectedRoleFilter, selectedStatusFilter, userSortBy]);

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/users/${userId}`, {
        is_active: !currentStatus
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data) {
        setUsers(prev => prev.map(user => 
          user.id === userId ? { ...user, is_active: !currentStatus } : user
        ));
        showNotification(currentStatus ? 'تم تعطيل المستخدم' : 'تم تفعيل المستخدم', 'success');
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      showNotification('حدث خطأ في تعديل حالة المستخدم', 'error');
    }
  };
  
  // Fetch data for reports
  const fetchPlayersForReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/players`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPlayers(response.data);
    } catch (error) {
      console.error('خطأ في جلب اللاعبين:', error);
    }
  };

  const fetchTeamsForReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/teams`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      console.error('خطأ في جلب الفرق:', error);
    }
  };

  const fetchMatchesForReports = async () => {
    try {
      const token = localStorage.getItem('token');
      // استخدام API التقارير للحصول على المباريات مرتبة من الأحدث للأقدم
      const response = await axios.get(`${API}/api/reports/all-matches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // البيانات من API التقارير تأتي في format مختلف
      const allMatches = [
        ...(response.data.matches_by_status?.finished || []),
        ...(response.data.matches_by_status?.upcoming || []),
        ...(response.data.matches_by_status?.cancelled || [])
      ];
      
      console.log('🔄 Reports - أول 3 مباريات من API:', allMatches.slice(0, 3).map(m => ({
        date: m.match_date,
        home: m.home_team_name,
        away: m.away_team_name
      })));
      
      setMatches(allMatches);
    } catch (error) {
      console.error('خطأ في جلب المباريات:', error);
    }
  };

  const fetchTournamentsForReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tournaments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTournaments(response.data);
    } catch (error) {
      console.error('خطأ في جلب البطولات:', error);
    }
  };

  const fetchUsersForReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('خطأ في جلب المستخدمين:', error);
    }
  };

  // Load all reports data when entering settings
  const loadReportsData = async () => {
    console.log('🔄 Loading reports data...');
    await Promise.all([
      fetchPlayersForReports(),
      fetchTeamsForReports(),
      fetchMatchesForReports(),
      fetchTournamentsForReports(),
      fetchUsersForReports()
    ]);
    console.log('✅ Reports data loaded successfully');
  };

  // Advanced Public Page Customization States
  const [publicSectionSettings, setPublicSectionSettings] = useState({
    nextMatch: {
      enabled: true,
      title: "المباراة القادمة",
      titleColor: "#ffffff",
      backgroundColor: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
      showCountdown: true,
      showTeamLogos: true,
      showVenue: true,
      position: 1
    },
    players: {
      enabled: true,
      title: "نجوم الأمجاد",
      titleColor: "#1f2937",
      backgroundColor: "#f9fafb",
      maxPlayers: 6,
      showStats: true,
      showAvatars: true,
      position: 2
    },
    achievements: {
      enabled: true,
      title: "إنجازات النادي",
      subtitle: "فخورون بتاريخنا العريق",
      titleColor: "#1f2937",
      backgroundColor: "#f9fafb", 
      achievements: [
        { title: "سنة تأسيس", value: "1974", icon: "🏢", color: "#f97316" },
        { title: "لاعب نشط", value: "250", icon: "⭐", color: "#f97316" },
        { title: "بطولة محلية", value: "20", icon: "🏆", color: "#f97316" },
        { title: "مباراة فوز", value: "150", icon: "🎯", color: "#f97316" }
      ],
      position: 3
    },
    footer: {
      enabled: true,
      title: "نادي الأمجاد",
      description: "نادي الأمجاد الرياضي يسعى لتحقيق التميز في جميع البطولات والمسابقات المحلية",
      backgroundColor: "#1f2937",
      textColor: "#ffffff",
      contact: {
        phone: "+966 123 456 789",
        email: "info@alamjad-fc.sa",
        address: "الرياض، المملكة العربية السعودية"
      },
      links: [
        { title: "الأخبار", url: "#news" },
        { title: "المباريات", url: "#matches" },
        { title: "اللاعبين", url: "#players" },
        { title: "الإنجازات", url: "#achievements" }
      ],
      socialMedia: {
        twitter: "https://twitter.com/alamjad_club",
        instagram: "https://instagram.com/alamjad_club", 
        youtube: "https://youtube.com/@alamjad_club",
        facebook: "https://facebook.com/alamjad_club"
      },
      position: 4
    }
  });

  const [showSectionCustomizer, setShowSectionCustomizer] = useState(false);
  // Fetch all data for reports and load saved font
  useEffect(() => {
    fetchReportsData();
    loadSavedFont(); // Load saved font on component mount
  }, []);

  const fetchReportsData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [playersRes, teamsRes, matchesRes, tournamentsRes, usersRes] = await Promise.all([
        axios.get(`${API}/players`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/teams`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/matches`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/tournaments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/users`, { headers }).catch(() => ({ data: [] }))
      ]);

      setPlayers(playersRes.data || []);
      setTeams(teamsRes.data || []);
      setMatches(matchesRes.data || []);
      setTournaments(tournamentsRes.data || []);
      setUsers(usersRes.data || []);
    } catch (error) {
      console.error('خطأ في جلب البيانات:', error);
    }
  };

  // Fetch club settings when component loads
  useEffect(() => {
    fetchClubSettings();
  }, []);

  // Load reports data when entering reports section
  useEffect(() => {
    if (activeSection === 'unified-reports') {
      loadReportsData();
    }
  }, [activeSection]);

  const fetchClubSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/club-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('Fetched club settings:', response.data);
      setClubSettings(response.data);
    } catch (error) {
      console.error('خطأ في جلب إعدادات النادي:', error);
    }
  };
  const [newsArticles, setNewsArticles] = useState([]);
  const [newsFeeds, setNewsFeeds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showAddNewsModal, setShowAddNewsModal] = useState(false);
  const [showEditNewsModal, setShowEditNewsModal] = useState(false);
  const [showAddFeedModal, setShowAddFeedModal] = useState(false);
  const [showAddTournamentModal, setShowAddTournamentModal] = useState(false);
  const [showEditTournamentModal, setShowEditTournamentModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [editingTournament, setEditingTournament] = useState(null);
  const [newFeed, setNewFeed] = useState({
    name: '',
    url: '',
    type: 'rss',
    x_username: '' // For X accounts
  });
  const [newNews, setNewNews] = useState({
    title: '',
    content: '',
    summary: '',
    category: 'general',
    published: false,
    tags: [],
    image: null,
    imagePreview: null,
    images: []
  });
  const [newsImageUrlInput, setNewsImageUrlInput] = useState('');
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'analyst',
    is_active: true
  });
  const [newTournament, setNewTournament] = useState({
    name: '',
    tournament_type: 'league',
    category_id: '',
    start_date: '',
    end_date: '',
    location: '',
    description: '',
    status: 'upcoming'
  });
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: []
  });
  const [selectedFont, setSelectedFont] = useState(() => {
    // Initialize with saved font from localStorage
    return localStorage.getItem('arabicFont') || 'Cairo';
  });
  const [dashboardBgFile, setDashboardBgFile] = useState(null);
  const [dashboardBgPreview, setDashboardBgPreview] = useState(null);
  
  // Public page management states
  const [publicPageSettings, setPublicPageSettings] = useState({
    heroTitle: 'نادي الأمجاد',
    heroSubtitle: 'التميز في كل مباراة',
    heroImage: '',
    // Core sections
    showHero: true,
    showMatches: true,
    showNews: true,
    showAchievements: true,
    showTwitter: true,
    showGallery: true,
    showAnnouncements: true,
    // Legacy names for backward compatibility
    showNewsSection: true,
    showMatchesSection: true,
    showPlayersSection: true,
    showStatisticsSection: true,
    showGallerySection: true,
    showSponsorsSection: true,
    // News ticker settings
    newsTickerEnabled: true,
    newsTickerSpeed: 50,
    newsTickerDirection: 'right',
    newsTickerBackground: '#ea580c',
    newsTickerTextColor: '#ffffff',
    // RSS and Tweets settings
    showRssSection: true,
    maxTweetsCount: 5,
    maxRssItems: 10,
    rssUpdateInterval: 30, // minutes
    enableAutoRefresh: true,
    // Footer settings
    footerEnabled: true,
    footerText: 'نادي الأمجاد - جميع الحقوق محفوظة',
    footerBackground: '#1f2937',
    footerTextColor: '#ffffff',
    // Social media
    socialLinksEnabled: true,
    facebookUrl: '',
    twitterUrl: '',
    instagramUrl: '',
    youtubeUrl: '',
    // Contact info
    contactEnabled: true,
    phoneNumber: '',
    emailAddress: '',
    address: '',
    // Twitter Box settings
    twitterUsername: 'alamjad_club',
    twitterDisplayName: 'نادي الأمجاد السعودي',
    twitterTweetsCount: 3,
    twitterAutoSlide: true,
    twitterAutoSlideSpeed: 5,
    customSections: []
  });
  
  // Loading page settings
  const [loadingPageSettings, setLoadingPageSettings] = useState({
    enabled: true,
    title: 'مرحباً بكم في نادي الأمجاد',
    subtitle: 'جاري تحميل الموقع...',
    backgroundColor: '#ea580c',
    textColor: '#ffffff',
    logoEnabled: true,
    animationType: 'fade',
    duration: 3000
  });

  // Reports states
  const [reportSettings, setReportSettings] = useState({
    includeLogo: true,
    includeHeader: true,
    includeFooter: true,
    headerText: 'نادي الأمجاد - التقرير',
    footerText: 'تم إنشاء هذا التقرير تلقائياً',
    dateFormat: 'arabic'
  });
  
  // Media management states
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [storageStats, setStorageStats] = useState({ totalSize: 0, fileCount: 0 });

  // Club announcements states
  const [clubAnnouncements, setClubAnnouncements] = useState([]);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [newAnnouncement, setNewAnnouncement] = useState({
    title: '',
    content: '',
    priority: 'normal',
    category: 'general',
    is_active: true,
    show_on_homepage: true,
    show_on_ticker: false,
    start_date: '',
    end_date: ''
  });

  // Local Twitter management states
  const [localTweets, setLocalTweets] = useState([]);
  const [twitterSettings, setTwitterSettings] = useState({
    show_twitter_box: true,
    follow_button_text: 'تابعنا على تويتر',
    twitter_username: 'alamjad_club',
    display_name: 'نادي الأمجاد',
    bio: 'النادي الرياضي الرائد في المنطقة',
    box_title: 'آخر التغريدات'
  });

  const savePublicSectionSettings = async (sectionKey, settings) => {
    try {
      const token = localStorage.getItem('token');
      const updatedSettings = {
        ...publicSectionSettings,
        [sectionKey]: settings
      };
      
      await axios.put(`${API}/public-section-settings`, updatedSettings, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      setPublicSectionSettings(updatedSettings);
      
      // Show success notification
      showNotification('✅ تم حفظ إعدادات القسم بنجاح!', 'success');
      
    } catch (error) {
      console.error('خطأ في حفظ إعدادات القسم:', error);
      showNotification('❌ فشل في حفظ إعدادات القسم', 'error');
    }
  };

  // Arabic fonts options
  const arabicFonts = [
    { name: 'Cairo', label: 'القاهرة - Cairo' },
    { name: 'Tajawal', label: 'تجول - Tajawal' },
    { name: 'Almarai', label: 'المرايا - Almarai' },
    { name: 'Amiri', label: 'أميري - Amiri' },
    { name: 'Noto Sans Arabic', label: 'نوتو سانس - Noto Sans Arabic' },
    { name: 'IBM Plex Sans Arabic', label: 'آي بي إم - IBM Plex Sans Arabic' }
  ];

  const sections = [
    { id: 'general', label: 'المعلومات العامة', icon: '🏢' },
    { id: 'users', label: 'إدارة المستخدمين', icon: '👥' },
    { id: 'employees', label: 'الموظفين', icon: '🪪' },
    { id: 'seasons', label: 'إدارة المواسم', icon: '📅' },
    { id: 'news', label: 'إدارة الأخبار', icon: '📰' },
    { id: 'media', label: 'إدارة الصور', icon: '📸' },
    { id: 'contact-requests', label: 'طلبات التواصل', icon: '📧' },
    { id: 'board-management', label: 'إدارة مجلس الإدارة', icon: '👔' },
    { id: 'public-page', label: 'إدارة صفحة الجمهور', icon: '🌐' },
    { id: 'legal-pages', label: 'الصفحات القانونية', icon: '📄' },
    { id: 'loading-page', label: 'صفحة التحميل', icon: '⏳' },
    { id: 'unified-reports', label: 'التقارير والإحصائيات', icon: '📊' },
    { id: 'appearance', label: 'الشعار والألوان', icon: '🎨' },
    { id: 'dashboard-bg', label: 'خلفية لوحة التحكم', icon: '🖼️' },
    { id: 'fonts', label: 'الخطوط العربية', icon: '🔤' }
  ];

  useEffect(() => {
    fetchEssentialData(); // Only fetch essential data first
    loadSavedFont();
  }, []);

  useEffect(() => {
    // Fetch section-specific data when activeSection changes  
    if (activeSection === 'users' && users.length === 0) {
      fetchUsers();
    } else if (activeSection === 'news') {
      if (newsArticles.length === 0) fetchNews();
      if (newsFeeds.length === 0) fetchNewsFeeds();
    } else if (activeSection === 'dashboard-bg') {
      // Fetch dashboard background when entering the section
      fetchDashboardBackground();
    }
  }, [activeSection]);

  const fetchEssentialData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Basic club settings - always needed
      const response = await axios.get(`${API}/club-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let clubData = response.data;
      
      // Update favicon_current if favicon_file_id exists but favicon_current doesn't
      if (clubData.favicon_file_id && !clubData.favicon_current) {
        clubData.favicon_current = `${API}/files/${clubData.favicon_file_id}`;
      }
      
      setClubSettings(clubData);
      
      if (clubData.logo_file_id) {
        setLogoPreview(`${API}/files/${clubData.logo_file_id}`);
      }
      
      // Fetch other data
      const [usersRes, newsRes, feedsRes, tournamentsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/auth/users`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/news`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/news-feeds`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/tournaments`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/categories`, { headers: { 'Authorization': `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setUsers(usersRes.data);
      setNewsArticles(newsRes.data);
      setNewsFeeds(feedsRes.data);
      setTournaments(tournamentsRes.data);
      setCategories(categoriesRes.data);
      
      setLoading(false);
    } catch (error) {
      console.error('خطأ في جلب الإعدادات الأساسية:', error);
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      // استخدام API الجديد لجلب المستخدمين
      const response = await axios.get(`${API}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data);
      console.log('✅ تم جلب المستخدمين بنجاح:', response.data.length, 'مستخدم');
    } catch (error) {
      console.error('❌ خطأ في جلب المستخدمين:', error);
      
      // عرض رسالة خطأ للمستخدم
      if (error.response?.status === 403) {
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
        notification.textContent = '⚠️ ليس لديك صلاحية لعرض قائمة المستخدمين';
        document.body.appendChild(notification);
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 4000);
      }
    }
  };

  const fetchDashboardBackground = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard-background`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data && response.data.background_filename) {
        // Set preview to show current background
        setDashboardBgPreview(`${API}/files/${response.data.background_filename}`);
      } else {
        setDashboardBgPreview(null);
      }
    } catch (error) {
      console.error('خطأ في جلب خلفية لوحة التحكم:', error);
      setDashboardBgPreview(null);
    }
  };

  const fetchTournaments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tournaments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTournaments(response.data);
    } catch (error) {
      console.error('خطأ في جلب البطولات:', error);
    }
  };

  // Fetch uploaded files with better error handling
  const fetchUploadedFiles = async () => {
    setLoadingFiles(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/files/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = response.data;
      const files = data.files || data || [];
      
      setUploadedFiles(files);
      
      // Calculate storage stats
      const totalSize = data.total_size || files.reduce((acc, file) => acc + (file.size || 0), 0);
      setStorageStats({
        totalSize: totalSize,
        fileCount: data.total_count || files.length
      });
      
      console.log(`تم جلب ${files.length} ملف بنجاح`);
      
    } catch (error) {
      console.error('خطأ في جلب الملفات:', error);
      alert('فشل في جلب الملفات. تأكد من وجود ملفات في المجلد.');
      setUploadedFiles([]);
      setStorageStats({ totalSize: 0, fileCount: 0 });
    } finally {
      setLoadingFiles(false);
    }
  };

  // Search files
  const searchFiles = async (query, fileType = null) => {
    if (!query.trim()) {
      fetchUploadedFiles();
      return;
    }
    
    setLoadingFiles(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ query });
      if (fileType) params.append('file_type', fileType);
      
      const response = await axios.get(`${API}/files/search?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const files = response.data.files || [];
      setUploadedFiles(files);
      
    } catch (error) {
      console.error('خطأ في البحث:', error);
      alert('فشل في البحث');
    } finally {
      setLoadingFiles(false);
    }
  };

  // Delete selected files - Updated to use bulk delete
  const handleDeleteFiles = async () => {
    if (selectedFiles.length === 0) return;
    
    if (!window.confirm(`هل أنت متأكد من حذف ${selectedFiles.length} ملف؟`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Use bulk delete endpoint
      const response = await axios.delete(`${API}/files/bulk-delete`, {
        headers: { 'Authorization': `Bearer ${token}` },
        data: selectedFiles  // Send file IDs in request body
      });
      
      console.log('Bulk delete response:', response.data);
      
      // Refresh files list
      fetchUploadedFiles();
      setSelectedFiles([]);
      
      // Show success message with details
      const deletedCount = response.data.deleted_count || selectedFiles.length;
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `تم حذف ${deletedCount} ملف بنجاح!`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
    } catch (error) {
      console.error('خطأ في حذف الملفات:', error);
      
      // Show error details if available
      const errorMsg = error.response?.data?.detail || 'حدث خطأ أثناء حذف الملفات';
      alert(`فشل في حذف الملفات: ${errorMsg}`);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Club announcements functions
  const fetchClubAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API}/club-announcements`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setClubAnnouncements(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب إعلانات النادي:', error);
    }
  };

  // Contact Requests Functions
  const fetchContactRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      console.log('🔍 جلب طلبات التواصل...');
      const response = await axios.get(`${API}/contact-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('📥 طلبات التواصل المستلمة:', response.data);
      setContactRequests(response.data.requests || []);
    } catch (error) {
      console.error('❌ خطأ في جلب طلبات التواصل:', error);
    }
  };

  const updateContactRequestStatus = async (requestId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/contact-requests/${requestId}/status`, {
        status: status
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // تحديث الحالة محلياً
      setContactRequests(prev => prev.map(req => 
        req.id === requestId 
          ? { ...req, status, ...(status === 'read' && { read_at: new Date().toISOString() }) }
          : req
      ));
      
      showNotification(`تم ${status === 'read' ? 'وضع علامة مقروء' : 'وضع علامة مجاب'} بنجاح`);
    } catch (error) {
      console.error('خطأ في تحديث حالة الطلب:', error);
    }
  };

  const deleteContactRequest = async (requestId) => {
    if (!confirm('هل تريد حذف هذا الطلب؟ سيتم نقله للمحذوفات.')) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Find the request to move to deleted
      const requestToDelete = contactRequests.find(req => req.id === requestId);
      if (requestToDelete) {
        // Add to deleted requests
        setDeletedRequests(prev => [...prev, { ...requestToDelete, deleted_at: new Date().toISOString() }]);
      }
      
      // Remove from active requests
      setContactRequests(prev => prev.filter(req => req.id !== requestId));
      
      showNotification('تم نقل الطلب إلى المحذوفات');
    } catch (error) {
      console.error('خطأ في حذف الطلب:', error);
    }
  };

  const restoreContactRequest = (requestId) => {
    const requestToRestore = deletedRequests.find(req => req.id === requestId);
    if (requestToRestore) {
      // Remove deleted_at property
      const { deleted_at, ...restoredRequest } = requestToRestore;
      
      setContactRequests(prev => [...prev, restoredRequest]);
      setDeletedRequests(prev => prev.filter(req => req.id !== requestId));
      
      showNotification('تم استعادة الطلب بنجاح');
    }
  };

  const permanentDeleteRequest = (requestId) => {
    if (!confirm('هل تريد حذف هذا الطلب نهائياً؟ لا يمكن التراجع عن هذا الإجراء.')) return;
    
    setDeletedRequests(prev => prev.filter(req => req.id !== requestId));
    showNotification('تم حذف الطلب نهائياً');
  };

  // Load contact requests when settings page opens
  useEffect(() => {
    if (activeSection === 'contact-requests') {
      fetchContactRequests();
      fetchLegalPages();
      fetchBoardMembers();
    }
  }, [activeSection]);

  // Board Members Functions
  const fetchBoardMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/board-members`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setBoardMembers(response.data.members || []);
    } catch (error) {
      console.error('خطأ في جلب أعضاء مجلس الإدارة:', error);
    }
  };

  const deleteBoardMember = async (memberId) => {
    if (!confirm('هل أنت متأكد من حذف هذا العضو؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/board-members/${memberId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // إشعار نجاح
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-xl mr-2">✅</span>
          <span>تم حذف عضو مجلس الإدارة بنجاح</span>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
      fetchBoardMembers();
      
    } catch (error) {
      console.error('خطأ في حذف عضو مجلس الإدارة:', error);
      alert('حدث خطأ في حذف عضو مجلس الإدارة');
    }
  };

  const editBoardMember = (member) => {
    setEditingBoardMember(member);
    setBoardMemberForm({
      name: member.name || '',
      position: member.position || '',
      description: member.description || '',
      email: member.email || '',
      phone: member.phone || '',
      experience: member.experience || '',
      image_url: member.image_url || '',
      order: member.order || 0
    });
    setShowBoardMemberModal(true);
  };

  const resetBoardMemberForm = () => {
    setEditingBoardMember(null);
    setBoardMemberForm({
      name: '',
      position: '',
      description: '',
      email: '',
      phone: '',
      experience: '',
      image_url: '',
      order: 0
    });
  };

  // Legal Pages Functions
  const fetchLegalPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/legal-pages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLegalPages(response.data);
    } catch (error) {
      console.error('خطأ في جلب الصفحات القانونية:', error);
    }
  };

  const saveLegalPages = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/legal-pages`, legalPages, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // إشعار نجاح
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-xl mr-2">✅</span>
          <span>تم حفظ الصفحات القانونية بنجاح</span>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
    } catch (error) {
      console.error('خطأ في حفظ الصفحات القانونية:', error);
      alert('حدث خطأ في حفظ الصفحات القانونية');
    }
  };

  const handleSaveAnnouncement = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const announcementData = {
        ...newAnnouncement,
        start_date: newAnnouncement.start_date ? new Date(newAnnouncement.start_date).toISOString() : null,
        end_date: newAnnouncement.end_date ? new Date(newAnnouncement.end_date).toISOString() : null
      };

      if (editingAnnouncement) {
        // Update existing announcement
        await axios.put(`${API}/club-announcements/${editingAnnouncement.id}`, announcementData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new announcement
        await axios.post(`${API}/club-announcements`, announcementData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      // Reset form and close modal
      setNewAnnouncement({
        title: '',
        content: '',
        priority: 'normal',
        category: 'general',
        is_active: true,
        show_on_homepage: true,
        show_on_ticker: false,
        start_date: '',
        end_date: ''
      });
      setEditingAnnouncement(null);
      setShowAnnouncementModal(false);
      
      // Refresh announcements list
      await fetchClubAnnouncements();
      
      // تحديث البيانات العامة لتحديث الشريط الإخباري
      window.dispatchEvent(new CustomEvent('refreshPublicData'));
      
      // Show success message
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = editingAnnouncement ? 'تم تحديث الإعلان بنجاح!' : 'تم إضافة الإعلان بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

    } catch (error) {
      console.error('خطأ في حفظ الإعلان:', error);
      alert('حدث خطأ أثناء حفظ الإعلان: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setNewAnnouncement({
      ...announcement,
      start_date: announcement.start_date ? new Date(announcement.start_date).toISOString().split('T')[0] : '',
      end_date: announcement.end_date ? new Date(announcement.end_date).toISOString().split('T')[0] : ''
    });
    setShowAnnouncementModal(true);
  };

  const handleDeleteAnnouncement = async (announcementId, announcementTitle) => {
    if (!window.confirm(`هل أنت متأكد من حذف إعلان "${announcementTitle}"؟`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.delete(`${API}/club-announcements/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh announcements list
      await fetchClubAnnouncements();
      
      // تحديث البيانات العامة لتحديث الشريط الإخباري
      window.dispatchEvent(new CustomEvent('refreshPublicData'));
      
      // Show success message
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حذف الإعلان بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

    } catch (error) {
      console.error('خطأ في حذف الإعلان:', error);
      alert('حدث خطأ أثناء حذف الإعلان: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateAnnouncement = async (announcementId, updatedData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.put(`${API}/club-announcements/${announcementId}`, updatedData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh announcements list
      await fetchClubAnnouncements();
      
      // تحديث البيانات العامة لتحديث الشريط الإخباري
      window.dispatchEvent(new CustomEvent('refreshPublicData'));
      
      // Show success message
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم تحديث الإعلان بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

    } catch (error) {
      console.error('خطأ في تحديث الإعلان:', error);
      alert('حدث خطأ أثناء تحديث الإعلان: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Local Twitter management functions
  const fetchLocalTweets = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API}/local-tweets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLocalTweets(response.data);
    } catch (error) {
      console.error('خطأ في جلب التغريدات:', error);
    }
  };

  const fetchTwitterSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API}/twitter-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTwitterSettings(response.data);
    } catch (error) {
      console.error('خطأ في جلب إعدادات تويتر:', error);
    }
  };

  const updateTwitterSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.put(`${API}/twitter-settings`, twitterSettings, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      alert('تم حفظ إعدادات تويتر بنجاح!');
    } catch (error) {
      console.error('خطأ في حفظ إعدادات تويتر:', error);
      alert('خطأ في حفظ إعدادات تويتر!');
    }
  };

  const fetchNews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/news`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNewsArticles(response.data);
    } catch (error) {
      console.error('خطأ في جلب الأخبار:', error);
    }
  };

  const fetchNewsFeeds = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/news-feeds`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setNewsFeeds(response.data);
    } catch (error) {
      console.error('خطأ في جلب مصادر الأخبار:', error);
    }
  };

  const loadSavedFont = () => {
    const savedFont = localStorage.getItem('arabicFont') || 'Cairo';
    console.log('Loading saved font:', savedFont); // Debug log
    setSelectedFont(savedFont);
    applyFont(savedFont);
  };

  const applyFont = (fontName) => {
    // Apply font to the entire document
    document.documentElement.style.setProperty('--arabic-font', fontName);
    const style = document.createElement('style');
    style.textContent = `
      * {
        font-family: '${fontName}', system-ui, -apple-system, sans-serif !important;
      }
      body, html, #root, .App {
        font-family: '${fontName}', system-ui, -apple-system, sans-serif !important;
      }
      input, textarea, select, button {
        font-family: '${fontName}', system-ui, -apple-system, sans-serif !important;
      }
    `;
    const existingStyle = document.getElementById('arabic-font-style');
    if (existingStyle) {
      existingStyle.remove();
    }
    style.id = 'arabic-font-style';
    document.head.appendChild(style);
  };

  const handleFontChange = (fontName) => {
    console.log('Changing font to:', fontName); // Debug log
    setSelectedFont(fontName);
    applyFont(fontName);
    localStorage.setItem('arabicFont', fontName);
    
    // Show confirmation with better styling
    const notification = document.createElement('div');
    notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
    notification.textContent = `تم تطبيق الخط ${arabicFonts.find(f => f.name === fontName)?.label} وحفظه بنجاح!`;
    document.body.appendChild(notification);
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
  };

  // Optimized fetchAllData - now only refetches current section data
  const fetchAllData = async () => {
    await fetchEssentialData();
    
    // Only refresh the current section's data to avoid unnecessary API calls
    if (activeSection === 'users') {
      await fetchUsers();
    } else if (activeSection === 'news') {
      await fetchNews();
      await fetchNewsFeeds();
    } else if (activeSection === 'media') {
      await fetchUploadedFiles();
    } else if (activeSection === 'public-page') {
      await fetchClubAnnouncements();
      await fetchLocalTweets();
      await fetchTwitterSettings();
    }
  };

  // Auto-load announcements when entering public-page section
  useEffect(() => {
    if (activeSection === 'public-page') {
      fetchClubAnnouncements();
      fetchLocalTweets();
      fetchTwitterSettings();
      fetchSponsors();
      // Also load current public page settings
      loadPublicPageSettings();
    } else if (activeSection === 'loading-page') {
      // Load loading page settings when entering loading page section
      loadLoadingPageSettingsForAdmin();
    }
  }, [activeSection]);

  const fetchSponsors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/sponsors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      console.log('✅ تم جلب الرعاة:', response.data);
      setSponsors(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب الرعاة:', error);
    }
  };

  const handleSaveSponsor = async () => {
    try {
      const token = localStorage.getItem('token');
      
      console.log('🔄 محاولة حفظ الراعي:', newSponsor);
      
      if (editingSponsor) {
        // Update existing sponsor
        console.log('📝 تحديث راعي موجود:', editingSponsor.id);
        const response = await axios.put(`${API}/sponsors/${editingSponsor.id}`, newSponsor, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ تم التحديث:', response.data);
        showNotification('تم تحديث الراعي بنجاح', 'success');
      } else {
        // Create new sponsor
        console.log('➕ إضافة راعي جديد');
        const response = await axios.post(`${API}/sponsors`, newSponsor, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('✅ تم الإضافة:', response.data);
        showNotification('تم إضافة الراعي بنجاح', 'success');
      }
      
      fetchSponsors();
      setShowSponsorModal(false);
      setEditingSponsor(null);
      setNewSponsor({
        name: '',
        logo_file_id: null,
        logo_url: '',
        website_url: '',
        description: '',
        is_active: true,
        order: 0
      });
    } catch (error) {
      console.error('❌ خطأ في حفظ الراعي:', error);
      console.error('❌ تفاصيل الخطأ:', error.response?.data);
      showNotification('فشل حفظ الراعي: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  const handleUploadSponsorLogo = async (file) => {
    try {
      console.log('📤 بدء رفع شعار الراعي:', file.name);
      const formData = new FormData();
      formData.append('file', file);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      console.log('✅ تم رفع الشعار بنجاح، file_id:', response.data.file_id);
      
      setNewSponsor(prev => ({
        ...prev,
        logo_file_id: response.data.file_id
      }));
      
      showNotification('تم رفع الشعار بنجاح', 'success');
    } catch (error) {
      console.error('❌ خطأ في رفع الشعار:', error);
      console.error('❌ تفاصيل الخطأ:', error.response?.data);
      showNotification('فشل رفع الشعار: ' + (error.response?.data?.detail || error.message), 'error');
    }
  };

  // Load loading page settings for admin
  const loadLoadingPageSettingsForAdmin = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/loading-page-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data) {
        console.log('Loaded loading page settings for admin:', response.data);
        setLoadingPageSettings(prev => ({...prev, ...response.data}));
      }
    } catch (error) {
      console.error('خطأ في تحميل إعدادات صفحة التحميل للإدارة:', error);
    }
  };

  // Load public page settings
  const loadPublicPageSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/public-page-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data) {
        console.log('Loaded public page settings:', response.data);
        setPublicPageSettings(prev => ({...prev, ...response.data}));
      }
    } catch (error) {
      console.error('خطأ في تحميل إعدادات الصفحة العامة:', error);
    }
  };

  const handleSaveClubSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('Saving club settings:', clubSettings);
      
      const response = await axios.put(`${API}/club-settings`, clubSettings, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Club settings save response:', response.data);
      
      // تحديث الـ state المحلي بالاستجابة من الـ server
      setClubSettings(response.data);
      
      // إرسال event لتحديث الألوان في المكونات الأخرى
      window.dispatchEvent(new CustomEvent('colorsUpdated', {
        detail: { 
          primary_color: response.data.primary_color,
          secondary_color: response.data.secondary_color
        }
      }));
      
      // تحديث البيانات العامة
      window.dispatchEvent(new CustomEvent('refreshPublicData'));
      
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حفظ وتطبيق الألوان بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
    } catch (error) {
      console.error('خطأ في حفظ إعدادات النادي:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMessage = 'فشل في حفظ إعدادات النادي';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`خطأ: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleIconUpload = async (event, iconType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (2MB limit)
    if (file.size > 2 * 1024 * 1024) {
      alert('حجم الملف كبير جداً. يجب أن يكون أقل من 2 ميجابايت.');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('يرجى رفع ملف صورة صحيح (PNG, JPG, SVG)');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/files/upload`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.file_id) {
        // Create image URL from uploaded file
        const imageUrl = `${API}/api/files/${response.data.file_id}`;
        
        // Update club settings with the new icon URL
        setClubSettings({
          ...clubSettings,
          [iconType]: imageUrl
        });

        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg z-50';
        notification.innerHTML = `
          <div class="flex items-center">
            <span class="text-xl mr-2">✅</span>
            <span>تم رفع الأيقونة بنجاح!</span>
          </div>
        `;
        document.body.appendChild(notification);
        setTimeout(() => {
          if (document.body.contains(notification)) {
            document.body.removeChild(notification);
          }
        }, 4000);
      }
    } catch (error) {
      console.error('خطأ في رفع الأيقونة:', error);
      alert('حدث خطأ في رفع الأيقونة. يرجى المحاولة مرة أخرى.');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      console.log('حفظ الإعدادات - البيانات المرسلة:', clubSettings);
      let settingsToSave = {...clubSettings};
      
      // Handle favicon upload if there's a new file
      if (clubSettings.favicon_file) {
        try {
          const formData = new FormData();
          formData.append('file', clubSettings.favicon_file);
          
          const uploadResponse = await axios.post(`${API}/files/upload`, formData, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          if (uploadResponse.data.file_id) {
            settingsToSave.favicon_file_id = uploadResponse.data.file_id;
            settingsToSave.favicon_current = `${API}/files/${uploadResponse.data.file_id}`;
            console.log('تم رفع الأيقونة بنجاح:', uploadResponse.data.file_id);
          }
        } catch (uploadError) {
          console.error('خطأ في رفع الأيقونة:', uploadError);
          alert('فشل في رفع الأيقونة، سيتم حفظ باقي الإعدادات');
        }
        
        // Remove the file object before sending to backend
        delete settingsToSave.favicon_file;
        delete settingsToSave.favicon_preview;
      }
      
      // Ensure browser_tab_title is included
      if (!settingsToSave.browser_tab_title && clubSettings.browser_tab_title) {
        settingsToSave.browser_tab_title = clubSettings.browser_tab_title;
      }
      
      console.log('البيانات النهائية للحفظ:', settingsToSave);
      
      // Save settings
      const saveResponse = await axios.put(`${API}/club-settings`, settingsToSave, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      console.log('استجابة الحفظ:', saveResponse.data);
      
      // Update page title and favicon immediately
      if (settingsToSave.browser_tab_title) {
        document.title = settingsToSave.browser_tab_title;
        console.log('تم تحديث عنوان الصفحة إلى:', settingsToSave.browser_tab_title);
      }
      
      if (settingsToSave.favicon_file_id) {
        const faviconLink = document.querySelector('link[rel="icon"]') || 
                          document.querySelector('link[rel="shortcut icon"]');
        if (faviconLink) {
          faviconLink.href = `${API}/files/${settingsToSave.favicon_file_id}`;
          console.log('تم تحديث الأيقونة إلى:', faviconLink.href);
        } else {
          // Create new favicon link if it doesn't exist
          const newFaviconLink = document.createElement('link');
          newFaviconLink.rel = 'icon';
          newFaviconLink.type = 'image/x-icon';
          newFaviconLink.href = `${API}/files/${settingsToSave.favicon_file_id}`;
          document.head.appendChild(newFaviconLink);
          console.log('تم إنشاء أيقونة جديدة:', newFaviconLink.href);
        }
      }
      
      // Refresh club settings from server to ensure consistency
      const refreshResponse = await axios.get(`${API}/club-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      let refreshedSettings = refreshResponse.data;
      
      // Update favicon_current if favicon_file_id exists
      if (refreshedSettings.favicon_file_id) {
        refreshedSettings.favicon_current = `${API}/files/${refreshedSettings.favicon_file_id}`;
      }
      
      setClubSettings(refreshedSettings);
      console.log('تم تحديث الإعدادات من الخادم:', refreshedSettings);
      
      // Success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حفظ جميع الإعدادات بنجاح! ستظهر التغييرات في علامة التبويب فوراً.';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);
      
    } catch (error) {
      console.error('خطأ في حفظ الإعدادات:', error);
      console.error('تفاصيل الخطأ:', error.response?.data);
      alert(`فشل في حفظ الإعدادات: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePublicPageSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      console.log('Saving public page settings:', publicPageSettings);
      
      const response = await axios.put(`${API}/public-page-settings`, publicPageSettings, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Public page settings save response:', response.data);
      
      // تحديث البيانات العامة
      window.dispatchEvent(new CustomEvent('refreshPublicData'));
      
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حفظ إعدادات الصفحة العامة بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
      
      console.log('✅ Public page settings saved successfully');
      
    } catch (error) {
      console.error('❌ خطأ في حفظ إعدادات الصفحة العامة:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMessage = 'فشل في حفظ إعدادات الصفحة العامة';
      if (error.response?.status === 401) {
        errorMessage = 'انتهت صلاحية جلسة المستخدم. يرجى تسجيل الدخول مرة أخرى.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Show error notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `خطأ: ${errorMessage}`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 5000);
      
    } finally {
      setSaving(false);
    }
  };

  // Auto-save functionality for public page settings
  const [autoSaveTimeout, setAutoSaveTimeout] = useState(null);
  
  const triggerAutoSave = () => {
    // Clear existing timeout
    if (autoSaveTimeout) {
      clearTimeout(autoSaveTimeout);
    }
    
    // Set new timeout for auto-save after 2 seconds of inactivity
    const timeout = setTimeout(() => {
      handleSavePublicPageSettings();
    }, 2000);
    
    setAutoSaveTimeout(timeout);
  };

  // Enhanced function to update public page settings with auto-save
  const updatePublicPageSettings = (newSettings) => {
    setPublicPageSettings(newSettings);
    triggerAutoSave();
  };

  const handleLogoUpload = async () => {
    if (!logoFile) {
      alert('يرجى اختيار ملف أولاً');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Step 1: Upload file
      const formData = new FormData();
      formData.append('file', logoFile);

      console.log('Uploading logo file...');
      const uploadResponse = await axios.post(`${API}/files/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Upload response:', uploadResponse.data);
      const fileId = uploadResponse.data.file_id;
      
      if (!fileId) {
        throw new Error('لم يتم الحصول على معرف الملف');
      }

      // Step 2: Update club settings with new logo
      const updatedSettings = { 
        ...clubSettings, 
        logo_file_id: fileId 
      };
      
      console.log('Updating club settings with logo:', updatedSettings);
      const settingsResponse = await axios.put(`${API}/club-settings`, updatedSettings, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Settings update response:', settingsResponse.data);

      // Step 3: Update local state
      setClubSettings(updatedSettings);
      setLogoPreview(`${API}/files/${fileId}`);
      setLogoFile(null);
      
      // Step 4: Send events to update other components
      window.dispatchEvent(new CustomEvent('logoUpdated', {
        detail: { fileId: fileId }
      }));
      
      window.dispatchEvent(new CustomEvent('refreshPublicData'));
      
      // Step 5: Show success message
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم رفع الشعار وحفظه بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

    } catch (error) {
      console.error('خطأ في رفع الشعار:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMessage = 'فشل في رفع الشعار';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`خطأ: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  // Export and Print Functions
  const exportToExcel = async (data, filename, sheetName = 'Sheet1') => {
    try {
      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.writeFile(workbook, `${filename}.xlsx`);
      
      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم تصدير ملف Excel بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);
    } catch (error) {
      console.error('خطأ في تصدير Excel:', error);
      alert('فشل في تصدير ملف Excel');
    }
  };

  const printSection = async (elementId, title = 'تقرير نادي الأمجاد') => {
    try {
      const element = document.getElementById(elementId);
      if (!element) {
        alert('لم يتم العثور على العنصر للطباعة');
        return;
      }

      // Show progress notification
      const progressNotif = document.createElement('div');
      progressNotif.className = 'fixed top-4 right-4 bg-orange-500 text-white px-6 py-3 rounded-lg shadow-lg z-[9999] flex items-center gap-3';
      progressNotif.innerHTML = '<div class="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div><span>جاري إنشاء ملف PDF بجودة عالية...</span>';
      document.body.appendChild(progressNotif);

      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([import('html2canvas'), import('jspdf')]);
      // High quality canvas capture
      const canvas = await html2canvas(element, {
        scale: 4,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        imageTimeout: 15000,
        removeContainer: true,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      // PDF setup with margins
      const margin = 10; // mm
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const contentWidth = pageWidth - (margin * 2);
      const headerHeight = 6;
      const footerHeight = 10;
      const contentAreaHeight = pageHeight - headerHeight - footerHeight;
      
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let currentPage = 1;
      let totalPages = Math.ceil(imgHeight / contentAreaHeight);
      
      // Helper: draw header on each page
      const drawHeader = (pageNum) => {
        pdf.setFillColor(249, 115, 22);
        pdf.rect(0, 0, pageWidth, 2.5, 'F');
      };
      
      // Helper: draw footer on each page
      const drawFooter = (pageNum) => {
        const footerY = pageHeight - 6;
        pdf.setDrawColor(220, 220, 220);
        pdf.setLineWidth(0.3);
        pdf.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`${pageNum} / ${totalPages}`, pageWidth / 2, footerY, { align: 'center' });
      };
      
      // First page
      drawHeader(currentPage);
      pdf.addImage(imgData, 'JPEG', margin, headerHeight, imgWidth, imgHeight, undefined, 'FAST');
      drawFooter(currentPage);
      heightLeft -= contentAreaHeight;
      
      // Additional pages
      while (heightLeft > 0) {
        pdf.addPage();
        currentPage++;
        drawHeader(currentPage);
        
        const position = headerHeight - (imgHeight - heightLeft);
        pdf.addImage(imgData, 'JPEG', margin, position, imgWidth, imgHeight, undefined, 'FAST');
        drawFooter(currentPage);
        heightLeft -= contentAreaHeight;
      }
      
      pdf.save(`${title}.pdf`);
      
      // Remove progress, show success
      if (document.body.contains(progressNotif)) document.body.removeChild(progressNotif);
      
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-[9999]';
      notification.textContent = 'تم إنشاء ملف PDF بجودة عالية!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) document.body.removeChild(notification);
      }, 3000);
    } catch (error) {
      console.error('خطأ في الطباعة:', error);
      // Remove progress notification if exists
      document.querySelectorAll('.fixed.top-4.right-4.bg-orange-500').forEach(el => el.remove());
      alert('فشل في إنشاء ملف PDF');
    }
  };

  const printPage = () => {
    window.print();
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // استخدام API الجديد لإضافة المستخدمين
      const response = await axios.post(`${API}/users`, newUser, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // إغلاق المودال وإعادة تعيين النموذج
      setShowAddUserModal(false);
      setNewUser({
        username: '',
        password: '',
        full_name: '',
        email: '',
        role: 'analyst',
        is_active: true
      });

      // إعادة تحميل قائمة المستخدمين
      await fetchAllData();

      // عرض رسالة نجاح
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `تم إضافة المستخدم "${response.data.user.username}" بنجاح! 🎉`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);

    } catch (error) {
      console.error('خطأ في إضافة المستخدم:', error);
      const errorMsg = error.response?.data?.detail || 'فشل في إضافة المستخدم';
      
      // عرض رسالة خطأ
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `❌ ${errorMsg}`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);
    } finally {
      setSaving(false);
    }
  };

  // Dashboard background upload functions
  const handleDashboardBgFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('حجم الملف كبير جداً. يرجى اختيار ملف أصغر من 5 ميجابايت.');
        return;
      }
      
      setDashboardBgFile(file);
      
      // Preview
      const reader = new FileReader();
      reader.onload = (e) => setDashboardBgPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleDashboardBgUpload = async () => {
    if (!dashboardBgFile) {
      alert('يرجى اختيار ملف أولاً');
      return;
    }

    setSaving(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.post(`${API}/dashboard-background`, {
            background_image: e.target.result
          }, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          console.log('تم رفع الخلفية بنجاح:', response.data);

          // Refresh the background from server
          await fetchDashboardBackground();

          // Clear the upload file 
          setDashboardBgFile(null);

          // Show success notification
          const notification = document.createElement('div');
          notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
          notification.textContent = 'تم رفع وحفظ صورة الخلفية بنجاح! ستظهر في لوحة التحكم فوراً.';
          document.body.appendChild(notification);
          setTimeout(() => {
            if (document.body.contains(notification)) {
              document.body.removeChild(notification);
            }
          }, 4000);

        } catch (error) {
          console.error('خطأ في رفع الصورة:', error);
          alert(`فشل في رفع الصورة: ${error.response?.data?.detail || error.message}`);
        } finally {
          setSaving(false);
        }
      };
      reader.readAsDataURL(dashboardBgFile);
    } catch (error) {
      console.error('خطأ في معالجة الملف:', error);
      alert('فشل في معالجة الملف');
      setSaving(false);
    }
  };

  const handleDashboardBgDelete = async () => {
    if (!confirm('هل أنت متأكد من حذف صورة الخلفية الحالية؟ لن يمكن التراجع عن هذا الإجراء.')) {
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/dashboard-background`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Clear all background related state
      setDashboardBgPreview(null);
      setDashboardBgFile(null);

      console.log('تم حذف الخلفية بنجاح');

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حذف صورة الخلفية بنجاح! تم إرجاع لوحة التحكم للشكل الافتراضي.';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);

    } catch (error) {
      console.error('خطأ في حذف الصورة:', error);
      alert(`فشل في حذف صورة الخلفية: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };



  const getRolePermissions = (role) => {
    const permissions = {
      administrator: ['إدارة كاملة', 'إعدادات النظام', 'إدارة المستخدمين', 'جميع الأقسام'],
      supervisor: ['إدارة البيانات', 'التقارير', 'المباريات', 'اللاعبين'],
      coach: ['إدارة اللاعبين', 'إدارة المباريات', 'التدريب', 'التقارير الأساسية'],
      analyst: ['مشاهدة التقارير', 'تحليل البيانات', 'الإحصائيات'],
      editor: ['إدارة الأخبار', 'إدارة المحتوى', 'النشر'],
      viewer: ['مشاهدة فقط']
    };
    return permissions[role] || ['صلاحيات محدودة'];
  };

  const handleEditUser = (user) => {
    setEditingUser({
      ...user,
      password: '' // Don't pre-fill password for security
    });
    setShowEditUserModal(true);
  };



  const handleDeleteUser = async (userId, username) => {
    // تأكيد الحذف مع تحذير واضح
    if (!confirm(`⚠️ هل أنت متأكد من حذف المستخدم "${username}"؟\n\n🔴 تحذير: لا يمكن التراجع عن هذا الإجراء.\nسيتم حذف جميع بيانات المستخدم نهائياً.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // استخدام API الجديد لحذف المستخدمين
      const response = await axios.delete(`${API}/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // إعادة تحميل قائمة المستخدمين
      await fetchAllData();

      // عرض رسالة نجاح
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-lg mr-2">🗑️</span>
          <div>
            <div class="font-semibold">تم حذف المستخدم بنجاح</div>
            <div class="text-sm opacity-90">المستخدم "${username}" تم حذفه نهائياً</div>
          </div>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 5000);

    } catch (error) {
      console.error('خطأ في حذف المستخدم:', error);
      const errorMsg = error.response?.data?.detail || 'فشل في حذف المستخدم';
      
      // عرض رسالة خطأ مفصلة
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.innerHTML = `
        <div class="flex items-center">
          <span class="text-lg mr-2">❌</span>
          <div>
            <div class="font-semibold">فشل في حذف المستخدم</div>
            <div class="text-sm opacity-90">${errorMsg}</div>
          </div>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 5000);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // استخدام API الجديد لتحديث المستخدمين
      const response = await axios.put(`${API}/users/${editingUser.id}`, {
        username: editingUser.username,
        full_name: editingUser.full_name,
        email: editingUser.email,
        role: editingUser.role,
        is_active: editingUser.is_active,
        ...(editingUser.password && { password: editingUser.password }) // إضافة كلمة المرور فقط إذا تم تغييرها
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // إغلاق المودال
      setShowEditUserModal(false);
      setEditingUser(null);

      // إعادة تحميل البيانات
      await fetchAllData();

      // عرض رسالة نجاح
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `تم تحديث بيانات المستخدم "${response.data.user.username}" بنجاح! ✅`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);

    } catch (error) {
      console.error('خطأ في تحديث المستخدم:', error);
      const errorMsg = error.response?.data?.detail || 'فشل في تحديث المستخدم';
      
      // عرض رسالة خطأ
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `❌ ${errorMsg}`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 4000);
    }
  };

  const handleAddRole = async (e) => {
    e.preventDefault();
    try {
      // This would typically be an API call, but for now we'll simulate it
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = `تم إضافة الدور "${newRole.name}" بنجاح!`;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      setShowRoleModal(false);
      setNewRole({
        name: '',
        description: '',
        permissions: []
      });
    } catch (error) {
      console.error('خطأ في إضافة الدور:', error);
      alert('فشل في إضافة الدور');
    }
  };

  // Fetch activities from backend
  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/activity-log`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setActivities(response.data.slice(0, 3)); // Show only latest 3 activities
    } catch (error) {
      console.error('خطأ في جلب سجل النشاط:', error);
      // Use fallback data if API fails
      setActivities([
        { action: "إضافة لاعب جديد", user: "الإدارة", time: "منذ 5 دقائق", icon: "👤", color: "bg-green-100 text-green-600" },
        { action: "تحديث نتيجة مباراة", user: "المدرب", time: "منذ 15 دقيقة", icon: "🏟️", color: "bg-blue-100 text-blue-600" },
        { action: "إضافة خبر جديد", user: "المسؤول", time: "منذ ساعة", icon: "📰", color: "bg-yellow-100 text-yellow-600" }
      ]);
    }
  };

  // Fetch latest news from backend
  const fetchLatestNews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/news?limit=3&published=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setLatestNews(response.data.slice(0, 3)); // Show only latest 3 news
    } catch (error) {
      console.error('خطأ في جلب آخر الأخبار:', error);
      // Use fallback data if API fails
      setLatestNews([
        { title: "فوز النادي في المباراة الأخيرة", time: "منذ ساعتين", category: "نتائج", created_at: new Date().toISOString() },
        { title: "تجديد عقد اللاعب محمد أحمد", time: "منذ يوم", category: "انتقالات", created_at: new Date(Date.now() - 86400000).toISOString() },
        { title: "بدء التسجيل للموسم الجديد", time: "منذ يومين", category: "إعلانات", created_at: new Date(Date.now() - 172800000).toISOString() }
      ]);
    }
  };

  // Helper function to get activity icon and color
  const getActivityDisplay = (action) => {
    if (action.includes('لاعب')) return { icon: "👤", color: "bg-green-100 text-green-600" };
    if (action.includes('مباراة')) return { icon: "🏟️", color: "bg-blue-100 text-blue-600" };
    if (action.includes('خبر')) return { icon: "📰", color: "bg-yellow-100 text-yellow-600" };
    if (action.includes('بطولة')) return { icon: "🏆", color: "bg-purple-100 text-purple-600" };
    if (action.includes('فريق')) return { icon: "👥", color: "bg-indigo-100 text-indigo-600" };
    if (action.includes('إعدادات')) return { icon: "⚙️", color: "bg-gray-100 text-gray-600" };
    return { icon: "📋", color: "bg-orange-100 text-orange-600" };
  };
  // Handle image upload for news
  const handleNewsImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الصورة كبير جداً. الحد الأقصى 5MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('يرجى اختيار ملف صورة صالح');
      return;
    }

    try {
      // Upload file to server
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/api/upload`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Add uploaded image URL to images array
      const imageUrl = `${API}/api/files/${response.data.file_id}`;
      setNewNews(prev => ({
        ...prev,
        images: [...prev.images, imageUrl]
      }));

      alert('تم رفع الصورة بنجاح!');
      e.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('حدث خطأ في رفع الصورة');
    }
  };

  const handleAddNews = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      let newsData = { ...newNews };
      
      // Keep images array (URLs)
      newsData.images = newNews.images || [];
      
      // Set first image as main image if no main image and images exist
      if (newsData.images.length > 0 && !newsData.image_file_id) {
        newsData.image_file_id = newsData.images[0];
      }
      
      // Remove image and imagePreview from data before sending
      delete newsData.image;
      delete newsData.imagePreview;
      
      console.log('📰 Final news data to send:', newsData);
      
      await axios.post(`${API}/news`, newsData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Refresh news list
      fetchNews();

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم إضافة الخبر بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      setShowAddNewsModal(false);
      setNewNews({
        title: '',
        content: '',
        summary: '',
        category: 'general',
        published: false,
        tags: [],
        image: null,
        imagePreview: null,
        images: []
      });
      setNewsImageUrlInput('');
      
      // Refresh news list
      fetchAllData();
    } catch (error) {
      console.error('خطأ في إضافة الخبر:', error);
      alert(`فشل في إضافة الخبر: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNews = async (newsId, newsTitle) => {
    if (!confirm(`هل أنت متأكد من حذف الخبر "${newsTitle}"؟`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/news/${newsId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حذف الخبر بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      // Refresh news list
      fetchAllData();
    } catch (error) {
      console.error('خطأ في حذف الخبر:', error);
      alert(`فشل في حذف الخبر: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Edit news function
  const handleEditNews = (article) => {
    setEditingNews({
      ...article,
      newImage: null,
      newImagePreview: null
    });
    setShowEditNewsModal(true);
  };

  // News update function (missing from implementation)
  const handleUpdateNews = async (newsId, updatedNews) => {
    try {
      const token = localStorage.getItem('token');
      
      let finalNewsData = { ...updatedNews };
      
      // Handle image upload if there's a new image
      if (editingNews.newImage) {
        console.log('🖼️ Uploading new image for edited news:', editingNews.newImage.name);
        const formData = new FormData();
        formData.append('file', editingNews.newImage);
        
        const imageResponse = await axios.post(`${API}/files/upload`, formData, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('📤 Image upload response:', imageResponse.data);
        
        if (imageResponse.data && imageResponse.data.file_id) {
          finalNewsData.image_file_id = imageResponse.data.file_id;
          console.log('✅ New image file_id assigned:', finalNewsData.image_file_id);
        }
      } else if (editingNews.image_file_id) {
        // Keep existing image if no new image uploaded
        finalNewsData.image_file_id = editingNews.image_file_id;
      }
      
      console.log('📰 Final updated news data:', finalNewsData);
      
      await axios.put(`${API}/news/${newsId}`, finalNewsData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم تحديث الخبر بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      // Refresh news list
      fetchAllData();
    } catch (error) {
      console.error('خطأ في تحديث الخبر:', error);
      alert(`فشل في تحديث الخبر: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Tournament Management Functions - Combined and Fixed
  const handleAddTournamentNew = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.post(`${API}/tournaments`, newTournament, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم إضافة البطولة بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      setShowAddTournamentModal(false);
      setNewTournament({
        name: '',
        tournament_type: 'league',
        category_id: '',
        start_date: '',
        end_date: '',
        location: '',
        description: '',
        status: 'upcoming'
      });
      
      // Refresh tournaments list
      fetchTournaments();
    } catch (error) {
      console.error('خطأ في إضافة البطولة:', error);
      alert(`فشل في إضافة البطولة: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEditTournament = (tournament) => {
    setEditingTournament({
      ...tournament
    });
    setShowEditTournamentModal(true);
  };

  const handleUpdateTournament = async (tournamentId, updatedTournament) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API}/tournaments/${tournamentId}`, updatedTournament, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم تحديث البطولة بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      // Refresh tournaments list
      fetchTournaments();
    } catch (error) {
      console.error('خطأ في تحديث البطولة:', error);
      alert(`فشل في تحديث البطولة: ${error.response?.data?.detail || error.message}`);
    }
  };

  const handleDeleteTournament = async (tournamentId, tournamentName) => {
    if (!confirm(`هل أنت متأكد من حذف البطولة "${tournamentName}"؟`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/tournaments/${tournamentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Show success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حذف البطولة بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 3000);

      // Refresh tournaments list
      fetchTournaments();
    } catch (error) {
      console.error('خطأ في حذف البطولة:', error);
      alert(`فشل في حذف البطولة: ${error.response?.data?.detail || error.message}`);
    }
  };

  // News Feeds management functions
  const handleAddFeed = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare feed data based on type
      const feedData = { ...newFeed };
      
      if (newFeed.type === 'x_account') {
        // Convert X username to RSS feed URL using nitter or similar service
        feedData.url = `https://nitter.net/${newFeed.x_username}/rss`;
        feedData.x_username = newFeed.x_username;
      }
      
      await axios.post(`${API}/news-feeds`, feedData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = newFeed.type === 'x_account' 
        ? `تم إضافة حساب X @${newFeed.x_username} بنجاح!`
        : 'تم إضافة مصدر الأخبار بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);

      setShowAddFeedModal(false);
      setNewFeed({
        name: '',
        url: '',
        type: 'rss',
        x_username: ''
      });

      // Refresh feeds list
      fetchAllData();
    } catch (error) {
      console.error('خطأ في إضافة مصدر الأخبار:', error);
      alert(`فشل في إضافة مصدر الأخبار: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFeed = async (feedId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا المصدر؟')) return;
    
    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API}/news-feeds/${feedId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Success notification
      const notification = document.createElement('div');
      notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
      notification.textContent = 'تم حذف مصدر الأخبار بنجاح!';
      document.body.appendChild(notification);
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 3000);

      // Refresh feeds list
      fetchAllData();
    } catch (error) {
      console.error('خطأ في حذف مصدر الأخبار:', error);
      alert(`فشل في حذف مصدر الأخبار: ${error.response?.data?.detail || error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-800">⚙️ إعدادات النظام</h1>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar Navigation */}
        <div className="md:w-64 space-y-2">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <h3 className="font-semibold text-gray-800 mb-3">أقسام الإعدادات</h3>
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-right px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeSection === section.id
                    ? 'bg-orange-100 text-orange-700 border border-orange-200'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <span className="mr-2">{section.icon}</span>
                {section.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            
            {/* General Settings */}
            {activeSection === 'general' && (
              <div className="space-y-8">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">🏢 المعلومات العامة للنادي</h3>
                  <p className="text-gray-600">إعدادات أساسية للنادي وتحسينات SEO للموقع</p>
                </div>
                
                {/* Basic Club Information */}
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="text-orange-600 mr-2">ℹ️</span>
                    المعلومات الأساسية
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        اسم النادي الكامل <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={clubSettings.club_name || ''}
                        onChange={(e) => setClubSettings({...clubSettings, club_name: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="مثال: نادي الأمجاد والرياضي"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        اسم التطبيق/الموقع <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={clubSettings.app_name || ''}
                        onChange={(e) => setClubSettings({...clubSettings, app_name: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="مثال: منصة نادي الأمجاد الرسمية"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        العنوان الفرعي للموقع <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={clubSettings.website_subtitle || ''}
                        onChange={(e) => setClubSettings({...clubSettings, website_subtitle: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="مثال: الموقع الرسمي، النادي الرياضي، المنصة الرقمية"
                      />
                      <div className="mt-2 text-sm text-gray-500 flex items-center">
                        <span className="mr-2">💡</span>
                        يظهر تحت اسم النادي في رأس الصفحة العامة
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        سنة التأسيس <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={clubSettings.established_year || ''}
                        onChange={(e) => setClubSettings({...clubSettings, established_year: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="مثال: 1995"
                        min="1900"
                        max={new Date().getFullYear()}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        المدينة/المنطقة <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={clubSettings.location || ''}
                        onChange={(e) => setClubSettings({...clubSettings, location: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                        placeholder="مثال: الرياض، المملكة العربية السعودية"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      وصف النادي <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={clubSettings.description || ''}
                      onChange={(e) => setClubSettings({...clubSettings, description: e.target.value})}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                      placeholder="وصف شامل عن النادي وأنشطته ورؤيته... (يُستخدم في محركات البحث)"
                    />
                    <div className="mt-2 text-sm text-gray-500">
                      الطول المُوصى به: 120-160 حرف لمحركات البحث
                    </div>
                  </div>
                </div>

                {/* Browser Tab & Icon Settings */}
                <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="text-indigo-600 mr-2">🖥️</span>
                    إعدادات علامة التبويب والأيقونة
                  </h4>
                  
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        اسم علامة التبويب (Tab Title) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={clubSettings.browser_tab_title || ''}
                        onChange={(e) => setClubSettings({...clubSettings, browser_tab_title: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="مثال: نادي الأمجاد - الموقع الرسمي"
                      />
                      <div className="mt-2 text-sm text-gray-500 flex items-center">
                        <span className="mr-2">💡</span>
                        هذا النص يظهر في علامة تبويب المتصفح (أعلى الصفحة)
                      </div>
                      {clubSettings.browser_tab_title && (
                        <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                          <div className="text-sm text-indigo-700 mb-2">معاينة علامة التبويب:</div>
                          <div className="bg-white px-3 py-2 rounded border text-sm text-gray-700 flex items-center">
                            <span className="mr-2">🏆</span>
                            {clubSettings.browser_tab_title}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        أيقونة التطبيق/علامة التبويب (Favicon)
                      </label>
                      <div className="space-y-3">
                        <div className="flex items-center space-x-4 space-x-reverse">
                          <input
                            type="file"
                            accept="image/*,.ico,.svg"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              if (file) {
                                setClubSettings({...clubSettings, favicon_file: file});
                                // Create preview
                                const reader = new FileReader();
                                reader.onload = (e) => {
                                  setClubSettings(prev => ({...prev, favicon_preview: e.target.result}));
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                          />
                        </div>
                        
                        {(clubSettings.favicon_preview || clubSettings.favicon_current) && (
                          <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                            <div className="text-sm text-indigo-700 mb-2">معاينة الأيقونة:</div>
                            <div className="flex items-center space-x-4 space-x-reverse">
                              <img 
                                src={clubSettings.favicon_preview || clubSettings.favicon_current} 
                                alt="Favicon preview" 
                                className="w-8 h-8 rounded border"
                              />
                              <div className="text-sm text-gray-600">
                                <div>الحجم المُوصى به: 32x32 بكسل</div>
                                <div>الصيغ المدعومة: ICO, PNG, SVG</div>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="text-sm text-gray-500">
                          💡 الأيقونة تظهر في علامة تبويب المتصفح وعند حفظ الموقع كإشارة مرجعية
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        وصف قصير للتطبيق (Short Description)
                      </label>
                      <input
                        type="text"
                        value={clubSettings.app_short_description || ''}
                        onChange={(e) => setClubSettings({...clubSettings, app_short_description: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                        placeholder="مثال: الموقع الرسمي لنادي الأمجاد"
                        maxLength="50"
                      />
                      <div className="mt-2 text-sm text-gray-500">
                        وصف مختصر يظهر عند تثبيت التطبيق على الهاتف (50 حرف كحد أقصى)
                      </div>
                    </div>
                  </div>
                  
                  {/* Live Preview Section */}
                  <div className="mt-6 pt-6 border-t border-indigo-200">
                    <h5 className="text-sm font-semibold text-gray-700 mb-3">معاينة مباشرة:</h5>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center space-x-3 space-x-reverse">
                        <div className="w-6 h-6 bg-gradient-to-br from-orange-500 to-orange-600 rounded flex items-center justify-center text-white text-xs font-bold">
                          {clubSettings.favicon_preview ? (
                            <img src={clubSettings.favicon_preview} alt="Preview" className="w-full h-full rounded" />
                          ) : 'أ'}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {clubSettings.browser_tab_title || 'نادي الأمجاد - الموقع الرسمي'}
                          </div>
                          <div className="text-xs text-gray-500">
                            alamjadclub.sa
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* SEO Settings */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="text-blue-600 mr-2">🔍</span>
                    تحسين محركات البحث (SEO)
                  </h4>
                  
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        عنوان الصفحة (Title)
                      </label>
                      <input
                        type="text"
                        value={clubSettings.page_title || ''}
                        onChange={(e) => setClubSettings({...clubSettings, page_title: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="مثال: نادي الأمجاد - الموقع الرسمي | رياضة وتقنية"
                      />
                      <div className="mt-2 text-sm text-gray-500">
                        يظهر في عنوان علامة التبويب ونتائج البحث (50-60 حرف مُوصى به)
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        وصف الصفحة (Meta Description)
                      </label>
                      <textarea
                        value={clubSettings.meta_description || ''}
                        onChange={(e) => setClubSettings({...clubSettings, meta_description: e.target.value})}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="وصف جذاب يظهر في نتائج محركات البحث..."
                      />
                      <div className="mt-2 text-sm text-gray-500">
                        يظهر تحت الرابط في نتائج البحث (150-160 حرف مُوصى به)
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        الكلمات المفتاحية (Keywords)
                      </label>
                      <input
                        type="text"
                        value={clubSettings.keywords || ''}
                        onChange={(e) => setClubSettings({...clubSettings, keywords: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        placeholder="نادي رياضي، كرة قدم، الرياض، تدريب، بطولات"
                      />
                      <div className="mt-2 text-sm text-gray-500">
                        فصل الكلمات بفاصلة (،) - استخدم 5-10 كلمات مفتاحية
                      </div>
                    </div>
                  </div>
                </div>

                {/* Enhanced Social Media Settings */}
                <EnhancedSocialMediaManager 
                  clubSettings={clubSettings}
                  setClubSettings={setClubSettings}
                />
                  
                {/* Contact Information */}
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="text-purple-600 mr-2">📞</span>
                    معلومات التواصل
                  </h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        رقم الهاتف
                      </label>
                      <input
                        type="tel"
                        value={clubSettings.phone || ''}
                        onChange={(e) => setClubSettings({...clubSettings, phone: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="+966 11 123 4567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        البريد الإلكتروني
                      </label>
                      <input
                        type="email"
                        value={clubSettings.email || ''}
                        onChange={(e) => setClubSettings({...clubSettings, email: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="info@alamjadclub.sa"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        العنوان الكامل
                      </label>
                      <textarea
                        value={clubSettings.address || ''}
                        onChange={(e) => setClubSettings({...clubSettings, address: e.target.value})}
                        rows={2}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        placeholder="الرياض، حي النهضة، شارع الملك فهد..."
                      />
                    </div>
                  </div>
                </div>

                {/* System Preferences */}
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="text-gray-600 mr-2">⚙️</span>
                    تفضيلات النظام
                  </h4>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          سجل النشاط التلقائي
                        </label>
                        <p className="text-xs text-gray-500">
                          تفعيل التحديث التلقائي لسجل النشاط في لوحة التحكم كل 30 ثانية
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={clubSettings.auto_refresh_enabled !== false}
                          onChange={(e) => setClubSettings({...clubSettings, auto_refresh_enabled: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          إشعارات النشاط
                        </label>
                        <p className="text-xs text-gray-500">
                          إظهار إشعارات عند إضافة أنشطة جديدة أو تحديث البيانات
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={clubSettings.activity_notifications !== false}
                          onChange={(e) => setClubSettings({...clubSettings, activity_notifications: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </div>

                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-start">
                        <span className="text-orange-600 mr-2 mt-0.5">💡</span>
                        <div>
                          <h5 className="text-sm font-medium text-orange-800">ملاحظة</h5>
                          <p className="text-xs text-orange-700 mt-1">
                            سجل النشاط يعرض آخر 3 أنشطة في لوحة التحكم الرئيسية ويتم تحديثه تلقائياً كل 30 ثانية عند التفعيل.
                            يمكنك عرض سجل النشاط الكامل من خلال الإعدادات المتقدمة.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 space-x-reverse">
                  <button
                    onClick={() => setClubSettings({})}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    إعادة تعيين
                  </button>
                  <button
                    onClick={handleSaveSettings}
                    disabled={saving}
                    className="px-8 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 disabled:opacity-50 transition-all duration-300 font-medium shadow-lg"
                  >
                    {saving ? 'جاري الحفظ...' : 'حفظ التغييرات'}
                  </button>
                </div>
              </div>
            )}

            {/* Advanced Advertisement Banner Management System */}
            {activeSection === 'public-page' && (
              <div className="space-y-8">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
                    <span className="text-orange-600 mr-3">🚀</span>
                    إدارة الشريط الإعلاني المتطور
                  </h3>
                  <p className="text-gray-600 text-lg">تحكم كامل واحترافي في الشريط الإعلاني مع تأثيرات متقدمة وإدارة الإعلانات المباشرة</p>
                </div>

                <AdvancedAdvertisementManager />

                {/* Sponsors Management Section */}
                <div className="border-t border-gray-200 pt-8 mt-8">
                  <div className="flex justify-between items-center mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-800">🤝 إدارة الرعاة</h3>
                      <p className="text-gray-600 text-sm mt-1">إضافة وتعديل رعاة النادي الذين سيظهرون في الشريط المتحرك</p>
                    </div>
                    <button
                      onClick={async () => {
                        setEditingSponsor(null);
                        // Get current sponsors count for order
                        try {
                          const token = localStorage.getItem('token');
                          const response = await axios.get(`${API}/sponsors`, {
                            headers: { 'Authorization': `Bearer ${token}` }
                          });
                          const currentSponsors = response.data || [];
                          setNewSponsor({
                            name: '',
                            logo_file_id: null,
                            logo_url: '',
                            website_url: '',
                            description: '',
                            is_active: true,
                            order: currentSponsors.length
                          });
                        } catch (error) {
                          setNewSponsor({
                            name: '',
                            logo_file_id: null,
                            logo_url: '',
                            website_url: '',
                            description: '',
                            is_active: true,
                            order: 0
                          });
                        }
                        setShowSponsorModal(true);
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                    >
                      <span className="mr-2">+</span>
                      إضافة راعي جديد
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sponsors.map((sponsor) => (
                      <div key={sponsor.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow">
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 flex items-center justify-center h-40">
                          {sponsor.logo_file_id ? (
                            <img
                              src={`${API}/files/${sponsor.logo_file_id}`}
                              alt={sponsor.name}
                              className="max-h-32 max-w-full object-contain"
                            />
                          ) : (
                            <div className="text-4xl">🤝</div>
                          )}
                        </div>
                        <div className="p-4">
                          <h4 className="font-bold text-gray-900 text-lg mb-1">{sponsor.name}</h4>
                          {sponsor.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{sponsor.description}</p>
                          )}
                          {sponsor.website_url && (
                            <a
                              href={sponsor.website_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 text-xs hover:underline mb-3 block"
                            >
                              🌐 {sponsor.website_url}
                            </a>
                          )}
                          <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                            <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                              sponsor.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {sponsor.is_active ? '✓ نشط' : '○ غير نشط'}
                            </span>
                            <div className="flex space-x-2 space-x-reverse">
                              <button
                                onClick={() => {
                                  setEditingSponsor(sponsor);
                                  setNewSponsor({
                                    name: sponsor.name,
                                    logo_file_id: sponsor.logo_file_id,
                                    logo_url: sponsor.logo_url || '',
                                    website_url: sponsor.website_url || '',
                                    description: sponsor.description || '',
                                    is_active: sponsor.is_active,
                                    order: sponsor.order
                                  });
                                  setShowSponsorModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                ✏️ تعديل
                              </button>
                              <button
                                onClick={async () => {
                                  if (!window.confirm('هل أنت متأكد من حذف هذا الراعي؟')) return;
                                  
                                  try {
                                    const token = localStorage.getItem('token');
                                    await axios.delete(`${API}/sponsors/${sponsor.id}`, {
                                      headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    showNotification('تم حذف الراعي بنجاح', 'success');
                                    // Refresh sponsors
                                    const response = await axios.get(`${API}/sponsors`, {
                                      headers: { 'Authorization': `Bearer ${token}` }
                                    });
                                    setSponsors(response.data || []);
                                  } catch (error) {
                                    console.error('خطأ في حذف الراعي:', error);
                                    showNotification('فشل حذف الراعي', 'error');
                                  }
                                }}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                🗑️ حذف
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {sponsors.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                      <div className="text-5xl mb-4">🤝</div>
                      <p className="text-gray-600 font-medium">لا توجد رعاة مضافين</p>
                      <p className="text-gray-500 text-sm mt-2">انقر على "إضافة راعي جديد" لإضافة راعي</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Professional Users Management with Advanced Permissions */}

            {activeSection === 'employees' && (
              <EmployeeManager clubSettings={clubSettings} />
            )}

            {activeSection === 'seasons' && (
              <SeasonManager />
            )}

            {activeSection === 'users' && (
              <div className="space-y-6" style={{fontFamily: 'Cairo, Tajawal, sans-serif'}}>
                {/* Clean Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h2 data-testid="users-page-title" className="text-2xl font-bold tracking-tight text-slate-900">إدارة المستخدمين</h2>
                    <p className="text-sm text-slate-500 mt-1">إدارة الحسابات والأدوار والصلاحيات</p>
                  </div>
                  <button
                    data-testid="add-user-button"
                    onClick={() => setShowAddUserModal(true)}
                    className="inline-flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg px-5 py-2.5 font-medium shadow-sm transition-colors text-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
                    إضافة مستخدم
                  </button>
                </div>

                {/* Stats Cards - Bento Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div data-testid="stat-total-users" className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      </div>
                    </div>
                    <p className="text-4xl font-black tracking-tighter text-slate-900">{users.length}</p>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">إجمالي المستخدمين</p>
                  </div>

                  <div data-testid="stat-active-users" className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><polyline points="16 11 18 13 22 9"/></svg>
                      </div>
                    </div>
                    <p className="text-4xl font-black tracking-tighter text-slate-900">{users.filter(u => u.is_active).length}</p>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">مستخدم نشط</p>
                  </div>

                  <div data-testid="stat-admins" className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                      </div>
                    </div>
                    <p className="text-4xl font-black tracking-tighter text-slate-900">{users.filter(u => ['admin', 'super_admin', 'manager'].includes(u.role)).length}</p>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">أدوار إدارية</p>
                  </div>

                  <div data-testid="stat-recent-activity" className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                      </div>
                    </div>
                    <p className="text-4xl font-black tracking-tighter text-slate-900">{users.filter(u => { if (!u.last_login) return false; return (new Date() - new Date(u.last_login)) < 86400000; }).length}</p>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-1">نشاط آخر 24 ساعة</p>
                  </div>
                </div>

                {/* Search & Filter Bar */}
                <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                      <input
                        data-testid="user-search-input"
                        type="text"
                        placeholder="بحث بالاسم أو البريد..."
                        value={userSearchTerm}
                        onChange={(e) => setUserSearchTerm(e.target.value)}
                        className="w-full pr-10 pl-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                      />
                    </div>
                    <select
                      data-testid="user-role-filter"
                      value={selectedRoleFilter}
                      onChange={(e) => setSelectedRoleFilter(e.target.value)}
                      className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white"
                    >
                      <option value="">جميع الأدوار</option>
                      <option value="admin">مدير عام</option>
                      <option value="manager">مدير</option>
                      <option value="coach">جهاز فني</option>
                      <option value="medical">جهاز طبي</option>
                      <option value="analyst">محلل</option>
                      <option value="editor">محرر</option>
                    </select>
                    <select
                      data-testid="user-status-filter"
                      value={selectedStatusFilter}
                      onChange={(e) => setSelectedStatusFilter(e.target.value)}
                      className="px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white"
                    >
                      <option value="">جميع الحالات</option>
                      <option value="active">نشط</option>
                      <option value="inactive">غير نشط</option>
                    </select>
                  </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">المستخدم</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">الدور</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">الحالة</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">آخر نشاط</th>
                          <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">الإجراءات</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {filteredUsers.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-12 text-center text-slate-400 text-sm">لا يوجد مستخدمين مطابقين للبحث</td>
                          </tr>
                        ) : (
                          filteredUsers.map((user, index) => (
                            <tr key={user.id || index} className="hover:bg-slate-50/50 transition-colors" data-testid={`user-row-${user.id}`}>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                    {user.username?.charAt(0).toUpperCase() || 'U'}
                                  </div>
                                  <div className="min-w-0">
                                    <div className="text-sm font-medium text-slate-900 truncate">{user.full_name || user.username}</div>
                                    <div className="text-xs text-slate-400 truncate">{user.email || user.username}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${getRoleColor(user.role)}`}>
                                  {getRoleName(user.role)}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${user.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                                  {user.is_active ? 'نشط' : 'غير نشط'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-500">
                                {user.last_login ? new Date(user.last_login).toLocaleDateString('ar-SA') : <span className="text-slate-300">-</span>}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-1">
                                  <button
                                    data-testid={`edit-user-btn-${user.id}`}
                                    onClick={() => handleEditUser(user)}
                                    className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                    title="تعديل"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
                                  </button>
                                  <button
                                    data-testid={`toggle-user-btn-${user.id}`}
                                    onClick={() => toggleUserStatus(user.id, user.is_active)}
                                    className={`p-2 rounded-lg transition-colors ${user.is_active ? 'text-slate-400 hover:text-red-600 hover:bg-red-50' : 'text-slate-400 hover:text-green-600 hover:bg-green-50'}`}
                                    title={user.is_active ? 'تعطيل' : 'تفعيل'}
                                  >
                                    {user.is_active ? (
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m4.9 4.9 14.2 14.2"/></svg>
                                    ) : (
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                                    )}
                                  </button>
                                  <button
                                    data-testid={`delete-user-btn-${user.id}`}
                                    onClick={() => handleDeleteUser(user.id, user.username)}
                                    className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                    title="حذف"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  {filteredUsers.length > 0 && (
                    <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/50 text-xs text-slate-400">
                      عرض {filteredUsers.length} من {users.length} مستخدم
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Contact Management Section */}



            {/* Logo and Colors - Enhanced Professional Version */}
            {activeSection === 'appearance' && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6 mb-8">
                  <div className="flex items-center mb-4">
                    <span className="text-4xl mr-4">🎨</span>
                    <div>
                      <h3 className="text-2xl font-bold text-orange-900">الهوية البصرية للنادي</h3>
                      <p className="text-orange-700">إدارة الشعار والألوان الرسمية للنادي</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Logo Management Section */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4">
                      <h4 className="text-xl font-bold text-white flex items-center">
                        📷 إدارة شعار النادي
                      </h4>
                    </div>
                    
                    <div className="p-6 space-y-6">
                      {/* Current Logo Display */}
                      <div className="text-center">
                        <div className="bg-gray-50 rounded-xl p-8 border-2 border-dashed border-gray-200">
                          {logoPreview || clubSettings?.logo_file_id ? (
                            <div className="space-y-4">
                              <p className="text-sm font-medium text-gray-700">الشعار الحالي:</p>
                              <div className="flex justify-center">
                                <img 
                                  src={logoPreview || `${API}/files/${clubSettings.logo_file_id}`} 
                                  alt="شعار النادي" 
                                  className="w-32 h-32 object-cover rounded-full border-4 border-white shadow-lg"
                                />
                              </div>
                              <div className="bg-blue-50 rounded-lg p-3">
                                <p className="text-xs text-blue-600">✅ الشعار مرفوع ومربوط بالنظام</p>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              <div className="w-32 h-32 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto shadow-lg">
                                <span className="text-white font-bold text-4xl">🏆</span>
                              </div>
                              <p className="text-gray-500 text-sm">لم يتم رفع شعار بعد</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Logo Upload Section */}
                      <div className="space-y-4">
                        <div className="border border-gray-200 rounded-lg p-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            اختر ملف الشعار الجديد
                          </label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files[0];
                              setLogoFile(file);
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (e) => setLogoPreview(e.target.result);
                                reader.readAsDataURL(file);
                              }
                            }}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100 file:transition-colors"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            يُفضل الصور المربعة بجودة عالية (PNG, JPG) - الحد الأقصى 5 ميجابايت
                          </p>
                        </div>
                        
                        <button
                          onClick={handleLogoUpload}
                          disabled={!logoFile || saving}
                          className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 shadow-lg"
                        >
                          {saving ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              جاري رفع الشعار...
                            </>
                          ) : (
                            <>
                              🚀 رفع وتطبيق الشعار
                            </>
                          )}
                        </button>
                      </div>

                      {/* Logo Guidelines */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <h5 className="font-medium text-blue-900 mb-2">💡 نصائح للشعار المثالي:</h5>
                        <ul className="text-sm text-blue-700 space-y-1">
                          <li>• استخدم صورة مربعة أو دائرية</li>
                          <li>• دقة عالية (على الأقل 512x512 بكسل)</li>
                          <li>• خلفية شفافة (PNG) للأفضل</li>
                          <li>• يظهر في جميع أنحاء النظام</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Colors Management Section */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                      <h4 className="text-xl font-bold text-white flex items-center">
                        🎨 ألوان الهوية البصرية
                      </h4>
                    </div>
                    
                    <div className="p-6 space-y-6">
                      {/* Color Preview */}
                      <div className="bg-gray-50 rounded-xl p-6">
                        <h5 className="font-medium text-gray-800 mb-4">معاينة الألوان الحالية:</h5>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="text-center">
                            <div 
                              className="w-full h-20 rounded-lg shadow-md border-2 border-white"
                              style={{ backgroundColor: clubSettings.primary_color }}
                            ></div>
                            <p className="text-sm font-medium text-gray-700 mt-2">اللون الأساسي</p>
                            <p className="text-xs text-gray-500">{clubSettings.primary_color}</p>
                          </div>
                          <div className="text-center">
                            <div 
                              className="w-full h-20 rounded-lg shadow-md border-2 border-white"
                              style={{ backgroundColor: clubSettings.secondary_color }}
                            ></div>
                            <p className="text-sm font-medium text-gray-700 mt-2">اللون الثانوي</p>
                            <p className="text-xs text-gray-500">{clubSettings.secondary_color}</p>
                          </div>
                        </div>
                      </div>

                      {/* Color Inputs */}
                      <div className="space-y-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            🔵 اللون الأساسي (المستخدم في الأزرار والعناوين)
                          </label>
                          <div className="flex items-center space-x-4 space-x-reverse bg-gray-50 rounded-lg p-4">
                            <div className="relative">
                              <input
                                type="color"
                                value={clubSettings.primary_color}
                                onChange={(e) => setClubSettings({...clubSettings, primary_color: e.target.value})}
                                className="w-12 h-12 border-2 border-white rounded-lg cursor-pointer shadow-md"
                              />
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={clubSettings.primary_color}
                                onChange={(e) => setClubSettings({...clubSettings, primary_color: e.target.value})}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                                placeholder="#ea580c"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            ⚪ اللون الثانوي (المستخدم في الخلفيات والحدود)
                          </label>
                          <div className="flex items-center space-x-4 space-x-reverse bg-gray-50 rounded-lg p-4">
                            <div className="relative">
                              <input
                                type="color"
                                value={clubSettings.secondary_color}
                                onChange={(e) => setClubSettings({...clubSettings, secondary_color: e.target.value})}
                                className="w-12 h-12 border-2 border-white rounded-lg cursor-pointer shadow-md"
                              />
                            </div>
                            <div className="flex-1">
                              <input
                                type="text"
                                value={clubSettings.secondary_color}
                                onChange={(e) => setClubSettings({...clubSettings, secondary_color: e.target.value})}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm"
                                placeholder="#ffffff"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Color Presets */}
                      <div>
                        <h5 className="font-medium text-gray-700 mb-3">🎭 ألوان مقترحة:</h5>
                        <div className="grid grid-cols-4 gap-2">
                          {[
                            { primary: '#ea580c', secondary: '#ffffff', name: 'البرتقالي' },
                            { primary: '#2563eb', secondary: '#ffffff', name: 'الأزرق' },
                            { primary: '#dc2626', secondary: '#ffffff', name: 'الأحمر' },
                            { primary: '#16a34a', secondary: '#ffffff', name: 'الأخضر' },
                            { primary: '#7c3aed', secondary: '#ffffff', name: 'البنفسجي' },
                            { primary: '#0891b2', secondary: '#ffffff', name: 'السماوي' },
                            { primary: '#ca8a04', secondary: '#ffffff', name: 'الذهبي' },
                            { primary: '#1f2937', secondary: '#ffffff', name: 'الرمادي' }
                          ].map((preset, index) => (
                            <button
                              key={index}
                              onClick={() => setClubSettings({
                                ...clubSettings, 
                                primary_color: preset.primary,
                                secondary_color: preset.secondary
                              })}
                              className="group relative w-full aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-gray-400 transition-all duration-200 hover:scale-105"
                              title={preset.name}
                            >
                              <div 
                                className="w-full h-2/3"
                                style={{ backgroundColor: preset.primary }}
                              ></div>
                              <div 
                                className="w-full h-1/3"
                                style={{ backgroundColor: preset.secondary }}
                              ></div>
                              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200"></div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Save Button */}
                      <button
                        onClick={handleSaveClubSettings}
                        disabled={saving}
                        className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 disabled:scale-100 shadow-lg"
                      >
                        {saving ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            جاري تطبيق الألوان...
                          </>
                        ) : (
                          <>
                            💾 حفظ وتطبيق الألوان
                          </>
                        )}
                      </button>

                      {/* Color Guidelines */}
                      <div className="bg-yellow-50 rounded-lg p-4">
                        <h5 className="font-medium text-yellow-800 mb-2">⚠️ ملاحظات مهمة:</h5>
                        <ul className="text-sm text-yellow-700 space-y-1">
                          <li>• الألوان تؤثر على جميع أجزاء النظام</li>
                          <li>• تأكد من التباين الجيد للقراءة</li>
                          <li>• يُحفظ التغيير فوراً في النظام</li>
                          <li>• يمكن التراجع في أي وقت</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dashboard Background Section */}
            {activeSection === 'dashboard-bg' && (
              <div className="space-y-6">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">🖼️ إعدادات خلفية لوحة التحكم</h3>
                  <p className="text-gray-600">قم بتخصيص خلفية لوحة التحكم بصورة احترافية تعكس هوية النادي</p>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="text-purple-600 mr-2">🎨</span>
                    إدارة خلفية لوحة التحكم
                  </h4>
                  
                  {/* Current Background Display */}
                  {dashboardBgPreview && (
                    <div className="mb-6">
                      <h5 className="font-medium text-gray-800 mb-3 flex items-center">
                        <span className="text-green-600 mr-2">✅</span>
                        الصورة الحالية
                      </h5>
                      <div className="relative group">
                        <img 
                          src={dashboardBgPreview} 
                          alt="خلفية لوحة التحكم الحالية" 
                          className="w-full h-48 object-cover rounded-xl border-3 border-purple-200 shadow-lg transition-transform group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute bottom-4 left-4 text-white">
                            <p className="text-sm font-medium">انقر لمعاينة الحجم الكامل</p>
                          </div>
                        </div>
                        <div className="absolute top-4 right-4">
                          <div className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-gray-800">
                            الخلفية النشطة
                          </div>
                        </div>
                      </div>
                      
                      {/* Current Background Actions */}
                      <div className="mt-4 flex space-x-3 space-x-reverse">
                        <button
                          onClick={() => window.open(dashboardBgPreview, '_blank')}
                          className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                        >
                          <span className="mr-2">🔍</span>
                          عرض بالحجم الكامل
                        </button>
                        <button
                          onClick={handleDashboardBgDelete}
                          disabled={saving}
                          className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          <span className="mr-2">🗑️</span>
                          {saving ? 'جاري الحذف...' : 'حذف الخلفية'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Upload New Background */}
                  <div className="border-2 border-dashed border-purple-300 rounded-xl p-8 text-center transition-colors hover:border-purple-400 hover:bg-purple-25">
                    <div className="space-y-4">
                      <div className="text-5xl text-purple-400">
                        {dashboardBgFile ? '📁' : '🖼️'}
                      </div>
                      <div>
                        <h5 className="font-semibold text-gray-800 mb-2 text-lg">
                          {dashboardBgFile ? '✅ تم اختيار ملف جديد' : dashboardBgPreview ? 'استبدال الخلفية الحالية' : 'رفع خلفية جديدة'}
                        </h5>
                        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                          اختر صورة عالية الجودة بنسبة عرض إلى ارتفاع 16:9 للحصول على أفضل نتيجة<br/>
                          <span className="text-purple-600 font-medium">الحد الأقصى: 5 ميجابايت • الصيغ المدعومة: PNG, JPG, WEBP</span>
                        </p>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleDashboardBgFileSelect}
                          className="hidden"
                          id="dashboard-bg-upload"
                        />
                        <label
                          htmlFor="dashboard-bg-upload"
                          className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white rounded-xl cursor-pointer transition-all duration-300 transform hover:scale-105 shadow-lg font-medium"
                        >
                          <span className="mr-2">📂</span>
                          {dashboardBgFile ? 'اختيار ملف آخر' : 'اختيار صورة'}
                        </label>
                      </div>
                      
                      {dashboardBgFile && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center justify-center space-x-2 space-x-reverse text-green-700">
                            <span className="text-lg">✅</span>
                            <div className="text-sm">
                              <p className="font-medium">ملف محدد: {dashboardBgFile.name}</p>
                              <p className="text-xs text-green-600">
                                الحجم: {(dashboardBgFile.size / (1024 * 1024)).toFixed(2)} ميجابايت
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* New Background Preview */}
                  {dashboardBgFile && dashboardBgPreview && (
                    <div className="mt-6">
                      <h5 className="font-medium text-gray-800 mb-3 flex items-center">
                        <span className="text-blue-600 mr-2">👀</span>
                        معاينة الصورة الجديدة
                      </h5>
                      <div className="relative">
                        <img 
                          src={dashboardBgPreview} 
                          alt="معاينة خلفية لوحة التحكم الجديدة" 
                          className="w-full h-48 object-cover rounded-xl border-3 border-blue-200 shadow-lg"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-xl flex items-center justify-center">
                          <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                            <span className="text-gray-800 text-sm font-medium">معاينة مع الشفافية</span>
                          </div>
                        </div>
                        <div className="absolute top-4 right-4">
                          <div className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                            جديد
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="mt-6 flex space-x-4 space-x-reverse">
                    {dashboardBgFile && (
                      <button
                        onClick={handleDashboardBgUpload}
                        disabled={saving}
                        className="flex-1 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:transform-none"
                      >
                        <span className="flex items-center justify-center">
                          <span className="mr-2">{saving ? '⏳' : '💾'}</span>
                          {saving ? 'جاري الرفع والحفظ...' : 'حفظ الخلفية الجديدة'}
                        </span>
                      </button>
                    )}
                  </div>

                  {/* Tips and Guidelines */}
                  <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-4">
                      <h6 className="font-semibold text-amber-900 mb-3 flex items-center">
                        <span className="mr-2 text-lg">💡</span>
                        نصائح للحصول على أفضل نتيجة
                      </h6>
                      <ul className="text-sm text-amber-800 space-y-2">
                        <li className="flex items-start">
                          <span className="mr-2 mt-0.5">•</span>
                          استخدم صور بدقة عالية (1920×1080 أو أكبر)
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 mt-0.5">•</span>
                          اختر ألوان متوافقة مع هوية النادي البرتقالية
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 mt-0.5">•</span>
                          تجنب الصور المزدحمة للحفاظ على وضوح النص
                        </li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                      <h6 className="font-semibold text-blue-900 mb-3 flex items-center">
                        <span className="mr-2 text-lg">ℹ️</span>
                        معلومات تقنية
                      </h6>
                      <ul className="text-sm text-blue-800 space-y-2">
                        <li className="flex items-start">
                          <span className="mr-2 mt-0.5">•</span>
                          الصورة ستظهر بشفافية 40% خلف المحتوى
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 mt-0.5">•</span>
                          سيتم ضغط الصورة تلقائياً لتحسين الأداء
                        </li>
                        <li className="flex items-start">
                          <span className="mr-2 mt-0.5">•</span>
                          يمكن تغيير الخلفية في أي وقت
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Arabic Fonts */}
            {activeSection === 'fonts' && (
              <div className="space-y-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">🔤 إعدادات الخطوط العربية</h3>
                
                <div className="border border-gray-200 rounded-lg p-6">
                  <h4 className="font-semibold text-gray-800 mb-4">اختيار الخط العربي للتطبيق</h4>
                  <p className="text-sm text-gray-600 mb-6">
                    اختر الخط العربي المفضل لديك ليتم تطبيقه على جميع أجزاء التطبيق
                  </p>

                  <div className="space-y-4">
                    {arabicFonts.map((font) => (
                      <div 
                        key={font.name} 
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedFont === font.name 
                            ? 'border-orange-500 bg-orange-50' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleFontChange(font.name)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h5 className="font-medium text-gray-900" style={{ fontFamily: font.name }}>
                              {font.label}
                            </h5>
                            <p className="text-sm text-gray-600 mt-1" style={{ fontFamily: font.name }}>
                              نموذج للنص العربي - هذا هو شكل الخط المحدد
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 ${
                            selectedFont === font.name 
                              ? 'bg-orange-500 border-orange-500' 
                              : 'border-gray-300'
                          }`}>
                            {selectedFont === font.name && (
                              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                                <span className="text-orange-500 text-xs">✓</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                    <h6 className="font-medium text-blue-900 mb-2">معاينة الخط المحدد</h6>
                    <div style={{ fontFamily: selectedFont }}>
                      <p className="text-lg text-blue-800 mb-2">نادي الأمجاد</p>
                      <p className="text-sm text-blue-600">
                        نحن نسعى للتميز والإبداع في عالم كرة القدم، مع التزام راسخ بتطوير المواهب الشابة وتحقيق الإنجازات الرياضية على جميع المستويات.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Landing Page Management */}
            {activeSection === 'landing' && (
              <div className="space-y-6">
                <LandingPageManagement />
              </div>
            )}

            {/* News Management - Enhanced Functional Section */}
            {activeSection === 'news' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-bold text-gray-800">📰 إدارة الأخبار</h3>
                  <div className="flex gap-2">
                  <TweetImportButton onImported={() => { fetchNews(); fetchAllData(); }} />
                  <button
                    data-testid="add-news-btn"
                    onClick={() => setShowAddNewsModal(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    + إنشاء خبر جديد
                  </button>
                  </div>
                </div>

                {/* Statistics */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                  <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-blue-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">إجمالي الأخبار</h4>
                        <p className="text-2xl font-bold text-gray-900">{newsArticles.length}</p>
                      </div>
                      <div className="p-3 rounded-full bg-blue-50">
                        <span className="text-xl">📰</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-green-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">المنشورة</h4>
                        <p className="text-2xl font-bold text-gray-900">{newsArticles.filter(n => n.published).length}</p>
                      </div>
                      <div className="p-3 rounded-full bg-green-50">
                        <span className="text-xl">✅</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-yellow-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">المسودات</h4>
                        <p className="text-2xl font-bold text-gray-900">{newsArticles.filter(n => !n.published).length}</p>
                      </div>
                      <div className="p-3 rounded-full bg-yellow-50">
                        <span className="text-xl">📝</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-purple-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">مع صور</h4>
                        <p className="text-2xl font-bold text-gray-900">{newsArticles.filter(n => n.image_file_id).length}</p>
                      </div>
                      <div className="p-3 rounded-full bg-purple-50">
                        <span className="text-xl">📷</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg shadow-md p-4 border-r-4 border-indigo-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-500 mb-1">هذا الشهر</h4>
                        <p className="text-2xl font-bold text-gray-900">
                          {newsArticles.filter(n => {
                            const newsDate = new Date(n.created_at);
                            const now = new Date();
                            return newsDate.getMonth() === now.getMonth() && newsDate.getFullYear() === now.getFullYear();
                          }).length}
                        </p>
                      </div>
                      <div className="p-3 rounded-full bg-indigo-50">
                        <span className="text-xl">📅</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* News Articles List */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h4 className="text-xl font-bold text-gray-800">قائمة الأخبار</h4>
                    <button
                      onClick={() => setShowAddNewsModal(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
                    >
                      + إضافة خبر جديد
                    </button>
                  </div>

                  <div className="p-6">
                    {newsArticles.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-4">📰</div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">لا توجد أخبار</h3>
                        <p className="text-gray-500 mb-4">ابدأ بإنشاء خبر جديد</p>
                        <button
                          onClick={() => setShowAddNewsModal(true)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                          + إنشاء خبر جديد
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {newsArticles.map((article) => (
                          <div key={article.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                              {/* Article Image */}
                              <div className="flex-shrink-0 ml-4">
                                {article.image_file_id ? (
                                  <div className="relative group">
                                    <img
                                      src={`${API}/files/${article.image_file_id}`}
                                      alt={article.title}
                                      className="w-24 h-24 object-cover rounded-lg shadow-md"
                                      onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.nextSibling.style.display = 'flex';
                                      }}
                                    />
                                    <div className="hidden w-24 h-24 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg items-center justify-center">
                                      <span className="text-white text-2xl">📰</span>
                                    </div>
                                    {/* Image indicator */}
                                    <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                                      📷
                                    </div>
                                  </div>
                                ) : (
                                  <div className="w-24 h-24 bg-gradient-to-br from-gray-400 to-gray-500 rounded-lg flex items-center justify-center">
                                    <span className="text-white text-2xl">📰</span>
                                  </div>
                                )}
                              </div>

                              {/* Article Content */}
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 space-x-reverse mb-2 flex-wrap">
                                  <h5 className="text-lg font-semibold text-gray-900">{article.title}</h5>
                                  <div className="flex items-center space-x-2 space-x-reverse">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                      article.published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {article.published ? 'منشور' : 'مسودة'}
                                    </span>
                                    <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                      {article.category}
                                    </span>
                                    {article.image_file_id && (
                                      <span className="px-2 py-1 bg-blue-100 text-blue-600 rounded text-xs flex items-center">
                                        📷 مع صورة
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {article.summary && (
                                  <p className="text-gray-600 mb-2 line-clamp-2">{article.summary}</p>
                                )}
                                <div className="flex items-center text-sm text-gray-500 space-x-4 space-x-reverse flex-wrap">
                                  <span>بواسطة: {article.author_name}</span>
                                  <span>تاريخ الإنشاء: {new Date(article.created_at).toLocaleDateString('ar-SA')}</span>
                                  {article.published && article.published_at && (
                                    <span>تاريخ النشر: {new Date(article.published_at).toLocaleDateString('ar-SA')}</span>
                                  )}
                                </div>
                              </div>

                              {/* Action Buttons */}
                              <div className="flex flex-col space-y-2 ml-4 flex-shrink-0">
                                <button
                                  onClick={() => handleEditNews(article)}
                                  className="text-blue-600 hover:text-blue-700 text-sm font-medium px-3 py-1 rounded hover:bg-blue-50 transition-colors"
                                >
                                  ✏️ تعديل
                                </button>
                                <button
                                  onClick={() => handleDeleteNews(article.id, article.title)}
                                  className="text-red-600 hover:text-red-700 text-sm font-medium px-3 py-1 rounded hover:bg-red-50 transition-colors"
                                >
                                  🗑️ حذف
                                </button>
                                {article.published && (
                                  <a
                                    href={`#public-news-${article.id}`}
                                    className="text-green-600 hover:text-green-700 text-sm font-medium px-3 py-1 rounded hover:bg-green-50 transition-colors text-center"
                                    title="عرض في الصفحة العامة"
                                  >
                                    👁️ معاينة
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* News Feeds Management */}
                <div className="bg-white rounded-lg shadow-md border border-gray-200">
                  <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                    <h4 className="text-xl font-bold text-gray-800">🔗 مصادر الأخبار</h4>
                    <button
                      onClick={() => setShowAddFeedModal(true)}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
                    >
                      + إضافة مصدر RSS
                    </button>
                  </div>

                  <div className="p-6">
                    {newsFeeds.length === 0 ? (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-4">🔗</div>
                        <h3 className="text-lg font-semibold text-gray-700 mb-2">لا توجد مصادر أخبار</h3>
                        <p className="text-gray-500 mb-4">ابدأ بإضافة مصدر RSS جديد</p>
                        <button
                          onClick={() => setShowAddFeedModal(true)}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                          + إضافة مصدر RSS
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {newsFeeds.map((feed) => (
                          <div key={feed.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3 space-x-reverse">
                                  <span className="text-2xl">
                                    {feed.type === 'x_account' ? '𝕏' : '🔗'}
                                  </span>
                                  <div>
                                    <h5 className="font-semibold text-gray-900">{feed.name}</h5>
                                    {feed.type === 'x_account' ? (
                                      <div>
                                        <p className="text-sm text-gray-500 mt-1">@{feed.x_username}</p>
                                        <div className="mt-3 bg-gray-50 rounded-lg p-3">
                                          <h6 className="text-xs font-semibold text-gray-600 mb-2">آخر 5 تغريدات:</h6>
                                          <TwitterFeed username={feed.x_username} />
                                        </div>
                                      </div>
                                    ) : (
                                      <p className="text-sm text-gray-500 mt-1">{feed.url}</p>
                                    )}
                                    {feed.last_updated && (
                                      <p className="text-xs text-gray-400 mt-1">
                                        آخر تحديث: {new Date(feed.last_updated).toLocaleDateString('ar-SA')}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2 space-x-reverse">
                                <button
                                  onClick={() => handleDeleteFeed(feed.id)}
                                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                                >
                                  🗑️ حذف
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Media Management Section */}
            {activeSection === 'media' && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-purple-50 to-pink-100 rounded-2xl p-6 mb-8">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center mb-4">
                      <span className="text-4xl mr-4">📸</span>
                      <div>
                        <h3 className="text-2xl font-bold text-purple-900">إدارة الصور</h3>
                        <p className="text-purple-700">عرض وإدارة جميع الصور المرفوعة في التطبيق</p>
                      </div>
                    </div>
                    <button
                      onClick={fetchUploadedFiles}
                      className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center"
                    >
                      🔄 تحديث
                    </button>
                  </div>
                </div>

                {/* Storage Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-white rounded-xl shadow-md p-6 border-r-4 border-blue-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">إجمالي الملفات</p>
                        <p className="text-3xl font-bold text-blue-600">{storageStats.fileCount}</p>
                      </div>
                      <div className="p-3 rounded-full bg-blue-50">
                        <span className="text-2xl">📁</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6 border-r-4 border-purple-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">حجم التخزين</p>
                        <p className="text-3xl font-bold text-purple-600">{formatFileSize(storageStats.totalSize)}</p>
                      </div>
                      <div className="p-3 rounded-full bg-purple-50">
                        <span className="text-2xl">💾</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-md p-6 border-r-4 border-orange-500">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-1">ملفات محددة</p>
                        <p className="text-3xl font-bold text-orange-600">{selectedFiles.length}</p>
                      </div>
                      <div className="p-3 rounded-full bg-orange-50">
                        <span className="text-2xl">✅</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Search and Filter Section */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                    <div className="flex-1 max-w-lg">
                      <div className="relative">
                        <input
                          type="text"
                          placeholder="البحث في الملفات..."
                          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                          onChange={(e) => {
                            const query = e.target.value;
                            if (query.length > 2 || query.length === 0) {
                              searchFiles(query);
                            }
                          }}
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-400">🔍</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <select 
                        className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                        onChange={(e) => searchFiles('', e.target.value || null)}
                      >
                        <option value="">جميع الأنواع</option>
                        <option value="image">الصور</option>
                        <option value="document">المستندات</option>
                        <option value="file">ملفات أخرى</option>
                      </select>
                      
                      <button
                        onClick={fetchUploadedFiles}
                        className="px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center"
                      >
                        <span className="mr-2">🔄</span>
                        تحديث
                      </button>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                {selectedFiles.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center text-red-700">
                        <span className="text-xl mr-2">⚠️</span>
                        <span className="font-medium">تم تحديد {selectedFiles.length} ملف</span>
                      </div>
                      <div className="flex space-x-3 space-x-reverse">
                        <button
                          onClick={() => setSelectedFiles([])}
                          className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
                        >
                          إلغاء التحديد
                        </button>
                        <button
                          onClick={handleDeleteFiles}
                          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                        >
                          🗑️ حذف المحدد
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Files Grid */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-lg font-bold text-gray-800">جميع الملفات المرفوعة</h4>
                    <div className="flex items-center space-x-3 space-x-reverse">
                      <button
                        onClick={() => {
                          if (selectedFiles.length === uploadedFiles.length) {
                            setSelectedFiles([]);
                          } else {
                            setSelectedFiles(uploadedFiles.map(f => f.id));
                          }
                        }}
                        className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                      >
                        {selectedFiles.length === uploadedFiles.length ? 'إلغاء الكل' : 'تحديد الكل'}
                      </button>
                    </div>
                  </div>

                  {loadingFiles ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
                      <span className="mr-3 text-gray-600">جاري تحميل الملفات...</span>
                    </div>
                  ) : uploadedFiles.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📸</div>
                      <h3 className="text-xl font-semibold text-gray-700 mb-2">لا توجد صور مرفوعة</h3>
                      <p className="text-gray-500">لم يتم رفع أي صور في التطبيق حتى الآن</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                      {uploadedFiles.map((file) => (
                        <div
                          key={file.id}
                          className={`relative group border-2 rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-md ${
                            selectedFiles.includes(file.id) 
                              ? 'border-blue-500 bg-blue-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => {
                            if (selectedFiles.includes(file.id)) {
                              setSelectedFiles(selectedFiles.filter(id => id !== file.id));
                            } else {
                              setSelectedFiles([...selectedFiles, file.id]);
                            }
                          }}
                        >
                          {/* Selection Checkbox */}
                          <div className={`absolute top-2 right-2 z-10 w-5 h-5 rounded border-2 ${
                            selectedFiles.includes(file.id)
                              ? 'bg-blue-600 border-blue-600'
                              : 'bg-white border-gray-300 group-hover:border-blue-400'
                          } flex items-center justify-center`}>
                            {selectedFiles.includes(file.id) && (
                              <span className="text-white text-xs">✓</span>
                            )}
                          </div>

                          {/* File Preview */}
                          {file.type?.startsWith('image/') ? (
                            <img 
                              src={`${API}/files/${file.id}`}
                              alt={file.filename}
                              className="w-full h-24 object-cover"
                            />
                          ) : (
                            <div className="w-full h-24 bg-gray-100 flex items-center justify-center">
                              <span className="text-2xl">📄</span>
                            </div>
                          )}

                          {/* File Info */}
                          <div className="p-2 bg-white">
                            <p className="text-xs font-medium text-gray-800 truncate" title={file.filename}>
                              {file.filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size || 0)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Printing and Export - Complete Section */}
            {activeSection === 'printing' && (
              <div className="space-y-8">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-100 rounded-2xl p-6 mb-8">
                  <div className="flex items-center mb-4">
                    <span className="text-4xl mr-4">🖨️</span>
                    <div>
                      <h3 className="text-2xl font-bold text-blue-900">الطباعة والتصدير</h3>
                      <p className="text-blue-700">إدارة خيارات الطباعة وتصدير البيانات</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* PDF Export Section */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4">
                      <h4 className="text-xl font-bold text-white flex items-center">
                        📄 تصدير PDF
                      </h4>
                    </div>
                    
                    <div className="p-6 space-y-4">
                      <div className="space-y-3">
                        <button
                          onClick={() => printSection('players-section', 'قائمة_اللاعبين')}
                          className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 text-red-800 rounded-lg transition-all duration-200 group"
                        >
                          <span className="flex items-center">
                            <span className="mr-3">👥</span>
                            <span>قائمة اللاعبين</span>
                          </span>
                          <span className="opacity-60 group-hover:opacity-100">PDF</span>
                        </button>
                        
                        <button
                          onClick={() => printSection('matches-section', 'تقرير_المباريات')}
                          className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 text-red-800 rounded-lg transition-all duration-200 group"
                        >
                          <span className="flex items-center">
                            <span className="mr-3">⚽</span>
                            <span>تقرير المباريات</span>
                          </span>
                          <span className="opacity-60 group-hover:opacity-100">PDF</span>
                        </button>
                        
                        <button
                          onClick={() => printSection('scorers-section', 'إحصائيات_الهدافين')}
                          className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 text-red-800 rounded-lg transition-all duration-200 group"
                        >
                          <span className="flex items-center">
                            <span className="mr-3">🏆</span>
                            <span>إحصائيات الهدافين</span>
                          </span>
                          <span className="opacity-60 group-hover:opacity-100">PDF</span>
                        </button>
                        
                        <button
                          onClick={() => printSection('dashboard-overview', 'تقرير_شامل')}
                          className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 text-red-800 rounded-lg transition-all duration-200 group"
                        >
                          <span className="flex items-center">
                            <span className="mr-3">📊</span>
                            <span>تقرير شامل</span>
                          </span>
                          <span className="opacity-60 group-hover:opacity-100">PDF</span>
                        </button>
                      </div>
                      
                      <div className="bg-red-50 rounded-lg p-4 mt-6">
                        <h5 className="font-medium text-red-800 mb-2">📄 عن تصدير PDF:</h5>
                        <ul className="text-sm text-red-700 space-y-1">
                          <li>• جودة عالية للطباعة</li>
                          <li>• يحافظ على التنسيق</li>
                          <li>• مناسب للتقارير الرسمية</li>
                          <li>• يمكن فتحه على أي جهاز</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Excel Export Section */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4">
                      <h4 className="text-xl font-bold text-white flex items-center">
                        📊 تصدير Excel
                      </h4>
                    </div>
                    
                    <div className="p-6 space-y-4">
                      <div className="space-y-3">
                        <button
                          onClick={() => exportToExcel([{name: 'بيانات اللاعبين', count: 'سيتم التحميل...'}], 'قائمة_اللاعبين', 'اللاعبين')}
                          className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 text-green-800 rounded-lg transition-all duration-200 group"
                        >
                          <span className="flex items-center">
                            <span className="mr-3">👥</span>
                            <span>بيانات اللاعبين</span>
                          </span>
                          <span className="opacity-60 group-hover:opacity-100">XLSX</span>
                        </button>
                        
                        <button
                          onClick={() => exportToExcel([{name: 'بيانات المباريات', count: 'سيتم التحميل...'}], 'بيانات_المباريات', 'المباريات')}
                          className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 text-green-800 rounded-lg transition-all duration-200 group"
                        >
                          <span className="flex items-center">
                            <span className="mr-3">⚽</span>
                            <span>بيانات المباريات</span>
                          </span>
                          <span className="opacity-60 group-hover:opacity-100">XLSX</span>
                        </button>
                        
                        <button
                          onClick={() => exportToExcel([{name: 'إحصائيات الهدافين', count: 'سيتم التحميل...'}], 'إحصائيات_الهدافين', 'الهدافين')}
                          className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 text-green-800 rounded-lg transition-all duration-200 group"
                        >
                          <span className="flex items-center">
                            <span className="mr-3">🏆</span>
                            <span>جدول الهدافين</span>
                          </span>
                          <span className="opacity-60 group-hover:opacity-100">XLSX</span>
                        </button>
                        
                        <button
                          onClick={() => exportToExcel([{name: 'بيانات الفرق', count: 'سيتم التحميل...'}], 'بيانات_الفرق', 'الفرق')}
                          className="w-full flex items-center justify-between px-4 py-3 bg-green-50 hover:bg-green-100 text-green-800 rounded-lg transition-all duration-200 group"
                        >
                          <span className="flex items-center">
                            <span className="mr-3">🏟️</span>
                            <span>بيانات الفرق</span>
                          </span>
                          <span className="opacity-60 group-hover:opacity-100">XLSX</span>
                        </button>
                      </div>
                      
                      <div className="bg-green-50 rounded-lg p-4 mt-6">
                        <h5 className="font-medium text-green-800 mb-2">📊 عن تصدير Excel:</h5>
                        <ul className="text-sm text-green-700 space-y-1">
                          <li>• يمكن التعديل والتحليل</li>
                          <li>• مناسب للإحصائيات</li>
                          <li>• سهل المشاركة والأرشفة</li>
                          <li>• يدعم الجداول المعقدة</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Direct Print Section */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4">
                    <h4 className="text-xl font-bold text-white flex items-center">
                      🖨️ الطباعة المباشرة
                    </h4>
                  </div>
                  
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <button
                        onClick={printPage}
                        className="flex flex-col items-center justify-center p-6 bg-purple-50 hover:bg-purple-100 text-purple-800 rounded-xl transition-all duration-200 group"
                      >
                        <span className="text-3xl mb-3 group-hover:scale-110 transition-transform">🖨️</span>
                        <span className="font-semibold text-lg">طباعة الصفحة</span>
                        <span className="text-sm opacity-75 mt-1">الصفحة الحالية</span>
                      </button>
                      
                      <button
                        onClick={() => window.open('/dashboard', '_blank')}
                        className="flex flex-col items-center justify-center p-6 bg-purple-50 hover:bg-purple-100 text-purple-800 rounded-xl transition-all duration-200 group"
                      >
                        <span className="text-3xl mb-3 group-hover:scale-110 transition-transform">📊</span>
                        <span className="font-semibold text-lg">طباعة لوحة التحكم</span>
                        <span className="text-sm opacity-75 mt-1">نسخة للطباعة</span>
                      </button>
                      
                      <button
                        onClick={() => window.open('/', '_blank')}
                        className="flex flex-col items-center justify-center p-6 bg-purple-50 hover:bg-purple-100 text-purple-800 rounded-xl transition-all duration-200 group"
                      >
                        <span className="text-3xl mb-3 group-hover:scale-110 transition-transform">🏠</span>
                        <span className="font-semibold text-lg">طباعة الصفحة العامة</span>
                        <span className="text-sm opacity-75 mt-1">نسخة للزوار</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Export Settings */}
                <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                    <h4 className="text-xl font-bold text-white flex items-center">
                      ⚙️ إعدادات التصدير
                    </h4>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">تنسيق التاريخ</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                          <option value="dd/mm/yyyy">DD/MM/YYYY</option>
                          <option value="mm/dd/yyyy">MM/DD/YYYY</option>
                          <option value="yyyy-mm-dd">YYYY-MM-DD</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">لغة التقارير</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                          <option value="arabic">العربية</option>
                          <option value="english">English</option>
                          <option value="both">الاثنين معاً</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="bg-orange-50 rounded-lg p-4">
                      <h5 className="font-medium text-orange-800 mb-2">💡 نصائح للتصدير:</h5>
                      <ul className="text-sm text-orange-700 space-y-1">
                        <li>• استخدم PDF للتقارير الرسمية والعروض</li>
                        <li>• استخدم Excel للتحليل والحسابات</li>
                        <li>• تأكد من البيانات قبل التصدير</li>
                        <li>• احفظ نسخة احتياطية دورياً</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}



            {/* Club Announcements Modal */}
            {showAnnouncementModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingAnnouncement ? 'تعديل إعلان النادي' : 'إضافة إعلان جديد'}
                    </h3>
                    <button
                      onClick={() => {
                        setShowAnnouncementModal(false);
                        setEditingAnnouncement(null);
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">عنوان الإعلان</label>
                      <input
                        type="text"
                        value={newAnnouncement.title}
                        onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        placeholder="عنوان الإعلان..."
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">محتوى الإعلان</label>
                      <textarea
                        value={newAnnouncement.content}
                        onChange={(e) => setNewAnnouncement({...newAnnouncement, content: e.target.value})}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        rows="4"
                        placeholder="محتوى الإعلان..."
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">الأولوية</label>
                        <select
                          value={newAnnouncement.priority}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, priority: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="low">منخفضة</option>
                          <option value="normal">عادية</option>
                          <option value="high">مهمة</option>
                          <option value="urgent">عاجلة</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">التصنيف</label>
                        <select
                          value={newAnnouncement.category}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, category: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="general">عام</option>
                          <option value="events">فعاليات</option>
                          <option value="matches">مباريات</option>
                          <option value="news">أخبار</option>
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">تاريخ البداية</label>
                        <input
                          type="date"
                          value={newAnnouncement.start_date}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, start_date: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">تاريخ النهاية</label>
                        <input
                          type="date"
                          value={newAnnouncement.end_date}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, end_date: e.target.value})}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newAnnouncement.is_active}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, is_active: e.target.checked})}
                          className="w-4 h-4 text-yellow-600 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="mr-2 text-sm text-gray-700">إعلان نشط</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newAnnouncement.show_on_homepage}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, show_on_homepage: e.target.checked})}
                          className="w-4 h-4 text-yellow-600 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="mr-2 text-sm text-gray-700">إظهار في الصفحة الرئيسية</span>
                      </label>

                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newAnnouncement.show_on_ticker}
                          onChange={(e) => setNewAnnouncement({...newAnnouncement, show_on_ticker: e.target.checked})}
                          className="w-4 h-4 text-yellow-600 bg-gray-100 border-gray-300 rounded focus:ring-yellow-500"
                        />
                        <span className="mr-2 text-sm text-gray-700">إظهار في الشريط الإخباري</span>
                      </label>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 space-x-reverse mt-6">
                    <button
                      onClick={() => {
                        setShowAnnouncementModal(false);
                        setEditingAnnouncement(null);
                      }}
                      className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      إلغاء
                    </button>
                    <button
                      onClick={handleSaveAnnouncement}
                      className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium"
                    >
                      {editingAnnouncement ? 'تحديث الإعلان' : 'إضافة الإعلان'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Loading Page Settings */}
            {activeSection === 'loading-page' && (
              <div className="space-y-8">
                <div className="border-b border-gray-200 pb-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">⏳ إعدادات صفحة التحميل</h3>
                  <p className="text-gray-600">تخصيص صفحة التحميل التي تظهر عند دخول الزوار للموقع</p>
                </div>

                {/* Loading Settings */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="text-purple-600 mr-2">⚙️</span>
                    إعدادات التحميل
                  </h4>

                  <div className="space-y-6">
                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
                      <div>
                        <span className="text-sm font-medium text-gray-900">تفعيل صفحة التحميل</span>
                        <p className="text-xs text-gray-500 mt-1">إظهار صفحة تحميل عند دخول الموقع</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={loadingPageSettings.enabled}
                          onChange={(e) => setLoadingPageSettings({...loadingPageSettings, enabled: e.target.checked})}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">العنوان الرئيسي</label>
                        <input
                          type="text"
                          value={loadingPageSettings.title}
                          onChange={(e) => setLoadingPageSettings({...loadingPageSettings, title: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="مرحباً بكم في نادي الأمجاد"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">النص الفرعي</label>
                        <input
                          type="text"
                          value={loadingPageSettings.subtitle}
                          onChange={(e) => setLoadingPageSettings({...loadingPageSettings, subtitle: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          placeholder="جاري تحميل الموقع..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">لون الخلفية</label>
                        <input
                          type="color"
                          value={loadingPageSettings.backgroundColor}
                          onChange={(e) => setLoadingPageSettings({...loadingPageSettings, backgroundColor: e.target.value})}
                          className="w-full h-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">لون النص</label>
                        <input
                          type="color"
                          value={loadingPageSettings.textColor}
                          onChange={(e) => setLoadingPageSettings({...loadingPageSettings, textColor: e.target.value})}
                          className="w-full h-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نوع الحركة</label>
                        <select
                          value={loadingPageSettings.animationType}
                          onChange={(e) => setLoadingPageSettings({...loadingPageSettings, animationType: e.target.value})}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="fade">تلاشي</option>
                          <option value="slide">انزلاق</option>
                          <option value="zoom">تكبير</option>
                          <option value="spin">دوران</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">مدة التحميل (بالميلي ثانية)</label>
                        <input
                          type="range"
                          min="1000"
                          max="10000"
                          step="500"
                          value={loadingPageSettings.duration}
                          onChange={(e) => setLoadingPageSettings({...loadingPageSettings, duration: parseInt(e.target.value)})}
                          className="w-full"
                        />
                        <div className="text-sm text-gray-500 mt-1">{loadingPageSettings.duration} ملي ثانية ({loadingPageSettings.duration / 1000} ثانية)</div>
                      </div>
                    </div>

                    {/* Preview */}
                    <div className="p-6 border-2 border-dashed border-gray-300 rounded-lg">
                      <h5 className="text-sm font-medium text-gray-700 mb-3">معاينة</h5>
                      <div 
                        className="w-full h-32 rounded-lg flex items-center justify-center text-center"
                        style={{ 
                          backgroundColor: loadingPageSettings.backgroundColor,
                          color: loadingPageSettings.textColor 
                        }}
                      >
                        <div>
                          <h4 className="text-xl font-bold mb-2">{loadingPageSettings.title}</h4>
                          <p className="text-sm">{loadingPageSettings.subtitle}</p>
                          <div className="mt-3">
                            <div className="w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin mx-auto"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const response = await axios.put(`${API}/loading-page-settings`, loadingPageSettings, {
                          headers: { 
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                          }
                        });

                        const notification = document.createElement('div');
                        notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
                        notification.textContent = 'تم حفظ إعدادات صفحة التحميل بنجاح!';
                        document.body.appendChild(notification);
                        setTimeout(() => {
                          if (document.body.contains(notification)) {
                            document.body.removeChild(notification);
                          }
                        }, 3000);

                        console.log('✅ Loading page settings saved successfully');
                      } catch (error) {
                        console.error('❌ خطأ في حفظ إعدادات صفحة التحميل:', error);
                        
                        const notification = document.createElement('div');
                        notification.className = 'fixed top-4 right-4 bg-red-500 text-white px-6 py-3 rounded-lg shadow-lg z-50';
                        notification.textContent = `خطأ: ${error.response?.data?.detail || 'فشل في حفظ الإعدادات'}`;
                        document.body.appendChild(notification);
                        setTimeout(() => {
                          if (document.body.contains(notification)) {
                            document.body.removeChild(notification);
                          }
                        }, 5000);
                      }
                    }}
                    className="px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 font-medium shadow-lg transition-all duration-300"
                  >
                    حفظ الإعدادات
                  </button>
                </div>
              </div>
            )}

            {/* Board Management Section */}
            {activeSection === 'board-management' && (
              <BoardManagementSection />
            )}
            {activeSection === 'legal-pages' && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4 flex items-center">
                    <span className="text-4xl mr-4">📄</span>
                    الصفحات القانونية
                  </h2>
                  <p className="text-gray-600">
                    إدارة محتوى سياسة الخصوصية وشروط الاستخدام
                  </p>
                </div>

                <div className="space-y-8">
                  {/* Privacy Policy */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="text-2xl mr-3">🔒</span>
                      سياسة الخصوصية
                    </h3>
                    <p className="text-gray-600 mb-4">
                      محتوى سياسة الخصوصية التي ستظهر للزوار في الصفحة العامة
                    </p>
                    <textarea
                      value={legalPages.privacy_policy}
                      onChange={(e) => setLegalPages({...legalPages, privacy_policy: e.target.value})}
                      className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
                      placeholder="اكتب نص سياسة الخصوصية هنا...

مثال:
نحن في نادي الأمجاد نلتزم بحماية خصوصية زوار موقعنا. تشرح هذه السياسة كيف نجمع ونستخدم ونحمي معلوماتك الشخصية.

المعلومات التي نجمعها:
- الاسم والبريد الإلكتروني عند التواصل معنا
- معلومات الاستخدام العامة للموقع

كيف نستخدم معلوماتك:
- للرد على استفساراتك
- لتحسين خدماتنا
- لإرسال النشرات الإخبارية (بموافقتك)

حماية المعلومات:
نستخدم تقنيات أمان متقدمة لحماية معلوماتك الشخصية..."
                    />
                  </div>

                  {/* Terms of Use */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center">
                      <span className="text-2xl mr-3">📋</span>
                      شروط الاستخدام
                    </h3>
                    <p className="text-gray-600 mb-4">
                      محتوى شروط الاستخدام التي ستظهر للزوار في الصفحة العامة
                    </p>
                    <textarea
                      value={legalPages.terms_of_use}
                      onChange={(e) => setLegalPages({...legalPages, terms_of_use: e.target.value})}
                      className="w-full h-64 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none"
                      placeholder="اكتب نص شروط الاستخدام هنا...

مثال:
بإستخدامك لموقع نادي الأمجاد، فإنك توافق على الشروط والأحكام التالية:

الاستخدام المقبول:
- يُسمح بتصفح الموقع للأغراض الشخصية والتعليمية
- يُمنع استخدام الموقع لأي أنشطة غير قانونية
- يُمنع محاولة اختراق أو إلحاق الضرر بالموقع

حقوق الملكية الفكرية:
- جميع المحتويات محمية بحقوق الطبع والنشر
- شعار النادي وعلاماته التجارية مملوكة للنادي
- يُمنع استخدام المحتوى دون إذن مسبق

المسؤولية:
- النادي غير مسؤول عن أي أضرار ناتجة عن استخدام الموقع
- المحتوى مقدم كما هو دون ضمانات..."
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-between pt-6 border-t">
                    <button
                      onClick={() => setLegalPages({privacy_policy: '', terms_of_use: ''})}
                      className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      مسح الكل
                    </button>
                    <button
                      onClick={saveLegalPages}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-3 rounded-lg font-bold transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
                    >
                      <span className="text-lg mr-2">💾</span>
                      حفظ الصفحات القانونية
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Requests Section */}
            {activeSection === 'contact-requests' && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-gray-800 mb-4 flex items-center">
                    <span className="text-4xl mr-4">📧</span>
                    طلبات التواصل
                  </h2>
                  <p className="text-gray-600">
                    إدارة الرسائل والطلبات الواردة من زوار الموقع
                  </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                  {/* Total Requests */}
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
                       onClick={() => setContactRequestsFilter('all')}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">إجمالي الطلبات</p>
                        <p className="text-2xl font-bold">{contactRequests.length}</p>
                      </div>
                      <div className="text-3xl opacity-80">📊</div>
                    </div>
                  </div>

                  {/* New Requests */}
                  <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
                       onClick={() => setContactRequestsFilter('pending')}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">طلب جديد</p>
                        <p className="text-2xl font-bold">{contactRequests.filter(r => r.status === 'pending').length}</p>
                      </div>
                      <div className="text-3xl opacity-80">🆕</div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
                       onClick={() => setContactRequestsFilter('read')}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">تم القراءة</p>
                        <p className="text-2xl font-bold">{contactRequests.filter(r => r.status === 'read').length}</p>
                      </div>
                      <div className="text-3xl opacity-80">👁️</div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
                       onClick={() => setContactRequestsFilter('replied')}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">تم الرد</p>
                        <p className="text-2xl font-bold">{contactRequests.filter(r => r.status === 'replied').length}</p>
                      </div>
                      <div className="text-3xl opacity-80">✅</div>
                    </div>
                  </div>

                  {/* Deleted Requests */}
                  <div className="bg-gradient-to-r from-gray-500 to-gray-600 rounded-xl p-6 text-white cursor-pointer hover:shadow-lg transition-shadow"
                       onClick={() => setContactRequestsFilter('deleted')}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-100 text-sm">المحذوفات</p>
                        <p className="text-2xl font-bold">{deletedRequests.length}</p>
                      </div>
                      <div className="text-3xl opacity-80">🗑️</div>
                    </div>
                  </div>
                </div>

                {/* Contact Requests List */}
                <div className="space-y-4">
                  {contactRequests.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">📭</div>
                      <h3 className="text-xl font-semibold text-gray-600 mb-2">لا توجد طلبات تواصل</h3>
                      <p className="text-gray-500">عندما يرسل الزوار رسائل عبر الموقع، ستظهر هنا</p>
                    </div>
                  ) : (
                    contactRequests.map((request) => (
                      <div key={request.id} className={`border rounded-xl p-6 transition-all hover:shadow-lg ${
                        request.status === 'pending' ? 'border-yellow-300 bg-yellow-50' :
                        request.status === 'read' ? 'border-blue-300 bg-blue-50' :
                        request.status === 'replied' ? 'border-green-300 bg-green-50' :
                        'border-gray-300 bg-gray-50'
                      }`}>
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 space-x-reverse mb-2">
                              <h3 className="text-lg font-semibold text-gray-800">{request.name}</h3>
                              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                request.status === 'pending' ? 'bg-yellow-200 text-yellow-800' :
                                request.status === 'read' ? 'bg-blue-200 text-blue-800' :
                                request.status === 'replied' ? 'bg-green-200 text-green-800' :
                                'bg-gray-200 text-gray-800'
                              }`}>
                                {request.status === 'pending' ? 'في الانتظار' :
                                 request.status === 'read' ? 'تم القراءة' :
                                 request.status === 'replied' ? 'تم الرد' :
                                 request.status}
                              </span>
                            </div>
                            <p className="text-gray-600 text-sm mb-1">📧 {request.email}</p>
                            {request.phone && <p className="text-gray-600 text-sm mb-1">📱 {request.phone}</p>}
                            <p className="text-gray-500 text-xs">
                              📅 {new Date(request.created_at).toLocaleDateString('ar-SA')} في {new Date(request.created_at).toLocaleTimeString('ar-SA')}
                            </p>
                          </div>
                          <div className="flex space-x-2 space-x-reverse">
                            {request.status === 'pending' && (
                              <button
                                onClick={() => updateContactRequestStatus(request.id, 'read')}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                              >
                                وضع علامة مقروء
                              </button>
                            )}
                            {request.status === 'read' && (
                              <button
                                onClick={() => updateContactRequestStatus(request.id, 'replied')}
                                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                              >
                                وضع علامة مجاب
                              </button>
                            )}
                            <button
                              onClick={() => deleteContactRequest(request.id)}
                              className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                            >
                              حذف
                            </button>
                          </div>
                        </div>
                        
                        <div className="border-t pt-4">
                          <h4 className="font-semibold text-gray-800 mb-2">📝 {request.subject}</h4>
                          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{request.message}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Reports, Statistics, Printing & Export Section - Professional */}
            {activeSection === 'unified-reports' && (
              <Reports 
                reportSettings={reportSettings}
                setReportSettings={setReportSettings}
                matches={matches}
                players={players}
                teams={teams}
                tournaments={tournaments}
                clubInfo={clubSettings}
                categories={categories}
              />
            )}

          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAddUserModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md animate-in fade-in" onClick={(e) => e.stopPropagation()} style={{fontFamily: 'Cairo, Tajawal, sans-serif'}}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h2 data-testid="add-user-modal-title" className="text-lg font-bold text-slate-900">إضافة مستخدم جديد</h2>
              <button onClick={() => setShowAddUserModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <form onSubmit={handleAddUser} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">اسم المستخدم</label>
                <input
                  data-testid="add-user-username"
                  type="text"
                  required
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                  placeholder="أدخل اسم المستخدم"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">كلمة المرور</label>
                <input
                  data-testid="add-user-password"
                  type="password"
                  required
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                  placeholder="أدخل كلمة المرور"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">الاسم الكامل</label>
                <input
                  data-testid="add-user-fullname"
                  type="text"
                  required
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                  placeholder="أدخل الاسم الكامل"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">البريد الإلكتروني</label>
                <input
                  data-testid="add-user-email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                  placeholder="اختياري"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">الدور</label>
                <select
                  data-testid="add-user-role"
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 bg-white transition-all"
                >
                  <option value="analyst">محلل</option>
                  <option value="coach">جهاز فني</option>
                  <option value="medical">جهاز طبي</option>
                  <option value="manager">إداري</option>
                  <option value="admin">مشرف</option>
                  <option value="editor">محرر</option>
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  data-testid="add-user-submit"
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white py-2.5 px-4 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 shadow-sm"
                >
                  {saving ? 'جاري الإضافة...' : 'إضافة المستخدم'}
                </button>
                <button
                  data-testid="add-user-cancel"
                  type="button"
                  onClick={() => setShowAddUserModal(false)}
                  className="flex-1 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 py-2.5 px-4 rounded-lg font-medium text-sm transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Professional Edit User Modal */}
      {showEditUserModal && editingUser && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => { setShowEditUserModal(false); setEditingUser(null); }}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} style={{fontFamily: 'Cairo, Tajawal, sans-serif'}}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white font-bold text-sm">
                  {editingUser.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div>
                  <h2 data-testid="edit-user-modal-title" className="text-lg font-bold text-slate-900">تعديل المستخدم</h2>
                  <p className="text-xs text-slate-400">{editingUser.username}</p>
                </div>
              </div>
              <button
                data-testid="edit-user-close"
                onClick={() => { setShowEditUserModal(false); setEditingUser(null); }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>

            <form onSubmit={handleUpdateUser} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info Column */}
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>
                    المعلومات الأساسية
                  </h3>
                  
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">اسم المستخدم</label>
                    <input
                      data-testid="edit-user-username"
                      type="text"
                      required
                      value={editingUser.username}
                      onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">الاسم الكامل</label>
                    <input
                      data-testid="edit-user-fullname"
                      type="text"
                      required
                      value={editingUser.full_name}
                      onChange={(e) => setEditingUser({ ...editingUser, full_name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">البريد الإلكتروني</label>
                    <input
                      data-testid="edit-user-email"
                      type="email"
                      value={editingUser.email || ''}
                      onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                      placeholder="البريد الإلكتروني"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1.5">كلمة المرور الجديدة</label>
                    <input
                      data-testid="edit-user-password"
                      type="password"
                      value={editingUser.password}
                      onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                      className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all"
                      placeholder="اتركه فارغاً لعدم التغيير"
                    />
                    <p className="text-[11px] text-slate-400 mt-1">اتركه فارغاً إذا كنت لا تريد تغيير كلمة المرور</p>
                  </div>

                  <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      data-testid="edit-user-active-toggle"
                      type="checkbox"
                      checked={editingUser.is_active}
                      onChange={(e) => setEditingUser({ ...editingUser, is_active: e.target.checked })}
                      className="w-4 h-4 text-orange-500 bg-white border-slate-300 rounded focus:ring-orange-500 focus:ring-2"
                    />
                    <div>
                      <span className="text-sm font-medium text-slate-900">حساب نشط</span>
                      <p className="text-[11px] text-slate-400">تفعيل أو إيقاف حساب المستخدم</p>
                    </div>
                  </label>
                </div>

                {/* Role Column */}
                <div className="space-y-4">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                    الدور والصلاحيات
                  </h3>

                  <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
                    {Object.entries(ROLE_TEMPLATES).map(([roleKey, roleData]) => (
                      <label
                        key={roleKey}
                        data-testid={`edit-user-role-${roleKey}`}
                        className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all duration-150 ${
                          editingUser.role === roleKey
                            ? 'border-orange-400 bg-orange-50/50 ring-1 ring-orange-200'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <input
                          type="radio"
                          name="role"
                          value={roleKey}
                          checked={editingUser.role === roleKey}
                          onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                          className="w-4 h-4 text-orange-500 border-slate-300 focus:ring-orange-500"
                        />
                        <div className={`w-8 h-8 ${roleData.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <span className="text-white text-sm">{roleData.icon}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-900">{roleData.name}</div>
                          <div className="text-[11px] text-slate-400 truncate">{roleData.description}</div>
                        </div>
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full flex-shrink-0">
                          {roleData.permissions.length}
                        </span>
                      </label>
                    ))}
                  </div>

                  {editingUser.role && ROLE_TEMPLATES[editingUser.role] && (
                    <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                      <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">صلاحيات هذا الدور</h4>
                      <div className="flex flex-wrap gap-1.5">
                        {ROLE_TEMPLATES[editingUser.role].permissions.map((permission, idx) => (
                          <span
                            key={idx}
                            className="text-[10px] px-2 py-0.5 bg-white text-slate-600 rounded-full border border-slate-200"
                          >
                            {permission.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-slate-100">
                <button
                  data-testid="edit-user-cancel"
                  type="button"
                  onClick={() => { setShowEditUserModal(false); setEditingUser(null); }}
                  className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg px-5 py-2.5 font-medium text-sm transition-colors"
                >
                  إلغاء
                </button>
                <button
                  data-testid="edit-user-submit"
                  type="submit"
                  className="bg-[#F97316] hover:bg-[#EA580C] text-white rounded-lg px-6 py-2.5 font-medium text-sm shadow-sm transition-colors"
                >
                  حفظ التغييرات
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Role Modal */}
      {showRoleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">إضافة دور جديد</h2>
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                يمكنك إنشاء أدوار مخصصة بصلاحيات محددة لتناسب احتياجات النادي.
              </p>
            </div>
            
            <form onSubmit={handleAddRole} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم الدور</label>
                <input
                  type="text"
                  required
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-green-500 focus:border-green-500"
                  placeholder="مثل: مدير التسويق، مساعد مدرب"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">وصف الدور</label>
                <textarea
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-green-500 focus:border-green-500"
                  placeholder="وصف مختصر لمسؤوليات هذا الدور"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">الصلاحيات</label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                  {[
                    { id: 'view_dashboard', label: 'عرض لوحة التحكم' },
                    { id: 'manage_players', label: 'إدارة اللاعبين' },
                    { id: 'manage_matches', label: 'إدارة المباريات' },
                    { id: 'manage_categories', label: 'إدارة الفئات' },
                    { id: 'manage_teams', label: 'إدارة الفرق' },
                    { id: 'view_statistics', label: 'عرض الإحصائيات' },
                    { id: 'manage_news', label: 'إدارة الأخبار' },
                    { id: 'manage_settings', label: 'إدارة الإعدادات' },
                    { id: 'manage_users', label: 'إدارة المستخدمين' },
                    { id: 'export_data', label: 'تصدير البيانات' }
                  ].map((permission) => (
                    <label key={permission.id} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newRole.permissions.includes(permission.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setNewRole({
                              ...newRole,
                              permissions: [...newRole.permissions, permission.id]
                            });
                          } else {
                            setNewRole({
                              ...newRole,
                              permissions: newRole.permissions.filter(p => p !== permission.id)
                            });
                          }
                        }}
                        className="rounded border-gray-300 text-green-600 focus:ring-green-500 ml-2"
                      />
                      <span className="text-sm text-gray-700">{permission.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'جاري الإضافة...' : 'إضافة الدور'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowRoleModal(false)}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Feed Modal */}
      {showAddFeedModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">إضافة مصدر أخبار جديد</h2>
            <form onSubmit={handleAddFeed} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم المصدر *</label>
                <input
                  type="text"
                  required
                  value={newFeed.name}
                  onChange={(e) => setNewFeed({ ...newFeed, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-green-500 focus:border-green-500"
                  placeholder={newFeed.type === 'x_account' ? "مثال: حساب النادي الرسمي" : "مثال: أخبار الرياضة السعودية"}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">نوع المصدر</label>
                <select
                  value={newFeed.type}
                  onChange={(e) => {
                    setNewFeed({ 
                      ...newFeed, 
                      type: e.target.value,
                      url: '',
                      x_username: ''
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-green-500 focus:border-green-500"
                >
                  <option value="rss">🔗 RSS Feed</option>
                  <option value="atom">⚛️ Atom Feed</option>
                  <option value="x_account">𝕏 حساب X (تويتر)</option>
                </select>
              </div>

              {newFeed.type === 'x_account' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">اسم المستخدم في X *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">@</span>
                    <input
                      type="text"
                      required
                      value={newFeed.x_username}
                      onChange={(e) => {
                        const username = e.target.value.replace('@', '');
                        setNewFeed({ ...newFeed, x_username: username });
                      }}
                      className="w-full px-3 py-2 pl-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-green-500 focus:border-green-500"
                      placeholder="اسم المستخدم بدون @"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    💡 سيتم جلب آخر 5 تغريدات من هذا الحساب
                  </p>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {newFeed.type === 'atom' ? 'رابط Atom *' : 'رابط RSS *'}
                  </label>
                  <input
                    type="url"
                    required
                    value={newFeed.url}
                    onChange={(e) => setNewFeed({ ...newFeed, url: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-green-500 focus:border-green-500"
                    placeholder={newFeed.type === 'atom' ? "https://example.com/atom.xml" : "https://example.com/rss.xml"}
                  />
                </div>
              )}
              
              <div className="flex space-x-2 space-x-reverse pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'جاري الإضافة...' : 'إضافة المصدر'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddFeedModal(false);
                    setNewFeed({
                      name: '',
                      url: '',
                      type: 'rss',
                      x_username: ''
                    });
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add News Modal */}
      {showAddNewsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">إنشاء خبر جديد</h2>
            <form onSubmit={handleAddNews} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">عنوان الخبر *</label>
                <input
                  type="text"
                  required
                  value={newNews.title}
                  onChange={(e) => setNewNews({ ...newNews, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="عنوان الخبر..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ملخص الخبر</label>
                <textarea
                  value={newNews.summary}
                  onChange={(e) => setNewNews({ ...newNews, summary: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ملخص مختصر للخبر..."
                  rows={2}
                />
              </div>

              {/* Multiple Images Section */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">📸 صور الخبر (يمكن إضافة عدة صور)</label>
                
                <div className="bg-gray-50 p-4 rounded-xl mb-4">
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-bold text-gray-600 mb-2">رابط الصورة (URL):</label>
                      <div className="flex items-center space-x-2 space-x-reverse">
                        <input
                          type="text"
                          value={newsImageUrlInput}
                          onChange={(e) => setNewsImageUrlInput(e.target.value)}
                          className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none"
                          placeholder="https://example.com/image.jpg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            if (newsImageUrlInput.trim()) {
                              setNewNews({ 
                                ...newNews, 
                                images: [...newNews.images, newsImageUrlInput.trim()] 
                              });
                              setNewsImageUrlInput('');
                            }
                          }}
                          className="bg-blue-600 text-white px-6 py-2 rounded-lg font-bold hover:bg-blue-700 whitespace-nowrap"
                        >
                          + إضافة
                        </button>
                      </div>
                    </div>
                    
                    <div className="text-center text-gray-500 font-bold">أو</div>
                    
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleNewsImageUpload}
                        className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg"
                        id="news-image-upload"
                      />
                    </div>
                  </div>
                </div>

                {/* Images List */}
                {newNews.images && newNews.images.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 font-bold">الصور المضافة ({newNews.images.length}):</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {newNews.images.map((imageUrl, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={imageUrl}
                            alt={`صورة ${index + 1}`}
                            className="w-full h-24 object-cover rounded-lg border-2 border-gray-200"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect fill="%23ddd" width="100" height="100"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" fill="%23999"%3E❌%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = newNews.images.filter((_, i) => i !== index);
                              setNewNews({ ...newNews, images: newImages });
                            }}
                            className="absolute top-1 right-1 bg-red-600 text-white w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-xs font-bold"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">محتوى الخبر *</label>
                <textarea
                  value={newNews.content}
                  onChange={(e) => setNewNews({ ...newNews, content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="اكتب المحتوى التفصيلي للخبر هنا..."
                  rows={8}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">التصنيف</label>
                <select
                  value={newNews.category}
                  onChange={(e) => setNewNews({ ...newNews, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="general">عام</option>
                  <option value="match_results">نتائج المباريات</option>
                  <option value="transfers">انتقالات</option>
                  <option value="announcements">إعلانات</option>
                </select>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={newNews.published}
                    onChange={(e) => setNewNews({ ...newNews, published: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 ml-2"
                  />
                  <span className="text-sm font-medium text-gray-700">نشر الخبر فوراً</span>
                </label>
              </div>

              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'جاري الإنشاء...' : 'إنشاء الخبر'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddNewsModal(false);
                    setNewNews({
                      title: '',
                      content: '',
                      summary: '',
                      category: 'general',
                      published: false,
                      tags: []
                    });
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Tournament Modal */}
      {showAddTournamentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">🏆 إضافة بطولة جديدة</h2>
            <form onSubmit={handleAddTournamentNew} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم البطولة *</label>
                <input
                  type="text"
                  required
                  value={newTournament.name}
                  onChange={(e) => setNewTournament({ ...newTournament, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="مثال: كأس الملك سلمان"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نوع البطولة</label>
                  <select
                    value={newTournament.tournament_type}
                    onChange={(e) => setNewTournament({ ...newTournament, tournament_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="league">🏆 دوري</option>
                    <option value="cup">🥇 كأس</option>
                    <option value="friendly">🤝 ودية</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">الفئة *</label>
                  <select
                    required
                    value={newTournament.category_id}
                    onChange={(e) => setNewTournament({ ...newTournament, category_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">اختر الفئة</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ البدء *</label>
                  <input
                    type="date"
                    required
                    value={newTournament.start_date}
                    onChange={(e) => setNewTournament({ ...newTournament, start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">تاريخ الانتهاء</label>
                  <input
                    type="date"
                    value={newTournament.end_date}
                    onChange={(e) => setNewTournament({ ...newTournament, end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">المكان</label>
                <input
                  type="text"
                  value={newTournament.location}
                  onChange={(e) => setNewTournament({ ...newTournament, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="مثال: ملعب الملك فهد الدولي"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">وصف البطولة</label>
                <textarea
                  value={newTournament.description}
                  onChange={(e) => setNewTournament({ ...newTournament, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="وصف مختصر للبطولة..."
                  rows={3}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">حالة البطولة</label>
                <select
                  value={newTournament.status}
                  onChange={(e) => setNewTournament({ ...newTournament, status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="upcoming">📅 قادمة</option>
                  <option value="ongoing">▶️ جارية</option>
                  <option value="completed">✅ مكتملة</option>
                  <option value="cancelled">❌ ملغية</option>
                </select>
              </div>

              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'جاري الإضافة...' : 'إضافة البطولة'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTournamentModal(false);
                    setNewTournament({
                      name: '',
                      tournament_type: 'league',
                      category_id: '',
                      start_date: '',
                      end_date: '',
                      location: '',
                      description: '',
                      status: 'upcoming'
                    });
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit News Modal */}
      {showEditNewsModal && editingNews && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">تعديل الخبر</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateNews(editingNews.id, {
                title: editingNews.title,
                content: editingNews.content,
                summary: editingNews.summary,
                category: editingNews.category,
                published: editingNews.published,
                image_file_id: editingNews.image_file_id
              });
              setShowEditNewsModal(false);
              setEditingNews(null);
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">عنوان الخبر *</label>
                <input
                  type="text"
                  required
                  value={editingNews.title}
                  onChange={(e) => setEditingNews({ ...editingNews, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="عنوان الخبر..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ملخص الخبر</label>
                <textarea
                  value={editingNews.summary || ''}
                  onChange={(e) => setEditingNews({ ...editingNews, summary: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="ملخص مختصر للخبر..."
                  rows={2}
                />
              </div>

              {/* صورة الخبر الحالية والجديدة */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">صورة الخبر</label>
                
                {/* عرض الصورة الحالية */}
                {editingNews.image_file_id && !editingNews.newImagePreview && (
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 mb-2">الصورة الحالية:</p>
                    <div className="relative inline-block">
                      <img
                        src={`${API}/files/${editingNews.image_file_id}`}
                        alt="الصورة الحالية"
                        className="w-32 h-32 object-cover rounded-lg border shadow-md"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-32 h-32 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg items-center justify-center shadow-md">
                        <span className="text-white text-2xl">📰</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setEditingNews({ 
                            ...editingNews, 
                            image_file_id: null,
                            newImage: null,
                            newImagePreview: null 
                          });
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
                
                {/* معاينة الصورة الجديدة */}
                {editingNews.newImagePreview && (
                  <div className="mb-3">
                    <p className="text-sm text-green-600 mb-2">الصورة الجديدة:</p>
                    <div className="relative inline-block">
                      <img
                        src={editingNews.newImagePreview}
                        alt="معاينة الصورة الجديدة"
                        className="w-32 h-32 object-cover rounded-lg border shadow-md"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setEditingNews({ 
                            ...editingNews, 
                            newImage: null, 
                            newImagePreview: null 
                          });
                        }}
                        className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold transition-colors"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
                
                {/* رفع صورة جديدة */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          setEditingNews({
                            ...editingNews,
                            newImage: file,
                            newImagePreview: e.target.result
                          });
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                    id="edit-news-image"
                  />
                  <label
                    htmlFor="edit-news-image"
                    className="cursor-pointer flex flex-col items-center justify-center space-y-2"
                  >
                    <div className="text-gray-400 text-3xl">📷</div>
                    <div className="text-sm text-gray-600">
                      {editingNews.image_file_id || editingNews.newImagePreview 
                        ? 'استبدال الصورة' 
                        : 'إضافة صورة جديدة'
                      }
                    </div>
                    <div className="text-xs text-gray-400">PNG, JPG أو WEBP (حد أقصى 5MB)</div>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">محتوى الخبر *</label>
                <textarea
                  value={editingNews.content}
                  onChange={(e) => setEditingNews({ ...editingNews, content: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  placeholder="عدّل المحتوى التفصيلي للخبر..."
                  rows={8}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">التصنيف</label>
                <select
                  value={editingNews.category}
                  onChange={(e) => setEditingNews({ ...editingNews, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="general">عام</option>
                  <option value="match_results">نتائج المباريات</option>
                  <option value="transfers">انتقالات</option>
                  <option value="announcements">إعلانات</option>
                </select>
              </div>

              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingNews.published}
                    onChange={(e) => setEditingNews({ ...editingNews, published: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 ml-2"
                  />
                  <span className="text-sm font-medium text-gray-700">نشر الخبر</span>
                </label>
              </div>

              <div className="flex space-x-4 space-x-reverse">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  {saving ? 'جاري التحديث...' : 'تحديث الخبر'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowEditNewsModal(false);
                    setEditingNews(null);
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sponsor Modal */}
      {showSponsorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingSponsor ? '✏️ تعديل راعي' : '🤝 إضافة راعي جديد'}
              </h2>
            </div>
            
            <form onSubmit={(e) => { e.preventDefault(); handleSaveSponsor(); }} className="p-6 space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  اسم الراعي <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newSponsor.name}
                  onChange={(e) => setNewSponsor({...newSponsor, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="مثال: شركة النجوم"
                  required
                />
              </div>

              {/* Logo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  شعار الراعي
                </label>
                {newSponsor.logo_file_id && (
                  <div className="mb-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <img
                      src={`${API}/files/${newSponsor.logo_file_id}`}
                      alt="Preview"
                      className="max-h-32 mx-auto object-contain"
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleUploadSponsorLogo(e.target.files[0]);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                />
                <p className="text-xs text-gray-500 mt-1">الشعار سيظهر في الشريط المتحرك</p>
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رابط الموقع الإلكتروني
                </label>
                <input
                  type="url"
                  value={newSponsor.website_url}
                  onChange={(e) => setNewSponsor({...newSponsor, website_url: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="https://example.com"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  وصف مختصر
                </label>
                <textarea
                  value={newSponsor.description}
                  onChange={(e) => setNewSponsor({...newSponsor, description: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="نبذة عن الراعي..."
                />
              </div>

              {/* Active Status */}
              <div className="flex items-center space-x-3 space-x-reverse">
                <input
                  type="checkbox"
                  id="sponsor-active"
                  checked={newSponsor.is_active}
                  onChange={(e) => setNewSponsor({...newSponsor, is_active: e.target.checked})}
                  className="w-5 h-5 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <label htmlFor="sponsor-active" className="text-sm font-medium text-gray-700">
                  نشط (سيظهر في الشريط المتحرك)
                </label>
              </div>

              {/* Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الترتيب
                </label>
                <input
                  type="number"
                  value={newSponsor.order}
                  onChange={(e) => setNewSponsor({...newSponsor, order: parseInt(e.target.value) || 0})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">ترتيب العرض في الشريط (0 = الأول)</p>
              </div>

              {/* Actions */}
              <div className="flex space-x-3 space-x-reverse pt-4 border-t border-gray-200">
                <button
                  type="submit"
                  className="flex-1 bg-orange-600 hover:bg-orange-700 text-white py-3 px-6 rounded-lg font-medium transition-colors shadow-md hover:shadow-lg"
                >
                  {editingSponsor ? '💾 حفظ التعديلات' : '➕ إضافة الراعي'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowSponsorModal(false);
                    setEditingSponsor(null);
                    setNewSponsor({
                      name: '',
                      logo_file_id: null,
                      logo_url: '',
                      website_url: '',
                      description: '',
                      is_active: true,
                      order: 0
                    });
                  }}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-3 px-6 rounded-lg font-medium transition-colors"
                >
                  ✕ إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}


    </div>
  );
};

// Helper functions for sections management
const getSectionIcon = (sectionId) => {
  const icons = {
    hero: '🎨',
    achievements: '🏆', 
    news: '📰',
    matches: '⚽',
    tweets: '🐦',
    images: '🖼️'
  };
  return icons[sectionId] || '📋';
};

const getSectionDescription = (sectionId) => {
  const descriptions = {
    hero: 'القسم الرئيسي مع العنوان والوصف وآخر الأخبار',
    achievements: 'إنجازات وإحصائيات النادي',
    news: 'آخر الأخبار والمقالات (مرتبط تلقائياً)',
    matches: 'المباراة القادمة والنتائج (مرتبط تلقائياً)',
    tweets: 'آخر التغريدات من حسابات التويتر',
    images: 'معرض الصور والفيديوهات'
  };
  return descriptions[sectionId] || 'قسم قابل للتخصيص';
};

// Wrapper Components for Management Pages
const FeatureBoxesManagerWrapper = () => {
  const token = localStorage.getItem('token');
  return <FeatureBoxesManager token={token} />;
};

const StrategyManagerWrapper = () => {
  const token = localStorage.getItem('token');
  return <StrategyManager token={token} />;
};

const ShopManagerWrapper = () => {
  const token = localStorage.getItem('token');
  return <ShopManager token={token} />;
};

// Main App Component
function App() {
  useEffect(() => {
    // Apply saved font on app load
    const savedFont = localStorage.getItem('arabicFont') || 'Cairo';
    const style = document.createElement('style');
    style.textContent = `
      * {
        font-family: '${savedFont}', system-ui, -apple-system, sans-serif !important;
      }
      body, html, #root, .App {
        font-family: '${savedFont}', system-ui, -apple-system, sans-serif !important;
      }
      input, textarea, select, button {
        font-family: '${savedFont}', system-ui, -apple-system, sans-serif !important;
      }
    `;
    const existingStyle = document.getElementById('arabic-font-style');
    if (existingStyle) {
      existingStyle.remove();
    }
    style.id = 'arabic-font-style';
    document.head.appendChild(style);

    // Set simple title for public page
    document.title = 'نادي الأمجاد';
    
    // Update title when data changes
    const updatePublicTitle = () => {
      document.title = 'نادي الأمجاد';
    };
    
    window.addEventListener('logoUpdated', updatePublicTitle);
    window.addEventListener('refreshPublicData', updatePublicTitle);
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<ProfessionalLandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/board-of-directors" element={<BoardOfDirectorsSection />} />
              
              {/* Protected Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute>
                  <AppLayout>
                    <Dashboard />
                  </AppLayout>
                </ProtectedRoute>
              } />

              <Route path="/activity-log" element={
                <ProtectedRoute>
                  <ActivityLog />
                </ProtectedRoute>
              } />
            
            <Route path="/categories" element={
              <ProtectedRoute>
                <AppLayout>
                  <Categories />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/admin/players" element={
              <ProtectedRoute>
                <AppLayout>
                  <Players />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/teams" element={
              <ProtectedRoute>
                <AppLayout>
                  <SimpleTeamsComponent API={API} />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/matches" element={
              <ProtectedRoute>
                <AppLayout>
                  <Matches />
                </AppLayout>
              </ProtectedRoute>
            } />

            <Route path="/tournaments" element={
              <ProtectedRoute>
                <AppLayout>
                  <TournamentsSection />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/scorers" element={
              <ProtectedRoute>
                <AppLayout>
                  <UnifiedPlayersSection />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/governance-strategy-pro" element={
              <ProtectedRoute>
                <AppLayout>
                  <GovernanceStrategySectionPro />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/team-formation" element={
              <ProtectedRoute>
                <AppLayout>
                  <TeamFormationComponent />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/achievements" element={
              <ProtectedRoute>
                <AppLayout>
                  <SimpleAchievementManager />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Jobs Management Route */}
            <Route path="/jobs-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <JobsManagementSection API={API} />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Public Careers Page */}
            <Route path="/careers" element={
              <CareersPage API={API} />
            } />

            {/* Employee Verification Page */}
            <Route path="/verify/:employeeId" element={<EmployeeVerifyPage />} />
            
            {/* Employee Registration Page */}
            <Route path="/register-employee" element={<EmployeeRegistrationPage />} />
            <Route path="/register-player" element={<PlayerRegistrationPage />} />
            
            {/* Employee Profile/Card Page */}
            <Route path="/card/:employeeId" element={<EmployeeProfilePage />} />

            
            {/* Public All Players Page */}
            <Route path="/players" element={
              <AllPlayersPage API={API} />
            } />
            
            {/* Strategy Page */}
            <Route path="/strategy" element={
              <StrategyPage />
            } />
            
            {/* Shop Page */}
            <Route path="/shop" element={
              <ShopPage />
            } />
            
            {/* Feature Boxes Management */}
            <Route path="/feature-boxes" element={
              <ProtectedRoute>
                <AppLayout>
                  <FeatureBoxesManagerWrapper />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Strategy Management */}
            <Route path="/strategy-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <StrategyManagerWrapper />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            {/* Shop Management */}
            <Route path="/shop-management" element={
              <ProtectedRoute>
                <AppLayout>
                  <ShopManagerWrapper />
                </AppLayout>
              </ProtectedRoute>
            } />
            
            <Route path="/settings" element={
              <ProtectedRoute>
                <AppLayout>
                  <Settings />
                </AppLayout>
              </ProtectedRoute>
            } />
          </Routes>
          </Suspense>
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
